import BdFiInspectionView from './BdFiInspection';
import StageMatrixView from './StageMatrix';
import SaftaUpdateView from './SaftaUpdate';
import ManualRoutingUpdateView from './ManualRoutingUpdate';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { BarcodeFormat, DecodeHintType } from '@zxing/library';
import { api, track, toggleTheme } from '../App';
import ExportBar, { canSchedule } from '../components/ExportBar';
import SchedulesView from './Schedules';
import SizeRatioView from './SizeRatio';
import CriticalStagesBanner from '../components/CriticalStagesBanner';
import { canSee, factoryRightKey } from '../rights';
import { DailyProductionScreen } from './Production';
import GmtTrainingModal from './GmtTrainingModal';

// ---- Export column descriptors (key = row field, type: text|int|number|money|pct|date, total = sum in footer)
const C = (key, label, type = 'text', total = false) => ({ key, label, type, total });
const ORDER_COLS = [C('tas_no', 'Order No'), C('buyer', 'Buyer'), C('style_name', 'Style'), C('factory', 'Factory'), C('merchandiser', 'Merchandiser'), C('orderqty', 'Order Qty', 'int', true), C('bal_qty', 'Balance Qty', 'int', true), C('order_value', 'Value', 'money', true), C('completion_pct', 'Done %', 'pct'), C('Delay_days', 'Delay Days', 'int'), C('delivery_dt', 'Delivery', 'date'), C('health', 'Health')];
const BD_LIST_COLS = [C('order_no', 'TAS'), C('buyer', 'Buyer'), C('merchandiser', 'Merchandiser'), C('pcs', 'Pcs', 'int', true), C('pending_pcs', 'Pending Pcs', 'int', true), C('value', 'Value', 'money', true), C('delivery_dt', 'Delivery', 'date'), C('tecpac', 'Tech Pack', 'int'), C('specs', 'Specs', 'int'), C('bom', 'BOM', 'int'), C('costsheet_pending', 'Cost Sheet Pending', 'int', true)];
const BD_SUM_COLS = [C('key', 'Bucket'), C('count', 'Orders', 'int', true), C('pcs', 'Pcs', 'int', true), C('pending_pcs', 'Pending Pcs', 'int', true), C('value', 'Value', 'money', true), C('costsheet_pending', 'Cost Sheet Pending', 'int', true)];
const TAS_STATUS_COLS = [C('tas_no', 'TAS'), C('buyer', 'Buyer'), C('status', 'Status'), C('order_pcs', 'Order Pcs', 'int', true), C('delivered_pcs', 'Delivered Pcs', 'int', true), C('invoiced', 'Invoiced', 'int', true), C('delivery_dt', 'Delivery', 'date')];
const FABRIC_ROUTING_COLS = [C('TasNo', 'TAS'), C('customer', 'Customer'), C('fabric_name', 'Fabric'), C('CurrentPendingStage', 'Pending Stage'), C('stage_eta', 'Stage Est. Date', 'date'), C('meters', 'Meters', 'number', true), C('CurrentDelay', 'Delay', 'int'), C('NewDeliveryDate', 'New Delivery', 'date')];
const GSP_ROUTING_COLS = [C('GspNo', 'GSP'), C('customer', 'Customer'), C('fabric_name', 'Fabric'), C('CurrentPendingStage', 'Pending Stage'), C('stage_eta', 'Stage Est. Date', 'date'), C('meters', 'Meters', 'number', true), C('CurrentDelay', 'Delay', 'int'), C('new_delivery', 'New Delivery', 'date')];
// TAS carries profit; the fabric (GSP) endpoints have no profit data at all, so a
// shared column set left a permanently blank GP% column. avg_rate is absent from
// the buyer/month endpoints and is derived from amount / qty instead.
const SALES_TAS_COLS = [C('label', 'Name'), C('invoice_count', 'Invoices', 'int', true), C('total_qty', 'Qty', 'int', true), C('total_amount', 'Amount', 'money', true), C('avg_rate', 'Avg Rate', 'number'), C('total_profit', 'GP', 'money', true), C('avg_gp_pct', 'GP %', 'pct')];
const SALES_GSP_COLS = [C('label', 'Name'), C('invoice_count', 'Invoices', 'int', true), C('total_qty', 'Qty (mtrs)', 'number', true), C('total_amount', 'Amount', 'money', true), C('avg_rate', 'Avg Rate', 'number')];
const MON3 = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// The same two computations the server repeats for scheduled runs (reports/filters.js).
const salesRow = r => {
  const qty = Number(r.total_qty) || 0, amt = Number(r.total_amount) || 0;
  const label = r.buyer || r.month_label || r.key || r.quality || r.design || r.name ||
    (r.yr && r.mo >= 1 && r.mo <= 12 ? `${MON3[r.mo - 1]}/${r.yr}` : '');
  return { ...r, label, avg_rate: r.avg_rate != null ? r.avg_rate : (qty ? amt / qty : null) };
};
const SALES_DERIVE = [
  { as: 'label', coalesce: ['buyer', 'month_label', 'key', 'quality', 'design', 'name'], monthFrom: { yr: 'yr', mo: 'mo' } },
  { as: 'avg_rate', ratio: { num: 'total_amount', den: 'total_qty' } },
];
// A '?from=&to=' string into the params object a schedule stores.
const qsToParams = qs => {
  const p = {};
  String(qs || '').replace(/^[?&]+/, '').split('&').filter(Boolean).forEach(kv => {
    const i = kv.indexOf('=');
    if (i > 0) p[kv.slice(0, i)] = decodeURIComponent(kv.slice(i + 1));
  });
  delete p._;
  return p;
};
const TAS_INV_COLS = [C('invoiceno', 'Invoice No'), C('invoicedate', 'Invoice Dt', 'date'), C('buyer', 'Buyer'), C('tas_no', 'TAS'), C('styles', 'Style(s)'), C('shades', 'Shade(s)'), C('merchendiser', 'Merch.'), C('qty', 'Qty', 'int', true), C('Inv_rate', 'Rate', 'number'), C('amount', 'Amount', 'money', true), C('ProfitAmount', 'GP', 'money', true), C('gp_pct', 'GP %', 'pct')];
const GSP_INV_COLS = [C('invoiceno', 'Invoice No'), C('invoicedate', 'Invoice Dt', 'date'), C('buyer', 'Buyer'), C('brand', 'Brand'), C('gspno', 'GSP No'), C('line_items', 'Items', 'int'), C('qty', 'Qty (mtrs)', 'number', true), C('Inv_rate', 'Rate', 'number'), C('Cost_per_unit', 'Cost/mtr', 'number'), C('amount', 'Amount', 'money', true), C('ProfitAmount', 'GP', 'money', true), C('gp_pct', 'GP %', 'pct'), C('Est_cost_per_unit', 'Est Cost/mtr', 'number'), C('est_gp_pct', 'Est %', 'pct')];
const TAS_COST_COLS = [C('Tas_No', 'TAS'), C('buyer', 'Buyer'), C('factory', 'Factory'), C('est_pct', 'Est %', 'pct'), C('sc_pct', 'SC %', 'pct'), C('inv_pct', 'Inv %', 'pct')];
const GSP_COST_COLS = [C('GspNo', 'GSP'), C('buyer', 'Buyer'), C('brand', 'Factory'), C('est_pct', 'Est %', 'pct'), C('inv_pct', 'Inv %', 'pct')];
const PROD_COLS = [C('tas_no', 'TAS'), C('status', 'Status'), C('buyer', 'Buyer'), C('factory', 'Factory'), C('merchandiser', 'Merchandiser'), C('order', 'Order', 'int', true), C('cut', 'Cut', 'int', true), C('input', 'Input', 'int', true), C('output', 'Output', 'int', true), C('ready', 'Ready', 'int', true), C('ready_pct', 'Ready %', 'pct'), C('stage_delay', 'Stage Delay', 'int')];
const PREORDER_COLS = [C('tas_no', 'TAS'), C('buyer', 'Buyer'), C('style_name', 'Style'), C('preorder_pending_count', 'Pending Stages', 'int', true), C('preorder_pending_names', 'Pending Stage Names')];
const OTIF_MONTH_COLS = [C('month', 'Month'), C('order_qty', 'Order Qty', 'int', true), C('delivered_qty', 'Delivered Qty', 'int', true), C('infull_pct', 'InFull %', 'pct'), C('on_time', 'On Time', 'int', true), C('delayed', 'Delayed', 'int', true), C('ontime_pct', 'OnTime %', 'pct')];
const OTIF_MONTH_COLS_SRV = [C('yr', 'Year'), C('mo', 'Month'), ...OTIF_MONTH_COLS.slice(1)];
const OTIF_ROW_COLS = [C('tas_no', 'Order No'), C('buyer', 'Buyer'), C('style_name', 'Style'), C('order_dt', 'Order Dt', 'date'), C('delivery_dt', 'Delivery Dt', 'date'), C('invoice_dt', 'Invoice Dt', 'date'), C('orderqty', 'Order Qty', 'int', true), C('dspqty', 'Disp Qty', 'int', true), C('fill_rate', 'Fill %', 'pct'), C('days_late', 'Days Late', 'int'), C('otif', 'Status')];
const DEV_COLS = [C('name', 'Silhouette'), C('season', 'Season'), C('product', 'Product'), C('designer', 'Designer'), C('mapping_count', 'Mappings', 'int', true), C('total_qty', 'Qty', 'int', true), C('days_since_created', 'Days Old', 'int')];
const BUYER_HEALTH_COLS = [C('buyer', 'Buyer'), C('tas_count', 'TAS', 'int', true), C('gsp_count', 'GSP', 'int', true), C('tas_overdue', 'TAS Overdue', 'int', true), C('gsp_overdue', 'GSP Overdue', 'int', true), C('pending_pcs', 'Pending Pcs', 'int', true), C('pending_mtrs', 'Pending Mtrs', 'int', true)];

// Normalize human-readable values to Title Case so the UI renders consistently
// regardless of how the ERP stored them (UPPERCASE / Proper Case / mixed).
// Leaves part-codes (alphanumeric with digits, no spaces, len>6) alone.
function titleCase(s) {
  if (!s) return s;
  const str = String(s);
  if (!/\s/.test(str) && /[0-9]/.test(str) && str.length > 6) return str;
  return str.toLowerCase().replace(/\b[a-z]/g, c => c.toUpperCase());
}

function fmt(n) { return (n || 0).toLocaleString('en-IN'); }

// Format any ISO YYYY-MM-DD (or datetime) string as DD-MM-YYYY for display.
function fmtDMY(s) {
  if (!s) return '';
  const str = String(s).trim().slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : str;
}

// Sort by TAS number numerically — handles "TAS/123", "TAS.123", "TAS/123 B" etc.
// Extracts all digit-runs and compares as numbers; ties fall back to lexical.
function tasSortKey(tas) {
  const s = String(tas || '');
  return (s.match(/\d+/g) || []).map(Number);
}
function compareTas(a, b) {
  const ka = tasSortKey(a.tas_no);
  const kb = tasSortKey(b.tas_no);
  for (let i = 0; i < Math.max(ka.length, kb.length); i++) {
    const x = ka[i] ?? 0, y = kb[i] ?? 0;
    if (x !== y) return x - y;
  }
  return String(a.tas_no || '').localeCompare(String(b.tas_no || ''));
}
function fmtCr(n) {
  if (!n) return '₹0';
  const cr = n / 10000000;
  if (cr >= 1) return `₹${cr.toFixed(2)} Cr`;
  return `₹${(n / 100000).toFixed(1)} L`;
}
function fmtPct(n) { return (n == null || isNaN(n)) ? '—' : `${n.toFixed(1)}%`; }
// Rates / per-unit costs carry paise (339.60, 332.95) — never round those away or the
// figure stops matching the ERP screen. Whole numbers stay clean (no trailing ".00").
function fmtRate(n) {
  if (n == null || isNaN(n)) return '—';
  const v = Number(n);
  return `₹${v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function pctClass(n) {
  if (n == null) return 'var(--text3)';
  if (n >= 20) return 'var(--green)';
  if (n >= 10) return 'var(--yellow)';
  if (n >= 0)  return 'var(--text3)';
  return 'var(--red)';
}

// Indian financial year of today (e.g., '2627' for Apr-2026 → Mar-2027)
function currentFY() {
  const now = new Date();
  const startYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return String(startYear).slice(2) + String(startYear + 1).slice(2);
}
// Apr 1 → Mar 31 ISO date strings for the current FY
function currentFYRange() {
  const n = new Date();
  const sy = n.getMonth() >= 3 ? n.getFullYear() : n.getFullYear() - 1;
  return { from: `${sy}-04-01`, to: `${sy + 1}-03-31` };
}

// ============================================================
// IN DEVELOPMENT view — forecasting entries (inv_forecaste + det)
// ============================================================
// ============================================================
// ORDER TAKING (form 8133 "Order through Sample Garment").
// Flow: pick CLIENT (debtor) → scan the sample's QR/barcode → each scan pulls
// the full Magic Link data (sp_GetMagicLinkData fields) into a grid, qty 1.
// Order date = today (auto). Save → ord_sampleorder(+_det).
// ============================================================
// Header notification bell — per-user feed with server-side read tracking.
// Admins see all alerts; other users see broadcasts + their own overdue orders.
// Opening the panel marks the visible unread items read (persists across devices).
// Order alerts deep-link via onOrderClick; the daily summary (link "view:home")
// jumps to the War Room via onNavigate.
function NotificationBell({ onOrderClick, onNavigate, btnStyle }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const load = () => api('/notifications')
    .then(d => { if (d && Array.isArray(d.items)) { setItems(d.items); setUnread(d.unread || 0); } })
    .catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 90000); return () => clearInterval(t); }, []);
  function toggle() {
    const willOpen = !open; setOpen(willOpen);
    if (willOpen) {
      const unreadIds = items.filter(i => !i.is_read).map(i => i.id);
      if (unreadIds.length) {
        api('/notifications/read', { method: 'POST', body: JSON.stringify({ ids: unreadIds }) }).catch(() => {});
        setItems(prev => prev.map(i => ({ ...i, is_read: true })));
        setUnread(0);
      }
    }
  }
  function clickItem(i) {
    if (!i.link) return;
    if (String(i.link).startsWith('view:')) { onNavigate && onNavigate(i.link.slice(5)); }
    else if (onOrderClick) { onOrderClick({ tas_no: i.link }); }
    setOpen(false);
  }
  const sevColor = s => s === 'red' ? 'var(--red)' : s === 'amber' ? 'var(--yellow)' : 'var(--teal)';
  const ago = ts => {
    if (!ts) return '';
    const d = new Date(String(ts).replace(' ', 'T'));
    const m = Math.floor((Date.now() - d.getTime()) / 60000);
    if (m < 1) return 'now'; if (m < 60) return m + 'm';
    const h = Math.floor(m / 60); if (h < 24) return h + 'h';
    return Math.floor(h / 24) + 'd';
  };
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button onClick={toggle} title="Notifications"
        style={{ ...(btnStyle || { background: 'none', border: '1px solid var(--border2)', color: 'var(--text3)', borderRadius: 4, cursor: 'pointer', height: 24, boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }), fontSize: 13, padding: '0 8px', position: 'relative' }}>
        🔔
        {unread > 0 && (
          <span style={{ position: 'absolute', top: -6, right: -6, minWidth: 15, height: 15, padding: '0 3px', borderRadius: 8, background: 'var(--red)', color: '#fff', fontSize: 9, fontWeight: 700, lineHeight: '15px', textAlign: 'center' }}>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 140 }} />
          <div style={{ position: 'fixed', top: 64, right: 10, width: 'min(320px, calc(100vw - 20px))', maxHeight: '72vh', overflowY: 'auto', zIndex: 141,
            background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: 'var(--shadow)', textAlign: 'left' }}>
            <div style={{ padding: '9px 12px', borderBottom: '1px solid var(--border)', fontSize: 10, letterSpacing: 1.5, color: 'var(--text3)', fontWeight: 600 }}>NOTIFICATIONS</div>
            {items.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>No notifications</div>
            ) : items.map(i => (
              <div key={i.id} onClick={() => clickItem(i)}
                style={{ padding: '9px 12px', borderBottom: '1px solid var(--border)', cursor: i.link ? 'pointer' : 'default',
                  background: i.is_read ? 'transparent' : 'var(--gold-bg)', display: 'flex', gap: 9 }}>
                <span style={{ width: 7, height: 7, borderRadius: 4, background: sevColor(i.severity), marginTop: 5, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{i.title}</span>
                    {!i.is_read && <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: 0.5, color: '#fff', background: 'var(--red)', borderRadius: 3, padding: '0 4px' }}>NEW</span>}
                  </div>
                  {i.body && <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 1 }}>{i.body}</div>}
                  <div style={{ fontSize: 9, color: 'var(--text3)', marginTop: 2 }}>
                    {ago(i.created_at)}{i.is_read ? ' · ✓ read' : ''}{i.link ? ' · tap to open' : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function OrderTakingView({ Header, onBack }) {
  const [clients, setClients] = useState([]);
  const [partyid, setPartyid] = useState('');
  const [deliveryDt, setDeliveryDt] = useState('');
  const [lines, setLines] = useState([]);      // full magic-link row per scan
  const [scan, setScan] = useState('');
  const [msg, setMsg] = useState(null);         // {t:'ok'|'err', text}
  const [saving, setSaving] = useState(false);
  const [recent, setRecent] = useState([]);
  const [scanning, setScanning] = useState(false);   // camera open?
  const [flash, setFlash] = useState(null);           // barcode just added → brief row highlight
  const [scanFlash, setScanFlash] = useState(null);   // 'ok' | 'err' | null — flashes scanner box border + bg on every scan result (iOS-friendly visual cue when silent mode silences the beep)
  const [lastSaved, setLastSaved] = useState(null);   // snapshot of the most recently saved order { orderId, entryNo, buyerName, deliveryDt, lines, savedAt } — drives the post-save "Order saved · 000005" line + Print button.
  const scanBlockRef = useRef(null);                  // scroll target — when user taps Scan, page jumps the camera box to the top of the viewport so the items table below is immediately visible without manual scrolling.
  const accent = 'var(--gold)';
  const orderDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  // Refs so the camera callback (created once) always sees the latest values.
  const clientRef = useRef(false);
  const qrRef = useRef(null);
  const lastScanRef = useRef({ code: '', t: 0 });
  const flashTimer = useRef(null);
  const audioRef = useRef(null);
  // Haptic feedback — Android Chrome only (iOS Safari ignores vibrate). Guarded so it never throws.
  const buzz = (p) => { try { if (navigator.vibrate) navigator.vibrate(p); } catch (e) {} };
  // Audio beep — works on iOS + Android. AudioContext must be unlocked by a user gesture
  // (we resume it on the Scan-button tap / Enter), then later scan callbacks can beep.
  function ensureAudio() {
    try {
      if (!audioRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx) audioRef.current = new Ctx();
      }
      if (audioRef.current && audioRef.current.state === 'suspended') audioRef.current.resume();
    } catch (e) {}
  }
  function beep(freq, dur) {
    try {
      const ac = audioRef.current; if (!ac) return;
      const o = ac.createOscillator(), g = ac.createGain();
      o.type = 'sine'; o.frequency.value = freq;
      o.connect(g); g.connect(ac.destination);
      const t = ac.currentTime;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.3, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur / 1000);
      o.start(t); o.stop(t + dur / 1000 + 0.02);
    } catch (e) {}
  }
  // Success = single buzz + high beep + green flash; reject = double buzz + low beep + red flash.
  // Bumped haptic from 70 → 150 ms so it's perceptible on Android.
  // scanFlash gives iOS a strong VISUAL cue (silent-mode safe, since iOS Safari
  // ignores navigator.vibrate completely and may have audio muted).
  const flashScanner = (kind, ms) => {
    setScanFlash(kind);
    setTimeout(() => setScanFlash(prev => (prev === kind ? null : prev)), ms);
  };
  const feedbackOk  = () => { buzz(150);             beep(880, 110); flashScanner('ok',  400); };
  const feedbackErr = () => { buzz([110, 70, 110]);  beep(300, 180); flashScanner('err', 600); };
  useEffect(() => () => { if (flashTimer.current) clearTimeout(flashTimer.current); }, []);
  const clientChosen = !!partyid;
  useEffect(() => { clientRef.current = !!partyid; }, [partyid]);

  const loadRecent = () => api('/orders/sample-orders').then(d => setRecent(Array.isArray(d) ? d : []));
  useEffect(() => {
    api('/orders/buyers').then(d => setClients(Array.isArray(d) ? d : []));
    loadRecent();
  }, []);

  // Closure-safe: duplicate + client checks use refs / functional updates so it
  // works from both the text field and the (long-lived) camera callback.
  function addScan(raw) {
    const bc = String(raw || '').trim();
    if (!bc) return;
    if (!clientRef.current) { setMsg({ t: 'err', text: 'Select a client first' }); return; }
    api(`/orders/scan/${encodeURIComponent(bc)}`).then(d => {
      if (d && d.barcode) {
        setLines(prev => {
          if (prev.some(l => l.barcode === d.barcode)) {
            setMsg({ t: 'err', text: `Already added: ${d.barcode}` });
            feedbackErr();        // double buzz + low beep = duplicate/reject
            return prev;
          }
          setMsg({ t: 'ok', text: `✓ Added ${d.style || bc}` });
          feedbackOk();           // buzz + high beep = success
          setFlash(d.barcode);    // highlight the new row briefly
          if (flashTimer.current) clearTimeout(flashTimer.current);
          flashTimer.current = setTimeout(() => setFlash(null), 1500);
          return [{ ...d }, ...prev];
        });
      } else { setMsg({ t: 'err', text: `Barcode not found: ${bc}` }); feedbackErr(); }
    }).catch(() => { setMsg({ t: 'err', text: `Barcode not found: ${bc}` }); feedbackErr(); });
  }
  const removeLine = bc => setLines(prev => prev.filter(l => l.barcode !== bc));

  // When user taps Scan, jump the scanner block to the top of the viewport so
  // the items table directly below it is visible as scans accumulate. Run on
  // the next paint so the camera div has its final height.
  useEffect(() => {
    if (!scanning) return;
    const id = setTimeout(() => {
      try { scanBlockRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {}
    }, 120);
    return () => clearTimeout(id);
  }, [scanning]);

  // Camera scanner — ZXing BrowserMultiFormatReader.
  // Swapped from html5-qrcode (WASM decoder, unreliable on iOS 16 Safari) to
  // @zxing/browser which uses the pure-JS port of the Java ZXing decoder.
  // ZXing has built-in frame-rotation tolerance, accepts low-contrast frames
  // when TRY_HARDER is on, and is the reference impl iOS Safari scanners
  // converge to. Same scan callback semantics (buzz + addScan).
  useEffect(() => {
    if (!scanning) return;
    let cancelled = false;
    let controls = null;
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.CODE_128, BarcodeFormat.QR_CODE]);
    hints.set(DecodeHintType.TRY_HARDER, true);

    const reader = new BrowserMultiFormatReader(hints, {
      delayBetweenScanAttempts: 80,    // ~12 attempts/sec
      delayBetweenScanSuccess:  1500,  // dedupe identical reads
    });
    qrRef.current = reader;

    (async () => {
      try {
        // Prefer an explicit back-camera deviceId. iOS Safari's
        // facingMode:'environment' sometimes picks the front camera with rotated
        // frames the decoder can't handle. Listing devices needs camera permission
        // first — granted when reader.decodeFromVideoDevice runs below.
        let deviceId;
        try {
          const cams = await BrowserMultiFormatReader.listVideoInputDevices();
          const back = cams.find(c => /back|rear|environment/i.test(c.label || ''));
          deviceId = back ? back.deviceId : (cams[cams.length - 1]?.deviceId);
        } catch (_) { /* fall through — let zxing pick */ }

        const videoEl = document.getElementById('qr-cam-video');
        if (!videoEl) throw new Error('video element #qr-cam-video not in DOM');
        // iOS attributes (also set on the JSX side, but defensive here in case React rerendered):
        videoEl.setAttribute('playsinline', 'true');
        videoEl.setAttribute('webkit-playsinline', 'true');
        videoEl.muted = true;

        controls = await reader.decodeFromVideoDevice(deviceId, videoEl, (result /*, err */) => {
          if (cancelled || !result) return;
          const code = String(result.getText() || '').trim();
          if (!code) return;
          const now = Date.now();
          if (code === lastScanRef.current.code && now - lastScanRef.current.t < 1800) return;
          lastScanRef.current = { code, t: now };
          buzz(40); // instant read tick

          // If the QR decodes to a magicLinkView URL, resolve to the underlying
          // barcode via the API (the QR encodes a numeric token id, not the
          // barcode itself). Otherwise treat as a raw scanned barcode.
          const magicMatch = code.match(/magicLinkView\.php\?id=([^&\s]+)/i);
          if (magicMatch) {
            let tokenId = null;
            try {
              // The `id` param is base64 of the numeric token id (e.g. "MTE0"
              // → "114", "OTk~" → "99"). The PHP encoder maps `=` padding to
              // `~` to make the value URL-safe, and uses standard alphabet
              // otherwise. Reverse: `~` → `=`, also handle URL-safe variants
              // (- → +, _ → /).
              const b64 = decodeURIComponent(magicMatch[1])
                .replace(/~/g, '=')
                .replace(/-/g, '+')
                .replace(/_/g, '/');
              const decoded = atob(b64);
              tokenId = parseInt(decoded, 10);
            } catch (e) { /* fall through */ }
            if (!tokenId) {
              setMsg({ t: 'err', text: 'QR not recognized' });
              return;
            }
            api(`/orders/resolve-magic-token/${tokenId}`).then(r => {
              if (r?.barcode) addScan(r.barcode);
              else            setMsg({ t: 'err', text: r?.error || 'QR not recognized' });
            }).catch(() => setMsg({ t: 'err', text: 'Network error resolving QR' }));
            return;
          }

          addScan(code);
        });
      } catch (err) {
        if (!cancelled) {
          console.error('[zxing scanner]', err);
          setMsg({ t: 'err', text: 'Camera unavailable: ' + (err?.message || err) });
          setScanning(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      try { controls?.stop(); } catch (e) {}
      try {
        const v = document.getElementById('qr-cam-video');
        if (v?.srcObject) v.srcObject.getTracks().forEach(t => t.stop());
      } catch (e) {}
    };
  }, [scanning]);

  function save() {
    if (!clientChosen) { setMsg({ t: 'err', text: 'Select a client first' }); return; }
    if (!lines.length) { setMsg({ t: 'err', text: 'Scan at least one garment' }); return; }
    setSaving(true);
    // Snapshot the inputs BEFORE clearing them, so the post-save Print button
    // still has the items + buyer + delivery to render in the popup.
    const snapshotLines = lines.map(l => ({ ...l }));
    const snapshotPartyid = partyid;
    const snapshotDelivery = deliveryDt;
    const buyerName = clients.find(c => String(c.id) === String(snapshotPartyid))?.name || '';
    api('/orders/sample-order', { method: 'POST', body: JSON.stringify({
      partyid: parseInt(snapshotPartyid), delivery_dt: snapshotDelivery || null,
      lines: snapshotLines.map(l => ({ barcode: l.barcode, silhouetteid: l.silhouetteid })),
    }) }).then(r => {
      setSaving(false);
      if (r && r.success) {
        const saved = {
          orderId:  r.orderId,
          entryNo:  r.entryNo || String(r.orderId),
          buyerName,
          deliveryDt: snapshotDelivery,
          lines: snapshotLines,
          savedAt: new Date(),
        };
        setLastSaved(saved);
        setMsg({ t: 'ok', text: `✓ Order #${saved.entryNo} saved · ${r.lines} item${r.lines !== 1 ? 's' : ''}` });
        setLines([]); setPartyid(''); setDeliveryDt(''); setScanning(false);
        loadRecent();
      } else setMsg({ t: 'err', text: r?.error || 'Save failed' });
    }).catch(() => { setSaving(false); setMsg({ t: 'err', text: 'Save failed' }); });
  }

  // Print via a dedicated OFF-SCREEN iframe that holds its OWN complete
  // document, then call print() on the IFRAME's window.
  //
  // Why not inject into the main page (the previous approach): on Android
  // Chrome window.print() hands the job to the system print service and
  // returns immediately — and `afterprint`/focus fire right away too, not
  // when the dialog closes. So ANY "hide the app, show a print canvas,
  // restore afterwards" scheme restores the app BEFORE Android's async print
  // preview captures the DOM → the preview shows the live app instead of the
  // catalogue/receipt (exactly the reported bug).
  //
  // An iframe with its own document sidesteps this entirely: the main page is
  // never hidden or modified, and printing the iframe's window prints ONLY
  // the iframe's document. The app UI is structurally excluded from the job,
  // so there is nothing to "restore" and nothing to race. focus() before
  // print() makes iOS Safari print the iframe rather than the parent.
  function printViaIframe(html) {
    try {
      document.getElementById('dj-print-frame')?.remove();

      const iframe = document.createElement('iframe');
      iframe.id = 'dj-print-frame';
      iframe.setAttribute('aria-hidden', 'true');
      // Real (large) dimensions so iOS renders & prints the iframe, but parked
      // far off-screen so nothing flashes on the main screen.
      iframe.style.cssText =
        'position:fixed; left:-10000px; top:0; width:1200px; height:900px; border:0; opacity:0; pointer-events:none;';
      document.body.appendChild(iframe);

      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(html);
      doc.close();

      const win = iframe.contentWindow;
      let fired = false;
      const fire = () => {
        if (fired) return; fired = true;
        try {
          win.focus();      // iOS: print the iframe's doc, not the parent
          win.print();
        } catch (e) {
          setMsg({ t: 'err', text: 'Print failed: ' + e.message });
        }
        // The iframe is invisible and off-screen, so it's harmless to leave in
        // place until well after any dialog interaction. Remove it later.
        setTimeout(() => { try { iframe.remove(); } catch (_) {} }, 60000);
      };

      // Wait for catalogue images so photos are present in the print. Remote
      // images can be slow, so cap the wait.
      const imgs = Array.from(doc.images || []);
      if (imgs.length === 0) { setTimeout(fire, 150); return; }
      let pending = imgs.length;
      const tick = () => { if (--pending <= 0) fire(); };
      imgs.forEach(img => {
        if (img.complete) tick();
        else {
          img.addEventListener('load',  tick, { once: true });
          img.addEventListener('error', tick, { once: true });
        }
      });
      setTimeout(fire, 4000); // hard cap
    } catch (err) {
      setMsg({ t: 'err', text: 'Print failed: ' + (err?.message || err) });
    }
  }

  // Open the server-generated PDF (catalogue or order receipt) in a new tab.
  // The device's native PDF viewer gives the user Print AND Share for free —
  // far more reliable than window.print() on mobile, which Android/iOS handle
  // inconsistently. Auth travels as a ?token= query param because a new-tab
  // navigation can't send the Authorization header. Must run inside the click
  // handler (a real user gesture) so mobile doesn't block the new tab.
  function openPdf(orderId, kind) {
    if (!orderId) { setMsg({ t: 'err', text: 'No order to open' }); return; }
    const token = localStorage.getItem('dj_token') || '';
    const file = kind === 'catalogue' ? 'catalogue.pdf' : 'receipt.pdf';
    const url = `/api/orders/sample-order/${orderId}/${file}?token=${encodeURIComponent(token)}`;
    try { track('print_' + kind, { orderId }); } catch {}
    // Open the PDF in a new tab → native PDF viewer (Print + Share built in).
    // If the new tab is popup-blocked (some mobile webviews), fall back to
    // navigating the current tab to the PDF.
    const w = window.open(url, '_blank');
    if (!w || w.closed || typeof w.closed === 'undefined') {
      window.location.href = url;
    }
  }

  // Build a printable HTML receipt from the saved snapshot — header + items +
  // footer. Pure client-side (no extra API call); the data was already in
  // memory at save time. Renders into a hidden iframe (no pop-up) so mobile
  // browsers' pop-up blockers can't kill it.
  function printOrder(o) {
    if (!o) return;
    const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
    const fmt = d => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
    const rowsHtml = o.lines.map((l, i) => `
      <tr>
        <td>${i + 1}</td>
        <td class="bc">${esc(l.barcode)}</td>
        <td>${esc(l.style || '')}</td>
        <td>${esc(l.product_category || '')}</td>
        <td>${esc(l.sub_category || '')}</td>
        <td>${esc(l.sizes || '')}</td>
        <td>${esc(l.fabric_code || '')}</td>
        <td>${esc(l.shade || '')}</td>
        <td class="r">${l.gmt_cost ? 'Rs ' + esc(l.gmt_cost) : ''}</td>
      </tr>`).join('');
    const html = `<!doctype html><html><head><meta charset="utf-8">
<title>Sample Order #${esc(o.entryNo)}</title>
<style>
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif; color: #111; margin: 0; padding: 0; }
  .hdr { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #1a237e; padding-bottom: 10px; margin-bottom: 14px; }
  .hdr h1 { font-size: 18px; margin: 0; color: #1a237e; letter-spacing: 0.5px; }
  .hdr .sub { font-size: 11px; color: #666; margin-top: 4px; }
  .meta { font-size: 12px; }
  .meta b { display: inline-block; min-width: 90px; color: #555; font-weight: 500; }
  table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 11px; }
  th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; vertical-align: middle; }
  th { background: #f3f4f6; font-weight: 600; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: #444; }
  td.bc { font-family: ui-monospace, Menlo, Consolas, monospace; font-weight: 600; color: #1a237e; }
  td.r { text-align: right; }
  tr:nth-child(even) td { background: #fafafa; }
  .ft { display: flex; justify-content: space-between; margin-top: 22mm; font-size: 11px; color: #555; }
  .ft .box { border-top: 1px solid #999; padding-top: 4px; min-width: 180px; text-align: center; }
  @media print { body { margin: 0; } }
</style></head>
<body>
  <div class="hdr">
    <div>
      <h1>SAMPLE ORDER</h1>
      <div class="sub">Order through Sample Garment · FormID 8133</div>
    </div>
    <div class="meta" style="text-align:right">
      <div><b>Order No</b> #${esc(o.entryNo)}</div>
      <div><b>Order Date</b> ${fmt(o.savedAt)}</div>
      <div><b>Delivery</b> ${fmt(o.deliveryDt)}</div>
    </div>
  </div>
  <div class="meta" style="margin-bottom:8px;"><b>Buyer</b> ${esc(o.buyerName || '—')}</div>
  <table>
    <thead>
      <tr>
        <th style="width:36px">#</th>
        <th style="width:110px">Barcode</th>
        <th>Style</th>
        <th>Category</th>
        <th>Sub-Category</th>
        <th>Size</th>
        <th>Fabric</th>
        <th>Shade</th>
        <th style="width:110px" class="r">GMT Cost</th>
      </tr>
    </thead>
    <tbody>
      ${rowsHtml || '<tr><td colspan="9" style="text-align:center;color:#888;padding:14px">No items</td></tr>'}
    </tbody>
  </table>
  <div class="ft">
    <div class="box">Prepared by</div>
    <div class="box">Authorised Signatory</div>
  </div>
</body></html>`;
    printViaIframe(html);
  }

  // Fetch a full saved order by id (header + line specs + image URLs) and pipe
  // it into printOrder() or printCatalogue(). Used by the Print / Catalogue
  // icon buttons in the Recent Orders list — lets users reprint historical
  // orders without re-scanning. The /api/orders/sample-order/:id response shape
  // is already compatible with what printOrder/printCatalogue expect.
  function fetchAndPrint(orderId, kind) {
    setMsg({ t: 'ok', text: `Loading order #${orderId}…` });
    api(`/orders/sample-order/${orderId}`).then(o => {
      // Defensive: log what came back so we can diagnose blank popups in the
      // browser console (Settings → Inspect → Console on iOS Safari, or
      // Chrome devtools on Android).
      console.log('[fetchAndPrint] orderId=' + orderId + ' kind=' + kind + ' response:', o);
      if (!o) {
        setMsg({ t: 'err', text: 'Empty response — endpoint might not be deployed' });
        return;
      }
      if (o.error) { setMsg({ t: 'err', text: 'API: ' + o.error }); return; }
      if (!o.orderId) { setMsg({ t: 'err', text: 'Order not found (no orderId in response)' }); return; }
      if (!Array.isArray(o.lines) || o.lines.length === 0) {
        setMsg({ t: 'err', text: `Order #${o.entryNo || orderId} has no items` });
        return;
      }
      const snapshot = {
        orderId:    o.orderId,
        entryNo:    o.entryNo,
        buyerName:  o.buyerName,
        deliveryDt: o.deliveryDt,
        savedAt:    o.savedAt,
        lines:      o.lines,
      };
      setMsg({ t: 'ok', text: `Opening ${kind === 'catalogue' ? 'catalogue' : 'print'} for Order #${o.entryNo || o.orderId} · ${o.lines.length} items` });
      if (kind === 'catalogue') printCatalogue(snapshot); else printOrder(snapshot);
    }).catch(err => {
      console.error('[fetchAndPrint] error:', err);
      setMsg({ t: 'err', text: 'Failed to load order — ' + (err?.message || 'network error') });
    });
  }

  // Buyer-facing CATALOGUE — one page per scanned garment (silhouette image +
  // spec card), bookended by a cover page and a Thank You closer. Mirrors the
  // PowerPoint format used for client visits (e.g. "PETER ENGLAND CLIENT VISIT").
  // Image source: image_url from /orders/scan (resolves inv_garmentmap.img1 →
  // kmainmedia.ClientFilePath, served from mfgen.mindforgeerp.com).
  function printCatalogue(o) {
    if (!o) return;
    const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
    const fmt = d => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase() : '—';
    const upper = s => String(s ?? '').toUpperCase();
    const buyer = upper(o.buyerName || 'CLIENT');
    const visitDate = fmt(o.savedAt);

    // TAS logo as inline SVG — no external image dep, scales crisply when
    // printed. Approximation of the bitmap the user supplied: filled black
    // circle, white "TAS" text, thin horizontal line below.
    const tasLogoSvg = `<svg class="pg-logo" viewBox="0 0 200 240" xmlns="http://www.w3.org/2000/svg">
      <circle cx="100" cy="100" r="90" fill="#000"/>
      <text x="100" y="124" text-anchor="middle" font-family="Arial Black, Helvetica, sans-serif" font-weight="900" font-size="62" fill="#fff" letter-spacing="-2">TAS</text>
      <line x1="40" y1="210" x2="160" y2="210" stroke="#000" stroke-width="4" stroke-linecap="square"/>
    </svg>`;
    const leadTime = '120-150 Days';

    const pagesHtml = o.lines.map(l => {
      const hasImg = l.image_url && /^https?:\/\//i.test(l.image_url);
      return `
      <section class="pg garment">
        ${tasLogoSvg}
        <div class="pg-header">
          <div class="pg-title">${esc(upper(l.style || 'Untitled Garment'))}</div>
        </div>
        <div class="pg-body">
          <div class="pg-img">
            ${hasImg
              ? `<img src="${esc(l.image_url)}" alt="${esc(l.style || '')}" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" />
                 <div class="pg-img-fallback" style="display:none">NO IMAGE</div>`
              : `<div class="pg-img-fallback">NO IMAGE</div>`}
          </div>
          <table class="spec">
            <tr><td class="lbl">DESIGN NO.</td><td class="val">${esc(l.fabric_code || '—')}</td></tr>
            <tr><td class="lbl">GMT COST</td><td class="val green">${l.gmt_cost ? 'Rs. ' + esc(l.gmt_cost) + ' / PC' : '—'}</td></tr>
            <tr><td class="lbl">BLEND</td><td class="val">${esc(l.blend || '—')}</td></tr>
            <tr><td class="lbl">WEIGHT</td><td class="val">${l.weight ? esc(l.weight) + ' GSM' : '—'}</td></tr>
            <tr><td class="lbl">WIDTH</td><td class="val">${l.width ? esc(l.width) + ' CM' : '—'}</td></tr>
            <tr><td class="lbl moq" rowspan="2">MOQ</td>
                <td class="val">${l.moq_color ? esc(l.moq_color) + ' PCS / PER COLOUR' : '—'}</td></tr>
            <tr><td class="val">${l.moq_style ? esc(l.moq_style) + ' PCS / PER DESIGN' : '—'}</td></tr>
            <tr><td class="lbl">LEAD TIME</td><td class="val">${esc(leadTime)}</td></tr>
          </table>
        </div>
        <div class="pg-foot">Barcode <b>${esc(l.barcode)}</b></div>
      </section>`;
    }).join('');

    const html = `<!doctype html><html><head><meta charset="utf-8">
<title>${esc(buyer)} · Client Visit ${esc(visitDate)}</title>
<style>
  @page { size: A4 landscape; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: 'Helvetica Neue', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; color: #111; background: #555; }

  /* Page shell */
  section.pg { position: relative; width: 297mm; height: 210mm; padding: 9mm 14mm; background: #fff;
               page-break-after: always; break-after: page; display: flex; flex-direction: column;
               box-shadow: 0 2px 10px rgba(0,0,0,0.4); margin: 12px auto; overflow: hidden; }
  section.pg:last-child { page-break-after: auto; }

  /* TAS logo — sits in the top-right of every page */
  .pg-logo { position: absolute; top: 8mm; right: 12mm; width: 18mm; height: auto; z-index: 2; }

  /* ───────── COVER (minimal monochrome — REEBOK style) ───────── */
  section.pg.cover { background: #f0f1f2; padding: 0; justify-content: flex-start; }
  .cover-inner { padding: 38mm 22mm 0 22mm; }
  .cover h1 { font-size: 78px; margin: 0; color: #000; letter-spacing: -1px; font-weight: 900;
              line-height: 1.0; text-transform: uppercase; max-width: 80%; }
  .cover .presented { font-size: 26px; color: #111; font-weight: 400; margin-top: 28mm;
                      letter-spacing: 0; }
  .cover .sel-count { font-size: 13px; letter-spacing: 4px; color: #222; font-weight: 500;
                      text-transform: uppercase; position: absolute; bottom: 22mm; left: 22mm; }
  .cover .logo-cap { position: absolute; top: 12mm; right: 16mm; text-align: center; }
  .cover .logo-cap svg { width: 22mm; height: auto; }
  .cover .logo-cap .cap-line { width: 22mm; height: 1px; background: #000; margin: 3px auto 4px; }
  .cover .logo-cap .cap-txt  { font-size: 8px; letter-spacing: 3px; color: #111;
                               text-transform: uppercase; font-weight: 600; }

  /* ───────── GARMENT PAGE ───────── */
  .garment .pg-header { display: flex; align-items: baseline; justify-content: space-between;
                        padding-right: 24mm; /* leave room for logo */
                        border-bottom: 2px solid #c9a23d; padding-bottom: 6px; margin-bottom: 5mm; }
  .pg-title { font-size: 28px; font-weight: 800; color: #0d1b3d; letter-spacing: 2px;
              text-transform: uppercase; flex: 1; }
  .pg-eyebrow { font-size: 10px; letter-spacing: 6px; color: #999; text-transform: uppercase; margin-right: 6px; }

  /* image + spec centered as one cohesive block. Portrait product shots fill
     full HEIGHT (their max size); centering the group kills the dead gap that
     appears when image hugs left and spec hugs right */
  .pg-body { display: flex; gap: 14mm; flex: 1; align-items: stretch; justify-content: center; min-height: 0; }
  .pg-img { flex: 0 1 auto; min-width: 0; display: flex; align-items: center; justify-content: center; overflow: hidden; }
  .pg-img img { height: 100%; width: auto; max-width: 100%; max-height: 100%; object-fit: contain; }
  .pg-img-fallback { color: #ccc; font-size: 13px; letter-spacing: 3px; text-transform: uppercase; }

  table.spec { flex: 0 0 60mm; align-self: center; border-collapse: collapse; font-size: 13px; }
  table.spec td { padding: 9px 10px; border: none !important; border-bottom: 1px solid #eee !important; vertical-align: middle; background: transparent !important; }
  table.spec tr:last-child td { border-bottom: none !important; }
  table.spec td.lbl,
  table.spec td.moq { font-weight: 600; color: #888; width: 22mm; letter-spacing: 0.5px;
                      text-transform: uppercase; font-size: 10px; background: transparent !important; border-left: none !important; }
  table.spec td.val { font-weight: 700; color: #0d1b3d; }
  table.spec td.val.green { color: #1a7a2e; font-size: 16px; font-weight: 800; }

  .pg-foot { text-align: right; font-size: 9.5px; color: #b0b0b0; letter-spacing: 2px; margin-top: 6px;
             text-transform: uppercase; }
  .pg-foot b { color: #0d1b3d; font-weight: 600; }

  /* ───────── THANK YOU ───────── */
  .thanks { justify-content: center; align-items: center; text-align: center; }
  .thanks .brand { font-size: 12px; letter-spacing: 8px; color: #999; margin-bottom: 22px; text-transform: uppercase; }
  .thanks h2 { font-size: 72px; margin: 0; color: #0d1b3d; letter-spacing: 8px; font-weight: 800; }
  .thanks .ruler { width: 80px; height: 3px; background: #c9a23d; margin: 24px auto 0; }
  .thanks .closing { font-size: 13px; letter-spacing: 4px; color: #888; margin-top: 24px; }

  @media print {
    body { background: #fff; }
    section.pg { box-shadow: none; margin: 0; }
  }
</style></head>
<body>

  <!-- Cover page — REEBOK-style minimal layout -->
  <section class="pg cover">
    <div class="logo-cap">
      <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
        <circle cx="100" cy="100" r="90" fill="#000"/>
        <text x="100" y="124" text-anchor="middle" font-family="Arial Black, Helvetica, sans-serif" font-weight="900" font-size="62" fill="#fff" letter-spacing="-2">TAS</text>
      </svg>
      <div class="cap-line"></div>
      <div class="cap-txt">By Rishab World</div>
    </div>
    <div class="cover-inner">
      <h1>${esc(buyer)}</h1>
      <div class="presented">Presented to : ${esc(o.buyerName || 'Buyer')}</div>
    </div>
    <div class="sel-count">SELECTIONS DONE TOTAL &mdash; ${o.lines.length}</div>
  </section>

  <!-- Garment pages — header bar wraps the title to leave room for the logo. -->
  ${pagesHtml.replace(/<section class="pg garment">/g, '<section class="pg garment">') /* no-op placeholder for future find/replace */}

  <!-- Thank You -->
  <section class="pg thanks">
    ${tasLogoSvg}
    <div>
      <div class="brand">Rishab World</div>
      <h2>THANK YOU</h2>
      <div class="ruler"></div>
      <div class="closing">${esc(buyer)} · ${esc(visitDate)}</div>
    </div>
  </section>

</body></html>`;
    printViaIframe(html);
  }

  const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '9px 12px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 13, outline: 'none' };
  const lbl = { fontSize: 10, letterSpacing: 1, color: 'var(--text3)', marginBottom: 4 };
  // Magic Link grid columns
  const cols = [
    ['Style', 'style'], ['Season', 'season'], ['Category', 'product_category'], ['Sub-Cat', 'sub_category'],
    ['Sample', 'sample_type'], ['Size', 'sizes'], ['Fabric', 'fabric_code'], ['Shade', 'shade'],
    ['Blend', 'blend'], ['GSM', 'weight'], ['Width', 'width'], ['GMT ₹', 'gmt_cost'], ['MOQ/Clr', 'moq_color'], ['MOQ/Sty', 'moq_style'],
  ];
  const th = { padding: '8px 10px', fontWeight: 600, color: 'var(--text3)', fontSize: 10, letterSpacing: 0.5, textAlign: 'left', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)' };
  const td = { padding: '8px 10px', fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap', borderBottom: '1px solid var(--grid-row-border)' };

  return (
    <div className="screen">
      <Header subtitle="DEVELOPMENT · ORDER" />
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: accent }}>New Order</div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>Order date · {orderDate}</div>
        </div>
        <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>{lines.length} item{lines.length !== 1 ? 's' : ''}</div>
      </div>

      <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Step 1 — Client + delivery */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: '2 1 220px' }}>
            <div style={lbl}>CLIENT <span style={{ color: 'var(--red)' }}>*</span></div>
            <select value={partyid} onChange={e => setPartyid(e.target.value)} style={{ ...inputStyle, borderColor: clientChosen ? 'var(--border)' : accent }}>
              <option value="">— select client —</option>
              {clients.map(b => <option key={b.id} value={b.id}>{titleCase(b.name)}</option>)}
            </select>
          </div>
          <div style={{ flex: '1 1 140px' }}>
            <div style={lbl}>DELIVERY</div>
            <input type="date" value={deliveryDt} onChange={e => setDeliveryDt(e.target.value)} style={inputStyle} />
          </div>
        </div>

        {/* Step 2 — Scan QR / barcode (locked until client chosen) */}
        <div ref={scanBlockRef}>
          <div style={lbl}>SCAN QR / BARCODE</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={scan} disabled={!clientChosen} onChange={e => setScan(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { ensureAudio(); addScan(scan); setScan(''); } }}
              placeholder={clientChosen ? 'Scan / type barcode, Enter…' : 'Select a client first'}
              style={{ ...inputStyle, flex: 1, borderColor: clientChosen ? accent : 'var(--border)', opacity: clientChosen ? 1 : 0.5 }} />
            <button onClick={() => { ensureAudio(); clientChosen && setScanning(s => !s); }} disabled={!clientChosen}
              style={{ padding: '0 14px', borderRadius: 6, border: `1px solid ${scanning ? 'var(--red)' : accent}`,
                background: scanning ? 'var(--red-bg)' : `${accent}1f`, color: scanning ? 'var(--red)' : accent,
                fontSize: 13, fontWeight: 600, cursor: clientChosen ? 'pointer' : 'not-allowed', opacity: clientChosen ? 1 : 0.5, whiteSpace: 'nowrap' }}>
              {scanning ? '✕ Stop' : '📷 Scan'}
            </button>
          </div>
        </div>

        {/* Live camera */}
        {scanning && (
          <div style={{
            border: `2px solid ${scanFlash === 'ok' ? '#22c55e' : scanFlash === 'err' ? '#ef4444' : accent}`,
            borderRadius: 8, overflow: 'hidden',
            background: scanFlash === 'ok' ? 'rgba(34,197,94,0.35)' : scanFlash === 'err' ? 'rgba(239,68,68,0.35)' : '#000',
            maxWidth: 280, margin: '0 auto',
            transition: 'border-color 80ms, background 80ms',
            boxShadow: scanFlash === 'ok' ? '0 0 16px rgba(34,197,94,0.65)' : scanFlash === 'err' ? '0 0 16px rgba(239,68,68,0.65)' : 'none'
          }}>
            {/* ZXing needs an explicit <video> element passed by ID to
                decodeFromVideoDevice(). The video must have playsinline + muted
                from the start (NOT set later) for iOS Safari to autoplay. */}
            <div id="qr-cam" style={{ width: '100%', height: 180, background: '#000' }}>
              <video
                id="qr-cam-video"
                playsInline
                muted
                autoPlay
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
            <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--text3)', padding: '6px' }}>Point the camera at the sample barcode — items add automatically.</div>
          </div>
        )}

        {msg && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12, color: msg.t === 'ok' ? 'var(--green)' : 'var(--red)' }}>{msg.text}</div>
            {/* Print button appears only after a successful save — uses the lastSaved snapshot. */}
            {msg.t === 'ok' && lastSaved && (
              <>
                <button onClick={() => openPdf(lastSaved.orderId, 'order')}
                  style={{ padding: '5px 11px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    border: `1px solid ${accent}`, background: `${accent}1f`, color: accent }}>
                  🖨 Print Order #{lastSaved.entryNo}
                </button>
                <button onClick={() => openPdf(lastSaved.orderId, 'catalogue')}
                  style={{ padding: '5px 11px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    border: `1px solid ${accent}`, background: 'transparent', color: accent }}>
                  📑 Catalogue
                </button>
              </>
            )}
          </div>
        )}

        {/* Magic Link grid */}
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          {lines.length === 0 ? (
            <div style={{ padding: 28, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>No items yet — scan a sample garment QR/barcode.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 920 }}>
                <thead>
                  <tr>
                    <th style={{ ...th }}>#</th>
                    <th style={{ ...th }}>Barcode</th>
                    {cols.map(c => <th key={c[1]} style={th}>{c[0]}</th>)}
                    <th style={{ ...th, textAlign: 'center' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={l.barcode} style={{ transition: 'background .5s', background: l.barcode === flash ? 'rgba(227,193,94,0.18)' : 'transparent' }}>
                      <td style={{ ...td, color: 'var(--text3)' }}>{lines.length - i}</td>
                      <td style={{ ...td, color: accent, fontWeight: 600 }}>{l.barcode}</td>
                      {cols.map(c => (
                        <td key={c[1]} style={{ ...td, color: c[1] === 'style' ? 'var(--text)' : 'var(--text2)', fontWeight: c[1] === 'style' ? 600 : 400 }}>
                          {c[1] === 'gmt_cost' && l[c[1]] ? `₹${l[c[1]]}`
                            : c[1] === 'weight' && l[c[1]] ? `${l[c[1]]} GSM`
                            : c[1] === 'width' && l[c[1]] ? `${l[c[1]]} cm`
                            : (l[c[1]] != null && l[c[1]] !== '' ? (typeof l[c[1]] === 'string' && c[1] !== 'blend' ? titleCase(l[c[1]]) : l[c[1]]) : '—')}
                        </td>
                      ))}
                      <td style={{ ...td, textAlign: 'center' }}>
                        <button onClick={() => removeLine(l.barcode)} style={{ background: 'none', border: 'none', color: 'var(--red)', fontSize: 15, cursor: 'pointer' }}>✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <button onClick={save} disabled={saving || !clientChosen || !lines.length}
          style={{ padding: '13px', borderRadius: 8, fontSize: 14, fontWeight: 600, letterSpacing: 1,
            cursor: (clientChosen && lines.length) ? 'pointer' : 'not-allowed', border: 'none',
            background: (clientChosen && lines.length) ? accent : 'var(--bg3)', color: (clientChosen && lines.length) ? 'var(--on-gold)' : 'var(--text3)' }}>
          {saving ? 'SAVING…' : `SAVE ORDER (${lines.length})`}
        </button>

        {/* Recent orders — each row has Print + Catalogue icon buttons that
            fetch the full order from /api/orders/sample-order/:id then call
            the same printOrder() / printCatalogue() the just-saved row uses. */}
        {recent.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 10, letterSpacing: 1.5, color: 'var(--text3)', marginBottom: 8 }}>RECENT ORDERS</div>
            {recent.slice(0, 10).map(o => (
              <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--grid-row-border)', fontSize: 12 }}>
                <span style={{ color: 'var(--text)', fontWeight: 600 }}>#{o.entryno || o.id}</span>
                <span style={{ color: 'var(--text2)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{titleCase(o.buyer) || '—'}</span>
                <span style={{ color: 'var(--text3)' }}>{o.items} pcs</span>
                <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 10, background: 'rgba(227,193,94,0.15)', color: accent }}>{(o.status || 'Pending').toUpperCase()}</span>
                <button title="Print" onClick={() => openPdf(o.id, 'order')}
                  style={{ background: 'none', border: `1px solid ${accent}55`, color: accent, borderRadius: 5, padding: '3px 8px', fontSize: 13, cursor: 'pointer' }}>🖨</button>
                <button title="Catalogue" onClick={() => openPdf(o.id, 'catalogue')}
                  style={{ background: 'none', border: `1px solid ${accent}55`, color: accent, borderRadius: 5, padding: '3px 8px', fontSize: 13, cursor: 'pointer' }}>📑</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ===== TAS STATUS — every garment TAS with status / delivered / invoice =====
function TasStatusView({ setView, Header, onOrderClick, pushBack }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [statusF, setStatusF] = useState('Pending');
  const [drill, setDrill] = useState(null);         // row opened in drill view
  const [pipes, setPipes] = useState({});           // tas_no -> pipeline totals
  const [expTile, setExpTile] = useState(null);       // 'order' | 'production' | null
  const [orderLines, setOrderLines] = useState(null); // shade-wise ORDER lines
  const [prodDetail, setProdDetail] = useState(null); // shade-wise PRODUCTION lines
  const [dispDetail, setDispDetail] = useState(null); // shade-wise DISPATCH lines
  const [stageList, setStageList] = useState(null);   // routing stage timeline
  const [editStage, setEditStage] = useState(null);   // uniqueid being date-edited
  const [editDate, setEditDate] = useState('');       // YYYY-MM-DD in the picker
  const [savingStage, setSavingStage] = useState(false);
  useEffect(() => {
    api('/orders/tas-status').then(d => setRows(Array.isArray(d) ? d : [])).catch(() => setRows([]));
  }, []);
  const fmtD = d => { if (!d) return '—'; try { const x = new Date(d); return `${String(x.getDate()).padStart(2, '0')}/${String(x.getMonth() + 1).padStart(2, '0')}/${String(x.getFullYear()).slice(2)}`; } catch { return '—'; } };
  // dd/mm/yyyy string -> days late (actual - plan). Negative/0 = on time.
  const dmy = s => { if (!s) return null; const p = String(s).split('/'); return p.length === 3 ? new Date(+p[2], +p[1] - 1, +p[0]) : null; };
  const delayDays = (planStr, actStr) => { const p = dmy(planStr), a = dmy(actStr); return (p && a) ? Math.round((a - p) / 86400000) : null; };
  // Save a stage's actual date via the ERP's own SP (audit preserved), then refresh.
  const saveStageDate = async (uniqueid) => {
    if (!editDate || !drill) return;
    const [y, m, d] = editDate.split('-');
    setSavingStage(true);
    try {
      const r = await api('/stages/update', { method: 'POST', body: JSON.stringify({ uniqueid, actual_date: `${d}/${m}/${y}`, remark: '', source: 'garment' }) });
      if (r && (r.success || r.message)) {
        setEditStage(null); setEditDate('');
        // refresh timeline + pending-stage pipeline for this TAS
        setStageList(null);
        api(`/orders/tas-stages/${drill.id}`).then(x => setStageList(Array.isArray(x) ? x : []));
        api(`/orders/tas-pipeline/${encodeURIComponent(drill.tas_no)}?hid=${drill.id}`).then(x => setPipes(p => ({ ...p, [drill.tas_no]: x })));
      }
    } finally { setSavingStage(false); }
  };
  const fmtN = n => (n || 0).toLocaleString('en-IN');
  // Display status: fully-delivered TAS show as "Delivered" even when the ERP
  // status field was never updated from Pending.
  const dispStatus = r => (r.order_pcs > 0 && (r.delivered_pcs || 0) >= r.order_pcs) ? 'Delivered' : (r.status || '—');
  const statuses = ['ALL', ...Array.from(new Set((rows || []).map(dispStatus)))];
  const list = (rows || []).filter(r => {
    if (statusF !== 'ALL' && dispStatus(r) !== statusF) return false;
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return String(r.tas_no || '').toLowerCase().includes(s) || String(r.buyer || '').toLowerCase().includes(s);
  });
  const pill = st => {
    const c = /pending/i.test(st) ? 'var(--yellow)' : /deliver|close|complete/i.test(st) ? 'var(--green)' : 'var(--text3)';
    return <span style={{ color: c, border: `1px solid ${c}`, borderRadius: 10, padding: '1px 8px', fontSize: 10, fontWeight: 600 }}>{st}</span>;
  };
  const accent = '#8b5cf6';

  // ── Drill view: one TAS opened from the list ──
  if (drill) {
    const pp = pipes[drill.tas_no];
    const st = dispStatus(drill);
    const dc = /deliver|close|ok|excess/i.test(st) ? 'var(--green)' : /cancel|suspend/i.test(st) ? 'var(--red)' : 'var(--yellow)';
    const dbg = dc === 'var(--green)' ? 'var(--green-bg)' : dc === 'var(--yellow)' ? 'var(--yellow-bg)' : 'var(--red-bg)';
    return (
      <div className="screen">
        <Header subtitle="TAS · STATUS" />
        <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
          <button onClick={() => window.history.back()} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: accent }}>{drill.tas_no}</div>
          <div style={{ fontSize: 9, padding: '3px 8px', borderRadius: 3, background: dbg, color: dc, whiteSpace: 'nowrap' }}>{st}</div>
          <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>{drill.buyer}</div>
        </div>
        <div style={{ padding: '16px' }}>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 11, color: 'var(--text3)', marginBottom: 14 }}>
            <span>Invoice {drill.invoiced ? <b style={{ color: 'var(--green)' }}>Yes · {fmtD(drill.invoice_dt)}</b> : <b>No</b>}</span>
            <span>Delivery <b style={{ color: 'var(--gold)' }}>{fmtD(drill.delivery_dt)}</b></span>
          </div>
          {/* Overdue critical stages + inline actual-date entry — shown on every critical TAS */}
          <div style={{ margin: '0 -16px' }}>
            <CriticalStagesBanner tasNo={drill.tas_no} orderType="garment" />
          </div>
          {!pp ? <div style={{ color: 'var(--text3)', fontSize: 12, padding: 20, textAlign: 'center' }}>Loading…</div> : (() => {
            // Pending stage now comes straight from the report source (view) via the
            // endpoint; null => not in the pending report = routing complete.
            const stg = pp.pending_stage || 'COMPLETED';
            const tiles = [
              { key: 'order', lb: 'ORDER', val: drill.order_pcs || 0, cc: 'var(--gold)', click: true },
              { key: 'stage', lb: 'PENDING STAGE', val: stg, cc: accent, click: true },
              { key: 'production', lb: 'PRODUCTION', val: pp.output || 0, cc: 'var(--teal)', click: true },
              { key: 'dispatch', lb: 'DISPATCHED', val: drill.delivered_pcs || 0, cc: 'var(--green)', click: true },
            ];
            const openTile = key => {
              setExpTile(prev => prev === key ? null : key);
              if (key === 'order' && orderLines === null) api(`/orders/${encodeURIComponent(drill.tas_no)}/shades`).then(d => setOrderLines(Array.isArray(d) ? d : [])).catch(() => setOrderLines([]));
              if (key === 'production' && prodDetail === null) api(`/orders/tas-production/${encodeURIComponent(drill.tas_no)}`).then(d => setProdDetail(Array.isArray(d) ? d : [])).catch(() => setProdDetail([]));
              if (key === 'dispatch' && dispDetail === null) api(`/orders/tas-dispatch/${drill.id}`).then(d => setDispDetail(Array.isArray(d) ? d : [])).catch(() => setDispDetail([]));
              if (key === 'stage' && stageList === null) api(`/orders/tas-stages/${drill.id}`).then(d => setStageList(Array.isArray(d) ? d : [])).catch(() => setStageList([]));
            };
            return (
            <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
              {tiles.map(t => (
                <div key={t.key}
                  onClick={t.click ? () => openTile(t.key) : undefined}
                  style={{ background: 'var(--bg2)', border: `1px solid ${t.click && expTile === t.key ? t.cc : 'var(--border)'}`, borderRadius: 12, padding: '18px 10px', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', cursor: t.click ? 'pointer' : 'default' }}>
                  <div style={{ fontSize: typeof t.val === 'number' ? 26 : 14, fontWeight: 700, color: t.cc, fontFamily: 'Inter, sans-serif', lineHeight: 1.25 }}>{typeof t.val === 'number' ? fmtN(t.val) : t.val}</div>
                  <div style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: 1.2, marginTop: 5 }}>{t.lb}{t.click ? (expTile === t.key ? ' ▲' : ' ▼') : ''}</div>
                </div>
              ))}
            </div>

            {/* ORDER detail — shade-wise (same as full breakdown, dispatch columns hidden) */}
            {expTile === 'order' && (
              <div style={{ marginTop: 12, border: '1px solid var(--border)', borderRadius: 10, overflowX: 'auto' }}>
                {orderLines === null ? <div style={{ padding: 16, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>Loading…</div> : orderLines.length === 0 ? <div style={{ padding: 16, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>No lines</div> : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, whiteSpace: 'nowrap' }}>
                    <thead><tr>{['PRODUCT','STYLE','SHADE','ORDER','RATE','AMOUNT','CONS.','APX MTRS','FACTORY','FABRIC'].map((h,i)=>(<th key={h} style={{ textAlign: (i>=3&&i<=7)?'right':'left', padding:'8px 12px', background:'var(--bg2)', color:'var(--text3)', fontSize:10, letterSpacing:0.6, borderBottom:'1px solid var(--border)' }}>{h}</th>))}</tr></thead>
                    <tbody>
                      {orderLines.map((l,i)=>{ const amt=(l.orderqty||0)*(l.rate||0); return (
                        <tr key={i}>
                          <td style={{ padding:'7px 12px', color:'var(--text)', borderBottom:'1px solid var(--border)' }}>{l.product||'—'}</td>
                          <td style={{ padding:'7px 12px', color:'var(--text)', borderBottom:'1px solid var(--border)' }}>{l.style_name||'—'}</td>
                          <td style={{ padding:'7px 12px', fontWeight:600, color:'var(--text)', borderBottom:'1px solid var(--border)' }}>{l.shade||'—'}</td>
                          <td style={{ padding:'7px 12px', textAlign:'right', fontWeight:700, color:'var(--gold)', borderBottom:'1px solid var(--border)' }}>{fmtN(l.orderqty)}</td>
                          <td style={{ padding:'7px 12px', textAlign:'right', color:'var(--text)', borderBottom:'1px solid var(--border)' }}>{l.rate?'₹'+fmtN(l.rate):'—'}</td>
                          <td style={{ padding:'7px 12px', textAlign:'right', color:'var(--text)', borderBottom:'1px solid var(--border)' }}>{amt?'₹'+(amt/100000).toFixed(2)+'L':'—'}</td>
                          <td style={{ padding:'7px 12px', textAlign:'right', color:'var(--text3)', borderBottom:'1px solid var(--border)' }}>{l.consumption?Number(l.consumption).toFixed(2):'—'}</td>
                          <td style={{ padding:'7px 12px', textAlign:'right', color:'var(--text3)', borderBottom:'1px solid var(--border)' }}>{l.Order_approx_mtrs?fmtN(Math.round(l.Order_approx_mtrs)):'—'}</td>
                          <td style={{ padding:'7px 12px', color:'var(--text3)', borderBottom:'1px solid var(--border)' }}>{l.factory||'—'}</td>
                          <td style={{ padding:'7px 12px', color:'var(--text3)', borderBottom:'1px solid var(--border)' }}>{l.Fabric_desc||'—'}</td>
                        </tr>
                      );})}
                      <tr>
                        <td colSpan={3} style={{ padding:'7px 12px', fontWeight:700, color:'var(--text)' }}>TOTAL</td>
                        <td style={{ padding:'7px 12px', textAlign:'right', fontWeight:700, color:'var(--gold)' }}>{fmtN(orderLines.reduce((a,b)=>a+(b.orderqty||0),0))}</td>
                        <td></td>
                        <td style={{ padding:'7px 12px', textAlign:'right', fontWeight:700, color:'var(--text)' }}>{'₹'+(orderLines.reduce((a,b)=>a+((b.orderqty||0)*(b.rate||0)),0)/100000).toFixed(2)+'L'}</td>
                        <td></td>
                        <td style={{ padding:'7px 12px', textAlign:'right', fontWeight:700, color:'var(--text)' }}>{fmtN(Math.round(orderLines.reduce((a,b)=>a+(b.Order_approx_mtrs||0),0)))}</td>
                        <td colSpan={2}></td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* PRODUCTION detail — summary tiles then shade-wise table */}
            {expTile === 'production' && (
              <div style={{ marginTop: 12 }}>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(90px, 1fr))', gap:8, marginBottom:10 }}>
                  {[['CUTTING', pp.cut, 'var(--teal)'], ['INPUT', pp.input, accent], ['OUTPUT', pp.output, 'var(--gold)'], ['READY', pp.ready, 'var(--green)']].map(([lb,val,cc])=>(
                    <div key={lb} style={{ background:'var(--bg3)', borderRadius:8, padding:'10px 6px', textAlign:'center' }}>
                      <div style={{ fontSize:16, fontWeight:700, color:cc, fontFamily:'Inter, sans-serif' }}>{fmtN(val||0)}</div>
                      <div style={{ fontSize:8.5, color:'var(--text3)', letterSpacing:1, marginTop:2 }}>{lb}</div>
                    </div>
                  ))}
                </div>
                <div style={{ border:'1px solid var(--border)', borderRadius:10, overflowX:'auto' }}>
                  {prodDetail === null ? <div style={{ padding:16, textAlign:'center', color:'var(--text3)', fontSize:12 }}>Loading…</div> : prodDetail.length === 0 ? <div style={{ padding:16, textAlign:'center', color:'var(--text3)', fontSize:12 }}>No production entries</div> : (
                    <table style={{ width:'100%', minWidth:480, borderCollapse:'collapse', fontSize:12, whiteSpace:'nowrap' }}>
                      <thead><tr>{['DESIGN','SHADE','CUT','INPUT','OUTPUT','READY'].map((h,i)=>(<th key={h} style={{ textAlign:i>=2?'right':'left', padding:'8px 12px', background:'var(--bg2)', color:'var(--text3)', fontSize:10, letterSpacing:0.6, borderBottom:'1px solid var(--border)' }}>{h}</th>))}</tr></thead>
                      <tbody>
                        {prodDetail.map((l,i)=>(
                          <tr key={i}>
                            <td style={{ padding:'7px 12px', color:'var(--text)', borderBottom:'1px solid var(--border)' }}>{l.design||'—'}</td>
                            <td style={{ padding:'7px 12px', color:'var(--text)', borderBottom:'1px solid var(--border)' }}>{l.shade||'—'}</td>
                            <td style={{ padding:'7px 12px', textAlign:'right', color:'var(--text)', borderBottom:'1px solid var(--border)' }}>{fmtN(l.cut)}</td>
                            <td style={{ padding:'7px 12px', textAlign:'right', color:'var(--text)', borderBottom:'1px solid var(--border)' }}>{fmtN(l.input)}</td>
                            <td style={{ padding:'7px 12px', textAlign:'right', fontWeight:700, color:'var(--teal)', borderBottom:'1px solid var(--border)' }}>{fmtN(l.output)}</td>
                            <td style={{ padding:'7px 12px', textAlign:'right', color:'var(--green)', borderBottom:'1px solid var(--border)' }}>{fmtN(l.ready)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}

            {/* DISPATCH detail — Garment Sales Invoice lines */}
            {expTile === 'dispatch' && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize:12.5, fontWeight:600, color:'var(--text)', marginBottom:8 }}>Garment Sales Invoice</div>
                <div style={{ display:'flex', gap:14, flexWrap:'wrap', fontSize:12, marginBottom:10 }}>
                  <span style={{ color:'var(--text3)' }}>Total Dispatched <b style={{ color:'var(--green)' }}>{fmtN(drill.delivered_pcs||0)}</b></span>
                  <span style={{ color:'var(--text3)' }}>Balance <b style={{ color:'var(--gold)' }}>{fmtN(Math.max(0,(drill.order_pcs||0)-(drill.delivered_pcs||0)))}</b></span>
                  <span style={{ color:'var(--text3)' }}>Invoice {drill.invoiced ? <b style={{ color:'var(--green)' }}>Yes</b> : <b>No</b>}</span>
                </div>
                <div style={{ border:'1px solid var(--border)', borderRadius:10, overflowX:'auto' }}>
                  {dispDetail === null ? <div style={{ padding:16, textAlign:'center', color:'var(--text3)', fontSize:12 }}>Loading…</div> : dispDetail.length === 0 ? <div style={{ padding:16, textAlign:'center', color:'var(--text3)', fontSize:12 }}>No invoice yet</div> : (
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, whiteSpace:'nowrap' }}>
                      <thead><tr>{['Invoice No','Invoice Dt','Factory Invoice','Factory Invoice Date','Style Name','Shade','Quantity','Rate','Amount'].map((h,i)=>(<th key={h} style={{ textAlign:i>=6?'right':'left', padding:'8px 12px', background:'var(--bg2)', color:'var(--text3)', fontSize:10, letterSpacing:0.6, borderBottom:'1px solid var(--border)' }}>{h}</th>))}</tr></thead>
                      <tbody>
                        {dispDetail.map((l,i)=>(
                          <tr key={i}>
                            <td style={{ padding:'7px 12px', color:'var(--text)', borderBottom:'1px solid var(--border)' }}>{l.invoiceno||'—'}</td>
                            <td style={{ padding:'7px 12px', color:'var(--text3)', borderBottom:'1px solid var(--border)' }}>{l.invoice_dt||'—'}</td>
                            <td style={{ padding:'7px 12px', color:'var(--text)', borderBottom:'1px solid var(--border)' }}>{l.factory_inv||'—'}</td>
                            <td style={{ padding:'7px 12px', color:'var(--text3)', borderBottom:'1px solid var(--border)' }}>{l.factory_dt||'—'}</td>
                            <td style={{ padding:'7px 12px', color:'var(--text)', borderBottom:'1px solid var(--border)' }}>{l.design||'—'}</td>
                            <td style={{ padding:'7px 12px', fontWeight:600, color:'var(--text)', borderBottom:'1px solid var(--border)' }}>{l.shade||'—'}</td>
                            <td style={{ padding:'7px 12px', textAlign:'right', fontWeight:700, color:'var(--green)', borderBottom:'1px solid var(--border)' }}>{fmtN(l.qty)}</td>
                            <td style={{ padding:'7px 12px', textAlign:'right', color:'var(--text)', borderBottom:'1px solid var(--border)' }}>{l.rate?'₹'+fmtN(l.rate):'—'}</td>
                            <td style={{ padding:'7px 12px', textAlign:'right', color:'var(--text)', borderBottom:'1px solid var(--border)' }}>{l.amount?'₹'+fmtN(Math.round(l.amount)):'—'}</td>
                          </tr>
                        ))}
                        <tr>
                          <td colSpan={6} style={{ padding:'7px 12px', fontWeight:700, color:'var(--text)' }}>TOTAL</td>
                          <td style={{ padding:'7px 12px', textAlign:'right', fontWeight:700, color:'var(--green)' }}>{fmtN(dispDetail.reduce((a,b)=>a+(b.qty||0),0))}</td>
                          <td></td>
                          <td style={{ padding:'7px 12px', textAlign:'right', fontWeight:700, color:'var(--text)' }}>{'₹'+fmtN(Math.round(dispDetail.reduce((a,b)=>a+(b.amount||0),0)))}</td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}

            {/* PENDING STAGE detail — routing timeline (completed + pending) */}
            {expTile === 'stage' && (
              <div style={{ marginTop: 12 }}>
                {stageList === null ? <div style={{ padding:16, textAlign:'center', color:'var(--text3)', fontSize:12 }}>Loading…</div> : stageList.length === 0 ? <div style={{ padding:16, textAlign:'center', color:'var(--text3)', fontSize:12 }}>No stages</div> : (() => {
                  // Order is past routing (dispatched / not in pending report) => every
                  // stage counts as complete, even ones whose date was never keyed in.
                  const allDone = !pp.pending_stage;
                  // Hide stages that were jumped over: missing a plan/actual date while a
                  // LATER stage is already actualised (out-of-order data noise).
                  const shown = stageList.filter((s, i) =>
                    !((!s.plan_dt || !s.actual_dt) && stageList.slice(i + 1).some(x => x.done)));
                  // Mark the SAME stage the tile shows (view's skip-forward CurrentPendingStage),
                  // so the timeline's "current" always agrees with the pending-stage tile.
                  const curIdx = allDone ? -1 : shown.findIndex(s => !s.done && s.stage === pp.pending_stage);
                  const doneCnt = allDone ? shown.length : (curIdx >= 0 ? curIdx : shown.filter(s => s.done).length);
                  return (
                  <>
                  <div style={{ fontSize:12, color:'var(--text3)', marginBottom:10 }}>{doneCnt} of {shown.length} stages complete</div>
                  <div style={{ position:'relative', paddingLeft:8 }}>
                    {shown.map((s,i)=>{
                      const isNextPending = i === curIdx;
                      // Everything before the current pending stage is treated as done
                      // (skip-forward: a reached stage implies earlier ones passed),
                      // even if their actual date was never keyed in.
                      const rowDone = s.done || allDone || (curIdx >= 0 && i < curIdx);
                      const dot = rowDone ? 'var(--green)' : isNextPending ? accent : 'var(--border)';
                      return (
                        <div key={i} style={{ display:'flex', gap:10, alignItems:'flex-start', padding:'6px 0' }}>
                          <div style={{ display:'flex', flexDirection:'column', alignItems:'center' }}>
                            <div style={{ width:12, height:12, borderRadius:'50%', background: rowDone ? dot : 'transparent', border:`2px solid ${dot}`, marginTop:2 }} />
                            {i < shown.length-1 && <div style={{ width:2, flex:1, minHeight:16, background:'var(--border)' }} />}
                          </div>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ display:'flex', justifyContent:'space-between', gap:8, flexWrap:'wrap' }}>
                              <span style={{ fontSize:12.5, fontWeight:600, color: rowDone ? 'var(--text)' : isNextPending ? accent : 'var(--text3)' }}>{s.stage}{isNextPending ? ' ◀ current' : ''}</span>
                              {(() => {
                                const dl = s.done ? delayDays(s.plan_dt, s.actual_dt) : null;
                                return (
                                  <span style={{ display:'flex', gap:8, alignItems:'baseline', fontSize:10.5, whiteSpace:'nowrap' }}>
                                    <span style={{ color: s.done ? 'var(--green)' : 'var(--text3)' }}>
                                      {s.done ? s.actual_dt : (rowDone ? '✓' : (s.plan_dt ? 'Plan '+s.plan_dt : 'pending'))}
                                    </span>
                                    {dl != null && (
                                      <span style={{ fontWeight:600, color: dl <= 0 ? 'var(--green)' : 'var(--red)' }}>
                                        {dl <= 0 ? 'On time' : dl + 'd late'}
                                      </span>
                                    )}
                                  </span>
                                );
                              })()}
                            </div>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:8 }}>
                              <div style={{ fontSize:9.5, color:'var(--text3)', letterSpacing:0.5 }}>{s.routing} routing</div>
                              {s.uniqueid && (editStage === s.uniqueid
                                ? (
                                  <span style={{ display:'flex', gap:6, alignItems:'center' }} onClick={e=>e.stopPropagation()}>
                                    <input type="date" value={editDate} onChange={e=>setEditDate(e.target.value)} style={{ fontSize:11, padding:'2px 4px', border:'1px solid var(--border)', borderRadius:4, background:'var(--bg2)', color:'var(--text)' }} />
                                    <button disabled={savingStage} onClick={()=>saveStageDate(s.uniqueid)} style={{ fontSize:10, padding:'2px 8px', border:'none', borderRadius:4, background:'var(--green)', color:'#fff', cursor:'pointer' }}>{savingStage?'…':'Save'}</button>
                                    <button onClick={()=>{ setEditStage(null); setEditDate(''); }} style={{ fontSize:10, padding:'2px 6px', border:'1px solid var(--border)', borderRadius:4, background:'transparent', color:'var(--text3)', cursor:'pointer' }}>✕</button>
                                  </span>
                                )
                                : <button onClick={()=>{ setEditStage(s.uniqueid); const a=dmy(s.actual_dt); const t=a?`${a.getFullYear()}-${String(a.getMonth()+1).padStart(2,'0')}-${String(a.getDate()).padStart(2,'0')}`:''; setEditDate(t); }} style={{ fontSize:9.5, padding:'1px 8px', border:`1px solid ${accent}`, borderRadius:10, background:'transparent', color:accent, cursor:'pointer' }}>{s.done?'Edit date':'Update date'}</button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  </>
                  );
                })()}
              </div>
            )}
            </>
            );
          })()}
        </div>
      </div>
    );
  }

  return (
    <div className="screen">
      <Header subtitle="TAS · STATUS" />
      <ExportBar reportKey="tas-status" title="TAS Status" subtitle={[statusF && statusF !== 'ALL' ? `status: ${statusF}` : '', q ? `search: ${q}` : ''].filter(Boolean).join(' | ')} sections={[{ name: 'Status', columns: TAS_STATUS_COLS, rows: list.map(r => ({ ...r, status: dispStatus(r) })) }]} source={{ endpoint: '/orders/tas-status', filters: [
        ...(q ? [{ t: 'search', q, keys: ['tas_no', 'buyer', 'status'] }] : []),
        ...(statusF && statusF !== 'ALL' ? (statusF === 'Delivered'
              ? [{ t: 'cmp', k: 'delivered_pcs', op: 'gte', k2: 'order_pcs' }]
              : [{ t: 'eq', k: 'status', v: statusF }, { t: 'cmp', k: 'delivered_pcs', op: 'lt', k2: 'order_pcs' }]) : []),
      ] }} />
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
        <button onClick={() => setView('rw')} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: accent }}>TAS Status</div>
        <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>{list.length} TAS</div>
      </div>

      {/* Status tabs */}
      <div style={{ padding: '10px 16px 4px', display: 'flex', gap: 6, flexWrap: 'wrap', borderBottom: '1px solid var(--border)' }}>
        {statuses.map(s => (
          <button key={s} onClick={() => setStatusF(s)}
            style={{
              background: statusF === s ? `${accent}26` : 'transparent',
              border: `1px solid ${statusF === s ? accent : 'var(--border)'}`,
              color: statusF === s ? accent : 'var(--text3)',
              padding: '5px 12px', borderRadius: 14, fontSize: 11, cursor: 'pointer', letterSpacing: 1
            }}>{String(s).toUpperCase()}</button>
        ))}
      </div>

      {/* Search box */}
      <div style={{ padding: '8px 16px 4px' }}>
        <input
          type="text" value={q} onChange={e => setQ(e.target.value)}
          placeholder="Search TAS, buyer…"
          style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 12, outline: 'none' }}
          onFocus={e => e.target.style.borderColor = accent}
          onBlur={e => e.target.style.borderColor = 'var(--border)'}
        />
      </div>

        {rows === null && <div style={{ color: 'var(--text3)', fontSize: 12, padding: 30, textAlign: 'center' }}>Loading…</div>}
        {rows !== null && list.length === 0 && <div style={{ color: 'var(--text3)', fontSize: 12, padding: 30, textAlign: 'center' }}>No TAS found.</div>}
        {rows !== null && list.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12, padding: '8px 16px 24px' }}>
            {list.map(r => {
              const ordQ = r.order_pcs || 0, dlvQ = r.delivered_pcs || 0;
              const raw = ordQ ? Math.min(100, dlvQ * 100 / ordQ) : 0;
              const full = ordQ > 0 && dlvQ >= ordQ;
              const pct = full ? 100 : raw > 99 ? (Math.floor(raw * 100) / 100).toFixed(2) : Math.round(raw);
              const st = dispStatus(r);
              const c = /close|ok|excess|deliver/i.test(st) || full ? 'var(--green)'
                      : /cancel|suspend/i.test(st) ? 'var(--red)'
                      : dlvQ > 0 ? 'var(--yellow)' : 'var(--red)';
              const bg = c === 'var(--green)' ? 'var(--green-bg)' : c === 'var(--yellow)' ? 'var(--yellow-bg)' : 'var(--red-bg)';
              return (
                <React.Fragment key={r.id}>
                <div
                     onClick={() => {
                       const k = r.tas_no;
                       // Register a back-action with the app's nav stack so browser/
                       // hardware back returns to THIS list, not the home hub.
                       if (pushBack) pushBack(() => setDrill(null));
                       setDrill(r); setExpTile(null); setOrderLines(null); setProdDetail(null); setDispDetail(null); setStageList(null);
                       if (!pipes[k]) api(`/orders/tas-pipeline/${encodeURIComponent(k)}?hid=${r.id}`).then(d => setPipes(p => ({ ...p, [k]: d }))).catch(() => setPipes(p => ({ ...p, [k]: { cut: 0, input: 0, output: 0, ready: 0 } })));
                     }}
                     style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderLeft: `3px solid ${c}`, borderRadius: 8, padding: 12, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 8 }}
                     onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg3)'; e.currentTarget.style.borderColor = c; }}
                     onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg2)'; e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.borderLeftColor = c; }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 600 }}>{r.tas_no}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.buyer}</div>
                    </div>
                    <div style={{ fontSize: 9, padding: '3px 7px', borderRadius: 3, background: bg, color: c, whiteSpace: 'nowrap', height: 'fit-content' }}>{st}</div>
                  </div>
                  <div>
                    <div style={{ height: 4, background: 'var(--border)', borderRadius: 2 }}>
                      <div style={{ height: '100%', background: c, width: `${pct}%`, borderRadius: 2 }} />
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3, textAlign: 'right' }}>{pct}%</div>
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text3)', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    <span>📦 {fmtN(r.order_pcs)}</span>
                    <span>🚚 {fmtN(r.delivered_pcs)}</span>
                    <span>Inv {r.invoiced ? 'Yes' : 'No'}</span>
                    <span style={{ color: 'var(--gold)', marginLeft: 'auto' }}>{fmtD(r.delivery_dt)}</span>
                  </div>
                </div>
                </React.Fragment>
              );
            })}
          </div>
        )}
    </div>
  );
}

// ===== BD (Bangladesh) hub view — two cards: Order in Hand / Upcoming Order =====
function BdView({ setView, Header }) {
  const bdAccent = '#006a4e';
  const [sum, setSum] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    api('/orders/bd-summary')
      .then(d => { if (alive) setSum(d); })
      .catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, []);
  // Live pending counts for the three approval cards.
  const [pc, setPc] = useState({});
  useEffect(() => {
    let alive = true;
    const n = (arr, f) => (Array.isArray(arr) ? arr.filter(f).length : null);
    Promise.all([
      api('/costflow/sales-approval').catch(() => []),
      api('/costflow/final-approval').catch(() => []),
      api('/shipments').catch(() => []),
      api('/costflow/factory-assign').catch(() => null),
      api('/fi/summary').catch(() => null),
    ]).then(([s, f, sh, fa, fiSum]) => {
      if (!alive) return;
      setPc({
        'bd-factory-assign': fa && Array.isArray(fa.pending) ? fa.pending.length : null,
        'bd-sales-approval': n(s, r => !r.approved),
        'bd-fi-inspection': fiSum ? Number(fiSum.pending_count || 0) : null,
        'bd-final-approval': n(f, r => !r.approved),
        'bd-shipment-update': n(sh, r => !(r.bl_no && r.bl_dt_s)),
      });
    });
    return () => { alive = false; };
  }, []);
  const cardBg = 'rgba(0,106,78,0.05)', cardBgHover = 'rgba(0,106,78,0.11)';
  const card = (target, label, data, subword, accentLocal, showCs) => (
    <div key={target} onClick={() => setView(target)}
      style={{ padding: '18px 20px', border: '1px solid var(--border)', borderRadius: 10, cursor: 'pointer',
        textAlign: 'center', flex: '1 1 160px', minWidth: 150, maxWidth: 260, height: 168, boxSizing: 'border-box',
        display: 'flex', flexDirection: 'column', justifyContent: 'center', transition: 'all 0.2s', background: cardBg }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = accentLocal; e.currentTarget.style.background = cardBgHover; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = cardBg; }}>
      <div style={{ color: accentLocal, fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 30 }}>{data ? fmt(data.count) : '…'}</div>
      <div style={{ color: accentLocal, fontSize: 12.5, fontWeight: 700, letterSpacing: 1.4, marginTop: 5 }}>{label}</div>
      <div style={{ color: 'var(--text3)', fontSize: 11, marginTop: 6 }}>{data ? `${fmt(data.pcs)} pcs ${subword}` : ' '}</div>
      <div style={{ color: 'var(--text2)', fontSize: 11, marginTop: 2, minHeight: 15 }}>{data ? fmtCr(data.value) : ' '}</div>
      <div style={{ minHeight: 15, marginTop: 4 }}>
        {showCs && data && data.count > 0 &&
          <span style={{ color: 'var(--red)', fontSize: 10.5, fontWeight: 600 }}>⚠ Sales Order Cost Sheet pending</span>}
      </div>
      <div style={{ color: 'var(--text3)', fontSize: 9, marginTop: 8, letterSpacing: 1 }}>OPEN →</div>
    </div>
  );
  return (
    <div className="screen">
      <Header subtitle="BD · BANGLADESH TEAM" />
      <ExportBar reportKey="bd-summary" title="Bangladesh Team — Summary" sections={[{ name: 'Summary', columns: BD_SUM_COLS, rows: sum ? [{ key: 'In Hand', ...sum.in_hand }, { key: 'Upcoming', ...sum.upcoming }] : [] }]} source={{ endpoint: '/orders/bd-summary' }} />
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
        <button onClick={() => setView('home')} style={{ background: 'none', border: 'none', color: bdAccent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: bdAccent }}>🇧🇩 Bangladesh Team</div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>order in hand &amp; upcoming orders</div>
        </div>
      </div>
      <div style={{ marginTop: 28, padding: '0 16px', display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
        {canSee('screen.sbd.order_in_hand') && card('bd-orders', 'ORDER IN HAND', sum && sum.in_hand, 'pending', bdAccent, true)}
        {canSee('screen.sbd.upcoming') && card('bd-upcoming', 'UPCOMING ORDER', sum && sum.upcoming, 'expected', bdAccent, false)}
      </div>
      {/* Costing workflow approvals */}
      <div style={{ margin: '22px auto 0', padding: '0 16px', maxWidth: 840, display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ height: 1, flex: 1, background: 'var(--border)' }} />
        <div style={{ fontSize: 10, letterSpacing: 2, color: 'var(--text3)', fontWeight: 700 }}>APPROVALS</div>
        <div style={{ height: 1, flex: 1, background: 'var(--border)' }} />
      </div>
      <div style={{ marginTop: 14, padding: '0 16px', display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
        {[['bd-factory-assign', 'FACTORY ASSIGNMENT', 'new TAS · approve factory', bdAccent],
          ['bd-sales-approval', 'COST SHEET APPROVAL', 'awaiting your approval', bdAccent],
          ['bd-fi-inspection', 'FI INSPECTION', 'propose / approve / reject', bdAccent],
          ['bd-final-approval', 'FINAL COSTING APPROVAL', 'awaiting your approval', bdAccent],
          ['bd-shipment-update', 'SHIPMENT UPDATE', 'awaiting BL details', bdAccent]]
          .filter(([v]) => canSee({ 'bd-factory-assign': 'screen.sbd.factory_assign', 'bd-sales-approval': 'screen.sbd.sales_approval', 'bd-fi-inspection': 'screen.sbd.fi_inspection', 'bd-final-approval': 'screen.sbd.final_approval', 'bd-shipment-update': 'screen.sbd.shipment_update' }[v]))
          .map(([v, label, sub, color]) => {
          const n = pc[v];
          return (
            <div key={v} onClick={() => setView(v)}
              style={{ padding: '18px 20px', border: '1px solid var(--border)', borderRadius: 10, cursor: 'pointer',
                textAlign: 'center', flex: '1 1 160px', minWidth: 150, maxWidth: 260, height: 168, boxSizing: 'border-box',
                display: 'flex', flexDirection: 'column', justifyContent: 'center', transition: 'all 0.2s', background: cardBg }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.background = cardBgHover; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = cardBg; }}>
              <div style={{ color, fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 30 }}>{n == null ? '…' : fmt(n)}</div>
              <div style={{ color, fontSize: 12.5, fontWeight: 700, letterSpacing: 1.4, marginTop: 5 }}>{label}</div>
              <div style={{ color: 'var(--text3)', fontSize: 11, marginTop: 6 }}>{sub}</div>
              <div style={{ color: 'var(--text2)', fontSize: 11, marginTop: 2, minHeight: 15 }}> </div>
              <div style={{ minHeight: 15, marginTop: 4 }}>
                {n > 0 && <span style={{ color: 'var(--red)', fontSize: 10.5, fontWeight: 600 }}>⚠ {n} pending</span>}
              </div>
              <div style={{ color: 'var(--text3)', fontSize: 9, marginTop: 8, letterSpacing: 1 }}>OPEN →</div>
            </div>
          );
        })}
      </div>
      {err && <div style={{ textAlign: 'center', color: 'var(--red)', fontSize: 11, marginTop: 24 }}>Could not load BD data.</div>}
      <div style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 10.5, marginTop: 24, padding: '0 30px', lineHeight: 1.6 }}>
        Order in Hand = TECPAC, SPECS &amp; BOM actual dates updated in pre-order routing. Upcoming = not yet updated.
      </div>
    </div>
  );
}

// ===== BD · FACTORY ASSIGNMENT — every new TAS lands here first. BD approves the
// factory from the Garment Sales Order (or picks another); only then does the TAS
// reach that factory's Cost Sheet list for the Sales Contract + Cost Sheet. =====
function BdFactoryAssignView({ setView, Header }) {
  const accent = '#006a4e';
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('pending');      // 'pending' | 'assigned'
  const [q, setQ] = useState('');
  const [pick, setPick] = useState({});            // tas_id -> chosen factory id
  const [busy, setBusy] = useState(null);          // tas_id being saved
  const load = () => api('/costflow/factory-assign').then(d => setData(d || {})).catch(() => setData({ error: true }));
  useEffect(() => { load(); }, []);

  const approve = r => {
    const fid = pick[r.tas_id] != null ? pick[r.tas_id] : r.factoryid;
    if (!fid) { alert('Select a factory first.'); return; }
    const fname = ((data.factories || []).find(f => String(f.id) === String(fid)) || {}).name || r.factory;
    const changed = String(fid) !== String(r.factoryid || '');
    if (changed && !window.confirm(`${r.tas_no}: assign to ${fname} instead of ${r.factory || 'the order factory'}?`)) return;
    setBusy(r.tas_id);
    api('/costflow/factory-assign', { method: 'POST', body: JSON.stringify({ tas_id: r.tas_id, factoryid: Number(fid) }) })
      .then(x => { setBusy(null); if (!x || !x.ok) { alert((x && x.error) || 'Approve failed'); return; } load(); })
      .catch(() => { setBusy(null); alert('Approve failed'); });
  };

  const uniq = arr => Object.values((arr || []).reduce((m, r) => { if (!m[r.tas_id]) m[r.tas_id] = r; return m; }, {}));
  const pending = data && !data.error ? uniq(data.pending) : null;
  const assigned = data && !data.error ? uniq(data.assigned) : null;
  const isDone = tab === 'assigned';
  const listName = isDone ? 'Assigned' : 'Pending Assignment';
  const t = q.trim().toLowerCase();
  const list = ((isDone ? assigned : pending) || []).filter(r => !t ||
    `${r.tas_no} ${r.buyer || ''} ${r.merchandiser || ''} ${r.factory || ''} ${r.order_factory || ''}`.toLowerCase().includes(t));
  const cell = { padding: '9px 10px', fontSize: 12, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
  const hd = { ...cell, color: 'var(--text3)', fontSize: 10, letterSpacing: 1, background: 'var(--bg3)', textAlign: 'left', textTransform: 'uppercase' };
  const tile = (key, label, n, sub, color) => (
    <div key={key} onClick={() => setTab(key)}
      style={{ flex: '1 1 220px', maxWidth: 340, padding: '16px 18px', borderRadius: 12, cursor: 'pointer', boxSizing: 'border-box',
        border: (tab === key ? 2 : 1) + 'px solid ' + (tab === key ? color : 'var(--border)'), background: tab === key ? color + '10' : 'var(--bg2)' }}>
      <div style={{ fontSize: 30, fontWeight: 700, color, fontFamily: 'Inter, sans-serif', lineHeight: 1 }}>{n == null ? '…' : fmt(n)}</div>
      <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.2, color, marginTop: 6 }}>{label}</div>
      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{sub}</div>
    </div>
  );
  const sel = { height: 32, padding: '0 8px', minWidth: 190, background: '#fffbe6', border: '1.5px solid #d4a72c', borderRadius: 4, color: 'var(--text)', fontSize: 12, fontWeight: 600, outline: 'none' };

  return (
    <div className="screen">
      <Header subtitle="BD · FACTORY ASSIGNMENT" />
      <ExportBar reportKey={isDone ? 'bd-factory-assigned' : 'bd-factory-assign'} title={`Factory Assignment - ${listName}`}
        sections={[{ name: listName,
          columns: isDone
            ? [C('tas_no', 'TAS'), C('buyer', 'Buyer'), C('merchandiser', 'Merchandiser'), C('order_factory', 'Order Factory'), C('factory', 'Assigned Factory'), C('approved_at', 'Approved On', 'date'), C('approved_by_name', 'Approved By')]
            : [C('tas_no', 'TAS'), C('buyer', 'Buyer'), C('merchandiser', 'Merchandiser'), C('enquirydate', 'Enquiry Date', 'date'), C('factory', 'Order Factory')],
          rows: list }]}
        source={{ endpoint: '/costflow/factory-assign' }} />
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
        <button onClick={() => setView('bd')} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: accent }}>🏭 Factory Assignment</div>
      </div>

      {/* Key indicators — each one also switches the list below */}
      <div style={{ padding: '16px 16px 4px', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {tile('pending', 'PENDING ASSIGNMENT', pending ? pending.length : null, 'new TAS · approve the factory', '#b5651d')}
        {tile('assigned', 'ASSIGNED', assigned ? assigned.length : null, 'sent to the factory for cost sheet', '#1b5e20')}
      </div>

      <div style={{ padding: '10px 16px 6px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)' }}>
          {listName.toUpperCase()}
          <span style={{ color: 'var(--text3)', fontWeight: 400 }}> · {list.length} TAS · {isDone ? 'factories approved here' : 'factory comes from the Garment Sales Order — change it if needed, then Approve'}</span>
        </div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="search TAS / buyer / factory…"
          style={{ marginLeft: 'auto', minWidth: 220, padding: '7px 10px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 12, outline: 'none' }} />
      </div>
      <div style={{ margin: '0 16px 24px', border: '1px solid var(--border)', borderRadius: 8, overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 860, borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={hd}>TAS</th><th style={hd}>Buyer</th><th style={hd}>Merchandiser</th>
            {isDone
              ? <><th style={hd}>Order Factory</th><th style={hd}>Assigned Factory</th><th style={hd}>Approved On</th><th style={hd}>Approved By</th></>
              : <><th style={hd}>Enquiry Date</th><th style={hd}>Factory</th><th style={hd}>Action</th></>}
          </tr></thead>
          <tbody>
            {!data && <tr><td colSpan={7} style={{ ...cell, textAlign: 'center', color: 'var(--text3)' }}>Loading…</td></tr>}
            {data && data.error && <tr><td colSpan={7} style={{ ...cell, textAlign: 'center', color: 'var(--red)' }}>Could not load.</td></tr>}
            {data && !data.error && list.length === 0 && <tr><td colSpan={7} style={{ ...cell, textAlign: 'center', color: 'var(--text3)' }}>
              {isDone ? 'No TAS assigned here yet.' : 'Nothing waiting — every new TAS has a factory.'}</td></tr>}
            {list.map(r => isDone ? (
              <tr key={r.tas_id}>
                <td style={{ ...cell, fontWeight: 700, color: accent }}>{r.tas_no}</td>
                <td style={cell}>{titleCase(r.buyer) || '—'}</td>
                <td style={cell}>{titleCase(r.merchandiser) || '—'}</td>
                <td style={{ ...cell, color: 'var(--text3)' }}>{r.order_factory || '—'}</td>
                <td style={{ ...cell, fontWeight: 700 }}>{r.factory || '—'}
                  {!!r.changed && <span style={{ marginLeft: 6, padding: '1px 7px', borderRadius: 10, fontSize: 9.5, fontWeight: 700, background: '#fff3e0', color: '#b5651d' }}>CHANGED</span>}</td>
                <td style={cell}>{fmtDMY(r.approved_at) || '—'}</td>
                <td style={cell}>{titleCase(r.approved_by_name) || '—'}</td>
              </tr>
            ) : (
              <tr key={r.tas_id}>
                <td style={{ ...cell, fontWeight: 700, color: accent }}>{r.tas_no}</td>
                <td style={cell}>{titleCase(r.buyer) || '—'}</td>
                <td style={cell}>{titleCase(r.merchandiser) || '—'}</td>
                <td style={cell}>{fmtDMY(r.enquirydate) || '—'}</td>
                <td style={cell}>
                  <select style={sel} value={pick[r.tas_id] != null ? pick[r.tas_id] : (r.factoryid || '')}
                    onChange={e => setPick(p => ({ ...p, [r.tas_id]: e.target.value }))}>
                    <option value="">— select factory —</option>
                    {(data.factories || []).map(f => <option key={f.id} value={f.id}>{f.name}{String(f.id) === String(r.factoryid) ? '  (order)' : ''}</option>)}
                  </select>
                </td>
                <td style={cell}>
                  <button onClick={() => approve(r)} disabled={busy === r.tas_id}
                    style={{ padding: '6px 16px', borderRadius: 4, border: 'none', background: accent, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: busy === r.tas_id ? 0.6 : 1 }}>
                    {busy === r.tas_id ? 'Saving…' : 'Approve'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===== BD · SALES CONTRACT COST SHEET APPROVAL — read-only factory costing + approve =====
function BdSalesApprovalView({ setView, Header }) {
  const accent = '#006a4e';
  const COLS = [['fab', 'Fab Price'], ['freight', 'Freight'], ['cif', 'Cif'], ['consumption', 'Consumption'], ['fabcost', 'Fab Cost'], ['cm', 'Cm'], ['bd', 'Bd Trims'], ['ind', 'Ind Trims'], ['china', 'China Trims'], ['wash', 'Wash'], ['transp', 'Transp'], ['cog', 'Cog'], ['exch', 'Exch'], ['inr', 'Inr']];
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(null);
  const [cost, setCost] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('pending');     // pending first; approved on demand
  const load = () => api('/costflow/sales-approval').then(d => setRows(Array.isArray(d) ? d : [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const open = r => { setSel(r); setCost(null); api(`/costflow/${encodeURIComponent(r.tas_no)}/sales-cost`).then(d => setCost(Array.isArray(d) ? d : [])).catch(() => setCost([])); };
  const approve = () => { setBusy(true); api(`/costflow/${encodeURIComponent(sel.tas_no)}/approve-sales`, { method: 'POST', body: JSON.stringify({ cost_id: sel.cost_id }) }).then(() => { setBusy(false); setSel(null); load(); }).catch(() => { setBusy(false); alert('Approve failed'); }); };
  const num = v => (v == null ? '—' : Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 }));
  const cell = { padding: '8px 10px', fontSize: 12, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
  const hd = { ...cell, color: 'var(--text3)', fontSize: 10, letterSpacing: 1, background: 'var(--bg3)', textAlign: 'left' };

  // detail — read-only cost grid + approve
  if (sel) {
    const delayDays = Number(sel.fi_delay_days || 0);
    const isOver20 = delayDays > 20;
    const shownFiDt = sel.fi_dt || sel.factory_fi_dt || sel.target_fi_dt || sel.est_fi_dt || '';
    return (
      <div className="screen">
        <ExportBar reportKey="bd-sales-cost" title={`${sel.tas_no} - Sales Contract Cost Sheet`}
          sections={[{ name: 'Cost Sheet', columns: [C('item','Item'), C('value','Value')],
            rows: [
              { item: 'Enquiry Date', value: fmtDMY(sel.enquirydate) || '—' },
              { item: 'Target FI Date (Est FI - 20d)', value: fmtDMY(sel.target_fi_dt) || '—' },
              { item: 'Factory FI Date', value: fmtDMY(shownFiDt) || '—' },
              ...((cost || []).flatMap(r => COLS.map(([k, lb]) => ({ item: lb, value: csR2(num(r[k])) }))))
            ] }]}
          source={{ endpoint: `/costflow/${encodeURIComponent(sel.tas_no)}/sales-cost` }} />
        <Header subtitle="BD · SALES COST SHEET APPROVAL" />
        <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
          <button onClick={() => setSel(null)} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
          <div>
            <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 20, color: accent }}>{sel.tas_no} <span style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 400 }}>· {titleCase(sel.buyer)} · {sel.factory}</span></div>
            <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 2 }}>
              {sel.merchandiser ? `Merch: ${titleCase(sel.merchandiser)}` : 'Sales Contract Cost Sheet & FI Date Review'}
            </div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            {sel.enquirydate && (
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 9.5, letterSpacing: 0.8, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase' }}>Enquiry Date</div>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text2)', marginTop: 2 }}>{fmtDMY(sel.enquirydate)}</div>
              </div>
            )}
            {sel.target_fi_dt && (
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 9.5, letterSpacing: 0.8, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase' }}>Target FI (Est - 20d)</div>
                <div style={{ fontSize: 12.5, fontWeight: 800, color: accent, marginTop: 2 }}>{fmtDMY(sel.target_fi_dt)}</div>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <label style={{ fontSize: 10, letterSpacing: 0.8, color: isOver20 ? '#c62828' : accent, fontWeight: 800, textTransform: 'uppercase', marginBottom: 3 }}>
                Factory FI Date {delayDays !== 0 ? `(${delayDays > 0 ? '+' : ''}${delayDays}d)` : ''}
              </label>
              <div style={{ height: 34, padding: '0 12px', display: 'flex', alignItems: 'center', gap: 6, borderRadius: 6, background: isOver20 ? '#ffebee' : 'var(--bg3)', border: `1.5px solid ${isOver20 ? '#c62828' : '#c3cbd5'}`, fontSize: 13, fontWeight: 700, color: isOver20 ? '#c62828' : 'var(--text)' }}>
                <span>{fmtDMY(shownFiDt) || '—'}</span>
                {isOver20 && <span style={{ fontSize: 10, fontWeight: 800 }}>⚠️ &gt;20d</span>}
              </div>
            </div>
            <div>
              {sel.approved
                ? <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--green)' }}>✓ Approved{sel.sales_approved_by_name ? ` by ${sel.sales_approved_by_name}` : ''}</span>
                : <button onClick={approve} disabled={busy || !cost || cost.length === 0} style={{ padding: '9px 18px', border: 'none', borderRadius: 8, background: 'var(--green)', color: '#fff', fontWeight: 700, cursor: 'pointer', opacity: (busy || !cost || cost.length === 0) ? 0.6 : 1 }}>Approve Cost Sheet &amp; FI Date</button>}
            </div>
          </div>
        </div>
        {isOver20 && (
          <div style={{ margin: '10px 16px 0', padding: '10px 14px', borderRadius: 8, background: '#ffebee', border: '1.5px solid #ef5350', color: '#c62828', fontSize: 12, fontWeight: 700 }}>
            ⚠️ FI Date Warning: Factory FI Date ({fmtDMY(shownFiDt)}) is +{delayDays} days after Target FI Date ({fmtDMY(sel.target_fi_dt)}), exceeding the 20-day buffer (Est FI: {fmtDMY(sel.est_fi_dt)}).
          </div>
        )}
        <div style={{ padding: '12px 16px 8px', fontSize: 11, color: 'var(--text2)' }}>Factory costing (as entered — read-only)</div>
        <div style={{ padding: '0 16px 24px' }}>
          {!cost && <div style={{ padding: 30, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>Loading…</div>}
          {cost && cost.length === 0 && <div style={{ padding: 30, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>No cost sheet rows.</div>}
          {cost && cost.map((r, i) => (
            <CostGridCard key={i} line={r} locked accent={accent} title={cost.length > 1 ? `LINE ${i + 1}` : 'COSTING'}
              calc={{ cif: r.cif, fabcost: r.fabcost, cog: r.cog, inr: r.inr }} onCell={() => {}} />
          ))}
        </div>
      </div>
    );
  }

  // list
  const list = (rows || []).filter(r => {
    if (tab === 'pending' ? r.approved : !r.approved) return false;
    const s = q.trim().toLowerCase();
    return !s || `${r.tas_no} ${r.buyer || ''} ${r.factory || ''}`.toLowerCase().includes(s);
  });
  const pend = rows ? rows.filter(r => !r.approved).length : null;
  const appr = rows ? rows.filter(r => r.approved).length : null;
  const isDone = tab === 'approved';
  const listName = isDone ? 'Approved' : 'Pending Approval';
  const tile = (key, label, n, sub, color) => (
    <div key={key} onClick={() => setTab(key)}
      style={{ flex: '1 1 220px', maxWidth: 340, padding: '16px 18px', borderRadius: 12, cursor: 'pointer', boxSizing: 'border-box',
        border: (tab === key ? 2 : 1) + 'px solid ' + (tab === key ? color : 'var(--border)'), background: tab === key ? color + '10' : 'var(--bg2)' }}>
      <div style={{ fontSize: 30, fontWeight: 700, color, fontFamily: 'Inter, sans-serif', lineHeight: 1 }}>{n == null ? '…' : fmt(n)}</div>
      <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.2, color, marginTop: 6 }}>{label}</div>
      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{sub}</div>
    </div>
  );
  return (
    <div className="screen">
      <Header subtitle="BD · SALES COST SHEET APPROVAL" />
      <ExportBar reportKey="bd-sales-approval" title={`Sales Contract Cost Sheet Approval - ${listName}`}
        sections={[{ name: listName,
          columns: [C('tas_no', 'TAS'), C('buyer', 'Buyer'), C('factory', 'Factory'), C('enquirydate', 'Enquiry Date', 'date'), C('fi_dt', 'FI Date', 'date'), C('merchandiser', 'Merchandiser'),
                    ...(isDone ? [C('approved_at', 'Approved On', 'date'), C('sales_approved_by_name', 'Approved By')] : [])],
          rows: list }]}
        source={{ endpoint: '/costflow/sales-approval' }} />
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
        <button onClick={() => setView('bd')} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: accent }}>Sales Contract Cost Sheet Approval</div>
      </div>

      {/* Key indicators — each one also switches the list below */}
      <div style={{ padding: '16px 16px 4px', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {tile('pending', 'PENDING APPROVAL', pend, 'entered by factory · awaiting approval', '#b5651d')}
        {tile('approved', 'APPROVED', appr, 'approved · locked', '#1b5e20')}
      </div>

      <div style={{ padding: '10px 16px 6px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)' }}>
          {listName.toUpperCase()}
          <span style={{ color: 'var(--text3)', fontWeight: 400 }}> · {list.length} TAS · {isDone ? 'click a TAS to view its approved cost sheet' : 'click a TAS to review and approve'}</span>
        </div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="search TAS / buyer / factory…"
          style={{ marginLeft: 'auto', minWidth: 220, padding: '7px 10px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 12, outline: 'none' }} />
      </div>
      <div style={{ margin: '0 16px 24px', border: '1px solid var(--border)', borderRadius: 8, overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 780, borderCollapse: 'collapse' }}>
          <thead><tr><th style={hd}>TAS</th><th style={hd}>Buyer</th><th style={hd}>Factory</th><th style={hd}>Enquiry Date</th><th style={hd}>FI Date</th><th style={hd}>Merchandiser</th>{isDone && <><th style={hd}>Approved On</th><th style={hd}>Approved By</th></>}<th style={hd}>Status</th></tr></thead>
          <tbody>
            {!rows && <tr><td colSpan={isDone ? 9 : 7} style={{ ...cell, textAlign: 'center', color: 'var(--text3)' }}>Loading…</td></tr>}
            {rows && list.length === 0 && <tr><td colSpan={isDone ? 9 : 7} style={{ ...cell, textAlign: 'center', color: 'var(--text3)' }}>{isDone ? 'No approved cost sheets yet.' : 'Nothing awaiting approval.'}</td></tr>}
            {list.map(r => (
              <tr key={r.tas_no} onClick={() => open(r)} style={{ cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <td style={{ ...cell, fontWeight: 700, color: accent }}>{r.tas_no} <span style={{ color: 'var(--text3)', fontWeight: 400 }}>›</span></td>
                <td style={cell}>{titleCase(r.buyer) || '—'}</td>
                <td style={cell}>{r.factory || '—'}</td>
                <td style={cell}>{fmtDMY(r.enquirydate) || '—'}</td>
                <td style={{ ...cell, fontWeight: r.fi_dt ? 600 : 400, color: Number(r.fi_delay_days || 0) > 20 ? '#c62828' : (r.fi_dt ? 'var(--text)' : 'var(--text3)') }}>
                  {fmtDMY(r.fi_dt) || '—'}
                  {Number(r.fi_delay_days || 0) > 20 && <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 8, background: '#ffebee', color: '#c62828', fontSize: 9.5, fontWeight: 700 }}>⚠️ +{r.fi_delay_days}d</span>}
                </td>
                <td style={cell}>{titleCase(r.merchandiser) || '—'}</td>
                {isDone && <><td style={cell}>{fmtDMY(r.approved_at) || '—'}</td><td style={cell}>{titleCase(r.sales_approved_by_name) || '—'}</td></>}
                <td style={cell}>{r.approved
                  ? <span style={{ padding: '2px 9px', borderRadius: 12, fontSize: 10, fontWeight: 700, background: '#e8f5e9', color: '#1b5e20' }}>APPROVED</span>
                  : <span style={{ padding: '2px 9px', borderRadius: 12, fontSize: 10, fontWeight: 700, background: '#fff3e0', color: '#b5651d' }}>AWAITING APPROVAL</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Local calendar date as YYYY-MM-DD (never toISOString — that shifts IST past midnight).
function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
// Soft copies captured with the shipment; the BL copy comes later at Shipment Update.
const SHIP_DOCS = ['fi_report', 'pkg_list', 'safta', 'azo', 'multi_price', 'invoice'];
function shipFileUrl(fid) { return `/api/shipments/file/${fid}?token=${encodeURIComponent(localStorage.getItem('dj_token') || '')}`; }
// Read a File as a data URL so it can be posted as JSON base64.
const readFileData = file => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
});
// Upload one attachment against a saved shipment.
const uploadShipDoc = (shipmentId, docType, file) => readFileData(file).then(data =>
  api('/shipments/file', { method: 'POST', body: JSON.stringify({ shipment_id: shipmentId, doc_type: docType, filename: file.name, data }) }));

// ===== SHIPMENT — export shipment entry (TAS). Header (ex-factory/BL, vessel/port/
// forwarder = manual text) + multi-TAS select + shade-wise qty (auto = order qty /
// manual = typed shipped qty). Writes dj_shipment / dj_shipment_det. =====
function ShipmentView({ setView, Header, factory, backTo }) {
  const accent = '#006a4e';
  const [tab, setTab] = useState('new');
  // Ex-factory date defaults to today; BL no/date/forwarder are NOT entered here —
  // they belong to Shipment Update, once the goods have actually shipped.
  const [hdr, setHdr] = useState({ ex_factory_dt: today(), remark: '' });
  const [docs, setDocs] = useState({});          // doc_type -> File, attached before save
  const [opts, setOpts] = useState(null);
  const [search, setSearch] = useState('');
  const [sel, setSel] = useState(() => new Set());
  const [rows, setRows] = useState([]);       // shade lines {tas_no,shade,shadeid,order_qty,ship_qty}
  const [loadingShades, setLoadingShades] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [draft, setDraft] = useState(null);        // edit form for the open saved shipment
  const [shipBusy, setShipBusy] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  const inp = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 13, outline: 'none' };
  const lbl = { fontSize: 10, letterSpacing: 1, color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 4, display: 'block' };

  const facQ = factory ? `?factory=${encodeURIComponent(factory)}` : '';
  useEffect(() => { api(`/shipments/tas-options${facQ}`).then(d => setOpts(Array.isArray(d) ? d : [])).catch(() => setOpts([])); }, [factory]);
  // Saved list loads up front too: it feeds the tab count and the Recent strip on the New tab.
  useEffect(() => { if (tab === 'saved' || saved === null) api(`/shipments${facQ}`).then(d => setSaved(Array.isArray(d) ? d : [])).catch(() => setSaved([])); }, [tab, factory]);
  // Jump to the Saved tab with one shipment opened.
  // Shipment number is a factory-wise series (ship_no); falls back to the row id for old rows.
  const shipLabel = s => `${!factory && s.factory ? titleCase(s.factory) + ' ' : ''}#${s.ship_no != null ? s.ship_no : s.id}`;
  // Open one saved shipment as its own screen (like opening a TAS); null = just the Saved list.
  const openSaved = s => { setTab('saved'); setExpanded(s ? s.id : null); if (s) loadDetail(s); };

  // Load shade-wise rows whenever the TAS selection changes.
  useEffect(() => {
    const list = [...sel];
    if (!list.length) { setRows([]); return; }
    setLoadingShades(true);
    api(`/shipments/shades?tasnos=${encodeURIComponent(list.join(','))}`)
      .then(d => {
        const arr = Array.isArray(d) ? d : [];
        setRows(prev => {
          const keyed = {}; prev.forEach(r => { keyed[r.tas_no + '|' + r.shadeid] = r.ship_qty; });
          return arr.map(r => ({ ...r, ship_qty: keyed[r.tas_no + '|' + r.shadeid] != null ? keyed[r.tas_no + '|' + r.shadeid] : '' }));
        });
      })
      .catch(() => setRows([]))
      .finally(() => setLoadingShades(false));
  }, [sel]);

  const toggleTas = t => setSel(s => { const n = new Set(s); n.has(t) ? n.delete(t) : n.add(t); return n; });
  const setShip = (tas, shadeid, v) => setRows(rs => rs.map(r => (r.tas_no === tas && r.shadeid === shadeid) ? { ...r, ship_qty: v } : r));

  // Group shade rows by TAS → one grid per TAS.
  const byTas = {};
  rows.forEach(r => { (byTas[r.tas_no] = byTas[r.tas_no] || []).push(r); });
  const buyerOf = {}; (opts || []).forEach(o => { buyerOf[o.tas_no] = o.buyer; });

  const save = () => {
    const lines = rows.map(r => ({
      tas_no: r.tas_no, shade: r.shade, shadeid: r.shadeid,
      order_qty: r.order_qty, ship_qty: (r.ship_qty === '' || r.ship_qty == null) ? 0 : (parseFloat(r.ship_qty) || 0)
    })).filter(l => l.ship_qty > 0);
    if (!lines.length) { alert('Enter a ship qty for at least one shade.'); return; }
    setSaving(true);
    // Save the shipment first — the attachments need its id.
    api('/shipments', { method: 'POST', body: JSON.stringify({ ...hdr, qty_mode: 'auto', lines }) })
      .then(async r => {
        const id = r && r.id;
        if (!id) {   // blocked by the server (e.g. over-shipment) — keep the form as entered
          setSaving(false);
          alert((r && r.error ? r.error : 'Save failed.') + (r && r.details ? '\n\n' + r.details.join('\n') : ''));
          return;
        }
        if (id) {
          for (const dt of SHIP_DOCS) {
            if (docs[dt]) { try { await uploadShipDoc(id, dt, docs[dt]); } catch (_) {} }
          }
        }
        setSaving(false);
        setHdr({ ex_factory_dt: today(), remark: '' }); setDocs({});
        setSel(new Set()); setRows([]); setTab('saved'); setSaved(null);
      })
      .catch(() => { setSaving(false); alert('Save failed.'); });
  };

  // Load one saved shipment's header + lines + files for its detail screen.
  const loadDetail = s => {
    if (s._detail) return;
    api(`/shipments/${s.id}`).then(d => { s._detail = d || {}; setSaved(x => [...x]); }).catch(() => { s._detail = { lines: [], files: [] }; setSaved(x => [...x]); });
  };

  const filteredOpts = (opts || []).filter(o => {
    const q = search.trim().toLowerCase();
    return !q || `${o.tas_no} ${o.buyer || ''}`.toLowerCase().includes(q);
  });

  // ===== Saved shipment — full screen, ← returns to the Saved list =====
  const openShip = expanded != null && saved ? saved.find(x => x.id === expanded) : null;
  if (openShip) {
    const s = openShip;
    // Edit / Delete only until Shipment Update adds BL details (the server enforces this too).
    const blDone = !!(s.bl_dt_s && s.bl_no && String(s.bl_no).trim());   // shipped = BL no + BL date
    const reloadList = () => api(`/shipments${facQ}`).then(d => setSaved(Array.isArray(d) ? d : [])).catch(() => {});
    const startEdit = () => setDraft({ ex_factory_dt: s._detail.ex_factory_dt || '', remark: s._detail.remark || '', qty: {} });
    const saveEdit = () => {
      const lines = (s._detail.lines || []).map(l => {
        const v = draft.qty[l.tas_no + '|' + l.shadeid];
        return { tas_no: l.tas_no, shadeid: l.shadeid, ship_qty: v != null && v !== '' ? parseFloat(v) || 0 : Number(l.ship_qty) || 0 };
      });
      setShipBusy(true);
      api(`/shipments/${s.id}`, { method: 'PUT', body: JSON.stringify({ ex_factory_dt: draft.ex_factory_dt, remark: draft.remark, lines }) })
        .then(r => {
          setShipBusy(false);
          if (!r || !r.ok) { alert(((r && r.error) || 'Save failed.') + (r && r.details ? '\n\n' + r.details.join('\n') : '')); return; }
          setDraft(null); s._detail = null; loadDetail(s); reloadList();
        })
        .catch(() => { setShipBusy(false); alert('Save failed.'); });
    };
    const del = () => {
      if (!window.confirm(`Delete shipment ${shipLabel(s)} (${s.tas_list || ''}, ${fmt(s.ship_qty)} pcs)? This cannot be undone.`)) return;
      setShipBusy(true);
      api(`/shipments/${s.id}`, { method: 'DELETE' })
        .then(r => {
          setShipBusy(false);
          if (!r || !r.ok) { alert((r && r.error) || 'Delete failed.'); return; }
          setDraft(null); setExpanded(null); setSaved(null); reloadList();
        })
        .catch(() => { setShipBusy(false); alert('Delete failed.'); });
    };
    const btn = (label, onClick, color, filled) => (
      <button onClick={onClick} disabled={shipBusy}
        style={{ padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: shipBusy ? 'default' : 'pointer',
          border: '1px solid ' + color, background: filled ? color : 'transparent', color: filled ? '#fff' : color, opacity: shipBusy ? 0.6 : 1 }}>{label}</button>
    );
    return (
      <div className="screen">
        {Header && (typeof Header === 'function' ? <Header subtitle={factory ? 'FACTORY · SHIPMENT' : 'TAS · SHIPMENT'} /> : Header)}
        {showVideo && <GmtTrainingModal factory={factory} initialStage={6} onClose={() => setShowVideo(false)} />}
        <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
          <button onClick={() => { setDraft(null); setExpanded(null); }} title="Back to saved shipments" style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
          <div>
            <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: accent }}>⛴ Shipment {shipLabel(s)}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>{factory ? factory + ' · ' : ''}{s.tas_list || '—'} · BL {s.bl_no || '—'}</div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ color: accent, fontWeight: 700, fontSize: 16 }}>{fmt(s.ship_qty)} pcs</div>
            {s._detail && !blDone && !draft && btn('✎ Edit', startEdit, accent)}
            {s._detail && !blDone && !draft && btn('🗑 Delete', del, '#c62828')}
            {draft && btn('Cancel', () => setDraft(null), 'var(--text3)')}
            {draft && btn(shipBusy ? 'Saving…' : 'Save Changes', saveEdit, accent, true)}
            {blDone && <span style={{ fontSize: 11, color: 'var(--text3)' }}>🔒 BL updated · locked</span>}
          </div>
        </div>
        <div style={{ padding: '4px 16px 24px' }}>
          {!s._detail
            ? <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Loading…</div>
            : <SavedShipmentBody detail={s._detail} lbl={lbl} accent={accent} draft={draft} setDraft={setDraft}
                onUpload={(dt, f) => uploadShipDoc(s.id, dt, f).then(() => { s._detail = null; loadDetail(s); }).catch(() => alert('Upload failed.'))} />}
        </div>
      </div>
    );
  }

  return (
    <div className="screen">
      {Header && (typeof Header === 'function' ? <Header subtitle={factory ? 'FACTORY · SHIPMENT' : 'TAS · SHIPMENT'} /> : Header)}
      {showVideo && <GmtTrainingModal factory={factory} initialStage={6} onClose={() => setShowVideo(false)} />}
      {/* Excel / PDF / Email / Schedule — eligible TAS and shade lines on the New tab,
          saved shipments on the Saved tab. */}
      <ExportBar
        reportKey={tab === 'saved' ? 'shipment-saved' : 'shipment-new'}
        title={`${factory || 'TAS'} - ${tab === 'saved' ? 'Saved Shipments' : 'Shipment Entry'}`}
        sections={tab === 'saved'
          ? [{ name: 'Saved Shipments',
               columns: [C('factory', 'Factory'), C('ship_no', 'Shipment No'), C('tas_list', 'TAS'), C('ex_factory_dt_s', 'Ex-Factory', 'date'),
                         C('ship_qty', 'Ship Qty', 'int', true), C('bl_no', 'BL No'), C('bl_dt_s', 'BL Date', 'date'),
                         C('vessel', 'Vessel'), C('destination_port', 'Destination Port'), C('forwarder', 'Forwarder')],
               rows: saved || [] }]
          : [{ name: 'Eligible TAS',
               columns: [C('tas_no', 'TAS'), C('buyer', 'Buyer'), C('order_qty', 'Order Qty', 'int', true), C('bal_qty', 'Balance', 'int', true)],
               rows: opts || [] },
             { name: 'Shade-wise Qty',
               columns: [C('tas_no', 'TAS'), C('shade', 'Shade'), C('order_qty', 'Order Qty', 'int', true),
                         C('prev_ship', 'Prev Ship', 'int', true), C('ship_qty', 'Ship Qty', 'int', true)],
               rows: rows || [] }]}
        source={{ endpoint: tab === 'saved' ? '/shipments' : '/shipments/tas-options' }}
        factory={factory} />
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
        <button onClick={() => (backTo ? backTo() : setView('tas'))} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: accent }}>⛴ {factory ? `${factory} — Shipment` : 'Export Shipment'}</div>
      </div>

      {/* Full-width tabs with a count, so the saved list is easy to find */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', padding: '0 16px' }}>
        {[['new', '＋ New Shipment'], ['saved', '📦 Saved Shipments']].map(([t, label]) => (
          <button key={t} onClick={() => setTab(t)}
            style={{ flex: '0 1 240px', padding: '12px 14px', background: 'none', border: 'none', cursor: 'pointer',
              borderBottom: `3px solid ${tab === t ? accent : 'transparent'}`, marginBottom: -1,
              color: tab === t ? accent : 'var(--text2)', fontSize: 14, fontWeight: tab === t ? 700 : 600 }}>
            {label}
            {t === 'saved' && saved && saved.length > 0 && (
              <span style={{ marginLeft: 8, padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                background: tab === t ? accent : 'var(--bg3)', color: tab === t ? '#fff' : 'var(--text2)' }}>{saved.length}</span>
            )}
          </button>
        ))}
      </div>

      {tab === 'new' && (
        <div style={{ padding: '16px' }}>
          {/* Header fields — shown first; date starts as today */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 18 }}>
            <div>
              <label style={lbl}>Ex Factory Date</label>
              <input type="date" style={inp} value={hdr.ex_factory_dt} onChange={e => setHdr({ ...hdr, ex_factory_dt: e.target.value })} />
              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>defaults to today</div>
            </div>
            <div style={{ gridColumn: 'span 2' }}><label style={lbl}>Remark</label><input style={inp} value={hdr.remark} onChange={e => setHdr({ ...hdr, remark: e.target.value })} /></div>
          </div>
          {/* TAS picker — pill chips (only TAS whose final costing is BD-approved) */}
          <div>
            <label style={lbl}>Select TAS ({sel.size} selected) · final costing approved only</label>
            <input style={{ ...inp, marginBottom: 10 }} placeholder="search TAS / buyer…" value={search} onChange={e => setSearch(e.target.value)} />
            {!opts && <div style={{ color: 'var(--text3)', fontSize: 12 }}>Loading…</div>}
            {opts && filteredOpts.length === 0 && (
              <div style={{ color: 'var(--text3)', fontSize: 12 }}>
                No eligible TAS — a TAS appears here once BD approves its Final Costing.
                {saved && saved.length > 0 && <> Already shipped? <span onClick={() => openSaved(null)} style={{ color: accent, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>View saved shipments →</span></>}
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {filteredOpts.map(o => {
                const on = sel.has(o.tas_no);
                return (
                  <button key={o.tas_no} onClick={() => toggleTas(o.tas_no)} title={titleCase(o.buyer) || ''}
                    style={{ padding: '6px 13px', borderRadius: 16, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      border: `1px solid ${on ? accent : 'var(--border)'}`,
                      background: on ? accent : 'var(--bg2)', color: on ? '#fff' : 'var(--text2)' }}>
                    {o.tas_no}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Shade-wise grid — one per selected TAS */}
          <div style={{ marginTop: 16 }}>
            {loadingShades && <div style={{ padding: 12, color: 'var(--text3)', fontSize: 12 }}>Loading shades…</div>}
            {!loadingShades && sel.size === 0 && <div style={{ padding: 12, color: 'var(--text3)', fontSize: 12 }}>Select TAS to view shade-wise quantity.</div>}
            {Object.keys(byTas).map(tn => {
              const list = byTas[tn];
              const num = v => parseFloat(v) || 0;
              const totOrder = list.reduce((a, r) => a + num(r.order_qty), 0);
              const totPrev = list.reduce((a, r) => a + num(r.prev_ship), 0);
              const totShip = list.reduce((a, r) => a + num(r.ship_qty), 0);
              const totBal = list.reduce((a, r) => a + (num(r.order_qty) - num(r.prev_ship) - num(r.ship_qty)), 0);
              const GC = 'minmax(140px, 1fr) 130px 130px 130px 130px';
              return (
                <div key={tn} style={{ border: SHIP_LINE, borderRadius: 8, overflow: 'hidden', marginBottom: 14 }}>
                  <div style={{ padding: '9px 12px', background: `${accent}12`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 700, color: accent, fontSize: 13 }}>{tn}</span>
                    <span style={{ fontSize: 11, color: 'var(--text3)' }}>{titleCase(buyerOf[tn]) || ''} · {list.length} shade{list.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: GC, background: 'var(--bg3)', fontSize: 10, letterSpacing: 1, color: 'var(--text2)', fontWeight: 700, textTransform: 'uppercase', borderTop: SHIP_LINE }}>
                    <span style={shipTd(0)}>Shade</span><span style={shipTd(1)}>Order Qty</span><span style={shipTd(2)}>Prev Ship</span><span style={shipTd(3)}>Ship Qty</span><span style={shipTd(4)}>Balance</span>
                  </div>
                  {list.map((r, i) => {
                    const bal = num(r.order_qty) - num(r.prev_ship) - num(r.ship_qty);
                    return (
                      <div key={r.shadeid + '|' + i} style={{ display: 'grid', gridTemplateColumns: GC, borderTop: SHIP_LINE, fontSize: 12, alignItems: 'stretch' }}>
                        <span style={{ ...shipTd(0), color: 'var(--text)' }}>{r.shade}</span>
                        <span style={{ ...shipTd(1), color: 'var(--text3)' }}>{fmt(r.order_qty)}</span>
                        <span style={{ ...shipTd(2), color: 'var(--text3)' }}>{fmt(r.prev_ship)}</span>
                        <span style={{ ...shipTd(3), padding: '4px 8px' }}>
                          <input type="number" value={r.ship_qty} placeholder="0" onChange={e => setShip(r.tas_no, r.shadeid, e.target.value)}
                            style={{ width: '100%', boxSizing: 'border-box', padding: '5px 10px', textAlign: 'right', fontSize: 12, fontWeight: 700, color: 'var(--text)', background: '#fffbe6', border: `1.5px solid ${accent}`, borderRadius: 4, outline: 'none' }} />
                        </span>
                        <span style={{ ...shipTd(4), fontWeight: 600, color: bal < 0 ? 'var(--red)' : 'var(--text2)' }}>{fmt(bal)}</span>
                      </div>
                    );
                  })}
                  <div style={{ display: 'grid', gridTemplateColumns: GC, borderTop: '2px solid #9aa5b1', fontSize: 12, fontWeight: 700, background: 'var(--bg3)' }}>
                    <span style={{ ...shipTd(0), color: 'var(--text)' }}>TOTAL</span>
                    <span style={{ ...shipTd(1), color: 'var(--text3)' }}>{fmt(totOrder)}</span>
                    <span style={{ ...shipTd(2), color: 'var(--text3)' }}>{fmt(totPrev)}</span>
                    <span style={{ ...shipTd(3), color: accent, paddingRight: 20 }}>{fmt(totShip)}</span>
                    <span style={{ ...shipTd(4), color: totBal < 0 ? 'var(--red)' : 'var(--text2)' }}>{fmt(totBal)}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Shipment details appear once a TAS is selected */}
          {sel.size > 0 && (
            <div style={{ marginTop: 18 }}>
              {/* Soft copies — attached with the shipment. BL copy comes at Shipment Update. */}
              <div style={{ border: SHIP_LINE, borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ padding: '9px 12px', background: `${accent}12`, fontWeight: 700, color: accent, fontSize: 13 }}>Attach Soft Copies</div>
                <div style={{ display: 'grid', gridTemplateColumns: SHIP_DC, background: 'var(--bg3)', fontSize: 10, letterSpacing: 1, color: 'var(--text2)', fontWeight: 700, textTransform: 'uppercase', borderTop: SHIP_LINE }}>
                  <span style={shipTd(0)}>Document</span><span style={{ ...shipTd(1), justifyContent: 'flex-start', textAlign: 'left' }}>File</span><span style={{ ...shipTd(2), justifyContent: 'center' }}>Action</span>
                </div>
                {SHIP_DOCS.map(dt => (
                  <div key={dt} style={{ display: 'grid', gridTemplateColumns: SHIP_DC, borderTop: SHIP_LINE, fontSize: 12, alignItems: 'stretch' }}>
                    <span style={{ ...shipTd(0), color: 'var(--text)' }}>{DOC_LABELS[dt]}</span>
                    <span style={{ ...shipTd(1), justifyContent: 'flex-start', textAlign: 'left' }}>{docs[dt]
                      ? <span style={{ color: accent, fontWeight: 600 }}>📎 {docs[dt].name}</span>
                      : <span style={{ color: 'var(--text3)' }}>— not attached —</span>}</span>
                    <span style={{ ...shipTd(2), justifyContent: 'center', padding: '5px 8px' }}>
                      <label style={{ width: '100%', boxSizing: 'border-box', textAlign: 'center', fontSize: 11, fontWeight: 700, color: accent, cursor: 'pointer', border: `1.5px solid ${accent}66`, background: `${accent}10`, borderRadius: 4, padding: '5px 10px' }}>
                        {docs[dt] ? 'Replace' : 'Attach'}
                        <input type="file" style={{ display: 'none' }}
                          onChange={e => { const f = e.target.files[0]; if (f) setDocs(d => ({ ...d, [dt]: f })); e.target.value = ''; }} />
                      </label>
                    </span>
                  </div>
                ))}
              </div>
    
              <button onClick={save} disabled={saving || rows.length === 0}
                style={{ marginTop: 18, width: '100%', padding: '11px', borderRadius: 8, border: 'none', cursor: saving ? 'default' : 'pointer',
                  background: accent, color: '#fff', fontSize: 14, fontWeight: 700, opacity: (saving || rows.length === 0) ? 0.6 : 1 }}>
                {saving ? 'Saving…' : 'Save Shipment'}
              </button>
            </div>
          )}
        </div>
      )}

      {tab === 'saved' && (
        <div style={{ padding: '12px 16px' }}>
          {!saved && <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Loading…</div>}
          {saved && saved.length === 0 && <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>No shipments yet.</div>}
          {saved && saved.map(s => (
            <div key={s.id} style={{ border: '1px solid var(--border)', borderLeft: `3px solid ${accent}`, borderRadius: 8, padding: 12, marginBottom: 10, background: 'var(--bg2)' }}>
              <div onClick={() => openSaved(s)} style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div style={{ fontWeight: 700, color: 'var(--text)' }}>{shipLabel(s)} · BL {s.bl_no || '—'}</div>
                  <div style={{ color: accent, fontWeight: 700 }}>{fmt(s.ship_qty)} pcs</div>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                  {s.vessel || '—'} → {s.destination_port || '—'} · {s.forwarder || '—'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>
                  Ex-factory {s.ex_factory_dt_s || '—'} · BL {s.bl_dt_s || '—'} · {s.tas_count} TAS ({s.qty_mode})
                </div>
                <div style={{ display: 'flex', alignItems: 'center', fontSize: 11, color: 'var(--text2)', marginTop: 3 }}>
                  <span>{s.tas_list}</span>
                  <span style={{ marginLeft: 'auto', color: accent, fontWeight: 600 }}>OPEN →</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Saved shipment preview — same layout as the New tab (header, soft copies, per-TAS
// shade grid with TOTAL), read-only except attaching any missing soft copy.
// Shipment tables: clearly visible cell borders; column 0 left-aligned, the rest right-aligned.
const SHIP_LINE = '1px solid #c3cbd5';
const SHIP_DC = 'minmax(140px, 220px) 1fr 130px';          // soft-copies table
const shipTd = col => ({ padding: '7px 12px', display: 'flex', alignItems: 'center', justifyContent: col ? 'flex-end' : 'flex-start', textAlign: col ? 'right' : 'left', borderLeft: col ? SHIP_LINE : 'none' });

function SavedShipmentBody({ detail, lbl, accent, onUpload, draft, setDraft }) {
  const editing = !!draft;
  const inp = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', background: '#fffbe6', border: `1.5px solid ${accent}`, borderRadius: 6, color: 'var(--text)', fontSize: 13, fontWeight: 600, outline: 'none' };
  const qtyOf = r => (editing && draft.qty[r.tas_no + '|' + r.shadeid] != null ? draft.qty[r.tas_no + '|' + r.shadeid] : r.ship_qty);
  const num = v => parseFloat(v) || 0;
  const box = { padding: '8px 10px', background: `${accent}10`, border: `1.5px solid ${accent}66`, borderRadius: 6, fontSize: 13, fontWeight: 600, color: 'var(--text)', minHeight: 18 };
  const byTas = {};
  (detail.lines || []).forEach(l => { (byTas[l.tas_no] = byTas[l.tas_no] || []).push(l); });
  const GC = 'minmax(140px, 1fr) 130px 130px 130px';
  const DC = SHIP_DC, LINE = SHIP_LINE, td = shipTd;
  return (
    <div style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <div><label style={lbl}>Ex Factory Date</label>{editing
          ? <input type="date" style={inp} value={draft.ex_factory_dt || ''} onChange={e => setDraft({ ...draft, ex_factory_dt: e.target.value })} />
          : <div style={box}>{detail.ex_factory_dt ? detail.ex_factory_dt.split('-').reverse().join('-') : '—'}</div>}</div>
        <div style={{ gridColumn: 'span 2' }}><label style={lbl}>Remark</label>{editing
          ? <input style={inp} value={draft.remark || ''} onChange={e => setDraft({ ...draft, remark: e.target.value })} />
          : <div style={box}>{detail.remark || '—'}</div>}</div>
      </div>

      <div style={{ marginTop: 14 }}>
        {Object.keys(byTas).map(tn => {
          const list = byTas[tn];
          const totOrder = list.reduce((a, r) => a + num(r.order_qty), 0);
          const totShip = list.reduce((a, r) => a + num(qtyOf(r)), 0);
          return (
            <div key={tn} style={{ border: LINE, borderRadius: 8, overflow: 'hidden', marginBottom: 12, background: 'var(--bg)' }}>
              <div style={{ padding: '9px 12px', background: `${accent}12`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 700, color: accent, fontSize: 13 }}>{tn}</span>
                <span style={{ fontSize: 11, color: 'var(--text3)' }}>{list[0]?.style_name || ''}{list[0]?.style_name ? ' · ' : ''}{list.length} shade{list.length !== 1 ? 's' : ''}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: GC, background: 'var(--bg3)', fontSize: 10, letterSpacing: 1, color: 'var(--text2)', fontWeight: 700, textTransform: 'uppercase', borderTop: LINE }}>
                <span style={td(0)}>Shade</span><span style={td(1)}>Order Qty</span><span style={td(2)}>Ship Qty</span><span style={td(3)}>Excess / Short</span>
              </div>
              {list.map((r, i) => {
                const diff = num(qtyOf(r)) - num(r.order_qty);
                return (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: GC, borderTop: LINE, fontSize: 12, alignItems: 'stretch' }}>
                    <span style={{ ...td(0), color: 'var(--text)' }}>{r.shade}</span>
                    <span style={{ ...td(1), color: 'var(--text3)' }}>{fmt(r.order_qty)}</span>
                    <span style={{ ...td(2), padding: '4px 8px' }}>{editing
                      ? <input type="number" min="0" value={qtyOf(r)} onChange={e => setDraft({ ...draft, qty: { ...draft.qty, [r.tas_no + '|' + r.shadeid]: e.target.value } })}
                          style={{ ...inp, padding: '4px 6px', textAlign: 'right', fontSize: 12 }} />
                      : <span style={{ display: 'block', width: '100%', boxSizing: 'border-box', textAlign: 'right', color: 'var(--text)', fontWeight: 700, padding: '5px 10px', background: `${accent}10`, border: `1.5px solid ${accent}66`, borderRadius: 4 }}>{fmt(r.ship_qty)}</span>}</span>
                    <span style={{ ...td(3), fontWeight: 600, color: diff < 0 ? 'var(--red)' : diff > 0 ? accent : 'var(--text3)' }}>{diff > 0 ? '+' : ''}{fmt(diff)}</span>
                  </div>
                );
              })}
              <div style={{ display: 'grid', gridTemplateColumns: GC, borderTop: '2px solid #9aa5b1', fontSize: 12, fontWeight: 700, background: 'var(--bg3)' }}>
                <span style={{ ...td(0), color: 'var(--text)' }}>TOTAL</span>
                <span style={{ ...td(1), color: 'var(--text3)' }}>{fmt(totOrder)}</span>
                <span style={{ ...td(2), color: accent, paddingRight: 20 }}>{fmt(totShip)}</span>
                <span style={{ ...td(3), color: totShip - totOrder < 0 ? 'var(--red)' : accent }}>{totShip - totOrder > 0 ? '+' : ''}{fmt(totShip - totOrder)}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 14, border: LINE, borderRadius: 8, overflow: 'hidden', background: 'var(--bg)' }}>
        <div style={{ padding: '9px 12px', background: `${accent}12`, fontWeight: 700, color: accent, fontSize: 13 }}>Attach Soft Copies</div>
        <div style={{ display: 'grid', gridTemplateColumns: DC, background: 'var(--bg3)', fontSize: 10, letterSpacing: 1, color: 'var(--text2)', fontWeight: 700, textTransform: 'uppercase', borderTop: LINE }}>
          <span style={td(0)}>Document</span><span style={{ ...td(1), justifyContent: 'flex-start', textAlign: 'left' }}>File</span><span style={{ ...td(2), justifyContent: 'center' }}>Action</span>
        </div>
        {SHIP_DOCS.map(dt => (
          <DocRow key={dt} label={DOC_LABELS[dt]} docType={dt} detail={detail} onUpload={onUpload} urlOf={shipFileUrl} cols={DC} td={td} line={LINE} />
        ))}
      </div>
    </div>
  );
}

// ===== EXPORT WORKFLOW — Final Costing → Ex Fact → Shipment Update (per TAS) =====
const DOC_LABELS = { costsheet: 'Cost Sheet', fi_report: 'FI Report', pkg_list: 'Packing List', safta: 'SAFTA', azo: 'AZO', multi_price: 'Multi-Price Cost Sheet', invoice: 'Invoice', bl_copy: 'BL Copy' };
function fileUrl(fid) { return `/api/shipflow/file/${fid}?token=${encodeURIComponent(localStorage.getItem('dj_token') || '')}`; }

// One attachment row: shows existing file (view link) + upload/replace button.
function DocRow({ label, docType, detail, onUpload, urlOf, cols, td, line }) {
  const url = urlOf || fileUrl;          // shipflow by default; shipments pass their own
  const f = (detail?.files || []).find(x => x.doc_type === docType);
  if (cols) return (                     // table layout: cols / td / line come from the parent table
    <div style={{ display: 'grid', gridTemplateColumns: cols, borderTop: line, fontSize: 12, alignItems: 'stretch' }}>
      <span style={{ ...td(0), color: 'var(--text)' }}>{label}</span>
      <span style={{ ...td(1), justifyContent: 'flex-start', textAlign: 'left' }}>{f
        ? <a href={url(f.id)} target="_blank" rel="noreferrer" style={{ color: '#006a4e', fontWeight: 600 }}>📎 {f.filename}</a>
        : <span style={{ color: 'var(--text3)' }}>— not attached —</span>}</span>
      <span style={{ ...td(2), justifyContent: 'center', padding: '5px 8px' }}>
        {onUpload
          ? <label style={{ width: '100%', boxSizing: 'border-box', textAlign: 'center', fontSize: 11, fontWeight: 700, color: '#006a4e', cursor: 'pointer', border: '1.5px solid #006a4e66', background: '#006a4e10', borderRadius: 4, padding: '5px 10px' }}>
              {f ? 'Replace' : 'Upload'}
              <input type="file" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) onUpload(docType, e.target.files[0]); e.target.value = ''; }} />
            </label>
          : <span style={{ color: 'var(--text3)' }}>—</span>}
      </span>
    </div>
  );
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontSize: 12, color: 'var(--text2)', width: 180 }}>{label}</span>
      {f
        ? <a href={url(f.id)} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#006a4e', fontWeight: 600 }}>📎 {f.filename}</a>
        : <span style={{ fontSize: 12, color: 'var(--text3)' }}>— not attached —</span>}
      <label style={{ marginLeft: 'auto', fontSize: 11, color: '#006a4e', cursor: 'pointer', border: '1px solid var(--border)', borderRadius: 14, padding: '3px 10px' }}>
        {f ? 'Replace' : 'Upload'}
        <input type="file" style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) onUpload(docType, e.target.files[0]); e.target.value = ''; }} />
      </label>
    </div>
  );
}

// ===== FINAL COSTING (factory) — opens as soon as a TAS is in production and its
// sales cost sheet is approved. Pre-filled from that approved sheet. App-only:
// final costing is deliberately NOT written back to the ERP. =====
function FinalCostEntry({ tas, factory, Header, onBack, onSaved }) {
  const accent = '#b5651d';
  const [data, setData] = useState(null);
  const [line, setLine] = useState(null);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  useEffect(() => {
    let live = true;
    api(`/costflow/${encodeURIComponent(tas)}/final-entry`).then(d => {
      if (!live) return;
      setData(d || {});
      setLine({ ...(((d && d.lines) || [])[0] || {}) });
    }).catch(() => live && setData({ error: 'Load failed' }));
    return () => { live = false; };
  }, [tas]);

  const locked = !!(data && data.locked);
  const h = (data && data.header) || {};
  // Show stored figures until the user edits; then show the live calculation.
  const live = !(data && data.prepared) || touched;
  const calc = !line ? {} : (live ? csCalc(line)
    : { cif: csR4(line.cif), fabcost: csR4(line.fabcost), cog: csR4(line.cog), inr: csR3(line.inr) });
  const onCell = (k, v) => { setTouched(true); setLine(l => ({ ...l, [k]: v })); };
  const save = () => {
    setBusy(true); setMsg('');
    api(`/costflow/${encodeURIComponent(tas)}/final-entry`, { method: 'POST', body: JSON.stringify({ lines: [{ ...line, ...calc }] }) })
      .then(r => {
        setBusy(false);
        if (!r || r.error) { setMsg((r && r.error) || 'Save failed'); return; }
        if (Array.isArray(r.lines) && r.lines[0]) { setLine({ ...r.lines[0] }); setTouched(false); }
        setData(d => ({ ...d, prepared: true }));
        setMsg('Final costing saved ✓');
        setTimeout(() => { onSaved && onSaved(); }, 900);
      })
      .catch(() => { setBusy(false); setMsg('Save failed'); });
  };

  return (
    <div className="screen">
      <ExportBar reportKey="final-cost-entry" title={`${tas} - Final Costing`}
        sections={[{ name: 'Final Costing', columns: [C('item','Item'), C('value','Value','dec2')],
          rows: line ? CS_COLS.map(c => ({ item: c.label, value: csR2(csRK(c.k, c.calc ? calc[c.k] : line[c.k])) })) : [] }]}
        source={{ endpoint: `/costflow/${encodeURIComponent(tas)}/final-entry` }} />
      {Header && (typeof Header === 'function' ? <Header subtitle="FACTORY · FINAL COST SHEET" /> : Header)}
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 20, color: accent }}>📑 {tas} — Final Costing</div>
          {(h.buyer || h.factory) && (
            <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 2 }}>
              {titleCase(h.buyer) || '—'}{h.factory ? ` · ${h.factory}` : ''}{h.broker ? ` · Merch: ${titleCase(h.broker)}` : ''}
            </div>
          )}
        </div>
        {locked && <span style={{ padding: '4px 10px', borderRadius: 20, background: '#e8f5e9', color: '#1b5e20', fontSize: 11, fontWeight: 700 }}>APPROVED · LOCKED</span>}
        {!locked && data && data.prepared && <span style={{ padding: '4px 10px', borderRadius: 20, background: '#fff3e0', color: accent, fontSize: 11, fontWeight: 700 }}>AWAITING BD APPROVAL</span>}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          {h.enquiry_dt_s && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 9.5, letterSpacing: 0.8, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase' }}>Enquiry Date</div>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text2)', marginTop: 2 }}>{fmtDMY(h.enquiry_dt_s)}</div>
            </div>
          )}
          {h.target_fi_dt && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 9.5, letterSpacing: 0.8, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase' }}>Target FI (Est - 20d)</div>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: accent, marginTop: 2 }}>{fmtDMY(h.target_fi_dt)}</div>
            </div>
          )}
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 9.5, letterSpacing: 0.8, color: Number(h.fi_delay_days || 0) > 20 ? '#c62828' : accent, fontWeight: 800, textTransform: 'uppercase' }}>
              Factory FI Date {Number(h.fi_delay_days || 0) !== 0 ? `(${Number(h.fi_delay_days) > 0 ? '+' : ''}${h.fi_delay_days}d)` : ''}
            </div>
            <div style={{ fontSize: 12.5, fontWeight: 800, color: Number(h.fi_delay_days || 0) > 20 ? '#c62828' : 'var(--text)', marginTop: 2 }}>
              {fmtDMY(h.fi_dt || h.target_fi_dt) || '—'}
            </div>
          </div>
        </div>
      </div>

      {!data && <div style={{ textAlign: 'center', padding: 50, color: 'var(--text3)', fontSize: 13 }}>Loading…</div>}
      {data && data.error && <div style={{ textAlign: 'center', padding: 50, color: '#c62828', fontSize: 13 }}>{data.error}</div>}

      {data && !data.error && line && (
        <>
          <div style={{ padding: '16px 16px 8px', display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>Final Cost Grid</div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>
              {data.source === 'sales' && !data.prepared
                ? 'Pre-filled from the approved Sales Contract Cost Sheet — change what differs in production'
                : '$ 4 decimals · ₹ 3'}
            </div>
          </div>
          <div style={{ padding: '0 16px' }}>
            <CostGridCard line={line} calc={calc} locked={locked} onCell={onCell} title="FINAL COSTING" accent={accent} />
          </div>
          <div style={{ padding: '16px 20px 30px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            {locked
              ? <div style={{ fontSize: 12, color: 'var(--text3)' }}>Final costing is approved by BD and can no longer be changed.</div>
              : <button onClick={save} disabled={busy}
                  style={{ padding: '11px 26px', borderRadius: 8, border: 'none', background: busy ? 'var(--text3)' : accent, color: '#fff', fontSize: 13, fontWeight: 700, cursor: busy ? 'default' : 'pointer' }}>
                  {busy ? 'Saving…' : (data.prepared ? 'Update Final Costing' : 'Save Final Costing')}
                </button>}
            {msg && <div style={{ fontSize: 12, fontWeight: 700, color: /✓/.test(msg) ? '#1b5e20' : '#c62828' }}>{msg}</div>}
          </div>
        </>
      )}
    </div>
  );
}

// ===== BD — FINAL COSTING APPROVAL. Sales vs Final side by side. =====
function BdFinalApprovalView({ setView, Header }) {
  const accent = '#b5651d';
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState(null);
  const [cmp, setCmp] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('pending');     // pending first; approved on demand
  const [q, setQ] = useState('');
  const load = () => api('/costflow/final-approval').then(d => setRows(Array.isArray(d) ? d : [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const open = r => { setSel(r); setCmp(null); api(`/costflow/${encodeURIComponent(r.tas_no)}/final-compare`).then(setCmp).catch(() => setCmp({})); };
  const approve = () => {
    setBusy(true);
    api(`/costflow/${encodeURIComponent(sel.tas_no)}/approve-final`, { method: 'POST', body: '{}' })
      .then(r => { setBusy(false); if (r && r.error) { alert(r.error); return; } setSel(null); load(); })
      .catch(() => { setBusy(false); alert('Approve failed'); });
  };
  const cell = { padding: '8px 10px', fontSize: 12, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
  const hd = { ...cell, color: 'var(--text3)', fontSize: 10, letterSpacing: 1, background: 'var(--bg3)', textAlign: 'left' };

  if (sel) {
    const s = cmp && cmp.sales, f = cmp && cmp.final;
    const ch = (cmp && cmp.header) || {};
    const shownFiDt = sel.fi_dt || ch.fi_dt || sel.target_fi_dt || ch.target_fi_dt || '';
    const delayDays = Number(sel.fi_delay_days ?? ch.fi_delay_days ?? 0);
    return (
      <div className="screen">
        <Header subtitle="BD · FINAL COSTING APPROVAL" />
        <ExportBar reportKey="bd-final-compare" title={`${sel.tas_no} - Sales Contract vs Final Costing`}
          sections={[{ name: 'Sales vs Final',
            columns: [C('item', 'Item'), C('sales', 'Sales Contract', 'dec2'), C('final', 'Final', 'dec2'), C('diff', 'Difference', 'dec2')],
            rows: CS_COLS.map(c => { const sv = cmp && cmp.sales ? csNum(cmp.sales[c.k]) : 0, fv = cmp && cmp.final ? csNum(cmp.final[c.k]) : 0;
              return { item: c.label, sales: csR2(csRK(c.k, sv)), final: csR2(csRK(c.k, fv)), diff: csR2(csR2(csRK(c.k, fv)) - csR2(csRK(c.k, sv))) }; }) }]}
          source={{ endpoint: `/costflow/${encodeURIComponent(sel.tas_no)}/final-compare` }} />
        <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
          <button onClick={() => setSel(null)} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
          <div>
            <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 20, color: accent }}>📑 {sel.tas_no}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 2 }}>{titleCase(sel.buyer)} · {titleCase(sel.factory)}</div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            {(sel.enquirydate || ch.enquiry_dt_s) && (
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 9.5, letterSpacing: 0.8, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase' }}>Enquiry Date</div>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text2)', marginTop: 2 }}>{fmtDMY(sel.enquirydate || ch.enquiry_dt_s)}</div>
              </div>
            )}
            {(sel.target_fi_dt || ch.target_fi_dt) && (
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 9.5, letterSpacing: 0.8, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase' }}>Target FI (Est - 20d)</div>
                <div style={{ fontSize: 12.5, fontWeight: 800, color: accent, marginTop: 2 }}>{fmtDMY(sel.target_fi_dt || ch.target_fi_dt)}</div>
              </div>
            )}
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 9.5, letterSpacing: 0.8, color: delayDays > 20 ? '#c62828' : accent, fontWeight: 800, textTransform: 'uppercase' }}>
                Factory FI Date {delayDays !== 0 ? `(${delayDays > 0 ? '+' : ''}${delayDays}d)` : ''}
              </div>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: delayDays > 20 ? '#c62828' : 'var(--text)', marginTop: 2 }}>
                {fmtDMY(shownFiDt) || '—'}
              </div>
            </div>
          </div>
        </div>
        {!cmp && <div style={{ textAlign: 'center', padding: 50, color: 'var(--text3)', fontSize: 13 }}>Loading…</div>}
        {cmp && (
          <>
            <div style={{ padding: '12px 16px 4px', fontSize: 12, fontWeight: 700, color: 'var(--text2)' }}>SALES CONTRACT vs FINAL</div>
            <div style={{ margin: '0 16px', border: '1px solid var(--border)', borderRadius: 8, overflowX: 'auto' }}>
              <table style={{ width: '100%', minWidth: 440, borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={hd}>Item</th>
                  <th style={{ ...hd, textAlign: 'right' }}>Sales Contract</th>
                  <th style={{ ...hd, textAlign: 'right' }}>Final</th>
                  <th style={{ ...hd, textAlign: 'right' }}>Difference</th>
                </tr></thead>
                <tbody>
                  {CS_COLS.map(c => {
                    const sv = s ? csNum(s[c.k]) : 0, fv = f ? csNum(f[c.k]) : 0;
                    const d = csR2(csR2(csRK(c.k, fv)) - csR2(csRK(c.k, sv)));   // difference of the figures shown
                    return (
                      <tr key={c.k} style={{ background: d ? 'rgba(181,101,29,0.06)' : 'transparent' }}>
                        <td style={{ ...cell, fontWeight: c.big ? 800 : 500 }}>{c.label} <span style={{ color: 'var(--text3)', fontSize: 10 }}>{csUnit(c)}</span></td>
                        <td style={{ ...cell, textAlign: 'right', color: 'var(--text3)' }}>{s ? csFmt(sv, c.k) : '—'}</td>
                        <td style={{ ...cell, textAlign: 'right', fontWeight: 700 }}>{f ? csFmt(fv, c.k) : '—'}</td>
                        <td style={{ ...cell, textAlign: 'right', fontWeight: 700, color: d > 0 ? '#c62828' : d < 0 ? '#1b5e20' : 'var(--text3)' }}>
                          {d ? (d > 0 ? '+' : '') + csFmt(d, c.k) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ padding: '16px 20px 30px', display: 'flex', alignItems: 'center', gap: 14 }}>
              {cmp.approved
                ? <div style={{ fontSize: 12, color: '#1b5e20', fontWeight: 700 }}>Already approved ✓</div>
                : <button onClick={approve} disabled={busy}
                    style={{ padding: '11px 26px', borderRadius: 8, border: 'none', background: busy ? 'var(--text3)' : accent, color: '#fff', fontSize: 13, fontWeight: 700, cursor: busy ? 'default' : 'pointer' }}>
                    {busy ? 'Approving…' : 'Approve Final Costing'}
                  </button>}
            </div>
          </>
        )}
      </div>
    );
  }

  // list — one row per TAS (the TAS help view can fan out), split by the indicator tiles
  const uniq = rows ? Object.values(rows.reduce((m, r) => { if (!m[r.tas_no]) m[r.tas_no] = r; return m; }, {})) : null;
  const pend = uniq ? uniq.filter(r => !r.approved).length : null;
  const appr = uniq ? uniq.filter(r => r.approved).length : null;
  const isDone = tab === 'approved';
  const listName = isDone ? 'Approved' : 'Pending Approval';
  const list = (uniq || []).filter(r => {
    if (isDone ? !r.approved : r.approved) return false;
    const t = q.trim().toLowerCase();
    return !t || `${r.tas_no} ${r.buyer || ''} ${r.factory || ''} ${r.merchandiser || ''}`.toLowerCase().includes(t);
  });
  const tile = (key, label, n, sub, color) => (
    <div key={key} onClick={() => setTab(key)}
      style={{ flex: '1 1 220px', maxWidth: 340, padding: '16px 18px', borderRadius: 12, cursor: 'pointer', boxSizing: 'border-box',
        border: (tab === key ? 2 : 1) + 'px solid ' + (tab === key ? color : 'var(--border)'), background: tab === key ? color + '10' : 'var(--bg2)' }}>
      <div style={{ fontSize: 30, fontWeight: 700, color, fontFamily: 'Inter, sans-serif', lineHeight: 1 }}>{n == null ? '…' : fmt(n)}</div>
      <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.2, color, marginTop: 6 }}>{label}</div>
      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{sub}</div>
    </div>
  );
  return (
    <div className="screen">
      <Header subtitle="BD · FINAL COSTING APPROVAL" />
      <ExportBar reportKey="bd-final-approval" title={`Final Costing Approval - ${listName}`}
        sections={[{ name: listName,
          columns: [C('tas_no', 'TAS'), C('buyer', 'Buyer'), C('factory', 'Factory'), C('enquirydate', 'Enquiry Date', 'date'), C('fi_dt', 'FI Date', 'date'), C('merchandiser', 'Merchandiser'),
                    ...(isDone ? [C('approved_at', 'Approved On', 'date'), C('final_approved_by_name', 'Approved By')] : [])],
          rows: list }]}
        source={{ endpoint: '/costflow/final-approval' }} />
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
        <button onClick={() => setView('bd')} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: accent }}>Final Costing Approval</div>
      </div>

      {/* Key indicators — each one also switches the list below */}
      <div style={{ padding: '16px 16px 4px', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {tile('pending', 'PENDING APPROVAL', pend, 'final costing prepared · awaiting approval', '#b5651d')}
        {tile('approved', 'APPROVED', appr, 'approved · locked', '#1b5e20')}
      </div>

      <div style={{ padding: '10px 16px 6px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)' }}>
          {listName.toUpperCase()}
          <span style={{ color: 'var(--text3)', fontWeight: 400 }}> · {list.length} TAS · {isDone ? 'click a TAS to view sales vs final' : 'click a TAS to compare and approve'}</span>
        </div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="search TAS / buyer / factory…"
          style={{ marginLeft: 'auto', minWidth: 220, padding: '7px 10px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 12, outline: 'none' }} />
      </div>
      <div style={{ margin: '0 16px 24px', border: '1px solid var(--border)', borderRadius: 8, overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 740, borderCollapse: 'collapse' }}>
          <thead><tr><th style={hd}>TAS</th><th style={hd}>Buyer</th><th style={hd}>Factory</th><th style={hd}>Enquiry Date</th><th style={hd}>FI Date</th><th style={hd}>Merchandiser</th>{isDone && <><th style={hd}>Approved On</th><th style={hd}>Approved By</th></>}<th style={hd}>Status</th></tr></thead>
          <tbody>
            {!rows && <tr><td colSpan={isDone ? 9 : 7} style={{ ...cell, textAlign: 'center', color: 'var(--text3)' }}>Loading…</td></tr>}
            {rows && list.length === 0 && (
              <tr><td colSpan={isDone ? 9 : 7} style={{ ...cell, textAlign: 'center', color: 'var(--text3)' }}>
                {isDone ? 'No approved final costings yet.' : 'Nothing awaiting final costing approval.'}
              </td></tr>
            )}
            {list.map(r => (
              <tr key={r.tas_no} onClick={() => open(r)} style={{ cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg3)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                <td style={{ ...cell, fontWeight: 700, color: accent }}>{r.tas_no} <span style={{ color: 'var(--text3)', fontWeight: 400 }}>›</span></td>
                <td style={cell}>{titleCase(r.buyer) || '—'}</td>
                <td style={cell}>{titleCase(r.factory) || '—'}</td>
                <td style={cell}>{fmtDMY(r.enquirydate) || '—'}</td>
                <td style={{ ...cell, fontWeight: r.fi_dt ? 600 : 400, color: Number(r.fi_delay_days || 0) > 20 ? '#c62828' : (r.fi_dt ? 'var(--text)' : 'var(--text3)') }}>
                  {fmtDMY(r.fi_dt) || '—'}
                  {Number(r.fi_delay_days || 0) > 20 && <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 8, background: '#ffebee', color: '#c62828', fontSize: 9.5, fontWeight: 700 }}>⚠️ +{r.fi_delay_days}d</span>}
                </td>
                <td style={cell}>{titleCase(r.merchandiser) || '—'}</td>
                {isDone && <><td style={cell}>{fmtDMY(r.approved_at) || '—'}</td><td style={cell}>{titleCase(r.final_approved_by_name) || '—'}</td></>}
                <td style={cell}>
                  {r.approved
                    ? <span style={{ padding: '2px 9px', borderRadius: 12, fontSize: 10, fontWeight: 700, background: '#e8f5e9', color: '#1b5e20' }}>APPROVED</span>
                    : <span style={{ padding: '2px 9px', borderRadius: 12, fontSize: 10, fontWeight: 700, background: '#fff3e0', color: accent }}>AWAITING APPROVAL</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===== SHIPMENT UPDATE — filled in once goods are shipped (Sharmin).
// BL date / BL no / BL copy + vessel, forwarder and destination port picked from
// the masters (with "+ add" when the name isn't there yet). =====
const blForm = s => ({ bl_dt: s.bl_dt_s || '', bl_no: s.bl_no || '', vessel: s.vessel || '', forwarder: s.forwarder || '', destination_port: s.destination_port || '' });

function ShipmentUpdateView({ Header, factory, backTo }) {
  const accent = '#1f6f8b';
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState(null);         // shipment being updated
  const [form, setForm] = useState({ bl_dt: '', bl_no: '', vessel: '', forwarder: '', destination_port: '' });
  const [masters, setMasters] = useState({ vessel: [], forwarder: [], port: [] });
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [tab, setTab] = useState('pending');    // BL Pending | Shipped
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(true); // shipped shipments open read-only until Edit

  const facQ = factory ? `?factory=${encodeURIComponent(factory)}` : '';
  const load = () => api(`/shipments${facQ}`).then(d => setRows(Array.isArray(d) ? d : [])).catch(() => setRows([]));
  useEffect(() => { load(); }, [factory]);
  useEffect(() => { api('/shipflow/masters').then(d => setMasters(d && d.vessel ? d : { vessel: [], forwarder: [], port: [] })).catch(() => {}); }, []);

  const open = s => {
    setSel(s); setMsg(''); setDetail(null);
    setEditing(!(s.bl_no && s.bl_dt_s));
    setForm(blForm(s));
    api(`/shipments/${s.id}`).then(setDetail).catch(() => setDetail({}));
  };
  const addMaster = async (type, key) => {
    const name = (window.prompt(`New ${type} name`) || '').trim();
    if (!name) return;
    const r = await api('/shipflow/master', { method: 'POST', body: JSON.stringify({ type, name }) }).catch(() => null);
    if (r && r.name) {
      setMasters(m => ({ ...m, [type]: [...(m[type] || []), r].sort((a, b) => a.name.localeCompare(b.name)) }));
      setForm(f => ({ ...f, [key]: r.name }));
    }
  };
  const upload = async (docType, file) => {
    if (!sel) return;
    await uploadShipDoc(sel.id, docType, file).catch(() => null);
    api(`/shipments/${sel.id}`).then(setDetail).catch(() => {});
  };
  const save = () => {
    setBusy(true); setMsg('');
    api(`/shipments/${sel.id}/bl`, { method: 'PUT', body: JSON.stringify(form) })
      .then(r => {
        setBusy(false); if (r && r.error) { setMsg(r.error); return; }
        const miss = [!String(form.bl_no || '').trim() && 'BL No', !form.bl_dt && 'BL Date'].filter(Boolean);
        setMsg(miss.length ? `Saved ✓ — still BL Pending: add ${miss.join(' and ')} to mark it Shipped` : 'Saved ✓ — marked Shipped');
        setSel(s => ({ ...s, bl_dt_s: form.bl_dt || null, bl_no: form.bl_no || null, vessel: form.vessel || null, forwarder: form.forwarder || null, destination_port: form.destination_port || null }));
        if (!miss.length) setEditing(false);          // shipped → back to the locked view
        load();
      })
      .catch(() => { setBusy(false); setMsg('Update failed'); });
  };
  // Delete the BL details: the shipment goes back to BL Pending (the BL copy file stays).
  const removeBl = () => {
    if (!window.confirm(`Delete the BL details of Shipment #${sel.ship_no || sel.id}?\n\nIt goes back to BL Pending and the factory can edit it again.`)) return;
    setBusy(true); setMsg('');
    api(`/shipments/${sel.id}/bl`, { method: 'DELETE' })
      .then(r => {
        setBusy(false); if (r && r.error) { setMsg(r.error); return; }
        const s = { ...sel, bl_dt_s: null, bl_no: null, vessel: null, forwarder: null, destination_port: null };
        setSel(s); setForm(blForm(s)); setEditing(true);
        setMsg('BL details deleted ✓ — back in BL Pending');
        load();
      })
      .catch(() => { setBusy(false); setMsg('Delete failed'); });
  };
  const isShipped = !!(sel && sel.bl_no && sel.bl_dt_s);   // as saved, not as typed
  const ro = !editing;

  const inpEdit = { width: '100%', boxSizing: 'border-box', height: 38, padding: '0 10px', background: '#fffbe6', border: '1.5px solid #d4a72c', borderRadius: 4, color: 'var(--text)', fontSize: 13, fontWeight: 600, outline: 'none' };
  const inp = ro ? { ...inpEdit, background: 'var(--bg3)', border: SHIP_LINE, color: 'var(--text)', opacity: 1, cursor: 'default' } : inpEdit;
  const lbl = { fontSize: 10.5, letterSpacing: 0.8, color: 'var(--text)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 5, display: 'block' };
  // Only shipments still waiting on BL details — once BL no + date are in, it drops off.
  const pending = (rows || []).filter(s => !(s.bl_no && s.bl_dt_s));
  const picker = (label, key, type) => (
    <div>
      <label style={lbl}>{label}</label>
      <div style={{ display: 'flex', gap: 6 }}>
        <select style={inp} disabled={ro} value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })}>
          <option value="">— select —</option>
          {(masters[type] || []).map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
          {form[key] && !(masters[type] || []).some(m => m.name === form[key]) && <option value={form[key]}>{form[key]}</option>}
        </select>
        {!ro && <button onClick={() => addMaster(type, key)} title={`Add ${label}`}
          style={{ padding: '0 12px', borderRadius: 4, border: `1.5px solid ${accent}66`, background: `${accent}10`, color: accent, fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>+</button>}
      </div>
    </div>
  );

  // ---- detail: one shipment being updated ----
  if (sel) {
    return (
      <div className="screen">
        <ExportBar reportKey="shipment-update-detail" title={`Shipment #${sel.id} - BL Details`}
          sections={[{ name: 'Shipment',
            columns: [C('field', 'Field'), C('value', 'Value')],
            rows: [{ field: 'TAS', value: sel.tas_list }, { field: 'Ship Qty', value: sel.ship_qty },
                   { field: 'Ex-Factory', value: sel.ex_factory_dt_s || '' }, { field: 'BL Date', value: form.bl_dt },
                   { field: 'BL No', value: form.bl_no }, { field: 'Vessel', value: form.vessel },
                   { field: 'Forwarder', value: form.forwarder }, { field: 'Destination Port', value: form.destination_port }] }]}
          source={{ endpoint: `/shipments/${sel.id}` }} />
        <Header subtitle="FACTORY · SHIPMENT UPDATE" />
        <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
          <button onClick={() => { setSel(null); setMsg(''); }} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 20, color: accent }}>🚢 Shipment #{sel.ship_no || sel.id}</div>
          <span style={{ fontSize: 11, color: 'var(--text3)' }}>{sel.tas_list} · {fmt(sel.ship_qty)} pcs · ex-factory {fmtDMY(sel.ex_factory_dt_s) || '—'}</span>
        </div>

        <div style={{ padding: 16 }}>
          <div style={{ border: SHIP_LINE, borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ padding: '9px 12px', background: `${accent}12`, fontWeight: 700, color: accent, fontSize: 13, borderBottom: SHIP_LINE, display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
              <span>BL Details {isShipped && <span style={{ marginLeft: 8, padding: '2px 9px', borderRadius: 12, fontSize: 10, fontWeight: 700, background: '#e8f5e9', color: '#1b5e20' }}>{ro ? 'SHIPPED · LOCKED' : 'SHIPPED · EDITING'}</span>}</span>
              <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text3)' }}><span style={{ color: '#c62828' }}>*</span> BL No + BL Date mark the shipment Shipped</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12, padding: 12 }}>
              <div><label style={lbl}>BL Date <span style={{ color: '#c62828' }}>*</span></label><input type="date" style={inp} disabled={ro} value={form.bl_dt} onChange={e => setForm({ ...form, bl_dt: e.target.value })} /></div>
              <div><label style={lbl}>BL No <span style={{ color: '#c62828' }}>*</span></label><input style={inp} disabled={ro} value={form.bl_no} onChange={e => setForm({ ...form, bl_no: e.target.value })} placeholder="type BL no" /></div>
              {picker('Vessel', 'vessel', 'vessel')}
              {picker('Forwarder', 'forwarder', 'forwarder')}
              {picker('Destination Port', 'destination_port', 'port')}
            </div>
          </div>

          {/* What was shipped — shade-wise, read-only */}
          {!detail && <div style={{ padding: 20, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>Loading…</div>}
          {detail && Object.entries((detail.lines || []).reduce((m, l) => { (m[l.tas_no] = m[l.tas_no] || []).push(l); return m; }, {})).map(([tn, list]) => {
            const GC = 'minmax(140px, 1fr) 130px 130px 130px';
            const n = v => parseFloat(v) || 0;
            const tO = list.reduce((x, r) => x + n(r.order_qty), 0), tS = list.reduce((x, r) => x + n(r.ship_qty), 0);
            return (
              <div key={tn} style={{ marginTop: 14, border: SHIP_LINE, borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ padding: '9px 12px', background: `${accent}12`, display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 700, color: accent, fontSize: 13 }}>{tn}</span>
                  <span style={{ fontSize: 11, color: 'var(--text3)' }}>{list.length} shade{list.length !== 1 ? 's' : ''}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: GC, background: 'var(--bg3)', fontSize: 10, letterSpacing: 1, color: 'var(--text2)', fontWeight: 700, textTransform: 'uppercase', borderTop: SHIP_LINE }}>
                  <span style={shipTd(0)}>Shade</span><span style={shipTd(1)}>Order Qty</span><span style={shipTd(2)}>Ship Qty</span><span style={shipTd(3)}>Excess / Short</span>
                </div>
                {list.map((r, i) => {
                  const d = n(r.ship_qty) - n(r.order_qty);
                  return (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: GC, borderTop: SHIP_LINE, fontSize: 12 }}>
                      <span style={{ ...shipTd(0), color: 'var(--text)' }}>{r.shade}</span>
                      <span style={{ ...shipTd(1), color: 'var(--text3)' }}>{fmt(r.order_qty)}</span>
                      <span style={{ ...shipTd(2), fontWeight: 700, color: 'var(--text)' }}>{fmt(r.ship_qty)}</span>
                      <span style={{ ...shipTd(3), fontWeight: 600, color: d < 0 ? 'var(--red)' : d > 0 ? accent : 'var(--text3)' }}>{d > 0 ? '+' : ''}{fmt(d)}</span>
                    </div>
                  );
                })}
                <div style={{ display: 'grid', gridTemplateColumns: GC, borderTop: '2px solid #9aa5b1', fontSize: 12, fontWeight: 700, background: 'var(--bg3)' }}>
                  <span style={{ ...shipTd(0), color: 'var(--text)' }}>TOTAL</span>
                  <span style={{ ...shipTd(1), color: 'var(--text3)' }}>{fmt(tO)}</span>
                  <span style={{ ...shipTd(2), color: accent }}>{fmt(tS)}</span>
                  <span style={{ ...shipTd(3), color: tS - tO < 0 ? 'var(--red)' : accent }}>{tS - tO > 0 ? '+' : ''}{fmt(tS - tO)}</span>
                </div>
              </div>
            );
          })}

          {/* Documents: BL copy to upload here + the soft copies attached at shipment (view only) */}
          <div style={{ marginTop: 14, border: SHIP_LINE, borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ padding: '9px 12px', background: `${accent}12`, fontWeight: 700, color: accent, fontSize: 13 }}>Documents</div>
            <div style={{ display: 'grid', gridTemplateColumns: SHIP_DC, background: 'var(--bg3)', fontSize: 10, letterSpacing: 1, color: 'var(--text2)', fontWeight: 700, textTransform: 'uppercase', borderTop: SHIP_LINE }}>
              <span style={shipTd(0)}>Document</span><span style={{ ...shipTd(1), justifyContent: 'flex-start', textAlign: 'left' }}>File</span><span style={{ ...shipTd(2), justifyContent: 'center' }}>Action</span>
            </div>
            <DocRow label={DOC_LABELS.bl_copy} docType="bl_copy" detail={detail} onUpload={ro ? null : upload} urlOf={shipFileUrl} cols={SHIP_DC} td={shipTd} line={SHIP_LINE} />
            {detail && (detail.files || []).filter(f => f.doc_type !== 'bl_copy').map(f => (
              <div key={f.id} style={{ display: 'grid', gridTemplateColumns: SHIP_DC, borderTop: SHIP_LINE, fontSize: 12 }}>
                <span style={{ ...shipTd(0), color: 'var(--text)' }}>{DOC_LABELS[f.doc_type] || f.doc_type}</span>
                <span style={{ ...shipTd(1), justifyContent: 'flex-start', textAlign: 'left' }}><a href={shipFileUrl(f.id)} target="_blank" rel="noreferrer" style={{ color: accent, fontWeight: 600 }}>📎 {f.filename}</a></span>
                <span style={{ ...shipTd(2), justifyContent: 'center', color: 'var(--text3)', fontSize: 11 }}>attached at shipment</span>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', gap: 14 }}>
            {ro ? <>
              <button onClick={() => { setEditing(true); setMsg(''); }} disabled={busy}
                style={{ padding: '11px 26px', borderRadius: 8, border: 'none', background: accent, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                ✏️ Edit
              </button>
              <button onClick={removeBl} disabled={busy}
                style={{ padding: '11px 22px', borderRadius: 8, border: '1.5px solid #c62828', background: '#ffebee', color: '#c62828', fontSize: 13, fontWeight: 700, cursor: busy ? 'default' : 'pointer' }}>
                {busy ? 'Deleting…' : '🗑 Delete BL Details'}
              </button>
            </> : <>
              <button onClick={save} disabled={busy}
                style={{ padding: '11px 26px', borderRadius: 8, border: 'none', background: busy ? 'var(--text3)' : accent, color: '#fff', fontSize: 13, fontWeight: 700, cursor: busy ? 'default' : 'pointer' }}>
                {busy ? 'Saving…' : 'Save Shipment Update'}
              </button>
              {isShipped && <button onClick={() => { setForm(blForm(sel)); setEditing(false); setMsg(''); }} disabled={busy}
                style={{ padding: '11px 22px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text2)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                Cancel
              </button>}
            </>}
            {msg && <span style={{ fontSize: 12, fontWeight: 700, color: /✓/.test(msg) ? '#1b5e20' : '#c62828' }}>{msg}</span>}
          </div>
        </div>
      </div>
    );
  }

  // ---- list of this factory's shipments: BL Pending / Shipped indicator tiles ----
  const shipped = (rows || []).filter(r => r.bl_no && r.bl_dt_s);
  const isDone = tab === 'shipped';
  const listName = isDone ? 'Shipped' : 'BL Pending';
  const t = q.trim().toLowerCase();
  const list = (isDone ? shipped : pending).filter(r => !t ||
    `${r.ship_no || r.id} ${r.tas_list || ''} ${r.bl_no || ''} ${r.vessel || ''} ${r.destination_port || ''} ${r.forwarder || ''}`.toLowerCase().includes(t));
  const cell = { padding: '9px 10px', fontSize: 12, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
  const hd = { ...cell, color: 'var(--text3)', fontSize: 10, letterSpacing: 1, background: 'var(--bg3)', textAlign: 'left', textTransform: 'uppercase' };
  const tile = (key, label, n, sub, color) => (
    <div key={key} onClick={() => setTab(key)}
      style={{ flex: '1 1 220px', maxWidth: 340, padding: '16px 18px', borderRadius: 12, cursor: 'pointer', boxSizing: 'border-box',
        border: (tab === key ? 2 : 1) + 'px solid ' + (tab === key ? color : 'var(--border)'), background: tab === key ? color + '10' : 'var(--bg2)' }}>
      <div style={{ fontSize: 30, fontWeight: 700, color, fontFamily: 'Inter, sans-serif', lineHeight: 1 }}>{rows ? fmt(n) : '…'}</div>
      <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.2, color, marginTop: 6 }}>{label}</div>
      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{sub}</div>
    </div>
  );
  return (
    <div className="screen">
      <Header subtitle="FACTORY · SHIPMENT UPDATE" />
      <ExportBar reportKey={isDone ? 'shipment-update-shipped' : 'shipment-update-pending'}
        title={`${factory || 'All factories'} - Shipment Update ${listName}`}
        sections={[{ name: listName,
          columns: [C('id', 'Shipment'), C('tas_list', 'TAS'), C('ex_factory_dt_s', 'Ex-Factory', 'date'),
                    C('ship_qty', 'Ship Qty', 'int', true), C('bl_no', 'BL No'), C('bl_dt_s', 'BL Date', 'date'), C('vessel', 'Vessel'),
                    C('destination_port', 'Destination Port'), C('forwarder', 'Forwarder')],
          rows: list }]}
        source={{ endpoint: '/shipments' }} />
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
        <button onClick={backTo} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 20, color: accent }}>🚢 {factory ? `${factory} — Shipment Update` : 'Shipment Update'}</div>
      </div>

      {/* Key indicators — each one also switches the list below */}
      <div style={{ padding: '16px 16px 4px', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {tile('pending', 'BL PENDING', pending.length, 'shipped from factory · BL details to enter', '#b5651d')}
        {tile('shipped', 'SHIPPED', shipped.length, 'BL details updated · locked', '#1b5e20')}
      </div>

      <div style={{ padding: '10px 16px 6px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)' }}>
          {listName.toUpperCase()}
          <span style={{ color: 'var(--text3)', fontWeight: 400 }}> · {list.length} shipment{list.length !== 1 ? 's' : ''} · {isDone ? 'click a shipment to view its BL details' : 'click a shipment to enter BL details'}</span>
        </div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="search shipment / TAS / BL / vessel…"
          style={{ marginLeft: 'auto', minWidth: 220, padding: '7px 10px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 12, outline: 'none' }} />
      </div>
      <div style={{ margin: '0 16px 24px', border: '1px solid var(--border)', borderRadius: 8, overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 900, borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={hd}>Shipment</th><th style={hd}>TAS</th><th style={hd}>Ex-Factory</th><th style={{ ...hd, textAlign: 'right' }}>Ship Qty</th>
            <th style={hd}>BL No</th><th style={hd}>BL Date</th><th style={hd}>Vessel</th><th style={hd}>Destination Port</th><th style={hd}>Forwarder</th><th style={hd}>Status</th>
          </tr></thead>
          <tbody>
            {!rows && <tr><td colSpan={10} style={{ ...cell, textAlign: 'center', color: 'var(--text3)' }}>Loading…</td></tr>}
            {rows && list.length === 0 && <tr><td colSpan={10} style={{ ...cell, textAlign: 'center', color: 'var(--text3)' }}>
              {isDone ? 'No shipments with BL details yet.' : 'No shipments pending BL details.'}</td></tr>}
            {list.map(r => (
              <tr key={r.id} onClick={() => open(r)} style={{ cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg3)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                <td style={{ ...cell, fontWeight: 700, color: accent }}>#{r.ship_no || r.id} <span style={{ color: 'var(--text3)', fontWeight: 400 }}>›</span></td>
                <td style={{ ...cell, fontWeight: 600 }}>{r.tas_list || '—'}</td>
                <td style={cell}>{fmtDMY(r.ex_factory_dt_s) || '—'}</td>
                <td style={{ ...cell, textAlign: 'right', fontWeight: 700 }}>{fmt(r.ship_qty)}</td>
                <td style={cell}>{r.bl_no || '—'}</td>
                <td style={cell}>{fmtDMY(r.bl_dt_s) || '—'}</td>
                <td style={cell}>{r.vessel || '—'}</td>
                <td style={cell}>{r.destination_port || '—'}</td>
                <td style={cell}>{r.forwarder || '—'}</td>
                <td style={cell}>{isDone
                  ? <span style={{ padding: '2px 9px', borderRadius: 12, fontSize: 10, fontWeight: 700, background: '#e8f5e9', color: '#1b5e20' }}>SHIPPED</span>
                  : <span style={{ padding: '2px 9px', borderRadius: 12, fontSize: 10, fontWeight: 700, background: '#fff3e0', color: '#b5651d' }}>BL PENDING</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===== FACTORY — factory list; click a factory → its two cost-sheet cards =====
// No ERP links here by design — users never leave the app; cost sheets are
// entered in-app and written straight into the ERP's own form-5258 tables.

// ===== COST GRID — in-app Sales Contract Cost Sheet entry.
// Saves straight into the ERP's own form-5258 tables (ord_garment_cost/_det), so an
// app-entered sheet is a real ERP entry. Grey columns are auto-computed by the ERP.
// $ = dollar price (4 decimals). Quantities, the exchange rate and the INR
// figure use 3. CS_DOLLAR is the single list both the grid and the API honour.
// Grouped so the sheet reads as three blocks instead of one 14-column scroller.
const CS_GROUPS = [
  { name: 'FABRIC', cols: [
    { k: 'fab', label: 'Fab Price', $: true },
    { k: 'freight', label: 'Freight', $: true },
    { k: 'cif', label: 'Cif', calc: true, $: true },
    { k: 'consumption', label: 'Consumption', unit: 'qty' },
    { k: 'fabcost', label: 'Fab Cost', calc: true, $: true },
  ] },
  { name: 'CM & TRIMS', cols: [
    { k: 'cm', label: 'Cm', $: true },
    { k: 'bd', label: 'Bd Trims', $: true },
    { k: 'ind', label: 'Ind Trims', $: true },
    { k: 'china', label: 'China Trims', $: true },
    { k: 'wash', label: 'Wash', $: true },
    { k: 'transp', label: 'Transp', $: true },
  ] },
  { name: 'TOTAL', cols: [
    { k: 'cog', label: 'Cog', calc: true, $: true },
    { k: 'exch', label: 'Exch', unit: 'rate' },
    { k: 'inr', label: 'Inr', calc: true, big: true },
  ] },
];
const CS_COLS = CS_GROUPS.flatMap(g => g.cols);
const csUnit = c => (c.$ ? '$' : c.unit === 'rate' ? '₹/$' : c.unit === 'qty' ? 'qty' : '₹');
const CS_CALC_BG = 'rgba(0,106,78,0.07)';
const CS_CALC_BD = 'rgba(0,106,78,0.35)';
const CS_INP_BG = '#fffbe6';                 // fields the user types into
const CS_INP_BD = '#d4a72c';

// A cost entry box: shows exactly what is typed while focused, and the figure with
// 2 decimals once you leave it (the value itself is kept as entered).
function CsInput({ value, onChange, style, accent }) {
  const [focus, setFocus] = useState(false);
  const raw = value == null ? '' : String(value);
  const shown = focus || raw === '' || !isFinite(Number(raw)) ? raw : Number(raw).toFixed(2);
  return (
    <input type={focus ? 'number' : 'text'} inputMode="decimal" step="any" value={shown} placeholder="0.00"
      onChange={e => onChange(e.target.value)} style={style}
      onFocus={e => { setFocus(true); e.target.style.borderColor = accent; e.target.style.boxShadow = `0 0 0 3px ${accent}22`; }}
      onBlur={e => { setFocus(false); e.target.style.borderColor = CS_INP_BD; e.target.style.boxShadow = 'none'; }} />
  );
}

// The cost grid, shared by the Sales Contract Cost Sheet and Final Costing so the
// two always look and calculate the same. `calc` holds the four derived values.
function CostGridCard({ line, calc, locked, onCell, title, accent = '#006a4e' }) {
  const LINE = '1px solid #c3cbd5';
  const fieldLbl = { fontSize: 10.5, letterSpacing: 0.6, color: 'var(--text)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 5,
    display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
  const box = {
    width: '100%', boxSizing: 'border-box', height: 38, padding: '0 10px', fontSize: 14, textAlign: 'right',
    borderRadius: 4, color: 'var(--text)', fontFamily: 'inherit', outline: 'none', display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
  };
  const entryBox = { ...box, fontWeight: 600, background: CS_INP_BG, border: `1.5px solid ${CS_INP_BD}` };
  const calcBox = { ...box, fontWeight: 800, background: CS_CALC_BG, border: `1.5px solid ${CS_CALC_BD}` };
  const lockedBox = { ...box, fontWeight: 600, background: 'var(--bg3)', border: LINE };
  return (
    <div style={{ border: LINE, borderRadius: 10, background: 'var(--bg2)', marginBottom: 12, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '10px 16px', background: `${accent}12`, borderBottom: LINE, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 14, fontWeight: 800, color: accent, letterSpacing: 1 }}>{title || 'COSTING'}</span>
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6, color: 'var(--text2)' }}>COG $</span>
          <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)' }}>{csFmt(calc.cog, 'cog')}</span>
        </span>
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 6, padding: '4px 12px', borderRadius: 6, background: accent, color: '#fff' }}>
          <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6 }}>INR ₹</span>
          <span style={{ fontSize: 17, fontWeight: 800 }}>{csFmt(calc.inr, 'inr')}</span>
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14, padding: 14 }}>
        {CS_GROUPS.map(g => (
          <div key={g.name} style={{ border: LINE, borderRadius: 8, background: 'var(--bg)', overflow: 'hidden' }}>
            <div style={{ padding: '8px 12px', background: 'var(--bg3)', borderBottom: LINE, fontSize: 12, letterSpacing: 1.5, fontWeight: 800, color: 'var(--text)' }}>{g.name}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(118px, 1fr))', gap: 12, padding: 12 }}>
              {g.cols.map(c => (
                <div key={c.k} style={{ minWidth: 0 }}>
                  <label style={fieldLbl} title={`${c.label} ${csUnit(c)}`}>{c.label} {csUnit(c)}</label>
                  {c.calc
                    ? <div style={{ ...calcBox, ...(c.big ? { fontSize: 16, color: accent } : {}) }}>{csFmt(calc[c.k], c.k)}</div>
                    : locked
                      ? <div style={lockedBox}>{csFmt(line[c.k], c.k)}</div>
                      : <CsInput value={line[c.k]} onChange={v => onCell(c.k, v)} style={entryBox} accent={accent} />}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const CS_DOLLAR = new Set(CS_COLS.filter(c => c.$).map(c => c.k));
const csNum = v => { const n = Number(v); return isFinite(n) ? n : 0; };
// Cascade from the rounded figures, so every number on screen can be re-derived
// by hand from the others (Cog x Exch = Inr, exactly).
const csR3 = n => Math.round((csNum(n) + Number.EPSILON) * 1000) / 1000;
const csR4 = n => Math.round((csNum(n) + Number.EPSILON) * 10000) / 10000;
const csRK = (k, v) => (CS_DOLLAR.has(k) ? csR4(v) : csR3(v));
const csCalc = r => {
  const cif = csR4(csNum(r.fab) + csNum(r.freight));
  const fabcost = csR4(csNum(r.consumption) * cif);
  const cog = csR4(fabcost + csNum(r.cm) + csNum(r.bd) + csNum(r.ind) + csNum(r.china) + csNum(r.wash) + csNum(r.transp));
  return { cif, fabcost, cog, inr: csR3(csNum(r.exch) * cog) };
};
// On screen every cost figure shows 2 decimals; the maths above keeps full precision.
const csR2 = n => Math.round((csNum(n) + Number.EPSILON) * 100) / 100;
const csFmt = (n, k) => csR2(csRK(k, n)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function CostSheetEntry({ tas, factory, Header, onBack, onSaved }) {
  const accent = '#006a4e';
  const [data, setData] = useState(null);
  const [lines, setLines] = useState([]);
  const [fiDt, setFiDt] = useState('');
  const [touched, setTouched] = useState({});   // lines the user actually changed
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  useEffect(() => {
    let live = true;
    api(`/costflow/${encodeURIComponent(tas)}/cost-entry`).then(d => {
      if (!live) return;
      setData(d || {});
      setLines(((d && d.lines) || []).map(l => ({ ...l })));
      const hdr = (d && d.header) || {};
      // Default to factory_fi_dt if already saved, else target_fi_dt (Est FI - 20 days)
      setFiDt(hdr.factory_fi_dt || hdr.target_fi_dt || '');
    }).catch(() => live && setData({ error: 'Load failed' }));
    return () => { live = false; };
  }, [tas]);

  const locked = !!(data && data.locked);
  const h = (data && data.header) || {};
  const targetFiDt = h.target_fi_dt || '';
  const fiDiffDays = useMemo(() => {
    if (!fiDt || !targetFiDt) return 0;
    const d1 = new Date(targetFiDt + 'T00:00:00Z');
    const d2 = new Date(fiDt + 'T00:00:00Z');
    if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return 0;
    return Math.round((d2.getTime() - d1.getTime()) / 86400000);
  }, [fiDt, targetFiDt]);
  const fiExceeds20 = fiDiffDays > 20;

  // A brand-new sheet is always computed live; an existing ERP sheet shows the
  // values the ERP has stored until the user actually edits that line.
  const live = i => !(data && data.existing) || !!touched[i];
  // The four computed cells exactly as DISPLAYED — this same object is what we save,
  // so the ERP can never hold a number different from the one the user saw.
  const shown = (ln, i) => (live(i)
    ? csCalc(ln)
    : { cif: csR4(ln.cif), fabcost: csR4(ln.fabcost), cog: csR4(ln.cog), inr: csR3(ln.inr) });
  const setCell = (i, k, v) => {
    setTouched(t => ({ ...t, [i]: true }));
    setLines(ls => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  };
  const addLine = () => setLines(ls => {
    setTouched(t => ({ ...t, [ls.length]: true }));
    return [...ls, { fab: 0, freight: 0, consumption: 0, cm: 0, bd: 0, ind: 0, china: 0, wash: 0, transp: 0, exch: csNum(ls[0] && ls[0].exch) || 96 }];
  });
  const delLine = i => setLines(ls => (ls.length > 1 ? ls.filter((_, j) => j !== i) : ls));
  const save = () => {
    if (fiExceeds20) {
      const ok = window.confirm(
        `⚠️ FI Date Warning:\n\n` +
        `Target FI Date (Est FI - 20 Days): ${fmtDMY(targetFiDt)}\n` +
        `Selected Factory FI Date: ${fmtDMY(fiDt)} (+${fiDiffDays} days beyond Target FI)\n\n` +
        `Factory FI Date should not be extended by more than 20 days (${fmtDMY(h.fi_dt) || 'Est FI Date'}).\n` +
        `Do you still want to save this Cost Sheet & FI Date with the delay warning?`
      );
      if (!ok) return;
    }
    setBusy(true); setMsg('');
    // Send the displayed computed values verbatim.
    const payload = lines.map((ln, i) => ({ ...ln, ...shown(ln, i) }));
    api(`/costflow/${encodeURIComponent(tas)}/cost-entry`, { method: 'POST', body: JSON.stringify({ lines: payload, factory_fi_dt: fiDt || null }) })
      .then(r => {
        setBusy(false);
        if (!r || r.error) { setMsg((r && r.error) || 'Save failed'); return; }
        // Show what the ERP actually stored, so app and ERP are visibly identical.
        if (Array.isArray(r.lines)) { setLines(r.lines.map(l => ({ ...l }))); setTouched({}); }
        if (r.factory_fi_dt !== undefined) setFiDt(r.factory_fi_dt || '');
        setData(d => ({
          ...d,
          existing: true,
          header: {
            ...(d && d.header),
            factory_fi_dt: r.factory_fi_dt !== undefined ? r.factory_fi_dt : fiDt,
            fi_delay_days: r.fi_delay_days !== undefined ? r.fi_delay_days : fiDiffDays,
          }
        }));
        const warnSuffix = fiExceeds20 ? ` (⚠️ Saved with +${fiDiffDays}d FI Date delay warning)` : '';
        setMsg(r.drift ? 'Saved, but some values differ — please inform IT'
          : (r.mode === 'updated' ? `Cost sheet updated ✓${warnSuffix}` : `Cost sheet saved ✓${warnSuffix}`));
        if (!r.drift) setTimeout(() => { onSaved && onSaved(); }, 900);
      })
      .catch(() => { setBusy(false); setMsg('Save failed'); });
  };

  return (
    <div className="screen">
      <ExportBar reportKey="cost-sheet-entry" title={`${tas} - Sales Contract Cost Sheet`}
        sections={[{ name: 'Cost Sheet', columns: [C('item','Item'), C('value','Value')],
          // one cost sheet per TAS → export its first line; calculated fields come from shown()
          rows: [
            { item: 'Target FI Date (Est FI - 20d)', value: fmtDMY(targetFiDt) || '—' },
            { item: 'Factory Confirmed FI Date', value: fmtDMY(fiDt || targetFiDt) || '—' },
            ...(lines[0] ? CS_COLS.map(c => ({ item: c.label, value: csR2(csRK(c.k, c.calc ? shown(lines[0], 0)[c.k] : lines[0][c.k])) })) : [])
          ] }]}
        source={{ endpoint: `/costflow/${encodeURIComponent(tas)}/cost-entry` }} />
      {Header && (typeof Header === 'function' ? <Header subtitle="FACTORY · SALES CONTRACT COST SHEET" /> : Header)}
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 20, color: accent }}>🧾 {tas} — Cost Sheet</div>
          {(h.buyer || h.factory) && (
            <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 2 }}>
              {titleCase(h.buyer) || '—'}{h.factory ? ` · ${h.factory}` : ''}{h.broker ? ` · Merch: ${titleCase(h.broker)}` : ''}
            </div>
          )}
        </div>
        {locked && <span style={{ padding: '4px 10px', borderRadius: 20, background: '#e8f5e9', color: '#1b5e20', fontSize: 11, fontWeight: 700 }}>APPROVED · LOCKED</span>}
        {data && data.existing && !locked && <span style={{ padding: '4px 10px', borderRadius: 20, background: '#fff3e0', color: '#b5651d', fontSize: 11, fontWeight: 700 }}>EDITING EXISTING</span>}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          {h.enquiry_dt_s && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 9.5, letterSpacing: 0.8, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase' }}>Enquiry Date</div>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text2)', marginTop: 2 }}>{fmtDMY(h.enquiry_dt_s)}</div>
            </div>
          )}
          {targetFiDt && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 9.5, letterSpacing: 0.8, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase' }}>Target FI (Est - 20d)</div>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: accent, marginTop: 2 }}>{fmtDMY(targetFiDt)}</div>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <label style={{ fontSize: 10, letterSpacing: 0.8, color: fiExceeds20 ? '#c62828' : accent, fontWeight: 800, textTransform: 'uppercase', marginBottom: 3 }}>
              Factory FI Date {targetFiDt && fiDiffDays !== 0 ? `(${fiDiffDays > 0 ? '+' : ''}${fiDiffDays}d)` : ''}
            </label>
            {locked ? (
              <div style={{ height: 34, padding: '0 12px', display: 'flex', alignItems: 'center', borderRadius: 6, background: 'var(--bg3)', border: '1px solid #c3cbd5', fontSize: 13, fontWeight: 700, color: fiExceeds20 ? '#c62828' : 'var(--text)' }}>
                {fmtDMY(fiDt || targetFiDt) || '—'}
              </div>
            ) : (
              <input
                type="date"
                value={fiDt}
                onChange={e => setFiDt(e.target.value)}
                style={{
                  height: 34, padding: '0 10px', borderRadius: 6,
                  background: fiExceeds20 ? '#ffebee' : CS_INP_BG,
                  border: `1.5px solid ${fiExceeds20 ? '#c62828' : CS_INP_BD}`,
                  fontSize: 13, fontWeight: 700, color: fiExceeds20 ? '#c62828' : 'var(--text)',
                  outline: 'none', fontFamily: 'inherit'
                }}
              />
            )}
          </div>
        </div>
      </div>
      {fiExceeds20 && (
        <div style={{ margin: '10px 16px 0', padding: '10px 14px', borderRadius: 8, background: '#ffebee', border: '1.5px solid #ef5350', color: '#c62828', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span>⚠️ FI Date Warning: Selected Factory FI Date ({fmtDMY(fiDt)}) is +{fiDiffDays} days after Target FI Date ({fmtDMY(targetFiDt)}), exceeding the 20-day buffer (Est FI: {fmtDMY(h.fi_dt)}). You can still save, but it will be flagged for BD review.</span>
        </div>
      )}
      {!fiExceeds20 && fiDiffDays > 0 && targetFiDt && (
        <div style={{ margin: '10px 16px 0', padding: '8px 14px', borderRadius: 8, background: '#fff3e0', border: '1px solid #ffb74d', color: '#b5651d', fontSize: 11.5, fontWeight: 600 }}>
          ℹ️ Factory FI Date ({fmtDMY(fiDt)}) is +{fiDiffDays} day{fiDiffDays === 1 ? '' : 's'} after Target FI Date ({fmtDMY(targetFiDt)}) — within the 20-day buffer (Max: {fmtDMY(h.fi_dt)}).
        </div>
      )}

      {!data && <div style={{ textAlign: 'center', padding: 50, color: 'var(--text3)', fontSize: 13 }}>Loading…</div>}
      {data && data.error && <div style={{ textAlign: 'center', padding: 50, color: '#c62828', fontSize: 13 }}>{data.error}</div>}

      {data && !data.error && (
        <>
          <div style={{ padding: '16px 16px 8px', display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', letterSpacing: 0.3 }}>Cost Grid</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text2)' }}>
              <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: CS_INP_BG, border: `1.5px solid ${CS_INP_BD}` }} />
              enter value
              <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: CS_CALC_BG, border: `1.5px solid ${CS_CALC_BD}`, marginLeft: 10 }} />
              calculated automatically
              <span style={{ color: 'var(--border)', margin: '0 4px' }}>|</span>
              <span>$ 4 decimals · ₹ 3</span>
            </div>
            {/* One cost sheet per TAS — no multi-line entry. */}
          </div>

          {/* One card per cost line, split into the three groups — no sideways scrolling. */}
          <div style={{ padding: '0 16px' }}>
            {lines.map((ln, i) => (
              <CostGridCard key={i} line={ln} calc={shown(ln, i)} locked={locked} accent={accent}
                title={lines.length > 1 ? `LINE ${i + 1}` : 'COSTING'} onCell={(k, v) => setCell(i, k, v)} />
            ))}
          </div>

          {/* per-sheet roll-up */}
          {lines.length > 1 && (
            <div style={{ margin: '10px 16px 0', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {[['Lines', lines.length],
                ['Total Cog $', csFmt(lines.reduce((a, ln, i) => a + csNum(shown(ln, i).cog), 0), 'cog')],
                ['Total Inr ₹', csFmt(lines.reduce((a, ln, i) => a + csNum(shown(ln, i).inr), 0), 'inr')]].map(([k, v]) => (
                <div key={k} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '8px 14px', background: 'var(--bg2)' }}>
                  <div style={{ fontSize: 9, letterSpacing: 1, color: 'var(--text3)', fontWeight: 700 }}>{String(k).toUpperCase()}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: accent, marginTop: 2 }}>{v}</div>
                </div>
              ))}
            </div>
          )}

          <div style={{ padding: '16px 20px 30px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            {locked ? (
              <div style={{ fontSize: 12, color: 'var(--text3)' }}>This cost sheet is approved by BD and can no longer be changed.</div>
            ) : (
              <button onClick={save} disabled={busy}
                style={{ padding: '11px 26px', borderRadius: 8, border: 'none', background: busy ? 'var(--text3)' : accent, color: '#fff', fontSize: 13, fontWeight: 700, cursor: busy ? 'default' : 'pointer' }}>
                {busy ? 'Saving…' : (data.existing ? 'Update Cost Sheet' : 'Save Cost Sheet')}
              </button>
            )}
            {msg && <div style={{ fontSize: 12, fontWeight: 700, color: /✓/.test(msg) ? '#1b5e20' : '#c62828' }}>{msg}</div>}
          </div>
        </>
      )}
    </div>
  );
}

// ===== SALES CONTRACT COST SHEET — per factory. Two indicators:
//   Pending Data Entry = TAS with no cost sheet yet (factory to enter)
//   Pending Approval   = cost sheets entered, awaiting Rishab (BD) approval
//   Approved           = approved by Rishab (read-only, locked)
// Used by the Factory section (admins pick a factory) and by factory logins, where
// the server already limits every list to the user's own factory.
export function CostSheetScreen({ factory, Header, onBack, onChanged, factoryLogin }) {
  const accent = '#006a4e';
  const [pending, setPending] = useState(null);   // no cost sheet yet
  const [entered, setEntered] = useState(null);   // entered via the app (approved or not)
  const [tab, setTab] = useState('entry');        // 'entry' | 'approval' | 'approved'
  const [openTas, setOpenTas] = useState(null);
  const [q, setQ] = useState('');
  const [showVideo, setShowVideo] = useState(false);
  const load = () => {
    api('/orders/sales-costsheet-pending').then(d => setPending(Array.isArray(d) ? d : [])).catch(() => setPending([]));
    api('/costflow/sales-approval').then(d => setEntered(Array.isArray(d) ? d : [])).catch(() => setEntered([]));
  };
  useEffect(load, []);
  const mine = r => factoryLogin || !factory || String(r.factory || '') === factory;
  const uniq = (arr, k) => { const seen = new Set(); return arr.filter(r => (seen.has(r[k]) ? false : (seen.add(r[k]), true))); };
  const entryRows = pending ? uniq(pending.filter(mine), 'tasno') : null;
  const apprRows = entered ? uniq(entered.filter(r => mine(r) && !r.approved), 'tas_no') : null;
  const doneRows = entered ? uniq(entered.filter(r => mine(r) && r.approved), 'tas_no') : null;
  const match = (...v) => { const t = q.trim().toLowerCase(); return !t || v.join(' ').toLowerCase().includes(t); };

  if (openTas) {
    return <CostSheetEntry tas={openTas} factory={factory} Header={Header}
      onBack={() => setOpenTas(null)}
      onSaved={() => { setOpenTas(null); load(); if (onChanged) onChanged(); }} />;
  }

  const cell = { padding: '9px 10px', fontSize: 12, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
  const hd = { ...cell, color: 'var(--text3)', fontSize: 10, letterSpacing: 1, background: 'var(--bg3)', textAlign: 'left' };
  const tile = (key, label, n, sub, color) => (
    <div key={key} onClick={() => setTab(key)}
      style={{ flex: '1 1 220px', maxWidth: 340, padding: '16px 18px', borderRadius: 12, cursor: 'pointer', boxSizing: 'border-box',
        border: (tab === key ? 2 : 1) + 'px solid ' + (tab === key ? color : 'var(--border)'), background: tab === key ? color + '10' : 'var(--bg2)' }}>
      <div style={{ fontSize: 30, fontWeight: 700, color, fontFamily: 'Inter, sans-serif', lineHeight: 1 }}>{n == null ? '…' : fmt(n)}</div>
      <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.2, color, marginTop: 6 }}>{label}</div>
      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{sub}</div>
    </div>
  );
  const isEntry = tab === 'entry', isDone = tab === 'approved';
  const src = isEntry ? entryRows : isDone ? doneRows : apprRows;
  const rows = (src || []).filter(r => isEntry ? match(r.tasno, r.buyer, r.broker) : match(r.tas_no, r.buyer, r.merchandiser));
  const loading = !src;
  const cols = isEntry
    ? [C('tasno', 'TAS'), C('buyer', 'Buyer'), C('enquirydate', 'Enquiry Date', 'date'), C('fi_dt', 'FI Date', 'date'), C('broker', 'Merchandiser')]
    : [C('tas_no', 'TAS'), C('buyer', 'Buyer'), C('enquirydate', 'Enquiry Date', 'date'), C('fi_dt', 'FI Date', 'date'), C('merchandiser', 'Merchandiser'),
       ...(isDone ? [C('approved_at', 'Approved On', 'date'), C('sales_approved_by_name', 'Approved By')] : [])];
  const listName = isEntry ? 'Pending Data Entry' : isDone ? 'Approved' : 'Pending Approval';
  const hint = isEntry ? 'click a TAS to enter its cost sheet' : isDone ? 'click a TAS to view its approved cost sheet' : 'click a TAS to view or correct it before approval';

  return (
    <div className="screen">
      {Header && (typeof Header === 'function' ? <Header subtitle="FACTORY · SALES CONTRACT COST SHEET" /> : Header)}
      {showVideo && <GmtTrainingModal factory={factory} initialStage={4} onClose={() => setShowVideo(false)} />}
      <ExportBar reportKey={isEntry ? 'factory-costsheet-pending' : isDone ? 'factory-costsheet-approved' : 'factory-costsheet-approval'}
        title={(factory || 'Factory') + ' - Cost Sheet ' + listName}
        sections={[{ name: listName, columns: cols, rows }]}
        factory={factory} />
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
        {onBack && <button onClick={onBack} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>}
        <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 20, color: accent }}>🧾 {factory ? factory + ' — ' : ''}Sales Contract Cost Sheet</div>
      </div>

      {/* Two key indicators — each one also switches the list below */}
      <div style={{ padding: '16px 16px 4px', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {tile('entry', 'PENDING DATA ENTRY', entryRows ? entryRows.length : null, 'cost sheets still to be entered', '#c62828')}
        {tile('approval', 'PENDING APPROVAL', apprRows ? apprRows.length : null, 'entered · awaiting Rishab approval', '#b5651d')}
        {tile('approved', 'APPROVED', doneRows ? doneRows.length : null, 'approved by Rishab · locked', '#1b5e20')}
      </div>

      <div style={{ padding: '10px 16px 6px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)' }}>
          {listName.toUpperCase()}
          <span style={{ color: 'var(--text3)', fontWeight: 400 }}> · {rows.length} TAS · {hint}</span>
        </div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="search TAS / buyer / merchandiser…"
          style={{ marginLeft: 'auto', minWidth: 220, padding: '7px 10px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 12, outline: 'none' }} />
      </div>
      <div style={{ margin: '0 16px 24px', border: '1px solid var(--border)', borderRadius: 8, overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 680, borderCollapse: 'collapse' }}>
          <thead><tr><th style={hd}>TAS</th><th style={hd}>Buyer</th><th style={hd}>Enquiry Date</th><th style={hd}>FI Date</th><th style={hd}>Merchandiser</th>{isDone && <><th style={hd}>Approved On</th><th style={hd}>Approved By</th></>}<th style={hd}>Status</th></tr></thead>
          <tbody>
            {loading && <tr><td colSpan={isDone ? 8 : 6} style={{ ...cell, textAlign: 'center', color: 'var(--text3)' }}>Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={isDone ? 8 : 6} style={{ ...cell, textAlign: 'center', color: 'var(--text3)' }}>
                {isEntry ? 'Nothing pending — every cost sheet has been entered.' : isDone ? 'No approved cost sheets yet.' : 'Nothing awaiting approval.'}
              </td></tr>
            )}
            {rows.map(r => {
              const tas = isEntry ? r.tasno : r.tas_no;
              return (
                <tr key={tas} onClick={() => setOpenTas(tas)} style={{ cursor: 'pointer' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg3)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                  <td style={{ ...cell, fontWeight: 700, color: accent }}>{tas} <span style={{ color: 'var(--text3)', fontWeight: 400 }}>›</span></td>
                  <td style={cell}>{titleCase(r.buyer) || '—'}</td>
                  <td style={cell}>{fmtDMY(r.enquirydate) || '—'}</td>
                  <td style={{ ...cell, fontWeight: r.fi_dt ? 600 : 400, color: Number(r.fi_delay_days || 0) > 20 ? '#c62828' : (r.fi_dt ? 'var(--text)' : 'var(--text3)') }}>
                    {fmtDMY(r.fi_dt) || '—'}
                    {Number(r.fi_delay_days || 0) > 20 && <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 8, background: '#ffebee', color: '#c62828', fontSize: 9.5, fontWeight: 700 }}>⚠️ +{r.fi_delay_days}d</span>}
                  </td>
                  <td style={cell}>{titleCase(isEntry ? r.broker : r.merchandiser) || '—'}</td>
                  {isDone && <><td style={cell}>{fmtDMY(r.approved_at) || '—'}</td><td style={cell}>{titleCase(r.sales_approved_by_name) || '—'}</td></>}
                  <td style={cell}>{isDone
                    ? <span style={{ padding: '2px 9px', borderRadius: 12, fontSize: 10, fontWeight: 700, background: '#e8f5e9', color: '#1b5e20' }}>APPROVED</span>
                    : isEntry
                    ? <span style={{ padding: '2px 9px', borderRadius: 12, fontSize: 10, fontWeight: 700, background: '#ffebee', color: '#c62828' }}>TO ENTER</span>
                    : <span style={{ padding: '2px 9px', borderRadius: 12, fontSize: 10, fontWeight: 700, background: '#fff3e0', color: '#b5651d' }}>AWAITING RISHAB</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ===== FAB INHOUSE (Garment Routing Stage 4_9, proc_routeid = 78) — per factory =====
// Two indicators:
//   Pending Fab Inhouse = open TAS where Stage 4_9 actual_dt is not set
//   Inhouse Completed   = open TAS where Stage 4_9 actual_dt is set
// Clicking a TAS opens the Fabric & Shade breakdown + date/remark entry form.
function FabInhouseEntry({ tas, factory, factoryId, Header, onBack, onSaved }) {
  const accent = '#006a4e';
  const [data, setData] = useState(null);
  const [inhouseDt, setInhouseDt] = useState('');
  const [remark, setRemark] = useState('');
  const [editing, setEditing] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [showVideo, setShowVideo] = useState(false);

  const qs = factoryId ? `?factoryid=${encodeURIComponent(factoryId)}` : (factory ? `?factory=${encodeURIComponent(factory)}` : '');

  useEffect(() => {
    let live = true;
    api(`/fabinhouse/${encodeURIComponent(tas)}/detail${qs}`).then(d => {
      if (!live) return;
      if (!d || d.error) { setData({ error: (d && d.error) || 'Load failed' }); return; }
      setData(d);
      const hdr = d.header || {};
      setInhouseDt(hdr.actual_inhouse_dt || today());
      setRemark(hdr.remark || '');
      setEditing(!hdr.actual_inhouse_dt);
    }).catch(() => live && setData({ error: 'Load failed' }));
    return () => { live = false; };
  }, [tas, factory, factoryId]);

  const h = (data && data.header) || {};
  const lines = (data && data.lines) || [];
  const estDt = h.est_inhouse_dt || '';
  const isCompleted = !!h.actual_inhouse_dt;
  const lockedByPcd = !!h.locked_by_pcd;
  const ro = !editing;

  const delayDays = useMemo(() => {
    const refDt = inhouseDt || today();
    if (!refDt || !estDt) return 0;
    const d1 = new Date(estDt + 'T00:00:00Z');
    const d2 = new Date(refDt + 'T00:00:00Z');
    if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return 0;
    return Math.round((d2.getTime() - d1.getTime()) / 86400000);
  }, [inhouseDt, estDt]);

  const save = (clearFlag = false) => {
    if (!clearFlag && !inhouseDt) { setMsg('Select Fabric Inhouse Date first.'); return; }
    if (clearFlag && !window.confirm(`Clear Fabric Inhouse Date for ${tas}? It will move back to Pending Fab Inhouse.`)) return;
    setBusy(true); setMsg('');
    api(`/fabinhouse/${encodeURIComponent(tas)}/save${qs}`, {
      method: 'POST',
      body: JSON.stringify({
        actual_inhouse_dt: clearFlag ? null : inhouseDt,
        remark: clearFlag ? '' : remark,
        clear: clearFlag,
        ...(factoryId ? { factoryid: factoryId } : {}),
        ...(factory ? { factory } : {}),
      }),
    }).then(r => {
      setBusy(false);
      if (!r || r.error) { setMsg((r && r.error) || 'Save failed'); return; }
      setData(d => ({
        ...d,
        header: { ...(d && d.header), actual_inhouse_dt: r.actual_inhouse_dt || null, remark: r.remark || '' }
      }));
      if (clearFlag) {
        setInhouseDt(today());
        setRemark('');
        setEditing(true);
        setMsg('Fabric Inhouse Date cleared ✓');
      } else {
        setEditing(false);
        setMsg('Fabric Inhouse Date saved ✓');
        setTimeout(() => { onSaved && onSaved(); }, 800);
      }
    }).catch(() => { setBusy(false); setMsg('Save failed'); });
  };

  const cell = { padding: '8px 10px', fontSize: 12, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
  const hd = { ...cell, color: 'var(--text3)', fontSize: 10, letterSpacing: 1, background: 'var(--bg3)', textAlign: 'left', textTransform: 'uppercase' };
  const totPcs = lines.reduce((a, r) => a + (Number(r.order_qty) || 0), 0);
  const totMtrs = lines.reduce((a, r) => a + (Number(r.approx_mtrs) || 0), 0);

  return (
    <div className="screen">
      <ExportBar reportKey="fab-inhouse-detail" title={`${tas} - Fabric Inhouse`}
        sections={[{ name: 'Fabric & Shade Breakdown',
          columns: [C('style_name', 'Style'), C('shade', 'Shade'), C('fabric_desc', 'Fabric Description'), C('order_qty', 'Order Pcs', 'int', true), C('consumption', 'Consumption', 'dec2'), C('approx_mtrs', 'Approx Meters', 'dec2', true)],
          rows: lines }]}
        source={{ endpoint: `/fabinhouse/${encodeURIComponent(tas)}/detail` }} />
      {Header && (typeof Header === 'function' ? <Header subtitle="FACTORY · FAB INHOUSE" /> : Header)}
      {showVideo && <GmtTrainingModal factory={factory} initialStage={3} onClose={() => setShowVideo(false)} />}
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 20, color: accent }}>🧵 {tas} — Fab Inhouse</div>
          {(h.buyer || h.factory) && (
            <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 2 }}>
              {titleCase(h.buyer) || '—'}{h.factory ? ` · ${h.factory}` : ''}{h.merchandiser ? ` · Merch: ${titleCase(h.merchandiser)}` : ''}
            </div>
          )}
        </div>
        {isCompleted && <span style={{ padding: '4px 10px', borderRadius: 20, background: '#e8f5e9', color: '#1b5e20', fontSize: 11, fontWeight: 700 }}>INHOUSE COMPLETED ✓</span>}
        {!isCompleted && <span style={{ padding: '4px 10px', borderRadius: 20, background: '#fff3e0', color: '#b5651d', fontSize: 11, fontWeight: 700 }}>PENDING FAB INHOUSE</span>}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          {h.enquiry_dt && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 9.5, letterSpacing: 0.8, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase' }}>Enquiry Date</div>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text2)', marginTop: 2 }}>{fmtDMY(h.enquiry_dt)}</div>
            </div>
          )}
          {estDt && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 9.5, letterSpacing: 0.8, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase' }}>Target Inhouse (+70d)</div>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: accent, marginTop: 2 }}>{fmtDMY(estDt)}</div>
            </div>
          )}
          {h.fi_dt && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 9.5, letterSpacing: 0.8, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase' }}>FI Date</div>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text2)', marginTop: 2 }}>{fmtDMY(h.fi_dt)}</div>
            </div>
          )}
        </div>
      </div>

      {!data && <div style={{ textAlign: 'center', padding: 50, color: 'var(--text3)', fontSize: 13 }}>Loading…</div>}
      {data && data.error && <div style={{ textAlign: 'center', padding: 50, color: '#c62828', fontSize: 13 }}>{data.error}</div>}

      {data && !data.error && (
        <div style={{ padding: 16 }}>
          {/* Upstream Fabric Milestones strip */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(175px, 1fr))', gap: 10, marginBottom: 14 }}>
            {[
              ['4_6 FAB ETD', h.fab_etd_act ? `Actual: ${fmtDMY(h.fab_etd_act)}` : (h.fab_etd_est ? `Est: ${fmtDMY(h.fab_etd_est)}` : '—'), !!h.fab_etd_act],
              ['4_7 SHIPMENT DOCS', h.fab_doc_act ? `Shared: ${fmtDMY(h.fab_doc_act)}` : 'Pending', !!h.fab_doc_act],
              ['4_8 FAB ETA', h.fab_eta_act ? `Actual: ${fmtDMY(h.fab_eta_act)}` : (h.fab_eta_est ? `Est: ${fmtDMY(h.fab_eta_est)}` : '—'), !!h.fab_eta_act],
              ['4_16 CUTTING (PCD)', h.pcd_actual_dt ? `Started: ${fmtDMY(h.pcd_actual_dt)}` : 'Not started', !!h.pcd_actual_dt],
            ].map(([lbl, val, done]) => (
              <div key={lbl} style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg2)' }}>
                <div style={{ fontSize: 9.5, letterSpacing: 1, color: 'var(--text3)', fontWeight: 700 }}>{lbl}</div>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: done ? '#1b5e20' : 'var(--text)', marginTop: 4 }}>{val}</div>
              </div>
            ))}
          </div>

          {/* Fabric Inhouse Date & Remark Entry Card */}
          <div style={{ border: '1px solid #c3cbd5', borderRadius: 8, overflow: 'hidden', marginBottom: 16, background: 'var(--bg2)' }}>
            <div style={{ padding: '10px 14px', background: `${accent}12`, borderBottom: '1px solid #c3cbd5', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <span style={{ fontWeight: 800, color: accent, fontSize: 13, letterSpacing: 0.6 }}>4_9 FABRIC INHOUSE CONFIRMATION</span>
              {estDt && (
                <span style={{ fontSize: 11.5, fontWeight: 700, color: delayDays > 0 ? '#c62828' : '#1b5e20' }}>
                  {delayDays > 0 ? `⚠️ +${delayDays}d late vs Target (${fmtDMY(estDt)})` : delayDays < 0 ? `✓ ${Math.abs(delayDays)}d early vs Target (${fmtDMY(estDt)})` : `✓ On Target (${fmtDMY(estDt)})`}
                </span>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, padding: 14 }}>
              <div>
                <label style={{ fontSize: 10.5, letterSpacing: 0.8, color: 'var(--text)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 5, display: 'block' }}>
                  Actual Fabric Inhouse Date <span style={{ color: '#c62828' }}>*</span>
                </label>
                {ro ? (
                  <div style={{ height: 38, padding: '0 12px', display: 'flex', alignItems: 'center', borderRadius: 4, background: 'var(--bg3)', border: '1px solid #c3cbd5', fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
                    {fmtDMY(inhouseDt) || '—'}
                  </div>
                ) : (
                  <input type="date" value={inhouseDt} max={today()} min={h.enquiry_dt || undefined}
                    onChange={e => setInhouseDt(e.target.value)}
                    style={{ width: '100%', boxSizing: 'border-box', height: 38, padding: '0 10px', background: '#fffbe6', border: '1.5px solid #d4a72c', borderRadius: 4, color: 'var(--text)', fontSize: 13, fontWeight: 700, outline: 'none' }} />
                )}
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ fontSize: 10.5, letterSpacing: 0.8, color: 'var(--text)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 5, display: 'block' }}>
                  Remark / Lot / Challan Reference
                </label>
                {ro ? (
                  <div style={{ height: 38, padding: '0 12px', display: 'flex', alignItems: 'center', borderRadius: 4, background: 'var(--bg3)', border: '1px solid #c3cbd5', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                    {remark || '—'}
                  </div>
                ) : (
                  <input type="text" value={remark} onChange={e => setRemark(e.target.value)} placeholder="Optional challan no, roll count, or fabric receipt note…"
                    style={{ width: '100%', boxSizing: 'border-box', height: 38, padding: '0 10px', background: '#fffbe6', border: '1.5px solid #d4a72c', borderRadius: 4, color: 'var(--text)', fontSize: 13, fontWeight: 600, outline: 'none' }} />
                )}
              </div>
            </div>
            <div style={{ padding: '0 14px 14px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              {ro ? (
                <>
                  <button onClick={() => { setEditing(true); setMsg(''); }} disabled={busy}
                    style={{ padding: '9px 22px', borderRadius: 6, border: 'none', background: accent, color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                    ✏️ Edit Inhouse Date
                  </button>
                  {!lockedByPcd && (
                    <button onClick={() => save(true)} disabled={busy}
                      style={{ padding: '9px 18px', borderRadius: 6, border: '1.5px solid #c62828', background: '#ffebee', color: '#c62828', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                      🗑 Clear Date
                    </button>
                  )}
                  {lockedByPcd && (
                    <span style={{ fontSize: 11.5, color: 'var(--text3)' }}>
                      🔒 Cutting (4_16 PCD: {fmtDMY(h.pcd_actual_dt)}) has started in Daily Production — Inhouse Date cannot be cleared.
                    </span>
                  )}
                </>
              ) : (
                <>
                  <button onClick={() => save(false)} disabled={busy || !inhouseDt}
                    style={{ padding: '10px 24px', borderRadius: 6, border: 'none', background: busy ? 'var(--text3)' : accent, color: '#fff', fontSize: 13, fontWeight: 700, cursor: busy ? 'default' : 'pointer' }}>
                    {busy ? 'Saving…' : (isCompleted ? 'Update Fab Inhouse' : 'Confirm Fab Inhouse')}
                  </button>
                  {isCompleted && (
                    <button onClick={() => { setInhouseDt(h.actual_inhouse_dt || today()); setRemark(h.remark || ''); setEditing(false); setMsg(''); }} disabled={busy}
                      style={{ padding: '10px 18px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text2)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                      Cancel
                    </button>
                  )}
                </>
              )}
              {msg && <span style={{ fontSize: 12.5, fontWeight: 700, color: /✓/.test(msg) ? '#1b5e20' : '#c62828' }}>{msg}</span>}
            </div>
          </div>

          {/* Fabric & Shade Breakdown Table */}
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)', marginBottom: 6 }}>
            FABRIC &amp; SHADE BREAKDOWN · {lines.length} shade{lines.length === 1 ? '' : 's'} · {fmt(totPcs)} pcs · {fmt(Math.round(totMtrs))} mtrs
          </div>
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: 680, borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={hd}>Style</th>
                  <th style={hd}>Shade</th>
                  <th style={hd}>Fabric Description</th>
                  <th style={{ ...hd, textAlign: 'right' }}>Order Pcs</th>
                  <th style={{ ...hd, textAlign: 'right' }}>Cons (mtr/pc)</th>
                  <th style={{ ...hd, textAlign: 'right' }}>Approx Fabric Mtrs</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((r, i) => (
                  <tr key={i}>
                    <td style={{ ...cell, fontWeight: 600 }}>{r.style_name || '—'}</td>
                    <td style={cell}>{r.shade || '—'}</td>
                    <td style={{ ...cell, whiteSpace: 'normal', minWidth: 220 }}>{r.fabric_desc || '—'}</td>
                    <td style={{ ...cell, textAlign: 'right', fontWeight: 700 }}>{fmt(r.order_qty)}</td>
                    <td style={{ ...cell, textAlign: 'right', color: 'var(--text3)' }}>{Number(r.consumption || 0).toFixed(2)}</td>
                    <td style={{ ...cell, textAlign: 'right', fontWeight: 700, color: accent }}>{fmt(Math.round(Number(r.approx_mtrs || 0)))}</td>
                  </tr>
                ))}
                {lines.length > 0 && (
                  <tr style={{ background: 'var(--bg3)', fontWeight: 800 }}>
                    <td colSpan={3} style={{ ...cell, fontWeight: 800 }}>TOTAL</td>
                    <td style={{ ...cell, textAlign: 'right', fontWeight: 800 }}>{fmt(totPcs)}</td>
                    <td style={cell}></td>
                    <td style={{ ...cell, textAlign: 'right', fontWeight: 800, color: accent }}>{fmt(Math.round(totMtrs))}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export function FabInhouseScreen({ factory, factoryId, Header, onBack, onChanged }) {
  const accent = '#006a4e';
  const [rows, setRows] = useState(null);
  const [tab, setTab] = useState('pending'); // 'pending' | 'completed'
  const [openTas, setOpenTas] = useState(null);
  const [q, setQ] = useState('');
  const [showVideo, setShowVideo] = useState(false);

  const qs = factoryId ? `?factoryid=${encodeURIComponent(factoryId)}` : (factory ? `?factory=${encodeURIComponent(factory)}` : '');
  const load = () => {
    api('/fabinhouse/list' + qs).then(d => setRows(Array.isArray(d) ? d : [])).catch(() => setRows([]));
  };
  useEffect(load, [factory, factoryId]);

  if (openTas) {
    return (
      <FabInhouseEntry
        tas={openTas}
        factory={factory}
        factoryId={factoryId}
        Header={Header}
        onBack={() => setOpenTas(null)}
        onSaved={() => { setOpenTas(null); load(); if (onChanged) onChanged(); }}
      />
    );
  }

  const pendingRows = rows ? rows.filter(r => r.bucket ? r.bucket === 'pending' : !r.completed) : null;
  const upcomingRows = rows ? rows.filter(r => r.bucket === 'upcoming') : null;
  const doneRows = rows ? rows.filter(r => r.completed) : null;
  const isDone = tab === 'completed';
  const isUpcoming = tab === 'upcoming';
  const src = isDone ? doneRows : isUpcoming ? upcomingRows : pendingRows;
  const t = q.trim().toLowerCase();
  const list = (src || []).filter(r => !t || `${r.tas_no} ${r.buyer || ''} ${r.merchandiser || ''} ${r.fabric_desc || ''} ${r.style_name || ''}`.toLowerCase().includes(t));

  const cell = { padding: '9px 10px', fontSize: 12, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
  const hd = { ...cell, color: 'var(--text3)', fontSize: 10, letterSpacing: 1, background: 'var(--bg3)', textAlign: 'left', textTransform: 'uppercase' };
  const tile = (key, label, n, sub, color) => (
    <div key={key} onClick={() => setTab(key)}
      style={{ flex: '1 1 220px', maxWidth: 340, padding: '16px 18px', borderRadius: 12, cursor: 'pointer', boxSizing: 'border-box',
        border: (tab === key ? 2 : 1) + 'px solid ' + (tab === key ? color : 'var(--border)'), background: tab === key ? color + '10' : 'var(--bg2)' }}>
      <div style={{ fontSize: 30, fontWeight: 700, color, fontFamily: 'Inter, sans-serif', lineHeight: 1 }}>{n == null ? '…' : fmt(n)}</div>
      <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.2, color, marginTop: 6 }}>{label}</div>
      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{sub}</div>
    </div>
  );

  const listName = isDone ? 'Inhouse Completed' : isUpcoming ? 'Upcoming (Fab ETD Pending)' : 'Pending Fab Inhouse';
  const cols = [
    C('tas_no', 'TAS'), C('buyer', 'Buyer'), C('merchandiser', 'Merchandiser'),
    C('fabric_desc', 'Fabric'), C('order_qty', 'Order Pcs', 'int', true), C('approx_mtrs', 'Approx Mtrs', 'dec2', true),
    C('enquiry_dt', 'Enquiry Date', 'date'), C('fab_etd_dt', 'Fab ETD', 'date'), C('est_inhouse_dt', 'Target Inhouse', 'date'),
    ...(isDone ? [C('actual_inhouse_dt', 'Actual Inhouse', 'date'), C('remark', 'Remark')] : [C('fi_dt', 'FI Date', 'date')])
  ];

  return (
    <div className="screen">
      {Header && (typeof Header === 'function' ? <Header subtitle="FACTORY · FAB INHOUSE" /> : Header)}
      {showVideo && <GmtTrainingModal factory={factory} initialStage={3} onClose={() => setShowVideo(false)} />}
      <ExportBar reportKey={isDone ? 'factory-fabinhouse-done' : isUpcoming ? 'factory-fabinhouse-upcoming' : 'factory-fabinhouse-pending'}
        title={`${factory || 'Factory'} - ${listName}`}
        sections={[{ name: listName, columns: cols, rows: list }]}
        source={{ endpoint: '/fabinhouse/list' + qs }}
        factory={factory} />
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
        {onBack && <button onClick={onBack} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>}
        <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 20, color: accent }}>🧵 {factory ? `${factory} — ` : ''}Fab Inhouse</div>
      </div>

      <div style={{ padding: '16px 16px 4px', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {tile('pending', 'PENDING FAB INHOUSE', pendingRows ? pendingRows.length : null, 'fabric dispatched / active · awaiting factory inhouse', '#c62828')}
        {tile('upcoming', 'UPCOMING (FAB ETD PENDING)', upcomingRows ? upcomingRows.length : null, 'fabric not yet dispatched · pre-inhouse pipeline', '#b5651d')}
        {tile('completed', 'INHOUSE COMPLETED', doneRows ? doneRows.length : null, 'fabric received inhouse · ready for cutting', '#1b5e20')}
      </div>

      <div style={{ padding: '10px 16px 6px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)' }}>
          {listName.toUpperCase()}
          <span style={{ color: 'var(--text3)', fontWeight: 400 }}> · {list.length} TAS · {isDone ? 'click a TAS to view or edit its Fab Inhouse date' : 'click a TAS to confirm Fabric Inhouse date'}</span>
        </div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="search TAS / buyer / fabric / merchandiser…"
          style={{ marginLeft: 'auto', minWidth: 240, padding: '7px 10px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 12, outline: 'none' }} />
      </div>

      <div style={{ margin: '0 16px 24px', border: '1px solid var(--border)', borderRadius: 8, overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 920, borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={hd}>TAS</th>
              <th style={hd}>Buyer</th>
              <th style={hd}>Merchandiser</th>
              <th style={hd}>Fabric</th>
              <th style={{ ...hd, textAlign: 'right' }}>Pcs</th>
              <th style={{ ...hd, textAlign: 'right' }}>Mtrs</th>
              <th style={hd}>Enquiry Date</th>
              <th style={hd}>Fab ETD</th>
              <th style={hd}>Target Inhouse</th>
              {isDone ? <><th style={hd}>Actual Inhouse</th><th style={hd}>Remark</th></> : <th style={hd}>FI Date</th>}
              <th style={hd}>Status</th>
            </tr>
          </thead>
          <tbody>
            {!rows && <tr><td colSpan={isDone ? 12 : 11} style={{ ...cell, textAlign: 'center', color: 'var(--text3)' }}>Loading…</td></tr>}
            {rows && list.length === 0 && (
              <tr><td colSpan={isDone ? 12 : 11} style={{ ...cell, textAlign: 'center', color: 'var(--text3)' }}>
                {isDone ? 'No TAS orders marked Fab Inhouse yet.' : 'No TAS orders pending Fab Inhouse.'}
              </td></tr>
            )}
            {list.map(r => {
              const d = Number(r.delay_days || 0);
              return (
                <tr key={r.tas_no} onClick={() => setOpenTas(r.tas_no)} style={{ cursor: 'pointer' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg3)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                  <td style={{ ...cell, fontWeight: 700, color: accent }}>{r.tas_no} <span style={{ color: 'var(--text3)', fontWeight: 400 }}>›</span></td>
                  <td style={cell}>{titleCase(r.buyer) || '—'}</td>
                  <td style={cell}>{titleCase(r.merchandiser) || '—'}</td>
                  <td style={{ ...cell, maxWidth: 210, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.fabric_desc || ''}>{r.fabric_desc || '—'}</td>
                  <td style={{ ...cell, textAlign: 'right', fontWeight: 600 }}>{fmt(r.order_qty)}</td>
                  <td style={{ ...cell, textAlign: 'right', fontWeight: 600, color: accent }}>{fmt(Math.round(Number(r.approx_mtrs || 0)))}</td>
                  <td style={cell}>{fmtDMY(r.enquiry_dt) || '—'}</td>
                  <td style={{ ...cell, color: r.fab_etd_done ? '#1b5e20' : 'var(--text3)', fontWeight: r.fab_etd_done ? 700 : 400 }}>
                    {fmtDMY(r.fab_etd_dt) || '—'}{r.fab_etd_done ? ' ✓' : ''}
                  </td>
                  <td style={{ ...cell, fontWeight: 600 }}>{fmtDMY(r.est_inhouse_dt) || '—'}</td>
                  {isDone ? (
                    <>
                      <td style={{ ...cell, fontWeight: 700, color: '#1b5e20' }}>{fmtDMY(r.actual_inhouse_dt) || '—'}</td>
                      <td style={{ ...cell, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--text2)' }} title={r.remark || ''}>{r.remark || '—'}</td>
                    </>
                  ) : (
                    <td style={cell}>{fmtDMY(r.fi_dt) || '—'}</td>
                  )}
                  <td style={cell}>
                    {isDone ? (
                      <span style={{ padding: '2px 9px', borderRadius: 12, fontSize: 10, fontWeight: 700, background: '#e8f5e9', color: '#1b5e20' }}>
                        INHOUSE {d > 0 ? `(+${d}d)` : '✓'}
                      </span>
                    ) : d > 0 ? (
                      <span style={{ padding: '2px 9px', borderRadius: 12, fontSize: 10, fontWeight: 700, background: '#ffebee', color: '#c62828' }}>
                        DELAYED +{d}d
                      </span>
                    ) : (
                      <span style={{ padding: '2px 9px', borderRadius: 12, fontSize: 10, fontWeight: 700, background: '#fff3e0', color: '#b5651d' }}>
                        PENDING
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function FactoryView({ setView, Header, initialFactory, initialFactoryId, isFactoryUser, user, onLogout }) {
  const accent = '#1A4F9C';
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(initialFactory || null);       // selected factory name
  const [costView, setCostView] = useState(null); // 'sales' | 'final' | null
  const [pending, setPending] = useState(null);   // sales-cost-sheet pending list
  const [fin, setFin] = useState(null);           // final-costing list for this factory
  const [fcTas, setFcTas] = useState(null);       // TAS open in the final-costing grid
  const [finTab, setFinTab] = useState('entry');  // 'entry' | 'approval' | 'approved'
  const [finQ, setFinQ] = useState('');
  const [showGmtTraining, setShowGmtTraining] = useState(false);
  const [trainingStage, setTrainingStage] = useState(1);
  const openVideo = (stage = 1) => { setTrainingStage(stage); setShowGmtTraining(true); };
  const loadPending = () => api('/orders/sales-costsheet-pending').then(d => setPending(Array.isArray(d) ? d : [])).catch(() => setPending([]));
  const [shipCount, setShipCount] = useState(null);   // TAS eligible to ship for this factory
  const [fabInhousePend, setFabInhousePend] = useState(null); // TAS pending Stage 4_9 FAB INHOUSE for this factory
  const loadFinal = () => { if (!sel) return; api(`/costflow/final-pending?factory=${encodeURIComponent(sel)}`).then(d => setFin(Array.isArray(d) ? d : [])).catch(() => setFin([])); };
  const loadFabInhouse = () => {
    if (!sel) return;
    api(`/fabinhouse/list?factory=${encodeURIComponent(sel)}`)
      .then(d => setFabInhousePend(Array.isArray(d) ? d.filter(r => r.bucket ? r.bucket === 'pending' : !r.completed).length : 0))
      .catch(() => setFabInhousePend(0));
  };
  useEffect(() => { api('/orders/factory-summary').then(d => setRows(Array.isArray(d) ? d : [])).catch(() => setRows([])); }, []);
  useEffect(() => { loadPending(); }, []);
  // Counts for the factory's cards, loaded as soon as a factory is opened.
  useEffect(() => {
    if (!sel) { setFin(null); setShipCount(null); setFabInhousePend(null); return; }
    setFin(null); setShipCount(null); setFabInhousePend(null);
    loadFinal();
    loadFabInhouse();
    api(`/shipments/tas-options?factory=${encodeURIComponent(sel)}`)
      .then(d => setShipCount(Array.isArray(d) ? d.length : 0)).catch(() => setShipCount(0));
  }, [sel]);
  // Only factories ticked for this user in Admin → User Rights → GMT Factory · Factories.
  const list = (rows || []).filter(r => {
    if (!canSee(factoryRightKey(r.factoryid))) return false;
    const s = q.trim().toLowerCase(); return !s || String(r.factory || '').toLowerCase().includes(s);
  });

  const selRow = (rows || []).find(r => String(r.factory || '') === sel) || null;
  const selFactoryId = selRow ? selRow.factoryid : null;
  const selTasCount = selRow ? Number(selRow.tas_count || 0) : null;
  const fp = (pending || []).filter(r => String(r.factory || '') === sel);
  const userRights = Array.isArray(user?.rights) ? user.rights : [];
  const hasExplicitFactoryRights = userRights.some(r => String(r).startsWith('screen.factory.'));
  const canSeeTab = rightKey => isFactoryUser ? (!hasExplicitFactoryRights || userRights.includes(rightKey)) : canSee(rightKey);

  const factoryHeader = (
    <div className="header">
      <div>
        <div className="header-logo">RISHAB</div>
        <div className="header-sub">FACTORY PORTAL · {sel || ''}</div>
      </div>
      <div style={{ textAlign: 'right', fontSize: 12, color: 'var(--text3)' }}>
        <div>{(user && user.name) || 'Factory'}</div>
        <div style={{ marginTop: 2, fontWeight: 700, color: accent }}>{sel || '— no factory mapped —'}</div>
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center', marginTop: 6 }}>
          {onLogout && <button onClick={onLogout} style={{ background: 'none', border: '1px solid var(--border2)', color: 'var(--text3)', padding: '3px 10px', borderRadius: 4, fontSize: 10, letterSpacing: 1, cursor: 'pointer' }}>LOGOUT</button>}
        </div>
      </div>
    </div>
  );
  const activeHeader = isFactoryUser ? () => factoryHeader : Header;
  const cell = { padding: '8px 10px', fontSize: 12, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
  const hd = { ...cell, color: 'var(--text3)', fontSize: 10, letterSpacing: 1, background: 'var(--bg3)', textAlign: 'left' };

  // Daily Production Entry (form 5582) — scoped to this factory
  if (sel && costView === 'production') {
    return <DailyProductionScreen factory={sel} factoryId={selFactoryId || initialFactoryId} Header={activeHeader} onBack={() => setCostView(null)} isFactoryUser={isFactoryUser} initialMode={isFactoryUser ? "new" : null} />;
  }

  // Fab Inhouse (Stage 4_9, proc_routeid = 78) — scoped to this factory
  if (sel && costView === 'fabinhouse') {
    return <FabInhouseScreen factory={sel} factoryId={selFactoryId || initialFactoryId} Header={activeHeader} onBack={() => setCostView(null)} onChanged={loadFabInhouse} />;
  }

  // Shipment (stage 1) — scoped to this factory. Shipment Update lives in BD now.
  if (sel && costView === 'shipment') {
    return <ShipmentView Header={activeHeader} setView={setView} factory={sel} backTo={() => setCostView(null)} />;
  }

  // Sales Contract Cost Sheet — pending data entry + pending approval for this factory
  if (sel && costView === 'sales') {
    return <CostSheetScreen factory={sel} Header={activeHeader} onBack={() => setCostView(null)} onChanged={loadPending} factoryLogin={isFactoryUser} />;
  }

  // Final Costing — one TAS open in the cost grid
  if (sel && costView === 'final' && fcTas) {
    return <FinalCostEntry tas={fcTas} factory={sel} Header={activeHeader}
      onBack={() => setFcTas(null)}
      onSaved={() => { setFcTas(null); loadFinal(); }} />;
  }

  // Final Costing — this factory's TAS with an approved sales sheet, split the same
  // way as the Sales Contract Cost Sheet: to enter / awaiting BD / approved.
  if (sel && costView === 'final') {
    const accent = '#b5651d';
    const seen = new Set();
    const all = (fin || []).filter(r => (seen.has(r.tas_no) ? false : (seen.add(r.tas_no), true)));  // help view can fan out
    const entryRows = all.filter(r => !r.prepared && !r.approved);
    const apprRows = all.filter(r => r.prepared && !r.approved);
    const doneRows = all.filter(r => r.approved);
    const isEntry = finTab === 'entry', isDone = finTab === 'approved';
    const t = finQ.trim().toLowerCase();
    const rows = (isEntry ? entryRows : isDone ? doneRows : apprRows)
      .filter(r => !t || [r.tas_no, r.buyer, r.merchandiser].join(' ').toLowerCase().includes(t));
    const listName = isEntry ? 'Pending Data Entry' : isDone ? 'Approved' : 'Pending Approval';
    const hint = isEntry ? 'click a TAS to enter its final costing' : isDone ? 'click a TAS to view its approved final costing' : 'click a TAS to view or correct it before approval';
    const cols = [C('tas_no', 'TAS'), C('buyer', 'Buyer'), C('enquirydate', 'Enquiry Date', 'date'), C('fi_dt', 'FI Date', 'date'), C('merchandiser', 'Merchandiser'), C('delivery_dt', 'Delivery', 'date'),
      ...(isDone ? [C('approved_at', 'Approved On', 'date'), C('final_approved_by_name', 'Approved By')] : [])];
    const tile = (key, label, n, sub, color) => (
      <div key={key} onClick={() => setFinTab(key)}
        style={{ flex: '1 1 220px', maxWidth: 340, padding: '16px 18px', borderRadius: 12, cursor: 'pointer', boxSizing: 'border-box',
          border: (finTab === key ? 2 : 1) + 'px solid ' + (finTab === key ? color : 'var(--border)'), background: finTab === key ? color + '10' : 'var(--bg2)' }}>
        <div style={{ fontSize: 30, fontWeight: 700, color, fontFamily: 'Inter, sans-serif', lineHeight: 1 }}>{fin ? fmt(n) : '…'}</div>
        <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.2, color, marginTop: 6 }}>{label}</div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{sub}</div>
      </div>
    );
    const span = isDone ? 9 : 7;
    return (
      <div className="screen">
        {isFactoryUser ? factoryHeader : (Header && <Header subtitle="FACTORY · FINAL COST SHEET" />)}
        {showGmtTraining && <GmtTrainingModal factory={sel} initialStage={trainingStage} onClose={() => setShowGmtTraining(false)} />}
        <ExportBar reportKey={isEntry ? 'factory-final-pending' : isDone ? 'factory-final-approved' : 'factory-final-approval'}
          title={`${sel} - Final Costing ${listName}`} sections={[{ name: listName, columns: cols, rows }]} source={{ endpoint: '/costflow/final-pending' }}
          factory={sel} />
        <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
          <button onClick={() => setCostView(null)} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 20, color: accent }}>📑 {sel} — Final Cost Sheet</div>
        </div>

        {/* Three key indicators — each one also switches the list below */}
        <div style={{ padding: '16px 16px 4px', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {tile('entry', 'PENDING DATA ENTRY', entryRows.length, 'final costing still to be entered', '#c62828')}
          {tile('approval', 'PENDING APPROVAL', apprRows.length, 'entered · awaiting BD approval', '#b5651d')}
          {tile('approved', 'APPROVED', doneRows.length, 'approved by BD · locked', '#1b5e20')}
        </div>

        <div style={{ padding: '10px 16px 6px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)' }}>
            {listName.toUpperCase()}
            <span style={{ color: 'var(--text3)', fontWeight: 400 }}> · {rows.length} TAS · {hint}</span>
          </div>
          <input value={finQ} onChange={e => setFinQ(e.target.value)} placeholder="search TAS / buyer / merchandiser…"
            style={{ marginLeft: 'auto', minWidth: 220, padding: '7px 10px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 12, outline: 'none' }} />
        </div>
        <div style={{ margin: '0 16px 24px', border: '1px solid var(--border)', borderRadius: 8, overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: 680, borderCollapse: 'collapse' }}>
            <thead><tr><th style={hd}>TAS</th><th style={hd}>Buyer</th><th style={hd}>Enquiry Date</th><th style={hd}>FI Date</th><th style={hd}>Merchandiser</th><th style={hd}>Delivery</th>{isDone && <><th style={hd}>Approved On</th><th style={hd}>Approved By</th></>}<th style={hd}>Status</th></tr></thead>
            <tbody>
              {!fin && <tr><td colSpan={span} style={{ ...cell, textAlign: 'center', color: 'var(--text3)' }}>Loading…</td></tr>}
              {fin && rows.length === 0 && (
                <tr><td colSpan={span} style={{ ...cell, textAlign: 'center', color: 'var(--text3)', lineHeight: 1.7 }}>
                  {isEntry
                    ? <>Nothing pending — every final costing has been entered.<br /><span style={{ fontSize: 11 }}>An order appears here as soon as BD approves its Sales Contract Cost Sheet.</span></>
                    : isDone ? 'No approved final costings yet.' : 'Nothing awaiting approval.'}
                </td></tr>
              )}
              {fin && rows.map(r => (
                <tr key={r.tas_no} onClick={() => setFcTas(r.tas_no)} style={{ cursor: 'pointer' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg3)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                  <td style={{ ...cell, fontWeight: 700, color: accent }}>{r.tas_no} <span style={{ color: 'var(--text3)', fontWeight: 400 }}>›</span></td>
                  <td style={cell}>{titleCase(r.buyer) || '—'}</td>
                  <td style={cell}>{fmtDMY(r.enquirydate) || '—'}</td>
                  <td style={{ ...cell, fontWeight: r.fi_dt ? 600 : 400, color: Number(r.fi_delay_days || 0) > 20 ? '#c62828' : (r.fi_dt ? 'var(--text)' : 'var(--text3)') }}>
                    {fmtDMY(r.fi_dt) || '—'}
                    {Number(r.fi_delay_days || 0) > 20 && <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 8, background: '#ffebee', color: '#c62828', fontSize: 9.5, fontWeight: 700 }}>⚠️ +{r.fi_delay_days}d</span>}
                  </td>
                  <td style={cell}>{titleCase(r.merchandiser) || '—'}</td>
                  <td style={cell}>{fmtDMY(r.delivery_dt) || '—'}</td>
                  {isDone && <><td style={cell}>{fmtDMY(r.approved_at) || '—'}</td><td style={cell}>{titleCase(r.final_approved_by_name) || '—'}</td></>}
                  <td style={cell}>{isDone
                    ? <span style={{ padding: '2px 9px', borderRadius: 12, fontSize: 10, fontWeight: 700, background: '#e8f5e9', color: '#1b5e20' }}>APPROVED</span>
                    : isEntry
                    ? <span style={{ padding: '2px 9px', borderRadius: 12, fontSize: 10, fontWeight: 700, background: '#ffebee', color: '#c62828' }}>TO ENTER</span>
                    : <span style={{ padding: '2px 9px', borderRadius: 12, fontSize: 10, fontWeight: 700, background: '#fff3e0', color: '#b5651d' }}>AWAITING BD</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // factory detail → the two cost-sheet cards (click → details inside)
  if (sel) {
    // Same tile layout and navy palette as the factory list, for one consistent section.
    const cardBg = 'rgba(26,79,156,0.04)', cardBgHover = 'rgba(26,79,156,0.11)';
    const sheetCard = (cv, label, color, n, sub, warn, stageNum) => (
      <div key={cv} onClick={() => setCostView(cv)}
        style={{ padding: '18px 20px', border: '1px solid var(--border)', borderRadius: 10, cursor: 'pointer',
          textAlign: 'center', flex: '1 1 160px', minWidth: 150, maxWidth: 260, height: 180, boxSizing: 'border-box',
          display: 'flex', flexDirection: 'column', justifyContent: 'center', transition: 'all 0.2s', background: cardBg }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.background = cardBgHover; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = cardBg; }}>
        <div style={{ color, fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 30 }}>{n == null ? '…' : fmt(n)}</div>
        <div style={{ color, fontSize: 12.5, fontWeight: 700, letterSpacing: 1.4, marginTop: 5 }}>{label}</div>
        <div style={{ color: 'var(--text3)', fontSize: 11, marginTop: 6 }}>{sub}</div>
        <div style={{ minHeight: 15, marginTop: 4 }}>
          {n > 0 && <span style={{ color: 'var(--red)', fontSize: 10.5, fontWeight: 600 }}>⚠ {n} {warn}</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 8 }}>
          <span style={{ color: 'var(--text3)', fontSize: 9, letterSpacing: 1 }}>OPEN →</span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openVideo(stageNum); }}
            style={{
              background: 'rgba(26,79,156,0.12)', border: '1px solid rgba(26,79,156,0.25)',
              color: '#1A4F9C', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700,
              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4
            }}
            title={`Watch Video Guide for ${label}`}
          >
            🎥 Video
          </button>
        </div>
      </div>
    );
    return (
      <div className="screen">
        {isFactoryUser ? factoryHeader : (Header && <Header subtitle="FACTORY" />)}
        <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
          {!isFactoryUser && <button onClick={() => { setSel(null); setCostView(null); }} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>}
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: accent }}>🏭 {sel}</div>
          <button
            type="button"
            onClick={() => openVideo(1)}
            style={{
              marginLeft: 8, padding: '5px 14px', borderRadius: 20, border: 'none',
              background: 'linear-gradient(135deg, #1A4F9C 0%, #0d2b5c 100%)',
              color: '#fff', fontSize: 11.5, fontWeight: 800, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 2px 8px rgba(26,79,156,0.3)'
            }}
            title="Open Bilingual GMT Factory Video Training Hub (English & Hindi)"
          >
            <span>🎥 Video Training (वीडियो ट्रेनिंग)</span>
          </button>
        </div>
        {showGmtTraining && <GmtTrainingModal factory={sel} initialStage={trainingStage} onClose={() => setShowGmtTraining(false)} />}
        <div style={{ padding: '16px', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {canSeeTab('screen.factory.daily_production') && sheetCard('production', 'DAILY PRODUCTION', accent, selTasCount, 'open TAS orders', 'open TAS', 2)}
          {canSeeTab('screen.factory.fab_inhouse') && sheetCard('fabinhouse', 'FAB INHOUSE', accent, fabInhousePend, 'pending fabric inhouse', 'pending', 3)}
          {canSeeTab('screen.factory.cost_sheet') && sheetCard('sales', 'COST SHEET', accent, pending ? fp.length : null, 'pending cost sheet', 'to enter', 4)}
          {canSeeTab('screen.factory.final_costing') && sheetCard('final', 'FINAL COSTING', accent, fin ? fin.filter(r => !r.prepared).length : null, 'awaiting final costing', 'to enter', 5)}
          {canSeeTab('screen.factory.shipment') && sheetCard('shipment', 'SHIPMENT', accent, shipCount, 'ready to ship', 'ready', 6)}
        </div>
      </div>
    );
  }

  // factory list
  return (
    <div className="screen">
      <Header subtitle="FACTORY" />
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
        <button onClick={() => setView('home')} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: accent }}>Factory</div>
        <button
          type="button"
          onClick={() => setShowGmtTraining(true)}
          style={{
            marginLeft: 8, padding: '5px 14px', borderRadius: 20, border: 'none',
            background: 'linear-gradient(135deg, #1A4F9C 0%, #0d2b5c 100%)',
            color: '#fff', fontSize: 11.5, fontWeight: 800, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 2px 8px rgba(26,79,156,0.3)'
          }}
          title="Open Bilingual GMT Factory Video Training Hub (English & Hindi)"
        >
          <span>🎥 Video Training (वीडियो ट्रेनिंग)</span>
        </button>
        <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>{list.length} factories</div>
      </div>
      {showGmtTraining && <GmtTrainingModal onClose={() => setShowGmtTraining(false)} />}
      <div style={{ padding: '10px 16px 4px' }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search factory…"
          style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 12, outline: 'none' }} />
      </div>
      <div style={{ padding: '12px 16px 24px', display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
        {!rows && <div style={{ color: 'var(--text3)', fontSize: 12 }}>Loading…</div>}
        {rows && list.length === 0 && <div style={{ color: 'var(--text3)', fontSize: 12 }}>No factories.</div>}
        {/* Same tile layout as the home cards: subtle tint, centred, OPEN → */}
        {list.map(r => {
          const cardBg = 'rgba(26,79,156,0.04)', cardBgHover = 'rgba(26,79,156,0.11)';
          return (
            <div key={r.factory} onClick={() => setSel(r.factory)}
              style={{ padding: '18px 20px', border: '1px solid var(--border)', borderRadius: 10, cursor: 'pointer',
                textAlign: 'center', flex: '1 1 160px', minWidth: 150, maxWidth: 260, height: 120, boxSizing: 'border-box',
                display: 'flex', flexDirection: 'column', justifyContent: 'center', transition: 'all 0.2s', background: cardBg }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = accent; e.currentTarget.style.background = cardBgHover; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = cardBg; }}>
              <div style={{ color: accent, fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 15, letterSpacing: 1, lineHeight: 1.35 }}>{r.factory}</div>
              <div style={{ color: 'var(--text3)', fontSize: 9, marginTop: 10, letterSpacing: 1 }}>OPEN →</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ===== MONTHLY SHIPMENT TARGET — garment (pcs/FI date) + fabric (mtrs/ex-factory
// ===== MONTHLY SHIPMENT TARGET — garment (pcs/FI date) + fabric (mtrs/ex-factory
// date) with current routing stage, filterable by month. =====
// ===== PARALLEL BOM MODULE (dj_bom_*) — Executive High-Level BOM Command Center
// 3 BOM Sections (Sewing, Finishing, Packing), Zero-Miss Product Base Checklist,
// Dual Shade Mapping (Garment Shade vs Raw Material Shade / DTM), Buyer Shortlisting,
// Copy from Old Style/TAS, Vendor Annual Forward Contracts, Gemini AI Auditor & Printouts =====
const bomToday = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
const CONS_TYPES = [
  'Style + Shade Wise',
  'Style + Size Wise',
  'Style + Shade + Size Wise',
  'Shade Wise',
  'Size Wise',
  'Lumpsum'
];
const BOM_TYPES = [
  { key: 'SEWING', label: '1. SEWING BOM', short: 'SEWING', icon: '🧵', color: '#4f46e5', bg: 'rgba(79,70,229,0.10)', border: 'rgba(79,70,229,0.35)' },
  { key: 'FINISHING', label: '2. FINISHING BOM', short: 'FINISHING', icon: '✨', color: '#0284c7', bg: 'rgba(2,132,199,0.10)', border: 'rgba(2,132,199,0.35)' },
  { key: 'PACKING', label: '3. PACKING BOM', short: 'PACKING', icon: '📦', color: '#059669', bg: 'rgba(5,150,105,0.10)', border: 'rgba(5,150,105,0.35)' }
];
const bomTypeMeta = bt => BOM_TYPES.find(x => x.key === (bt || 'SEWING')) || BOM_TYPES[0];
const bomTypeColor = bt => bomTypeMeta(bt).color;

// ============================================================================
// INTERACTIVE PIVOT MATRIX COMPONENT (Size × Shade × Raw Material Quality)
// ============================================================================
function BomPivotMatrix({ lines = [], mode = 'bom', tasNo = '' }) {
  const accent = '#7c3aed';
  const [rowMode, setRowMode] = useState('trim_quality'); // 'trim_quality' | 'trim_only' | 'quality_only' | 'trim_shade' | 'trim_vendor'
  const [colMode, setColMode] = useState(mode === 'master' ? 'mat_shade' : 'garment_shade'); // 'garment_shade' | 'size' | 'shade_x_size' | 'mat_shade' | 'mat_size' | 'buyer'
  const [valMode, setValMode] = useState(mode === 'master' ? 'price' : 'qty_val'); // 'qty' | 'val' | 'qty_val' | 'price' | 'cons' | 'count'

  const active = (lines || []).filter(l => !l.is_na);

  const resolvedColMode = colMode;

  const getColKey = l => {
    if (resolvedColMode === 'garment_shade') {
      const gs = String(l.shade || 'ALL').trim().toUpperCase();
      return gs === 'ALL' || !gs ? '👕 ALL GARMENT SHADES' : `👕 ${gs}`;
    }
    if (resolvedColMode === 'mat_shade') {
      return `🎨 ${String(l.mat_shade || (l.shade && l.shade !== 'ALL' ? `DTM (${l.shade})` : 'DTM / STD')).trim().toUpperCase()}`;
    }
    if (resolvedColMode === 'size') {
      return `📏 ${String(l.size || l.mat_size || 'ALL SIZES').trim().toUpperCase()}`;
    }
    if (resolvedColMode === 'mat_size') {
      return `📐 ${String(l.mat_size || l.size || 'STD SIZE').trim().toUpperCase()}`;
    }
    if (resolvedColMode === 'shade_x_size') {
      const sh = String(l.shade || 'ALL').trim().toUpperCase();
      const sz = String(l.size || 'ALL').trim().toUpperCase();
      return `👕 ${sh} · 📏 ${sz}`;
    }
    if (resolvedColMode === 'buyer') {
      return l.buyer_name || l.brand || 'ALL BUYERS';
    }
    return `👕 ${String(l.shade || 'ALL').trim().toUpperCase()}`;
  };

  const getRowInfo = l => {
    const bt = l.bom_type || 'SEWING';
    const trim = l.trim_type || 'OTHER';
    const qual = l.trim_code || l.material_name || 'Standard Spec';
    const msize = l.mat_size || '';
    const mshade = l.mat_shade || (l.shade ? `DTM (${l.shade})` : 'DTM');
    const unit = l.unit || 'NOS';
    const vendor = l.vendor || l.vendor_name || l.sourced_by || 'RWPL';

    if (rowMode === 'trim_only') {
      return { key: `${bt}|${trim}`, bt, primary: trim, secondary: `${unit} · ${l.cons_type || ''}`, msize, mshade, unit, vendor };
    }
    if (rowMode === 'quality_only') {
      return { key: `${bt}|${qual}|${msize}`, bt, primary: qual, secondary: `${trim}${msize ? ` · Size: ${msize}` : ''}`, msize, mshade, unit, vendor };
    }
    if (rowMode === 'trim_shade') {
      return { key: `${bt}|${trim}|${qual}|${mshade}`, bt, primary: `${trim} — ${qual}`, secondary: `🎨 Raw Shade: ${mshade}${msize ? ` · Size: ${msize}` : ''}`, msize, mshade, unit, vendor };
    }
    if (rowMode === 'trim_vendor') {
      return { key: `${bt}|${vendor}|${trim}|${qual}`, bt, primary: `${vendor} — ${trim}`, secondary: `${qual}${msize ? ` (${msize})` : ''}`, msize, mshade, unit, vendor };
    }
    // default: trim_quality
    return {
      key: `${bt}|${trim}|${qual}|${msize}`,
      bt,
      primary: trim,
      secondary: `${qual}${msize ? ` · Mat Size: ${msize}` : ''}`,
      msize,
      mshade,
      unit,
      vendor
    };
  };

  const colSet = new Set();
  const rowMap = new Map();
  const colTotals = {};
  let grandQty = 0, grandVal = 0;

  active.forEach(l => {
    const col = getColKey(l);
    colSet.add(col);
    const rInfo = getRowInfo(l);
    if (!rowMap.has(rInfo.key)) {
      rowMap.set(rInfo.key, { ...rInfo, matShades: new Set(), cells: {}, totQty: 0, totVal: 0, count: 0, prices: [], cons: [] });
    }
    const row = rowMap.get(rInfo.key);
    if (l.mat_shade) row.matShades.add(String(l.mat_shade).trim());
    if (!row.cells[col]) {
      row.cells[col] = { qty: 0, val: 0, count: 0, prices: [], cons: [], shades: new Set(), sizes: new Set() };
    }
    const c = row.cells[col];
    const q = Number(l.qty ?? l.used_in_boms ?? 0);
    const v = Number(l.line_value ?? l.value ?? (q * Number(l.price || 0)) ?? 0);
    const p = l.price != null && l.price !== '' ? Number(l.price) : null;
    const cs = l.consumption != null && l.consumption !== '' ? Number(l.consumption) : null;

    c.qty += q;
    c.val += v;
    c.count += 1;
    if (p != null) c.prices.push(p);
    if (cs != null) c.cons.push(cs);
    if (l.mat_shade) c.shades.add(l.mat_shade);
    if (l.mat_size || l.size) c.sizes.add(l.mat_size || l.size);

    row.totQty += q;
    row.totVal += v;
    row.count += 1;
    if (p != null) row.prices.push(p);
    if (cs != null) row.cons.push(cs);

    if (!colTotals[col]) colTotals[col] = { qty: 0, val: 0, count: 0 };
    colTotals[col].qty += q;
    colTotals[col].val += v;
    colTotals[col].count += 1;

    grandQty += q;
    grandVal += v;
  });

  const cols = [...colSet].sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
  const allRows = [...rowMap.values()];

  const money = n => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  const selCss = { height: 30, padding: '0 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' };

  const renderCell = cell => {
    if (!cell) return <span style={{ color: 'var(--border)', fontSize: 11 }}>—</span>;
    if (valMode === 'qty') {
      return <b style={{ color: 'var(--text)', fontSize: 12 }}>{fmt(Math.round(cell.qty))}</b>;
    }
    if (valMode === 'val') {
      return <b style={{ color: accent, fontSize: 12 }}>₹{money(cell.val)}</b>;
    }
    if (valMode === 'price') {
      const avgP = cell.prices.length ? (cell.prices.reduce((a, b) => a + b, 0) / cell.prices.length) : null;
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.25 }}>
          <b style={{ color: '#059669', fontSize: 12 }}>{avgP != null ? `₹${avgP.toFixed(2)}` : '—'}</b>
          {cell.sizes.size > 0 && <span style={{ fontSize: 9.5, color: 'var(--text3)' }}>Sz: {[...cell.sizes].slice(0, 2).join(',')}</span>}
        </div>
      );
    }
    if (valMode === 'cons') {
      const avgC = cell.cons.length ? (cell.cons.reduce((a, b) => a + b, 0) / cell.cons.length) : null;
      return <b style={{ color: '#0284c7', fontSize: 12 }}>{avgC != null ? avgC.toFixed(3) : '—'}</b>;
    }
    // default: qty_val
    const shadeHint = resolvedColMode === 'garment_shade' && cell.shades.size > 0
      ? [...cell.shades].filter(s => s && !/^DTM/i.test(s)).slice(0, 1)[0]
      : null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.25 }}>
        <b style={{ color: 'var(--text)', fontSize: 12 }}>{fmt(Math.round(cell.qty))}</b>
        {cell.val > 0 && <span style={{ fontSize: 10, color: accent, fontWeight: 700 }}>₹{money(cell.val)}</span>}
        {shadeHint && <span style={{ fontSize: 9.5, color: '#6d28d9', fontWeight: 700 }}>🎨 {shadeHint}</span>}
      </div>
    );
  };

  return (
    <div style={{ margin: '12px 16px 20px', borderRadius: 12, border: `1.5px solid ${accent}`, background: 'var(--bg2)', overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
      {/* Pivot Control Bar */}
      <div style={{ padding: '12px 16px', background: 'linear-gradient(135deg, rgba(124,58,237,0.12), rgba(2,132,199,0.08))', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: accent }}>
            📊 Interactive Pivot Matrix — Size × Shade × Raw Material Quality {tasNo ? `(${tasNo})` : ''}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 1 }}>
            Cross-tabulate Raw Material Quality, Trim Category, Garment Shade, Raw Material Shade &amp; Size like an Excel Pivot Table
          </div>
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 9.5, fontWeight: 800, color: 'var(--text3)', textTransform: 'uppercase' }}>Row Grouping (Y-Axis)</div>
            <select style={selCss} value={rowMode} onChange={e => setRowMode(e.target.value)}>
              <option value="trim_quality">Trim Category + Raw Material Quality + Mat Size</option>
              <option value="quality_only">Raw Material Quality + Mat Size Only</option>
              <option value="trim_shade">Trim + Quality + Raw Material Shade</option>
              <option value="trim_only">High-Level Trim Category Only</option>
              <option value="trim_vendor">Vendor / Supplier + Trim Quality</option>
            </select>
          </div>

          <div>
            <div style={{ fontSize: 9.5, fontWeight: 800, color: 'var(--text3)', textTransform: 'uppercase' }}>Column Pivot (X-Axis)</div>
            <select style={selCss} value={colMode} onChange={e => setColMode(e.target.value)}>
              {mode !== 'master' && <option value="garment_shade">👕 Garment Shade Only (HUSK, NAVY, OLIVE…)</option>}
              {mode !== 'master' && <option value="size">📏 Garment Size Only (28, 30, 32 / S, M, L…)</option>}
              {mode !== 'master' && <option value="shade_x_size">👕×📏 Garment Shade × Garment Size</option>}
              <option value="mat_shade">🎨 Raw Material Shade Only</option>
              <option value="mat_size">📐 Raw Material Size Only (24L, 6&quot;, 40TKT…)</option>
              {mode === 'master' && <option value="buyer">🏢 Buyer / Brand</option>}
            </select>
          </div>

          <div>
            <div style={{ fontSize: 9.5, fontWeight: 800, color: 'var(--text3)', textTransform: 'uppercase' }}>Cell Value</div>
            <select style={selCss} value={valMode} onChange={e => setValMode(e.target.value)}>
              {mode !== 'master' && <option value="qty_val">Total Qty + Value (₹)</option>}
              {mode !== 'master' && <option value="qty">Total Required Qty Only</option>}
              {mode !== 'master' && <option value="val">Total Value (₹) Only</option>}
              <option value="price">Rate ₹ ( &amp; Mat Size )</option>
              <option value="cons">Consumption / Pc</option>
            </select>
          </div>
        </div>
      </div>

      {/* Matrix Table Grouped by BOM Section */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'var(--bg3)', borderBottom: '2px solid var(--border)' }}>
              <th style={{ padding: '9px 12px', textAlign: 'left', fontSize: 10, fontWeight: 800, color: 'var(--text3)', textTransform: 'uppercase', position: 'sticky', left: 0, background: 'var(--bg3)', zIndex: 2, minWidth: 250, borderRight: '1px solid var(--border)' }}>
                Trim / Raw Material Quality Spec
              </th>
              <th style={{ padding: '9px 8px', textAlign: 'center', fontSize: 10, fontWeight: 800, color: 'var(--text3)', textTransform: 'uppercase', borderRight: '1px solid var(--border)' }}>
                Mat Size
              </th>
              <th style={{ padding: '9px 8px', textAlign: 'center', fontSize: 10, fontWeight: 800, color: 'var(--text3)', textTransform: 'uppercase', borderRight: '1px solid var(--border)' }}>
                🎨 Mat Shade
              </th>
              <th style={{ padding: '9px 8px', textAlign: 'center', fontSize: 10, fontWeight: 800, color: 'var(--text3)', textTransform: 'uppercase', borderRight: '2px solid var(--border)' }}>
                Unit
              </th>
              {cols.map(c => (
                <th key={c} style={{ padding: '9px 10px', textAlign: 'right', fontSize: 10.5, fontWeight: 800, color: '#0284c7', borderRight: '1px solid var(--border)', minWidth: 95, whiteSpace: 'nowrap' }}>
                  {c}
                </th>
              ))}
              <th style={{ padding: '9px 12px', textAlign: 'right', fontSize: 10.5, fontWeight: 800, color: accent, background: 'rgba(124,58,237,0.08)', minWidth: 115 }}>
                ROW TOTAL
              </th>
            </tr>
          </thead>
          <tbody>
            {BOM_TYPES.map(bt => {
              const secRows = allRows.filter(r => r.bt === bt.key);
              if (!secRows.length) return null;
              const secVal = secRows.reduce((a, r) => a + r.totVal, 0);
              const secPct = grandVal > 0 ? ((secVal / grandVal) * 100).toFixed(1) : '0.0';
              return (
                <React.Fragment key={bt.key}>
                  <tr style={{ background: bt.bg, borderTop: `1.5px solid ${bt.border}`, borderBottom: `1px solid ${bt.border}` }}>
                    <td colSpan={4 + cols.length} style={{ padding: '7px 12px', fontWeight: 800, fontSize: 12.5, color: bt.color }}>
                      {bt.icon} {bt.label} ({secRows.length} Quality Rows)
                    </td>
                    <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 800, fontSize: 12, color: bt.color }}>
                      {grandVal > 0 ? `₹${money(secVal)} (${secPct}%)` : ''}
                    </td>
                  </tr>
                  {secRows.map((r, rIdx) => (
                    <tr key={r.key} style={{ borderBottom: '1px solid var(--border)', background: rIdx % 2 ? 'var(--bg)' : 'transparent' }}>
                      <td style={{ padding: '7px 12px', position: 'sticky', left: 0, background: rIdx % 2 ? 'var(--bg)' : 'var(--bg2)', zIndex: 1, borderRight: '1px solid var(--border)' }}>
                        <div style={{ fontWeight: 800, color: 'var(--text)', fontSize: 12 }}>{r.primary}</div>
                        <div style={{ fontSize: 10.5, color: 'var(--text2)', marginTop: 1 }}>{r.secondary}</div>
                      </td>
                      <td style={{ padding: '7px 8px', textAlign: 'center', fontWeight: 700, color: 'var(--text2)', borderRight: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                        {r.msize || '—'}
                      </td>
                      <td style={{ padding: '7px 8px', textAlign: 'center', fontWeight: 700, color: '#6d28d9', borderRight: '1px solid var(--border)', whiteSpace: 'nowrap', fontSize: 11 }}>
                        {(() => {
                          const arr = [...(r.matShades || [])];
                          if (arr.length === 0) return r.mshade || 'DTM';
                          if (arr.every(s => /^DTM/i.test(s))) return 'DTM (Garment Shade)';
                          if (arr.length === 1) return arr[0];
                          return arr.slice(0, 2).join(' / ') + (arr.length > 2 ? ` +${arr.length - 2}` : '');
                        })()}
                      </td>
                      <td style={{ padding: '7px 8px', textAlign: 'center', color: 'var(--text3)', fontWeight: 700, borderRight: '2px solid var(--border)', fontSize: 11 }}>
                        {r.unit}
                      </td>
                      {cols.map(c => (
                        <td key={c} style={{ padding: '7px 10px', textAlign: 'right', borderRight: '1px solid var(--border)', background: r.cells[c] ? 'rgba(16,185,129,0.04)' : 'transparent' }}>
                          {renderCell(r.cells[c])}
                        </td>
                      ))}
                      <td style={{ padding: '7px 12px', textAlign: 'right', background: 'rgba(124,58,237,0.05)', fontWeight: 800 }}>
                        <div style={{ color: 'var(--text)', fontSize: 12.5 }}>{fmt(Math.round(r.totQty))} <span style={{ fontSize: 10, color: 'var(--text3)' }}>{r.unit}</span></div>
                        {r.totVal > 0 && <div style={{ fontSize: 10.5, color: accent }}>₹{money(r.totVal)}</div>}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
          {mode !== 'master' && cols.length > 0 && (
            <tfoot>
              <tr style={{ background: 'var(--bg3)', borderTop: `2px solid ${accent}`, fontWeight: 800 }}>
                <td colSpan={4} style={{ padding: '10px 12px', fontSize: 12.5, color: accent, borderRight: '2px solid var(--border)' }}>
                  GRAND TOTAL ACROSS ALL {cols.length} PIVOT COLUMNS
                </td>
                {cols.map(c => (
                  <td key={c} style={{ padding: '10px 10px', textAlign: 'right', borderRight: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 12, color: 'var(--text)' }}>{fmt(Math.round(colTotals[c]?.qty || 0))}</div>
                    {colTotals[c]?.val > 0 && <div style={{ fontSize: 10.5, color: accent }}>₹{money(colTotals[c].val)}</div>}
                  </td>
                ))}
                <td style={{ padding: '10px 12px', textAlign: 'right', background: 'rgba(124,58,237,0.14)', color: accent, fontSize: 13.5 }}>
                  <div>{fmt(Math.round(grandQty))} units</div>
                  <div>₹{money(grandVal)}</div>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function BomEntry({ tas, Header, onBack, onSaved }) {
  const accent = '#7c3aed';
  const blank = {
    bom_type: 'SEWING', trim_type: '', trim_code: '', mat_size: '', mat_shade: 'DTM', garment_shade: '', brand: tas.buyer || '',
    cons_type: CONS_TYPES[0], consumption: 1, unit: 'NOS', wastage: 3, price: '',
    sourced_by: 'RWPL', vendor: '', forward_contract_id: null, contract_no: null,
    image_url: '', remark: '', is_mandatory: false, is_na: false, na_reason: ''
  };
  const [head, setHead] = useState({
    entry_dt: bomToday(), exch: '', remark: '',
    product_type: tas.product_type || 'TROUSER',
    brand: tas.buyer || '',
    copied_from_tas: '', copied_from_style: ''
  });
  const [lines, setLines] = useState([]);
  const [parts, setParts] = useState(null);
  const [sugg, setSugg] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [openSugg, setOpenSugg] = useState(null);
  const [draft, setDraft] = useState(null);
  const [gen, setGen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [activeBomType, setActiveBomType] = useState('ALL');
  const [sourceFilter, setSourceFilter] = useState('ALL'); // 'ALL' | 'RWPL' | 'FACTORY'
  const [expandedRow, setExpandedRow] = useState(null);
  const [showOrderMatrix, setShowOrderMatrix] = useState(false);
  const [showEntryPivot, setShowEntryPivot] = useState(false);
  const [aiAudit, setAiAudit] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [previewImg, setPreviewImg] = useState(null);
  const [quickModal, setQuickModal] = useState(null); // null | { kind: 'vendor' | 'raw' | 'trim', rowIdx: number }
  const [qForm, setQForm] = useState({});

  useEffect(() => {
    api(`/bom/${encodeURIComponent(tas.tas_no)}/breakdown`)
      .then(d => setParts(d && d.byShade ? d : { byShade: [], bySize: [] })).catch(() => setParts({ byShade: [], bySize: [] }));
    api('/bom/items').then(d => setSugg(d && d.app ? d : { app: [], erp: [] })).catch(() => setSugg({ app: [], erp: [] }));
    api(`/bom/${encodeURIComponent(tas.tas_no)}/templates`).then(d => setTemplates(Array.isArray(d) ? d : [])).catch(() => setTemplates([]));
    api(`/bom/${encodeURIComponent(tas.tas_no)}`).then(d => {
      if (d && d.app && Array.isArray(d.app.lines) && d.app.lines.length) {
        const byTrim = new Map();
        d.app.lines.forEach(l => {
          const k = `${l.bom_type || 'SEWING'}|${l.trim_type}|${l.trim_code || ''}|${l.mat_shade || ''}`;
          if (!byTrim.has(k)) byTrim.set(k, {
            ...blank,
            bom_type: l.bom_type || 'SEWING',
            trim_type: l.trim_type,
            trim_code: l.trim_code || '',
            mat_size: l.mat_size || '',
            mat_shade: l.mat_shade || 'DTM',
            garment_shade: '',
            brand: l.brand || d.buyer || '',
            cons_type: l.cons_type || CONS_TYPES[0],
            consumption: l.consumption,
            unit: l.unit || 'NOS',
            wastage: l.wastage ?? 3,
            price: l.price ?? '',
            sourced_by: l.sourced_by || 'RWPL',
            vendor: l.vendor || '',
            forward_contract_id: l.forward_contract_id || null,
            contract_no: l.contract_no || null,
            image_url: l.image_url || '',
            remark: l.remark || '',
            is_na: !!l.is_na,
            na_reason: l.na_reason || ''
          });
        });
        setHead(h => ({
          ...h,
          entry_dt: d.app.entry_dt || h.entry_dt,
          exch: d.app.exch ?? h.exch,
          remark: d.app.remark || '',
          product_type: d.app.product_type || d.product_type || h.product_type,
          brand: d.app.brand || d.buyer || h.brand,
          copied_from_tas: d.app.copied_from_tas || '',
          copied_from_style: d.app.copied_from_style || ''
        }));
        setLines([...byTrim.values()]);
        return;
      }
      loadSuggested('auto');
    }).catch(() => setLines([{ ...blank }]));
  }, [tas.tas_no]);

  const loadSuggested = (mode = 'auto') => {
    setGen(true);
    api(`/bom/${encodeURIComponent(tas.tas_no)}/suggest?mode=${mode}`).then(sg => {
      if (sg && Array.isArray(sg.lines) && sg.lines.length) {
        setHead(h => ({
          ...h,
          exch: sg.exch ?? h.exch,
          product_type: sg.product_type || h.product_type,
          brand: sg.buyer || h.brand,
          copied_from_tas: sg.basis?.from_tas || '',
          copied_from_style: sg.basis?.from_style || ''
        }));
        setLines(sg.lines.map(l => ({ ...blank, ...l, mat_shade: l.mat_shade || 'DTM', cons_type: l.cons_type || CONS_TYPES[0] })));
        setDraft(sg.basis || {});
      } else {
        setLines([{ ...blank }]);
      }
      setGen(false);
    }).catch(() => { setLines([{ ...blank }]); setGen(false); });
  };

  const copyFromOldBom = (bomId) => {
    if (!bomId) return;
    setGen(true);
    api(`/bom/template/${bomId}`).then(res => {
      const srcRows = Array.isArray(res) ? res : (res?.lines || []);
      const srcHead = res?.head || {};
      if (srcRows.length) {
        setLines(srcRows.map(l => ({
          ...blank,
          ...l,
          bom_type: l.bom_type || 'SEWING',
          mat_shade: l.mat_shade || 'DTM',
          cons_type: l.cons_type || CONS_TYPES[0],
          sourced_by: l.sourced_by || 'RWPL',
          is_na: !!l.is_na,
          na_reason: l.na_reason || ''
        })));
        setHead(h => ({
          ...h,
          copied_from_tas: srcHead.tas_no || '',
          copied_from_style: srcHead.style_name || ''
        }));
        setDraft({
          kind: 'copied_style',
          from_tas: srcHead.tas_no,
          from_style: srcHead.style_name,
          from_buyer: srcHead.buyer
        });
      }
      setGen(false);
    }).catch(() => setGen(false));
  };

  const runAiAudit = (mode = 'audit', attachment = null) => {
    setAiBusy(true);
    api(`/bom/${encodeURIComponent(tas.tas_no)}/ai-audit`, {
      method: 'POST',
      body: JSON.stringify({
        mode,
        attachment,
        lines,
        product_type: head.product_type || tas.product_type || 'TROUSER',
        buyer: tas.buyer || ''
      })
    }).then(r => {
      setAiBusy(false);
      setAiAudit(r);
      if (mode === 'extract' && Array.isArray(r?.suggested_lines) && r.suggested_lines.length) {
        setLines(ls => [...ls.filter(x => x.trim_type), ...r.suggested_lines.map(x => ({ ...blank, ...x }))]);
      }
    }).catch(() => setAiBusy(false));
  };

  const handleTechpackUpload = e => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      runAiAudit('extract', { name: file.name, mimeType: file.type || 'image/png', data: reader.result });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleRowImage = (idx, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => set(idx, 'image_url', reader.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const num = v => (v === '' || v == null || isNaN(Number(v)) ? null : Number(v));
  const grainOf = ct => {
    const s = String(ct || '').toLowerCase();
    if (s.includes('lumpsum')) return 'lumpsum';
    if (s.includes('shade') && s.includes('size')) return 'both';
    if (s.includes('size') && !s.includes('style')) return 'only_size';
    if (s.includes('shade') && !s.includes('style')) return 'only_shade';
    if (s.includes('size')) return 'size';
    return 'shade';
  };
  const hasSize = !!(parts && parts.bySize && parts.bySize.length);
  const garmentShades = parts && parts.byShade
    ? [...new Set(parts.byShade.map(r => r.shade).filter(Boolean))]
    : [];
  const garmentSizes = parts && parts.bySize
    ? [...new Set(parts.bySize.map(r => r.size).filter(Boolean))]
    : [];

  const rowsFor = (ct, gmtShade) => {
    if (!parts) return [];
    const g = grainOf(ct);
    const baseRows = (g === 'size' || g === 'both' || g === 'only_size') && hasSize ? parts.bySize : parts.byShade;
    const gs = String(gmtShade || '').trim().toUpperCase();
    if (gs && gs !== 'ALL') {
      const f = baseRows.filter(r => String(r.shade || '').trim().toUpperCase() === gs);
      if (f.length) return f;
    }
    return baseRows;
  };

  // Preview expanded lines for any single trim row (used by the inline Breakdown Preview Drawer)
  const previewExpanded = l => {
    if (l.is_na || !parts) return [];
    const g = grainOf(l.cons_type);
    const cons = num(l.consumption) || 0;
    const wast = num(l.wastage) || 0;
    const rawMatShade = String(l.mat_shade || '').trim();
    const isDtm = !rawMatShade || rawMatShade.toUpperCase() === 'DTM' || rawMatShade.toUpperCase() === 'GARMENT SHADE';
    if (g === 'lumpsum') {
      const totalOrderPcs = (parts.byShade || []).reduce((a, r) => a + Number(r.pcs || 0), 0) || Number(tas.order_qty) || 0;
      const qty = Math.round(cons * (1 + wast / 100));
      return [{ style: parts.byShade[0]?.style || tas.style_name || 'ALL', shade: l.garment_shade || 'ALL', mat_shade: rawMatShade || 'STANDARD', size: 'ALL', pcs: totalOrderPcs, qty }];
    }
    const rows = rowsFor(l.cons_type, l.garment_shade);
    const m = new Map();
    rows.forEach(r => {
      const key = g === 'only_shade' ? `${r.shade}`
        : g === 'only_size' ? `${r.size}`
        : g === 'shade' ? `${r.style}|${r.shade}`
        : g === 'size' ? `${r.style}|${r.size}`
        : `${r.style}|${r.shade}|${r.size}`;
      const cur = m.get(key) || {
        style: g === 'only_shade' || g === 'only_size' ? 'ALL' : (r.style || tas.style_name || '—'),
        shade: (g === 'size' || g === 'only_size') ? (l.garment_shade || 'ALL') : (r.shade || 'ALL'),
        size: (g === 'size' || g === 'both' || g === 'only_size') ? (r.size || 'ALL') : 'ALL',
        pcs: 0
      };
      cur.pcs += Number(r.pcs || 0);
      m.set(key, cur);
    });
    return [...m.values()].map(x => ({
      ...x,
      mat_shade: isDtm ? (x.shade && x.shade !== 'ALL' ? `DTM (${x.shade})` : 'DTM') : rawMatShade,
      qty: Math.round(x.pcs * cons * (1 + wast / 100))
    }));
  };

  const expandCount = l => {
    if (l.is_na || !parts) return 0;
    const g = grainOf(l.cons_type);
    if (g === 'lumpsum') return 1;
    const rows = rowsFor(l.cons_type, l.garment_shade);
    return new Set(rows.map(r =>
      g === 'only_shade' ? `${r.shadeid || r.shade}`
      : g === 'only_size' ? `${r.sizeid || r.size}`
      : g === 'shade' ? `${r.designid}|${r.shadeid}`
      : g === 'size' ? `${r.designid}|${r.sizeid}`
      : `${r.designid}|${r.shadeid}|${r.sizeid}`
    )).size || 1;
  };
  const pcsFor = (ct, gmtShade) => rowsFor(ct, gmtShade).reduce((a, r) => a + Number(r.pcs || 0), 0);
  const lineQty = l => {
    if (l.is_na) return 0;
    const g = grainOf(l.cons_type);
    const base = g === 'lumpsum' ? 1 : (pcsFor(l.cons_type, l.garment_shade) || Number(tas.order_qty) || 0);
    return Math.round(base * (num(l.consumption) || 0) * (1 + (num(l.wastage) || 0) / 100));
  };
  const lineValue = l => l.is_na ? 0 : lineQty(l) * (num(l.price) || 0);
  const totalPcs = (parts && parts.byShade.length)
    ? parts.byShade.reduce((a, r) => a + Number(r.pcs || 0), 0)
    : (Number(tas.order_qty) || 0);
  const activeLines = lines.filter(l => !l.is_na);
  const grand = activeLines.reduce((a, l) => a + lineValue(l), 0);
  const costPerGarment = totalPcs > 0 ? (grand / totalPcs) : 0;
  const storedLines = activeLines.reduce((a, l) => a + expandCount(l), 0);

  // Zero-Miss Checklist validation
  const isRowComplete = l => String(l.trim_type || '').trim() && (l.is_na ? String(l.na_reason || '').trim() : (num(l.consumption) > 0));
  const unaccountedRows = lines.filter(l => !isRowComplete(l));
  const incomplete = unaccountedRows.length > 0;
  const completionPct = lines.length > 0 ? Math.round(((lines.length - unaccountedRows.length) / lines.length) * 100) : 0;

  const set = (i, k, v) => setLines(ls => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const applySugg = (i, s) => {
    setLines(ls => ls.map((l, j) => (j === i ? {
      ...l,
      bom_type: s.bom_type || l.bom_type || 'SEWING',
      trim_type: s.item,
      trim_code: s.description || l.trim_code,
      mat_size: s.mat_size || l.mat_size || '',
      mat_shade: s.mat_shade || l.mat_shade || 'DTM',
      brand: s.brand || s.buyer_name || l.brand || '',
      cons_type: s.cons_type || l.cons_type,
      consumption: s.consumption ?? l.consumption,
      unit: s.unit || l.unit,
      wastage: s.wastage ?? l.wastage,
      price: s.price ?? l.price,
      sourced_by: s.sourced_by || l.sourced_by || 'RWPL',
      vendor: s.sourced_by_vendor || l.vendor,
      image_url: s.image_url || l.image_url || ''
    } : l)));
    setOpenSugg(null);
  };

  const pool = sugg ? [...sugg.app, ...(sugg.erp || [])] : [];
  const matches = i => {
    const row = lines[i] || {};
    const q = String(row.trim_type || '').trim().toLowerCase();
    const seen = new Set();
    const buyerLower = String(tas.buyer || '').toLowerCase();
    const filtered = pool.filter(s => {
      const k = `${s.item}|${s.description}|${s.mat_size || ''}|${s.mat_shade || ''}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return !q || `${s.item} ${s.description || ''} ${s.mat_size || ''} ${s.mat_shade || ''}`.toLowerCase().includes(q);
    });
    filtered.sort((a, b) => {
      const ab = String(a.buyer_name || '').toLowerCase() === buyerLower ? 1 : 0;
      const bb = String(b.buyer_name || '').toLowerCase() === buyerLower ? 1 : 0;
      return bb - ab;
    });
    return filtered.slice(0, 10);
  };

  const save = () => {
    setBusy(true); setMsg('');
    api(`/bom/${encodeURIComponent(tas.tas_no)}`, {
      method: 'POST',
      body: JSON.stringify({ ...head, fallback_pcs: totalPcs, lines })
    })
      .then(r => {
        setBusy(false);
        if (r && r.error) { setMsg(r.error); return; }
        setMsg(`Saved ✓ ${r.bom_no || ''} · ${r.trims} trims · ${r.lines} lines · ₹${Math.round(r.value).toLocaleString('en-IN')}`);
        setTimeout(onSaved, 900);
      })
      .catch(() => { setBusy(false); setMsg('Save failed'); });
  };

  const lbl = { fontSize: 9.5, letterSpacing: 0.7, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase' };
  const inp = {
    width: '100%', boxSizing: 'border-box', height: 30, padding: '0 7px',
    background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6,
    color: 'var(--text)', fontSize: 11.5, fontWeight: 600, outline: 'none'
  };
  const pickCss = { ...inp, padding: '0 4px', cursor: 'pointer' };

  // Group lines by BOM_TYPES for executive section rendering
  const sectionsToRender = BOM_TYPES.filter(bt => activeBomType === 'ALL' || activeBomType === bt.key);

  const rwplLines = activeLines.filter(l => (l.sourced_by || 'RWPL') === 'RWPL');
  const facLines = activeLines.filter(l => l.sourced_by === 'FACTORY');
  const rwplVal = rwplLines.reduce((a, l) => a + lineValue(l), 0);
  const facVal = facLines.reduce((a, l) => a + lineValue(l), 0);
  const contractCount = activeLines.filter(l => l.contract_no).length;

  const secStats = BOM_TYPES.map(bt => {
    const sRows = lines.filter(l => (l.bom_type || 'SEWING') === bt.key);
    const sActive = sRows.filter(l => !l.is_na);
    const sVal = sActive.reduce((a, l) => a + lineValue(l), 0);
    const costPct = grand > 0 ? Math.round((sVal / grand) * 1000) / 10 : 0;
    const doneCnt = sRows.filter(isRowComplete).length;
    const compPct = sRows.length > 0 ? Math.round((doneCnt / sRows.length) * 100) : 100;
    const perPc = totalPcs > 0 ? sVal / totalPcs : 0;
    return { ...bt, rows: sRows, active: sActive, val: sVal, costPct, doneCnt, compPct, perPc };
  });

  return (
    <div className="screen" style={{ paddingBottom: 40 }}>
      <Header subtitle="TAS · EXECUTIVE BILL OF MATERIAL (ZERO-MISS ENGINE)" />

      {/* ─── 1. EXECUTIVE HERO BANNER ─── */}
      <div style={{
        margin: '12px 16px 0', padding: '16px 20px', borderRadius: 14,
        background: 'linear-gradient(135deg, rgba(124,58,237,0.12) 0%, rgba(79,70,229,0.05) 55%, rgba(5,150,105,0.07) 100%)',
        border: '1px solid rgba(124,58,237,0.28)', display: 'flex', flexDirection: 'column', gap: 14
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button onClick={onBack} style={{
            width: 34, height: 34, borderRadius: 8, border: '1px solid var(--border)',
            background: 'var(--bg2)', color: accent, fontSize: 18, fontWeight: 800, cursor: 'pointer'
          }}>←</button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: 20, color: 'var(--text)' }}>{tas.tas_no}</span>
              <span style={{ padding: '3px 10px', borderRadius: 20, background: accent, color: '#fff', fontSize: 10.5, fontWeight: 800, letterSpacing: 0.6 }}>
                {head.product_type || tas.product_type || 'TROUSER'}
              </span>
              <span style={{
                padding: '3px 10px', borderRadius: 20, fontSize: 10.5, fontWeight: 800,
                background: incomplete ? 'rgba(245,158,11,0.16)' : 'rgba(16,185,129,0.16)',
                color: incomplete ? '#d97706' : '#059669', border: `1px solid ${incomplete ? '#f59e0b' : '#10b981'}`
              }}>
                {incomplete ? `⏳ ${completionPct}% COMPLETE (${unaccountedRows.length} TO REVIEW)` : '✓ 100% ZERO-MISS VERIFIED'}
              </span>
              {contractCount > 0 && (
                <span style={{ padding: '3px 10px', borderRadius: 20, background: 'rgba(2,132,199,0.14)', color: '#0284c7', fontSize: 10.5, fontWeight: 800 }}>
                  📜 {contractCount} Forward Contract{contractCount > 1 ? 's' : ''} Locked
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <span>🏢 Buyer: <b style={{ color: 'var(--text)' }}>{titleCase(tas.buyer) || '—'}</b></span>
              <span>👔 Style: <b style={{ color: 'var(--text)' }}>{tas.style_name || '—'}</b></span>
              <span>🏭 Factory: <b style={{ color: 'var(--text)' }}>{titleCase(tas.factory) || '—'}</b></span>
              <span>👤 Merch: <b style={{ color: 'var(--text)' }}>{titleCase(tas.merchandiser) || '—'}</b></span>
            </div>
          </div>

          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => setShowEntryPivot(v => !v)} style={{
              padding: '7px 13px', borderRadius: 8, border: `1.5px solid ${accent}`,
              background: showEntryPivot ? accent : `${accent}15`, color: showEntryPivot ? '#fff' : accent,
              fontSize: 11.5, fontWeight: 800, cursor: 'pointer'
            }}>
              📊 {showEntryPivot ? 'Hide Raw Material Pivot Matrix' : 'Pivot Matrix (Size × Shade × Quality)'}
            </button>
            <button onClick={() => setShowOrderMatrix(v => !v)} style={{
              padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border)',
              background: showOrderMatrix ? accent : 'var(--bg2)', color: showOrderMatrix ? '#fff' : 'var(--text)',
              fontSize: 11.5, fontWeight: 700, cursor: 'pointer'
            }}>
              📋 Order Shade/Size Ratio ({garmentShades.length} Shades · {garmentSizes.length || 'Std'} Sizes)
            </button>
            <label style={{
              padding: '7px 13px', borderRadius: 8, border: '1.5px solid #0284c7',
              background: 'rgba(2,132,199,0.12)', color: '#0284c7', fontSize: 11.5, fontWeight: 800, cursor: 'pointer'
            }}>
              📷 AI Scan Trim Card / PDF
              <input type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={handleTechpackUpload} />
            </label>
            <button onClick={() => runAiAudit('audit')} disabled={aiBusy} style={{
              padding: '7px 14px', borderRadius: 8, border: 'none',
              background: 'linear-gradient(135deg, #7c3aed, #4f46e5)', color: '#fff',
              fontSize: 11.5, fontWeight: 800, cursor: 'pointer', boxShadow: '0 2px 8px rgba(124,58,237,0.3)'
            }}>
              {aiBusy ? '✨ Gemini Auditing…' : '✨ Gemini Zero-Miss Audit'}
            </button>
          </div>
        </div>

        {/* ─── EXECUTIVE COST & PERCENTAGE BREAKDOWN CARDS (SEWING · FINISHING · PACKING · TOTAL) ─── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(175px, 1fr))', gap: 10 }}>
          {secStats.map(st => {
            const active = activeBomType === st.key;
            return (
              <div key={st.key} onClick={() => setActiveBomType(active ? 'ALL' : st.key)}
                style={{
                  padding: '11px 14px', borderRadius: 10,
                  background: active ? st.bg : 'var(--bg2)',
                  border: `1.5px solid ${active ? st.color : st.border}`, cursor: 'pointer'
                }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ ...lbl, color: st.color }}>{st.icon} {st.label}</span>
                  <span style={{ padding: '2px 7px', borderRadius: 10, background: st.bg, color: st.color, fontSize: 11, fontWeight: 800 }}>
                    {st.costPct.toFixed(1)}% of Cost
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 4 }}>
                  <span style={{ fontSize: 19, fontWeight: 800, color: 'var(--text)' }}>₹{Math.round(st.val).toLocaleString('en-IN')}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: st.color }}>₹{st.perPc.toFixed(2)}/pc</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'var(--text3)', marginTop: 4 }}>
                  <span>{st.active.length} Trims</span>
                  <span style={{ color: st.compPct === 100 ? '#059669' : '#d97706', fontWeight: 700 }}>Checklist: {st.compPct}% ({st.doneCnt}/{st.rows.length})</span>
                </div>
                <div style={{ height: 5, borderRadius: 3, background: 'var(--border)', marginTop: 5, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${st.costPct}%`, background: st.color, transition: 'width 0.25s' }} />
                </div>
              </div>
            );
          })}

          <div onClick={() => setSourceFilter(sourceFilter === 'RWPL' ? 'ALL' : 'RWPL')}
            style={{ padding: '11px 14px', borderRadius: 10, background: sourceFilter === 'RWPL' ? 'rgba(21,128,61,0.12)' : 'var(--bg2)', border: `1px solid ${sourceFilter === 'RWPL' ? '#15803d' : 'var(--border)'}`, cursor: 'pointer' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ ...lbl, color: '#15803d' }}>RISHABH VS FACTORY</span>
              <span style={{ fontSize: 10.5, fontWeight: 800, color: '#15803d' }}>
                {grand > 0 ? Math.round((rwplVal / grand) * 100) : 0}% / {grand > 0 ? Math.round((facVal / grand) * 100) : 0}%
              </span>
            </div>
            <div style={{ fontSize: 12.5, fontWeight: 800, color: '#15803d', marginTop: 5 }}>
              RWPL: ₹{Math.round(rwplVal).toLocaleString('en-IN')} ({rwplLines.length})
            </div>
            <div onClick={e => { e.stopPropagation(); setSourceFilter(sourceFilter === 'FACTORY' ? 'ALL' : 'FACTORY'); }}
              style={{ fontSize: 12.5, fontWeight: 800, color: '#b45309', marginTop: 3 }}>
              Factory: ₹{Math.round(facVal).toLocaleString('en-IN')} ({facLines.length})
            </div>
          </div>

          <div style={{ padding: '11px 14px', borderRadius: 10, background: 'linear-gradient(135deg, rgba(124,58,237,0.16), rgba(79,70,229,0.08))', border: `2px solid ${accent}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ ...lbl, color: accent }}>💰 TOTAL BOM COST</span>
              <span style={{ padding: '2px 7px', borderRadius: 10, background: accent, color: '#fff', fontSize: 10.5, fontWeight: 800 }}>100%</span>
            </div>
            <div style={{ fontSize: 21, fontWeight: 800, color: accent, marginTop: 3 }}>₹{Math.round(grand).toLocaleString('en-IN')}</div>
            <div style={{ fontSize: 11, color: 'var(--text)', marginTop: 3, fontWeight: 700 }}>
              ₹{costPerGarment.toFixed(2)} / Garment · {fmt(totalPcs)} pcs
            </div>
          </div>
        </div>

        {/* Visual Stacked 100% Cost Distribution Bar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, fontWeight: 700, color: 'var(--text2)' }}>
            <span>📊 Total BOM Cost Share: <b style={{ color: accent }}>₹{Math.round(grand).toLocaleString('en-IN')} (₹{costPerGarment.toFixed(2)}/pc)</b></span>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              {secStats.map(st => (
                <span key={st.key} style={{ color: st.color }}>
                  {st.icon} {st.short}: <b>₹{Math.round(st.val).toLocaleString('en-IN')} ({st.costPct.toFixed(1)}%)</b>
                </span>
              ))}
            </div>
          </div>
          <div style={{ height: 8, borderRadius: 4, background: 'var(--border)', display: 'flex', overflow: 'hidden' }}>
            {secStats.map(st => (
              <div key={st.key} style={{ width: `${st.costPct}%`, background: st.color, transition: 'width 0.3s' }} title={`${st.label}: ₹${Math.round(st.val).toLocaleString('en-IN')} (${st.costPct.toFixed(1)}%)`} />
            ))}
          </div>
        </div>
      </div>

      {/* ─── LIVE RAW MATERIAL PIVOT MATRIX IN BOM ENTRY ─── */}
      {showEntryPivot && (
        <BomPivotMatrix
          tasNo={tas.tas_no}
          mode="bom"
          lines={activeLines.flatMap(l => previewExpanded(l).map(px => ({
            ...l,
            style: px.style,
            shade: px.shade,
            mat_shade: px.mat_shade,
            size: px.size,
            qty: px.qty,
            line_value: px.qty * (num(l.price) || 0)
          })))}
        />
      )}

      {/* ─── COLLAPSIBLE ORDER SHADE & SIZE BREAKDOWN MATRIX ─── */}
      {showOrderMatrix && parts && (
        <div style={{ margin: '10px 16px 0', padding: '12px 16px', borderRadius: 10, background: 'var(--bg2)', border: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <b style={{ fontSize: 12, color: accent }}>📊 Order Garment Shade & Size Breakdown ({tas.tas_no})</b>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>Every trim row expands automatically against these exact quantities</span>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {(parts.byShade || []).map((s, idx) => (
              <div key={idx} style={{ padding: '6px 12px', borderRadius: 8, background: 'var(--bg3)', border: '1px solid var(--border)', fontSize: 11.5 }}>
                <span style={{ color: 'var(--text3)' }}>{s.style || tas.style_name}: </span>
                <b style={{ color: '#0284c7' }}>👕 {s.shade}</b> → <b>{fmt(s.pcs)} pcs</b>
              </div>
            ))}
          </div>
          {hasSize && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8, paddingTop: 8, borderTop: '1px dashed var(--border)' }}>
              <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 700 }}>Size Ratio Pcs:</span>
              {(parts.bySize || []).map((sz, idx) => (
                <span key={idx} style={{ padding: '2px 8px', borderRadius: 5, background: 'var(--bg)', border: '1px solid var(--border)', fontSize: 11 }}>
                  {sz.shade ? `${sz.shade} · ` : ''}<b>{sz.size}</b>: {fmt(sz.pcs)}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── 2. WORKFLOW & TEMPLATE BAR (Product Checklist · Copy Old Style · Header Info) ─── */}
      <div style={{
        margin: '10px 16px 0', padding: '12px 16px', borderRadius: 12,
        border: '1px solid var(--border)', background: 'var(--bg2)',
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12, alignItems: 'end'
      }}>
        <div>
          <label style={{ ...lbl, display: 'block', marginBottom: 4 }}>1. Garment Product Base BOM</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <select style={{ ...pickCss, flex: 1 }} value={head.product_type}
              onChange={e => setHead({ ...head, product_type: e.target.value })}>
              {['TROUSER', 'SHIRT', 'HALF SLEEVE SHIRT', 'FULL SLEEVE SHIRT', 'JACKET', 'SHORTS', 'T-SHIRT', 'BLAZER'].map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <button onClick={() => loadSuggested('product_base')} style={{
              padding: '0 12px', height: 30, borderRadius: 6, border: `1px solid ${accent}`,
              background: `${accent}15`, color: accent, fontSize: 11, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap'
            }}>
              ↻ Load Checklist
            </button>
          </div>
        </div>

        <div>
          <label style={{ ...lbl, display: 'block', marginBottom: 4 }}>2. Copy BOM from Old Style / TAS (Auto-Remaps)</label>
          <select style={pickCss} defaultValue="" onChange={e => copyFromOldBom(e.target.value)}>
            <option value="">Select previous Style / TAS ({templates.length} available)…</option>
            {templates.map(t => (
              <option key={t.id} value={t.id}>
                {t.tas_no} · {t.style_name || 'Style'} · {t.buyer} ({t.product_type || 'GARMENT'} · {t.items} trims)
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ width: 130 }}>
            <label style={{ ...lbl, display: 'block', marginBottom: 4 }}>BOM Date</label>
            <input type="date" style={inp} value={head.entry_dt} onChange={e => setHead({ ...head, entry_dt: e.target.value })} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ ...lbl, display: 'block', marginBottom: 4 }}>Buyer Brand</label>
            <input style={inp} value={head.brand || ''} placeholder="Brand" onChange={e => setHead({ ...head, brand: e.target.value })} />
          </div>
        </div>

        <div>
          <label style={{ ...lbl, display: 'block', marginBottom: 4 }}>Special Merchandising / Trim Instruction</label>
          <input style={inp} value={head.remark} placeholder="e.g. All metal trims nickel-free, polybag warning in 4 languages…"
            onChange={e => setHead({ ...head, remark: e.target.value })} />
        </div>
      </div>

      {/* ─── AI AUDIT REPORT BANNER ─── */}
      {aiAudit && (
        <div style={{ margin: '10px 16px 0', padding: '14px 18px', borderRadius: 12, border: '1.5px solid #7c3aed', background: 'rgba(124,58,237,0.06)', fontSize: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <b style={{ color: '#6d28d9', fontSize: 13.5 }}>✨ Gemini AI Zero-Miss Completeness Audit {aiAudit.score != null ? `· Score: ${aiAudit.score}/100` : ''}</b>
            <button onClick={() => setAiAudit(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 800, color: '#6d28d9' }}>✕</button>
          </div>
          <div style={{ color: 'var(--text2)', marginBottom: 6 }}>{aiAudit.summary}</div>
          {Array.isArray(aiAudit.warnings) && aiAudit.warnings.length > 0 && (
            <ul style={{ margin: '4px 0 8px 18px', padding: 0, color: '#b45309' }}>
              {aiAudit.warnings.map((w, idx) => <li key={idx}>{w}</li>)}
            </ul>
          )}
          {Array.isArray(aiAudit.missing_critical) && aiAudit.missing_critical.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
              <b style={{ color: '#c62828' }}>1-Click Add Missing Recommended Trims:</b>
              {aiAudit.missing_critical.map((mc, idx) => (
                <button key={idx} onClick={() => setLines(ls => [...ls, { ...blank, ...mc, bom_type: mc.bom_type || 'SEWING' }])}
                  style={{ padding: '5px 11px', borderRadius: 6, border: '1px solid #c62828', background: '#ffebee', color: '#c62828', fontSize: 11, fontWeight: 800, cursor: 'pointer' }}>
                  + Add {mc.trim_type} ({mc.bom_type || 'SEWING'})
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── 3. SECTION FILTER PILLS & DUAL-SHADE LEGEND ─── */}
      <div style={{ margin: '12px 16px 0', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={() => setActiveBomType('ALL')} style={{
          padding: '7px 14px', borderRadius: 8,
          border: activeBomType === 'ALL' ? `2px solid ${accent}` : '1px solid var(--border)',
          background: activeBomType === 'ALL' ? `${accent}18` : 'var(--bg2)',
          color: activeBomType === 'ALL' ? accent : 'var(--text2)', fontSize: 12, fontWeight: 800, cursor: 'pointer'
        }}>
          ALL 3 SECTIONS ({lines.length})
        </button>
        {BOM_TYPES.map(bt => {
          const secRows = lines.filter(l => (l.bom_type || 'SEWING') === bt.key);
          const doneCnt = secRows.filter(isRowComplete).length;
          const active = activeBomType === bt.key;
          return (
            <button key={bt.key} onClick={() => setActiveBomType(bt.key)} style={{
              padding: '7px 14px', borderRadius: 8,
              border: active ? `2px solid ${bt.color}` : '1px solid var(--border)',
              background: active ? bt.bg : 'var(--bg2)',
              color: bt.color, fontSize: 12, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6
            }}>
              <span>{bt.icon} {bt.label}</span>
              <span style={{ padding: '1px 7px', borderRadius: 10, background: 'var(--bg)', fontSize: 10.5 }}>
                {doneCnt}/{secRows.length}
              </span>
            </button>
          );
        })}

        {sourceFilter !== 'ALL' && (
          <button onClick={() => setSourceFilter('ALL')} style={{
            padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border)',
            background: 'var(--bg3)', color: 'var(--text2)', fontSize: 11, fontWeight: 700, cursor: 'pointer'
          }}>
            Showing: {sourceFilter} Only ✕
          </button>
        )}

        <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)', display: 'flex', gap: 12, alignItems: 'center' }}>
          <span>👕 <b style={{ color: '#0284c7' }}>Garment Shade</b> = Order Color</span>
          <span>🎨 <b style={{ color: '#6d28d9' }}>Raw Mat Shade</b> = <code style={{ color: '#059669', fontWeight: 700 }}>DTM</code> (auto-matches Garment) or Fixed Trim Color</span>
          <span>🔍 Click <b>👁️</b> on any row to preview expanded PO lines</span>
        </div>
      </div>

      <datalist id="bom-vendor-presets">
        {(sugg?.vendors || []).map(v => <option key={v.id} value={v.vendor_name}>{`${v.vendor_name}${v.city ? ` (${v.city})` : ''}`}</option>)}
      </datalist>
      <datalist id="bom-mat-shade-presets">
        <option value="DTM">DTM (Dyed To Match Garment Shade)</option>
        <option value="GARMENT SHADE">Same as Garment Shade</option>
        <option value="BLACK">Raw Material Shade: BLACK</option>
        <option value="WHITE">Raw Material Shade: WHITE</option>
        <option value="RAW WHITE">Raw Material Shade: RAW WHITE</option>
        <option value="ANTIQUE BRASS">Raw Material Shade: ANTIQUE BRASS</option>
        <option value="NICKEL">Raw Material Shade: NICKEL</option>
        <option value="GUN METAL">Raw Material Shade: GUN METAL</option>
        <option value="TRANSPARENT">Raw Material Shade: TRANSPARENT</option>
        {garmentShades.map(gs => <option key={gs} value={gs}>{`Garment Shade: ${gs}`}</option>)}
      </datalist>

      {/* ─── 4. THREE HIGH-LEVEL BOM SECTIONS (SEWING · FINISHING · PACKING) ─── */}
      <div style={{ padding: '10px 16px 0' }}>
        {sectionsToRender.map(bt => {
          const secIndices = lines
            .map((l, idx) => ({ l, idx }))
            .filter(({ l }) => (l.bom_type || 'SEWING') === bt.key)
            .filter(({ l }) => sourceFilter === 'ALL' || (l.sourced_by || 'RWPL') === sourceFilter);

          const secActive = secIndices.filter(({ l }) => !l.is_na);
          const secVal = secActive.reduce((a, { l }) => a + lineValue(l), 0);
          const secDone = secIndices.filter(({ l }) => isRowComplete(l)).length;

          const GC = '34px minmax(150px, 1.15fr) minmax(185px, 1.4fr) 76px 128px 135px 138px 66px 58px 56px 74px 92px minmax(115px, 0.95fr) minmax(115px, 0.95fr) 105px 98px';
          const LINE = '1px solid var(--border)';
          const cel = (colIdx, right) => ({
            padding: '5px 6px', display: 'flex', alignItems: 'center',
            justifyContent: right ? 'flex-end' : 'flex-start',
            borderLeft: colIdx ? LINE : 'none', minWidth: 0, position: 'relative'
          });

          return (
            <div key={bt.key} style={{
              marginBottom: 18, borderRadius: 12, border: `1.5px solid ${bt.border}`,
              background: 'var(--bg2)', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.04)'
            }}>
              {/* Section Header Banner */}
              <div style={{
                padding: '10px 16px', background: bt.bg, borderBottom: `1px solid ${bt.border}`,
                display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap'
              }}>
                <span style={{ fontSize: 16 }}>{bt.icon}</span>
                <span style={{ fontWeight: 800, fontSize: 13.5, color: bt.color, letterSpacing: 0.5 }}>{bt.label}</span>
                <span style={{
                  padding: '2px 9px', borderRadius: 12, background: 'var(--bg)',
                  color: secDone === secIndices.length ? '#059669' : '#d97706', fontSize: 11, fontWeight: 800
                }}>
                  {secDone}/{secIndices.length} Accounted
                </span>
                <span style={{ fontSize: 11.5, color: 'var(--text2)' }}>
                  Rishabh: <b>{secActive.filter(x => (x.l.sourced_by || 'RWPL') === 'RWPL').length}</b> · Factory: <b>{secActive.filter(x => x.l.sourced_by === 'FACTORY').length}</b>
                </span>
                <span style={{ marginLeft: 'auto', fontWeight: 800, fontSize: 13, color: bt.color }}>
                  Section Total: ₹{Math.round(secVal).toLocaleString('en-IN')} ({grand > 0 ? ((secVal / grand) * 100).toFixed(1) : '0.0'}% of Total BOM · ₹{(totalPcs > 0 ? secVal / totalPcs : 0).toFixed(2)}/pc)
                </span>
                <button onClick={() => setLines(ls => [...ls, { ...blank, bom_type: bt.key }])} style={{
                  padding: '5px 12px', borderRadius: 6, border: `1.5px solid ${bt.color}`,
                  background: 'var(--bg)', color: bt.color, fontSize: 11, fontWeight: 800, cursor: 'pointer'
                }}>
                  + Add {bt.short} Trim
                </button>
              </div>

              {/* Section Table */}
              <div style={{ overflowX: 'auto' }}>
                <div style={{ minWidth: 1620 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: GC, background: 'var(--bg3)', borderBottom: LINE }}>
                    {[
                      ['Status', 0], ['Trim Category', 0], ['Raw Material / Quality Spec', 0], ['Mat Size', 0],
                      ['👕 Garment Shade', 0], ['🎨 Raw Mat Shade', 0],
                      ['Breakdown Rule', 0], ['Cons', 0], ['Unit', 0], ['Wast%', 0], ['Rate ₹', 0],
                      ['Bought By', 0], ['Vendor / Contract', 0], ['Remarks / N/A Reason', 0], ['Order Qty / Val', 1], ['Actions', 0]
                    ].map(([h, r], idx) => <span key={idx} style={{ ...cel(idx, r), ...lbl }}>{h}</span>)}
                  </div>

                  {secIndices.length === 0 ? (
                    <div style={{ padding: 20, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>
                      No trims in {bt.label}. Click <b>+ Add {bt.short} Trim</b> to add one.
                    </div>
                  ) : secIndices.map(({ l, idx: i }) => {
                    const isNa = !!l.is_na;
                    const complete = isRowComplete(l);
                    const isDtmShade = !l.mat_shade || ['DTM', 'GARMENT SHADE'].includes(String(l.mat_shade).trim().toUpperCase());
                    const isExpanded = expandedRow === i;

                    return (
                      <React.Fragment key={i}>
                        <div style={{
                          display: 'grid', gridTemplateColumns: GC, borderTop: LINE,
                          background: isNa ? 'rgba(100,116,139,0.06)' : !complete ? 'rgba(245,158,11,0.05)' : 'transparent',
                          opacity: isNa ? 0.68 : 1, alignItems: 'center'
                        }}>
                          {/* 0. Status Pill */}
                          <span style={{ ...cel(0), justifyContent: 'center' }} title={isNa ? `Marked N/A: ${l.na_reason}` : complete ? 'Complete' : 'Needs Consumption > 0 or N/A reason'}>
                            <span style={{
                              width: 20, height: 20, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 10, fontWeight: 800,
                              background: isNa ? '#cbd5e1' : complete ? '#d1fae5' : '#fef3c7',
                              color: isNa ? '#475569' : complete ? '#059669' : '#d97706'
                            }}>
                              {isNa ? '—' : complete ? '✓' : '!'}
                            </span>
                          </span>

                          {/* 1. Trim Category */}
                          <span style={cel(1)}>
                            <input style={{ ...inp, fontWeight: 700 }} value={l.trim_type} placeholder="Fly Zipper / Main Label…"
                              onChange={e => { set(i, 'trim_type', e.target.value); setOpenSugg(i); }}
                              onFocus={() => setOpenSugg(i)}
                              onBlur={() => setTimeout(() => setOpenSugg(c => (c === i ? null : c)), 180)} />
                            {openSugg === i && matches(i).length > 0 && (
                              <div style={{
                                position: 'absolute', top: '100%', left: 4, zIndex: 40, minWidth: 440, maxHeight: 250, overflowY: 'auto',
                                background: 'var(--bg2)', border: `1.5px solid ${accent}`, borderRadius: 8, boxShadow: '0 10px 28px rgba(0,0,0,0.22)'
                              }}>
                                {matches(i).map((s, k) => (
                                  <div key={k} onMouseDown={() => applySugg(i, s)}
                                    style={{ padding: '7px 12px', borderTop: k ? '1px solid var(--border)' : 'none', cursor: 'pointer', fontSize: 11.5 }}
                                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg3)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                                    <span style={{ fontSize: 9.5, fontWeight: 800, color: bomTypeColor(s.bom_type), marginRight: 6 }}>[{s.bom_type || 'SEWING'}]</span>
                                    <b>{s.item}</b>{s.description ? <span style={{ color: 'var(--text2)' }}> · {s.description}</span> : null}
                                    {s.buyer_name ? <span style={{ color: accent, fontWeight: 700 }}> · {s.buyer_name}</span> : null}
                                    <div style={{ color: 'var(--text3)', fontSize: 10.5, marginTop: 2 }}>
                                      {s.mat_size ? `Size: ${s.mat_size} · ` : ''}{s.mat_shade ? `Shade: ${s.mat_shade} · ` : ''}
                                      {s.consumption != null ? `Cons: ${s.consumption} ${s.unit || ''} · ` : ''}
                                      {s.price != null ? `₹${s.price} · ` : ''}{s.sourced_by ? `${s.sourced_by}` : ''}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </span>

                          {/* 2. Raw Material / Spec */}
                          <span style={{ ...cel(2), gap: 3 }}>
                            <input style={inp} disabled={isNa} value={l.trim_code || ''} placeholder="Quality / Code / Spec"
                              onChange={e => set(i, 'trim_code', e.target.value)} />
                            {!isNa && (
                              <button type="button" onClick={() => {
                                setQForm({
                                  bom_type: l.bom_type || 'SEWING',
                                  trim_type: l.trim_type || '',
                                  material_name: l.trim_code || '',
                                  mat_size: l.mat_size || '',
                                  mat_shade: l.mat_shade || 'DTM',
                                  buyer_name: tas.buyer || '',
                                  cons_type: l.cons_type || CONS_TYPES[0],
                                  consumption: l.consumption || 1,
                                  unit: l.unit || 'NOS',
                                  wastage: l.wastage ?? 3,
                                  price: l.price || '',
                                  sourced_by: l.sourced_by || 'RWPL',
                                  vendor_name: l.vendor || ''
                                });
                                setQuickModal({ kind: 'raw', rowIdx: i });
                              }} title="Save / Add this Raw Material & Spec to Master Library"
                                style={{ height: 28, padding: '0 6px', borderRadius: 5, border: '1px solid #059669', background: 'rgba(5,150,105,0.12)', color: '#059669', fontSize: 10.5, fontWeight: 800, cursor: 'pointer' }}>
                                +M
                              </button>
                            )}
                          </span>

                          {/* 3. Material Size */}
                          <span style={cel(3)}>
                            <input style={inp} disabled={isNa} value={l.mat_size || ''} placeholder="24L / 6&quot;"
                              onChange={e => set(i, 'mat_size', e.target.value)} />
                          </span>

                          {/* 4. Garment Shade (Order Shade Target) */}
                          <span style={cel(4)} title="Garment Shade from Order: ALL = applies to all garment shades, or pick 1 specific garment shade">
                            <select style={{
                              ...pickCss,
                              background: l.garment_shade ? 'rgba(2,132,199,0.12)' : 'var(--bg)',
                              borderColor: l.garment_shade ? '#0284c7' : 'var(--border)',
                              color: l.garment_shade ? '#0284c7' : 'var(--text)'
                            }} disabled={isNa} value={l.garment_shade || ''} onChange={e => set(i, 'garment_shade', e.target.value)}>
                              <option value="">ALL ({garmentShades.length || 1} Shades)</option>
                              {garmentShades.map(gs => <option key={gs} value={gs}>👕 {gs}</option>)}
                            </select>
                          </span>

                          {/* 5. Raw Material Shade (with DTM vs Fixed Color badge) */}
                          <span style={{ ...cel(5), gap: 4 }}>
                            <input list="bom-mat-shade-presets" style={{
                              ...inp,
                              borderColor: isDtmShade ? '#10b981' : '#8b5cf6',
                              background: isDtmShade ? 'rgba(16,185,129,0.08)' : 'rgba(139,92,246,0.08)'
                            }} disabled={isNa} value={l.mat_shade || ''} placeholder="DTM or Raw Shade"
                              onChange={e => set(i, 'mat_shade', e.target.value)} />
                          </span>

                          {/* 6. Breakdown Rule */}
                          <span style={cel(6)}>
                            <select style={pickCss} disabled={isNa} value={l.cons_type} onChange={e => set(i, 'cons_type', e.target.value)}>
                              {CONS_TYPES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </span>

                          {/* 7. Consumption */}
                          <span style={cel(7)}>
                            <input style={{ ...inp, textAlign: 'right' }} disabled={isNa} type="number" step="any" value={isNa ? 0 : (l.consumption ?? '')}
                              onChange={e => set(i, 'consumption', e.target.value)} />
                          </span>

                          {/* 8. Unit */}
                          <span style={cel(8)}>
                            <input style={inp} disabled={isNa} value={l.unit || ''} placeholder="NOS"
                              onChange={e => set(i, 'unit', e.target.value)} />
                          </span>

                          {/* 9. Wastage % */}
                          <span style={cel(9)}>
                            <input style={{ ...inp, textAlign: 'right' }} disabled={isNa} type="number" step="any" value={isNa ? 0 : (l.wastage ?? '')}
                              onChange={e => set(i, 'wastage', e.target.value)} />
                          </span>

                          {/* 10. Rate ₹ */}
                          <span style={cel(10)}>
                            <input style={{ ...inp, textAlign: 'right' }} disabled={isNa} type="number" step="any" value={isNa ? '' : (l.price ?? '')}
                              placeholder="0.00" onChange={e => set(i, 'price', e.target.value)} />
                          </span>

                          {/* 11. Bought By */}
                          <span style={cel(11)}>
                            <select style={{
                              ...pickCss, fontWeight: 800,
                              color: l.sourced_by === 'FACTORY' ? '#b45309' : '#15803d',
                              background: l.sourced_by === 'FACTORY' ? 'rgba(245,158,11,0.16)' : 'rgba(22,163,74,0.15)',
                              borderColor: l.sourced_by === 'FACTORY' ? '#f59e0b' : '#22c55e'
                            }} disabled={isNa} value={l.sourced_by || 'RWPL'} onChange={e => set(i, 'sourced_by', e.target.value)}>
                              <option value="RWPL">RISHABH</option>
                              <option value="FACTORY">FACTORY</option>
                            </select>
                          </span>

                          {/* 12. Vendor / Forward Contract */}
                          <span style={{ ...cel(12), flexDirection: 'column', alignItems: 'stretch' }}>
                            <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                              <input list="bom-vendor-presets" style={inp} disabled={isNa} value={l.vendor || ''} placeholder="Pick/Type Vendor"
                                onChange={e => set(i, 'vendor', e.target.value)} />
                              {!isNa && (
                                <button type="button" onClick={() => {
                                  setQForm({ vendor_name: l.vendor || '', vendor_category: l.bom_type || 'SEWING', city: '', phone: '', gstin: '', lead_time_days: 10, sourced_by: l.sourced_by || 'RWPL' });
                                  setQuickModal({ kind: 'vendor', rowIdx: i });
                                }} title="Add New Vendor to Master"
                                  style={{ height: 28, padding: '0 6px', borderRadius: 5, border: `1px solid ${accent}`, background: `${accent}15`, color: accent, fontSize: 11, fontWeight: 800, cursor: 'pointer' }}>
                                  +V
                                </button>
                              )}
                            </div>
                            {l.contract_no && <span style={{ fontSize: 9, color: '#0284c7', fontWeight: 800, marginTop: 1 }}>📜 {l.contract_no}</span>}
                          </span>

                          {/* 13. Remarks or Mandatory N/A Reason */}
                          <span style={cel(13)}>
                            {isNa ? (
                              <input style={{ ...inp, background: '#fee2e2', borderColor: '#ef4444', color: '#991b1b' }} value={l.na_reason || ''}
                                placeholder="Mandatory N/A reason…" onChange={e => set(i, 'na_reason', e.target.value)} />
                            ) : (
                              <input style={inp} value={l.remark || ''} placeholder="Note / Placement"
                                onChange={e => set(i, 'remark', e.target.value)} />
                            )}
                          </span>

                          {/* 14. Total Qty & Line Value */}
                          <span style={{ ...cel(14, 1), flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                            {isNa ? (
                              <span style={{ fontSize: 10, fontWeight: 800, color: '#64748b' }}>SKIPPED (N/A)</span>
                            ) : (
                              <>
                                <b style={{ fontSize: 12, color: 'var(--text)' }}>{fmt(lineQty(l))} <span style={{ fontSize: 9.5, fontWeight: 600, color: 'var(--text3)' }}>{l.unit || ''}</span></b>
                                <span style={{ fontSize: 9.5, color: accent, fontWeight: 700 }}>
                                  {expandCount(l)} PO ln{num(l.price) ? ` · ₹${Math.round(lineValue(l)).toLocaleString('en-IN')}` : ''}
                                </span>
                              </>
                            )}
                          </span>

                          {/* 15. Actions: Preview Breakdown, Upload Image, Split by Garment Shade, N/A, Delete */}
                          <span style={{ ...cel(15), gap: 3, justifyContent: 'center' }}>
                            {!isNa && (
                              <button onClick={() => setExpandedRow(isExpanded ? null : i)}
                                title="Preview exact Style / Garment Shade / Raw Material Shade / Size breakdown rows"
                                style={{
                                  height: 23, padding: '0 5px', borderRadius: 5,
                                  border: `1px solid ${isExpanded ? accent : 'var(--border)'}`,
                                  background: isExpanded ? accent : 'var(--bg)', color: isExpanded ? '#fff' : 'var(--text2)',
                                  fontSize: 10, cursor: 'pointer'
                                }}>
                                👁️
                              </button>
                            )}
                            {l.image_url ? (
                              <img src={l.image_url} alt="trim" onClick={() => setPreviewImg(l.image_url)}
                                style={{ width: 23, height: 23, objectFit: 'cover', borderRadius: 5, cursor: 'pointer', border: '1px solid var(--border)' }} />
                            ) : (
                              <label title="Upload Trim Artwork / Photo" style={{ width: 22, height: 23, borderRadius: 5, border: '1px dashed var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 10 }}>
                                📷
                                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleRowImage(i, e)} />
                              </label>
                            )}
                            {garmentShades.length > 1 && !isNa && (
                              <button onClick={() => {
                                const splitRows = garmentShades.map(gs => ({ ...l, garment_shade: gs, mat_shade: l.mat_shade && l.mat_shade !== 'DTM' ? l.mat_shade : gs }));
                                setLines(ls => [...ls.slice(0, i), ...splitRows, ...ls.slice(i + 1)]);
                              }} title="Split into 1 row per Garment Shade to assign different Raw Material Shades"
                                style={{ height: 23, padding: '0 5px', borderRadius: 5, border: '1px solid #0284c7', background: 'rgba(2,132,199,0.12)', color: '#0284c7', fontSize: 10, fontWeight: 800, cursor: 'pointer' }}>
                                ⇄
                              </button>
                            )}
                            <button onClick={() => set(i, 'is_na', !isNa)} title={isNa ? 'Restore Trim' : 'Mark Trim N/A (Not Applicable)'}
                              style={{ height: 23, padding: '0 5px', borderRadius: 5, border: '1px solid #64748b', background: isNa ? '#64748b' : 'transparent', color: isNa ? '#fff' : '#64748b', fontSize: 9.5, fontWeight: 800, cursor: 'pointer' }}>
                              N/A
                            </button>
                            {!l.is_mandatory && (
                              <button onClick={() => setLines(ls => (ls.length > 1 ? ls.filter((_, j) => j !== i) : [{ ...blank }]))} title="Delete row"
                                style={{ height: 23, width: 20, borderRadius: 5, border: '1px solid #c62828', background: '#ffebee', color: '#c62828', fontSize: 12, fontWeight: 800, cursor: 'pointer', lineHeight: 1 }}>×</button>
                            )}
                          </span>
                        </div>

                        {/* Inline Breakdown Preview Drawer */}
                        {isExpanded && !isNa && (
                          <div style={{ padding: '10px 16px', background: 'var(--bg3)', borderTop: '1px dashed var(--border)', fontSize: 11.5 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                              <b style={{ color: accent }}>🔍 Live PO Line Expansion Preview for "{l.trim_type || 'Trim'}" ({l.cons_type})</b>
                              <span style={{ color: 'var(--text3)', fontSize: 11 }}>
                                Formula: Garment Pcs × {l.consumption || 0} Cons + {l.wastage || 0}% Wastage
                              </span>
                            </div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              {previewExpanded(l).map((px, pidx) => (
                                <div key={pidx} style={{ padding: '6px 10px', borderRadius: 6, background: 'var(--bg2)', border: '1px solid var(--border)', display: 'flex', gap: 8, alignItems: 'center' }}>
                                  <span style={{ color: '#0284c7', fontWeight: 700 }}>👕 Gmt: {px.shade}</span>
                                  <span>→</span>
                                  <span style={{ color: '#6d28d9', fontWeight: 700 }}>🎨 Mat: {px.mat_shade}</span>
                                  {px.size !== 'ALL' && <span style={{ color: 'var(--text2)' }}>· Size: <b>{px.size}</b></span>}
                                  <span style={{ color: 'var(--text3)' }}>({fmt(px.pcs)} pcs)</span>
                                  <b style={{ color: '#059669' }}>= {fmt(px.qty)} {l.unit || 'NOS'}</b>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}

        {/* ─── STICKY EXECUTIVE CONFIRMATION FOOTER ─── */}
        <div style={{
          marginTop: 14, padding: '14px 18px', borderRadius: 12,
          border: `1.5px solid ${incomplete ? '#f59e0b' : accent}`,
          background: 'var(--bg2)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
        }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {BOM_TYPES.map(bt => (
              <button key={bt.key} onClick={() => setLines(ls => [...ls, { ...blank, bom_type: bt.key }])}
                style={{ padding: '7px 13px', borderRadius: 7, border: `1.5px solid ${bt.color}`, background: bt.bg, color: bt.color, fontSize: 11.5, fontWeight: 800, cursor: 'pointer' }}>
                + {bt.short} Trim
              </button>
            ))}
          </div>

          <div style={{ fontSize: 12, color: 'var(--text2)', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <span>Active Trims: <b>{activeLines.length}</b></span>
            <span>Expanded PO Lines: <b>{storedLines}</b></span>
            <span>Grand Total: <b style={{ color: accent, fontSize: 13.5 }}>₹{Math.round(grand).toLocaleString('en-IN')}</b> (<b>₹{costPerGarment.toFixed(2)}/pc</b>)</span>
          </div>

          <button onClick={save} disabled={busy || incomplete}
            title={incomplete ? `${unaccountedRows.length} trim row(s) need consumption > 0 or an N/A reason` : ''}
            style={{
              marginLeft: 'auto', padding: '10px 26px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 800,
              background: (busy || incomplete) ? 'var(--text3)' : 'linear-gradient(135deg, #7c3aed, #4f46e5)',
              color: '#fff', cursor: (busy || incomplete) ? 'default' : 'pointer',
              boxShadow: (busy || incomplete) ? 'none' : '0 4px 12px rgba(124,58,237,0.35)'
            }}>
            {busy ? 'Saving BOM…' : incomplete ? `Complete ${unaccountedRows.length} Trim(s) to Confirm` : '✓ Confirm & Save Parallel BOM'}
          </button>
          {msg && <span style={{ fontSize: 12, fontWeight: 800, color: /✓/.test(msg) ? '#15803d' : '#c62828' }}>{msg}</span>}
        </div>
      </div>

      {quickModal && (
        <div onClick={() => setQuickModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 480, background: 'var(--bg2)', border: `2px solid ${accent}`, borderRadius: 14, padding: 20, boxShadow: '0 16px 40px rgba(0,0,0,0.35)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <b style={{ fontSize: 15, color: accent }}>
                {quickModal.kind === 'vendor' ? '🏢 Add New Trim Vendor to Master' : '🧵 Add / Save Raw Material to Master Library'}
              </b>
              <button onClick={() => setQuickModal(null)} style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: 'var(--text3)' }}>✕</button>
            </div>

            {quickModal.kind === 'vendor' ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={lbl}>Vendor / Supplier Name *</div>
                  <input style={inp} value={qForm.vendor_name || ''} placeholder="e.g. YKK INDIA PVT LTD" onChange={e => setQForm({ ...qForm, vendor_name: e.target.value })} />
                </div>
                <div>
                  <div style={lbl}>Trim Category</div>
                  <select style={pickCss} value={qForm.vendor_category || 'ALL TRIMS'} onChange={e => setQForm({ ...qForm, vendor_category: e.target.value })}>
                    <option value="ALL TRIMS">ALL TRIMS</option>
                    <option value="SEWING">SEWING TRIMS</option>
                    <option value="FINISHING">FINISHING TRIMS</option>
                    <option value="PACKING">PACKING TRIMS</option>
                  </select>
                </div>
                <div>
                  <div style={lbl}>Default Bought By</div>
                  <select style={pickCss} value={qForm.sourced_by || 'RWPL'} onChange={e => setQForm({ ...qForm, sourced_by: e.target.value })}>
                    <option value="RWPL">RISHABH (RWPL)</option>
                    <option value="FACTORY">FACTORY</option>
                  </select>
                </div>
                <div>
                  <div style={lbl}>City / Location</div>
                  <input style={inp} value={qForm.city || ''} placeholder="e.g. Bangalore / Tirupur" onChange={e => setQForm({ ...qForm, city: e.target.value })} />
                </div>
                <div>
                  <div style={lbl}>Lead Time (Days)</div>
                  <input type="number" style={inp} value={qForm.lead_time_days ?? 10} onChange={e => setQForm({ ...qForm, lead_time_days: e.target.value })} />
                </div>
                <div>
                  <div style={lbl}>Phone / WhatsApp</div>
                  <input style={inp} value={qForm.phone || ''} placeholder="Mobile / Landline" onChange={e => setQForm({ ...qForm, phone: e.target.value })} />
                </div>
                <div>
                  <div style={lbl}>GSTIN</div>
                  <input style={inp} value={qForm.gstin || ''} placeholder="Optional GSTIN" onChange={e => setQForm({ ...qForm, gstin: e.target.value })} />
                </div>
                <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                  <button onClick={() => setQuickModal(null)} style={{ padding: '8px 14px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg)', cursor: 'pointer', fontWeight: 700 }}>Cancel</button>
                  <button onClick={() => {
                    api('/bom/masters/vendor', { method: 'POST', body: JSON.stringify(qForm) }).then(r => {
                      if (r && r.ok) {
                        set(quickModal.rowIdx, 'vendor', r.vendor_name || qForm.vendor_name.toUpperCase());
                        api('/bom/items').then(d => setSugg(d && d.app ? d : { app: [], erp: [], vendors: [] }));
                        setQuickModal(null);
                        setMsg(`Vendor "${r.vendor_name}" saved to Master ✓`);
                      }
                    });
                  }} style={{ padding: '8px 18px', borderRadius: 7, border: 'none', background: accent, color: '#fff', fontWeight: 800, cursor: 'pointer' }}>
                    ✓ Save Vendor & Apply to Row
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <div style={lbl}>BOM Section</div>
                  <select style={pickCss} value={qForm.bom_type || 'SEWING'} onChange={e => setQForm({ ...qForm, bom_type: e.target.value })}>
                    <option value="SEWING">1. SEWING BOM</option>
                    <option value="FINISHING">2. FINISHING BOM</option>
                    <option value="PACKING">3. PACKING BOM</option>
                  </select>
                </div>
                <div>
                  <div style={lbl}>Trim Category *</div>
                  <input style={inp} value={qForm.trim_type || ''} placeholder="e.g. Fly Zipper / Main Label" onChange={e => setQForm({ ...qForm, trim_type: e.target.value })} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={lbl}>Raw Material Name / Spec *</div>
                  <input style={inp} value={qForm.material_name || ''} placeholder="e.g. YKK #3 Brass Auto-Lock Zipper" onChange={e => setQForm({ ...qForm, material_name: e.target.value })} />
                </div>
                <div>
                  <div style={lbl}>Material Size</div>
                  <input style={inp} value={qForm.mat_size || ''} placeholder="e.g. 24L / 6.5 inch" onChange={e => setQForm({ ...qForm, mat_size: e.target.value })} />
                </div>
                <div>
                  <div style={lbl}>Material Shade</div>
                  <input style={inp} value={qForm.mat_shade || ''} placeholder="DTM / Black / Brass" onChange={e => setQForm({ ...qForm, mat_shade: e.target.value })} />
                </div>
                <div>
                  <div style={lbl}>Buyer / Brand Scope</div>
                  <input style={inp} value={qForm.buyer_name || ''} placeholder="Blank = All Buyers" onChange={e => setQForm({ ...qForm, buyer_name: e.target.value })} />
                </div>
                <div>
                  <div style={lbl}>Standard Rate ₹</div>
                  <input type="number" step="any" style={inp} value={qForm.price ?? ''} placeholder="0.00" onChange={e => setQForm({ ...qForm, price: e.target.value })} />
                </div>
                <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                  <button onClick={() => setQuickModal(null)} style={{ padding: '8px 14px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg)', cursor: 'pointer', fontWeight: 700 }}>Cancel</button>
                  <button onClick={() => {
                    api('/bom/masters/raw-material', { method: 'POST', body: JSON.stringify(qForm) }).then(r => {
                      if (r && r.ok) {
                        set(quickModal.rowIdx, 'trim_type', qForm.trim_type);
                        set(quickModal.rowIdx, 'trim_code', qForm.material_name);
                        set(quickModal.rowIdx, 'mat_size', qForm.mat_size);
                        set(quickModal.rowIdx, 'mat_shade', qForm.mat_shade);
                        if (qForm.price) set(quickModal.rowIdx, 'price', qForm.price);
                        api('/bom/items').then(d => setSugg(d && d.app ? d : { app: [], erp: [], vendors: [] }));
                        setQuickModal(null);
                        setMsg(`Raw Material "${qForm.material_name}" saved to Master ✓`);
                      }
                    });
                  }} style={{ padding: '8px 18px', borderRadius: 7, border: 'none', background: '#059669', color: '#fff', fontWeight: 800, cursor: 'pointer' }}>
                    ✓ Save Material to Master & Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {previewImg && (
        <div onClick={() => setPreviewImg(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src={previewImg} alt="Trim Preview" style={{ maxWidth: '88vw', maxHeight: '88vh', borderRadius: 10, background: '#fff', padding: 8 }} />
        </div>
      )}
    </div>
  );
}

// ============================================================================
// INTERACTIVE TRAINING GUIDE & WALKTHROUGH MODAL FOR PARALLEL BOM MODULE
// ============================================================================
function BomTrainingModal({ onClose }) {
  const accent = '#7c3aed';
  const [step, setStep] = useState(0);

  const steps = [
    {
      badge: 'STEP 1 OF 7 · NAVIGATION & ARCHITECTURE',
      title: 'How to Open the BOM Module & 3-Level Hierarchy',
      subtitle: 'Path: HOME (War Room) → RW → TAS → DAILY ACTIVITY → BOM',
      points: [
        ['1. 3 High-Level BOM Sections', 'Every Garment BOM is divided into 1. SEWING BOM (Thread, Zipper, Interlining, Pocketing, Buttons), 2. FINISHING BOM (Main Label, Wash Care, Size Label, Hang Tag, Price Ticket), and 3. PACKING BOM (Poly Bag, Carton Box, Tissue, Carton Sticker, BOPP Tape).'],
        ['2. 5 Parallel Masters (100% Safe)', 'Runs completely in parallel with ERP Form 5433 without modifying legacy tables. Pre-loaded with 183 Trim Categories, 612 Raw Materials, 485 Product Base Checklists, 181 Trim Vendors, and Annual Forward Contracts.'],
        ['3. Real-Time Cost & % Share', 'Every BOM automatically calculates Sewing BOM %, Finishing BOM %, Packing BOM %, Total BOM Cost (₹), and Cost Per Garment (₹/pc).']
      ],
      diagram: ['1. BOM TYPE (Sewing · Finishing · Packing)', '2. TRIM CATEGORY (183 Trims e.g. Fly Zipper, Poly Bag)', '3. RAW MATERIAL SPEC (612 Specs: Name + Size + Shade + Brand)', '4. PRODUCT + BUYER BASE BOM CHECKLIST (Trouser, Shirt, Shorts…)', '5. TAS ZERO-MISS BOM ENTRY + PO EXPANSION']
    },
    {
      badge: 'STEP 2 OF 7 · ADDING NEW VENDORS & MASTERS',
      title: 'How to Add a New Vendor, Trim Category, or Raw Material',
      subtitle: 'Two Ways: From the Master Hub OR Directly Inside BOM Entry (+V / +M)',
      points: [
        ['Way A — Master Hub (⚙️ 5 Parallel Masters Card)', 'On the BOM Command Center, click the purple "⚙️ 5 PARALLEL MASTERS & VENDORS" card. You get 5 tabs: (1) Annual Forward Contracts, (2) Product Base Checklists, (3) Raw Material Library, (4) Trim Category Master, and (5) Trim Vendor Master.'],
        ['Way B — Instant Inline "+V" (Add Vendor) Inside BOM Entry', 'While entering a BOM for any TAS, click the "+V" button inside the Vendor column on any row. Enter Vendor Name, City, Phone, GSTIN & Lead Time — it saves to the Vendor Master and applies to your row immediately!'],
        ['Way C — Instant Inline "+M" (Add Raw Material) Inside BOM Entry', 'Typed a new Raw Material quality/spec on a BOM row? Click "+M" next to the Spec field to save that Material Name, Size, Shade & Rate permanently into the Raw Material Master Library!']
      ],
      diagram: ['Click ⚙️ Masters Card OR Click +V / +M on any BOM Row', 'Fill Name · Category · Rate / City / Spec', 'Click Save → Immediately Available Across All TAS BOMs']
    },
    {
      badge: 'STEP 3 OF 7 · STARTING A TAS BOM IN 5 SECONDS',
      title: 'Product Base BOM Checklist + Copy from Old Style / TAS',
      subtitle: 'Never start from a blank screen — auto-load or copy with 1 click',
      points: [
        ['1. Auto-Load Garment Product Base BOM', 'When you click any Pending TAS, the system checks its Garment Product (TROUSER, SHIRT, SHORTS, JACKET, etc.) and Buyer, and automatically loads the mandatory Sewing, Finishing, and Packing trim checklist.'],
        ['2. Copy from Old Style / Previous TAS', 'Use the "2. Copy BOM from Old Style / TAS" dropdown at the top of BOM Entry to pick any of the 154+ historical BOMs. Even if the new Style Name is different, the system automatically remaps all trims to the NEW TAS Style Name, Garment Shades, and Size Ratio!'],
        ['3. Buyer Shortlisting', 'When you click inside any Trim Category cell, the autocomplete dropdown prioritizes Raw Materials previously used for that specific Buyer/Brand.']
      ],
      diagram: ['Pick Pending TAS', 'Auto-Loads Product + Buyer Checklist (or Copy Old Style)', 'Auto-Remaps to New TAS Style, Shades & Size Ratio']
    },
    {
      badge: 'STEP 4 OF 7 · GARMENT SHADE VS RAW MATERIAL SHADE',
      title: 'Dual-Shade Engine: Garment Shade vs Raw Material Shade',
      subtitle: 'Handles DTM (Dyed-To-Match), Fixed Trim Shades, and Per-Shade Splits',
      points: [
        ['Case 1 — Same as Garment Shade (DTM)', 'Leave or select "DTM" in 🎨 Raw Mat Shade. If the TAS has 3 Garment Shades (e.g. Navy, Olive, Black), the system automatically expands into DTM (NAVY), DTM (OLIVE), and DTM (BLACK).'],
        ['Case 2 — Fixed Raw Material Shade', 'If a trim has its own fixed shade across all garments (e.g. "ANTIQUE BRASS" button or "BLACK" main label), type/pick that shade in 🎨 Raw Mat Shade.'],
        ['Case 3 — Different Raw Material Shade per Garment Shade (⇄ Split Button)', 'Click the blue "⇄" button on the right of any trim row. It splits that trim into 1 row per Garment Shade so you can assign e.g. Garment NAVY → Raw Shade GOLD, and Garment BLACK → Raw Shade SILVER!']
      ],
      diagram: ['👕 Garment Shade (ALL or Specific Order Color)', '🎨 Raw Mat Shade (DTM or Fixed Shade)', '⇄ 1-Click Split Row per Garment Shade', '👁️ Click Eye Icon to Preview Exact Expanded PO Lines']
    },
    {
      badge: 'STEP 5 OF 7 · RISHABH VS FACTORY & FORWARD CONTRACTS',
      title: 'Purchasing Split (Rishabh vs Factory) & Annual Contracts',
      subtitle: 'Track who buys each trim and lock full-year vendor rates',
      points: [
        ['1. Bought By: RISHABH (RWPL) vs FACTORY', 'Every row has a green/amber toggle for RISHABH vs FACTORY. The top KPI card shows the exact ₹ value and % split between Rishabh-supplied trims and Factory-sourced trims.'],
        ['2. Annual Vendor Forward Contracts (📜)', 'In Master Tab 1, record full-year vendor contracts (Contract No, Vendor, Buyer, Trim, Locked Rate ₹, Annual Booked Qty, Validity).'],
        ['3. Auto-Rate Locking', 'Whenever a TAS BOM uses a contracted trim, the system automatically attaches the Contract No (📜 FC/2026-27/...) and locks the contracted rate & vendor.']
      ],
      diagram: ['Annual Forward Contract Master', 'Auto-Matches Buyer + Trim Type in TAS BOM', 'Locks Contract Rate ₹ & Tracks Balance Qty']
    },
    {
      badge: 'STEP 6 OF 7 · ZERO-MISS GATE & GEMINI AI AUDITOR',
      title: '100% Zero-Miss Protection (No Forgotten Trims)',
      subtitle: 'Eliminates the 20+ day production delay caused by a single missed trim',
      points: [
        ['1. Mandatory Zero-Miss Gate', 'You cannot confirm a BOM if any checklist trim is left blank or 0. If a trim is genuinely not required for a style, click "N/A" on that row and enter a mandatory N/A Reason (e.g. "Elastic waist — no fly zipper").'],
        ['2. ✨ Gemini AI Zero-Miss Audit', 'Click "✨ Gemini Zero-Miss Audit" in the top right. Gemini AI audits your Sewing, Finishing, and Packing sections against the Garment Product standard, gives a Completeness Score out of 100, and provides 1-click "+ Add" buttons for any missing critical trim.'],
        ['3. 📷 AI Scan Trim Card / Techpack PDF', 'Click "📷 AI Scan Trim Card / PDF" to upload a photo or PDF of a buyer trim card — Gemini Vision extracts the trims directly into your BOM grid.']
      ],
      diagram: ['Every Trim Must Have Cons > 0 OR Mandatory N/A Reason', '✨ Run Gemini AI Zero-Miss Audit (Score / 100)', '✓ Confirm & Save Parallel BOM']
    },
    {
      badge: 'STEP 7 OF 7 · IMAGES & PRODUCTION PRINTOUTS',
      title: 'Trim Artwork Photos & 1-Click Printouts',
      subtitle: 'Print Master BOM (with Rates) or Factory Trim Card (without Rates)',
      points: [
        ['1. Upload Trim Artwork / Photo (📷)', 'Click the camera icon (📷) on any row to attach a photo of the label, button, zipper, or polybag artwork. Click the thumbnail anytime to zoom full-screen.'],
        ['2. 🖨️ Print Master BOM (with Images & Rates)', 'Opens a clean, print-ready A4 document grouped by 1. SEWING BOM, 2. FINISHING BOM, and 3. PACKING BOM with images, Garment Shade, Raw Material Shade, Size, Qty, Rate ₹, and Value ₹.'],
        ['3. 🖨️ Print Factory Trim Card (Floor Copy)', 'Prints the complete 3-section Trim Card with images and exact quantities for the factory floor, automatically hiding confidential prices and values.']
      ],
      diagram: ['Upload Trim Photo (📷)', '🖨️ Print Master BOM (Management / Purchase)', '🖨️ Print Factory Trim Card (Production Floor)']
    }
  ];

  const cur = steps[step];

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.75)', backdropFilter: 'blur(3px)',
      zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: '100%', maxWidth: 820, background: 'var(--bg2)', border: `2px solid ${accent}`,
        borderRadius: 16, overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.45)', display: 'flex', flexDirection: 'column', maxHeight: '90vh'
      }}>
        {/* Top Header */}
        <div style={{
          padding: '16px 22px', background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
          color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1.2, opacity: 0.9 }}>🎥 INTERACTIVE BOM TRAINING WALKTHROUGH</div>
            <div style={{ fontSize: 18, fontWeight: 800, marginTop: 2 }}>{cur.title}</div>
          </div>
          <button onClick={onClose} style={{
            width: 32, height: 32, borderRadius: 8, border: '1px solid rgba(255,255,255,0.3)',
            background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 16, fontWeight: 800, cursor: 'pointer'
          }}>✕</button>
        </div>

        {/* Step Progress Pills */}
        <div style={{ padding: '10px 22px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {steps.map((s, idx) => (
            <button key={idx} onClick={() => setStep(idx)} style={{
              padding: '5px 11px', borderRadius: 20,
              border: step === idx ? `2px solid ${accent}` : '1px solid var(--border)',
              background: step === idx ? accent : 'var(--bg2)',
              color: step === idx ? '#fff' : 'var(--text2)', fontSize: 11, fontWeight: 800, cursor: 'pointer'
            }}>
              Step {idx + 1}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <span style={{ padding: '3px 10px', borderRadius: 12, background: `${accent}18`, color: accent, fontSize: 10.5, fontWeight: 800 }}>
              {cur.badge}
            </span>
            <div style={{ fontSize: 13, color: 'var(--text2)', fontWeight: 700, marginTop: 6 }}>{cur.subtitle}</div>
          </div>

          {/* Visual Flow Diagram */}
          <div style={{
            padding: '12px 14px', borderRadius: 10, background: 'var(--bg3)', border: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'center'
          }}>
            {cur.diagram.map((node, i) => (
              <React.Fragment key={i}>
                <div style={{
                  padding: '7px 12px', borderRadius: 8, background: 'var(--bg2)',
                  border: `1.5px solid ${accent}`, color: 'var(--text)', fontSize: 11.5, fontWeight: 800
                }}>
                  {node}
                </div>
                {i < cur.diagram.length - 1 && <span style={{ color: accent, fontWeight: 800, fontSize: 16 }}>→</span>}
              </React.Fragment>
            ))}
          </div>

          {/* Detailed Points */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {cur.points.map(([heading, body], idx) => (
              <div key={idx} style={{ padding: '12px 14px', borderRadius: 10, background: 'var(--bg)', border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: accent, marginBottom: 4 }}>{heading}</div>
                <div style={{ fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.55 }}>{body}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer Controls */}
        <div style={{
          padding: '14px 22px', background: 'var(--bg3)', borderTop: '1px solid var(--border)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} style={{
            padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)',
            background: 'var(--bg2)', color: step === 0 ? 'var(--text3)' : 'var(--text)', fontWeight: 800, cursor: step === 0 ? 'default' : 'pointer'
          }}>
            ← Previous Step
          </button>
          <span style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 700 }}>Slide {step + 1} of {steps.length}</span>
          {step < steps.length - 1 ? (
            <button onClick={() => setStep(s => s + 1)} style={{
              padding: '8px 20px', borderRadius: 8, border: 'none',
              background: accent, color: '#fff', fontWeight: 800, cursor: 'pointer'
            }}>
              Next Step →
            </button>
          ) : (
            <button onClick={onClose} style={{
              padding: '8px 20px', borderRadius: 8, border: 'none',
              background: '#059669', color: '#fff', fontWeight: 800, cursor: 'pointer'
            }}>
              ✓ Finish Training Guide
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// PARALLEL MASTERS & FORWARD CONTRACTS MANAGER COMPONENT (5 TABS)
// ============================================================================
function BomMastersManager({ onBack }) {
  const accent = '#7c3aed';
  const [data, setData] = useState(null);
  const [subTab, setSubTab] = useState('vendors'); // 'vendors' | 'contracts' | 'templates' | 'raw' | 'trims'
  const [q, setQ] = useState('');
  const [filterBomType, setFilterBomType] = useState('');
  const [filterProduct, setFilterProduct] = useState('TROUSER');
  const [rawViewMode, setRawViewMode] = useState('list'); // 'list' | 'pivot'
  const [msg, setMsg] = useState('');

  const [newVendor, setNewVendor] = useState({
    vendor_name: '', vendor_category: 'ALL TRIMS', contact_person: '', phone: '',
    email: '', gstin: '', city: '', lead_time_days: 10, payment_terms: '30 Days', sourced_by: 'RWPL'
  });
  const [newContract, setNewContract] = useState({
    contract_no: '', vendor_name: '', buyer_name: '', bom_type: 'SEWING', trim_type: '',
    trim_code: '', mat_size: '', mat_shade: '', unit: 'NOS', contract_rate: '', contract_qty: '',
    valid_from: bomToday(), valid_to: '2027-03-31', sourced_by: 'RWPL', remark: ''
  });
  const [newRaw, setNewRaw] = useState({
    bom_type: 'SEWING', trim_type: '', material_name: '', mat_size: '', mat_shade: 'DTM',
    brand: '', buyer_name: '', cons_type: CONS_TYPES[0], consumption: 1, unit: 'NOS',
    wastage: 3, price: '', sourced_by: 'RWPL', vendor_name: '', image_url: ''
  });
  const [newTpl, setNewTpl] = useState({
    product_type: 'TROUSER', buyer_name: '', bom_type: 'SEWING', trim_type: '',
    default_trim_code: '', cons_type: CONS_TYPES[0], consumption: 1, unit: 'NOS',
    wastage: 3, default_price: '', sourced_by: 'RWPL', is_mandatory: true
  });
  const [newTrim, setNewTrim] = useState({
    bom_type: 'SEWING', trim_type: '', canonical_group: '', default_cons_type: CONS_TYPES[0],
    default_consumption: 1, default_unit: 'NOS', default_wastage: 3, default_sourced_by: 'RWPL', is_mandatory: true
  });

  const load = () => api('/bom/masters').then(setData).catch(() => setData({ trimMasters: [], rawMaterials: [], productTemplates: [], forwardContracts: [], vendors: [] }));
  useEffect(() => { load(); }, []);

  const saveVendor = () => {
    api('/bom/masters/vendor', { method: 'POST', body: JSON.stringify(newVendor) }).then(r => {
      if (r && r.ok) {
        setMsg(`Vendor "${r.vendor_name}" saved to Master ✓`);
        setNewVendor({ ...newVendor, vendor_name: '', contact_person: '', phone: '', email: '', gstin: '', city: '' });
        load();
      } else setMsg(r?.error || 'Error saving vendor');
    });
  };

  const saveContract = () => {
    api('/bom/masters/forward-contract', { method: 'POST', body: JSON.stringify(newContract) }).then(r => {
      if (r && r.ok) {
        setMsg('Forward Contract saved ✓');
        setNewContract({ ...newContract, contract_no: '', trim_type: '', trim_code: '', contract_rate: '', contract_qty: '' });
        load();
      } else setMsg(r?.error || 'Error');
    });
  };

  const saveRaw = () => {
    api('/bom/masters/raw-material', { method: 'POST', body: JSON.stringify(newRaw) }).then(r => {
      if (r && r.ok) {
        setMsg('Raw Material saved ✓');
        setNewRaw({ ...newRaw, material_name: '', mat_size: '', mat_shade: 'DTM', price: '' });
        load();
      } else setMsg(r?.error || 'Error');
    });
  };

  const saveTpl = () => {
    api('/bom/masters/product-template', { method: 'POST', body: JSON.stringify({ ...newTpl, product_type: filterProduct }) }).then(r => {
      if (r && r.ok) {
        setMsg('Product Checklist Item saved ✓');
        setNewTpl({ ...newTpl, trim_type: '', default_trim_code: '', default_price: '' });
        load();
      } else setMsg(r?.error || 'Error');
    });
  };

  const saveTrimMaster = () => {
    api('/bom/masters/trim', { method: 'POST', body: JSON.stringify(newTrim) }).then(r => {
      if (r && r.ok) {
        setMsg(`Trim Category "${newTrim.trim_type}" saved ✓`);
        setNewTrim({ ...newTrim, trim_type: '', canonical_group: '' });
        load();
      } else setMsg(r?.error || 'Error');
    });
  };

  const inp = { height: 30, padding: '0 8px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 5, color: 'var(--text)', fontSize: 12 };
  const cell = { padding: '8px 10px', fontSize: 12, borderBottom: '1px solid var(--border)' };
  const hd = { ...cell, fontWeight: 700, fontSize: 10, letterSpacing: 0.8, background: 'var(--bg3)', textTransform: 'uppercase', textAlign: 'left' };

  return (
    <div style={{ padding: '12px 16px 28px' }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        <button onClick={onBack} style={{ padding: '7px 14px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg2)', fontWeight: 800, cursor: 'pointer' }}>← Back to TAS BOMs</button>
        {[
          ['vendors', `🏢 Trim Vendor Master (${data?.vendors?.length || 0})`],
          ['raw', `🧵 Raw Material Master (${data?.rawMaterials?.length || 0})`],
          ['trims', `🏷️ Trim Category Master (${data?.trimMasters?.length || 0})`],
          ['templates', `🛡️ Product Base BOM Checklists (${data?.productTemplates?.length || 0})`],
          ['contracts', `📜 Annual Forward Contracts (${data?.forwardContracts?.length || 0})`]
        ].map(([k, label]) => (
          <button key={k} onClick={() => { setSubTab(k); setMsg(''); }}
            style={{ padding: '7px 14px', borderRadius: 7, border: subTab === k ? `2px solid ${accent}` : '1px solid var(--border)',
              background: subTab === k ? `${accent}15` : 'var(--bg2)', color: subTab === k ? accent : 'var(--text)', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>
            {label}
          </button>
        ))}
        {msg && <span style={{ color: '#059669', fontWeight: 800, fontSize: 12 }}>{msg}</span>}
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="🔍 Search active master tab…"
          style={{ ...inp, marginLeft: 'auto', minWidth: 230 }} />
      </div>

      {/* 0. TRIM VENDOR / SUPPLIER MASTER */}
      {subTab === 'vendors' && (
        <div>
          <div style={{ padding: 14, borderRadius: 10, border: `1.5px solid ${accent}`, background: `${accent}08`, marginBottom: 14, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div><div style={{ fontSize: 10, fontWeight: 800 }}>Vendor / Supplier Name *</div><input style={{ ...inp, width: 190 }} placeholder="e.g. YKK INDIA / AVERY DENNISON" value={newVendor.vendor_name} onChange={e => setNewVendor({ ...newVendor, vendor_name: e.target.value })} /></div>
            <div><div style={{ fontSize: 10, fontWeight: 800 }}>Trim Specialization</div>
              <select style={inp} value={newVendor.vendor_category} onChange={e => setNewVendor({ ...newVendor, vendor_category: e.target.value })}>
                <option value="ALL TRIMS">ALL TRIMS</option>
                <option value="SEWING">SEWING TRIMS</option>
                <option value="FINISHING">FINISHING / LABELS</option>
                <option value="PACKING">PACKING / CARTONS</option>
              </select>
            </div>
            <div><div style={{ fontSize: 10, fontWeight: 800 }}>City / Location</div><input style={{ ...inp, width: 120 }} placeholder="Bangalore / Tirupur" value={newVendor.city} onChange={e => setNewVendor({ ...newVendor, city: e.target.value })} /></div>
            <div><div style={{ fontSize: 10, fontWeight: 800 }}>Contact Person</div><input style={{ ...inp, width: 130 }} placeholder="Contact Name" value={newVendor.contact_person} onChange={e => setNewVendor({ ...newVendor, contact_person: e.target.value })} /></div>
            <div><div style={{ fontSize: 10, fontWeight: 800 }}>Phone / WhatsApp</div><input style={{ ...inp, width: 125 }} placeholder="Phone No" value={newVendor.phone} onChange={e => setNewVendor({ ...newVendor, phone: e.target.value })} /></div>
            <div><div style={{ fontSize: 10, fontWeight: 800 }}>GSTIN</div><input style={{ ...inp, width: 140 }} placeholder="Optional GSTIN" value={newVendor.gstin} onChange={e => setNewVendor({ ...newVendor, gstin: e.target.value })} /></div>
            <div><div style={{ fontSize: 10, fontWeight: 800 }}>Lead Time (Days)</div><input type="number" style={{ ...inp, width: 80 }} value={newVendor.lead_time_days} onChange={e => setNewVendor({ ...newVendor, lead_time_days: e.target.value })} /></div>
            <div><div style={{ fontSize: 10, fontWeight: 800 }}>Default Bought By</div>
              <select style={inp} value={newVendor.sourced_by} onChange={e => setNewVendor({ ...newVendor, sourced_by: e.target.value })}>
                <option value="RWPL">RISHABH (RWPL)</option><option value="FACTORY">FACTORY</option>
              </select>
            </div>
            <button onClick={saveVendor} style={{ height: 30, padding: '0 16px', borderRadius: 6, border: 'none', background: accent, color: '#fff', fontWeight: 800, cursor: 'pointer' }}>+ Add New Vendor</button>
          </div>

          <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={hd}>Vendor Name</th><th style={hd}>Category</th><th style={hd}>City</th>
                <th style={hd}>Contact Person</th><th style={hd}>Phone</th><th style={hd}>GSTIN</th>
                <th style={{ ...hd, textAlign: 'right' }}>Lead Time</th><th style={hd}>Default Bought By</th>
              </tr></thead>
              <tbody>
                {(data?.vendors || [])
                  .filter(v => !q || `${v.vendor_name || v.name} ${v.vendor_category || ''} ${v.city || ''} ${v.contact_person || ''}`.toLowerCase().includes(q.toLowerCase()))
                  .map(v => (
                    <tr key={v.id}>
                      <td style={{ ...cell, fontWeight: 800, color: accent }}>{v.vendor_name || v.name}</td>
                      <td style={{ ...cell, fontWeight: 700 }}>{v.vendor_category || 'ALL TRIMS'}</td>
                      <td style={cell}>{v.city || '—'}</td>
                      <td style={cell}>{v.contact_person || '—'}</td>
                      <td style={cell}>{v.phone || '—'}</td>
                      <td style={cell}>{v.gstin || '—'}</td>
                      <td style={{ ...cell, textAlign: 'right' }}>{v.lead_time_days || 10} days</td>
                      <td style={cell}><span style={{ padding: '2px 7px', borderRadius: 4, fontSize: 10.5, fontWeight: 700, background: v.sourced_by === 'FACTORY' ? '#fef3c7' : '#dcfce7', color: v.sourced_by === 'FACTORY' ? '#b45309' : '#15803d' }}>{v.sourced_by || 'RWPL'}</span></td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 1. ANNUAL VENDOR FORWARD CONTRACTS */}
      {subTab === 'contracts' && (
        <div>
          <div style={{ padding: 12, borderRadius: 8, border: `1px solid ${accent}`, background: `${accent}08`, marginBottom: 14, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>Contract No</div><input style={{ ...inp, width: 125 }} placeholder="FC/2026-27/006" value={newContract.contract_no} onChange={e => setNewContract({ ...newContract, contract_no: e.target.value })} /></div>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>Vendor Name</div><input list="bom-vendor-presets" style={{ ...inp, width: 150 }} placeholder="OPTUS / ENERGYPAC" value={newContract.vendor_name} onChange={e => setNewContract({ ...newContract, vendor_name: e.target.value })} /></div>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>Buyer / Brand</div><input style={{ ...inp, width: 140 }} placeholder="STYLE UNION" value={newContract.buyer_name} onChange={e => setNewContract({ ...newContract, buyer_name: e.target.value })} /></div>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>BOM Type</div>
              <select style={inp} value={newContract.bom_type} onChange={e => setNewContract({ ...newContract, bom_type: e.target.value })}>
                <option value="SEWING">SEWING</option><option value="FINISHING">FINISHING</option><option value="PACKING">PACKING</option>
              </select>
            </div>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>Trim Type</div><input style={{ ...inp, width: 145 }} placeholder="Fly Zipper / Poly Bag" value={newContract.trim_type} onChange={e => setNewContract({ ...newContract, trim_type: e.target.value })} /></div>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>Material Spec</div><input style={{ ...inp, width: 165 }} placeholder="Spec / Quality" value={newContract.trim_code} onChange={e => setNewContract({ ...newContract, trim_code: e.target.value })} /></div>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>Rate ₹</div><input type="number" step="any" style={{ ...inp, width: 80 }} value={newContract.contract_rate} onChange={e => setNewContract({ ...newContract, contract_rate: e.target.value })} /></div>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>Annual Qty</div><input type="number" style={{ ...inp, width: 100 }} value={newContract.contract_qty} onChange={e => setNewContract({ ...newContract, contract_qty: e.target.value })} /></div>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>Valid To</div><input type="date" style={{ ...inp, width: 130 }} value={newContract.valid_to} onChange={e => setNewContract({ ...newContract, valid_to: e.target.value })} /></div>
            <button onClick={saveContract} style={{ height: 30, padding: '0 14px', borderRadius: 6, border: 'none', background: accent, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>+ Add Forward Contract</button>
          </div>

          <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={hd}>Contract No</th><th style={hd}>Vendor</th><th style={hd}>Buyer</th><th style={hd}>BOM Type</th>
                <th style={hd}>Trim Type</th><th style={hd}>Material Spec</th><th style={{ ...hd, textAlign: 'right' }}>Locked Rate</th>
                <th style={{ ...hd, textAlign: 'right' }}>Booked Qty</th><th style={{ ...hd, textAlign: 'right' }}>Consumed</th>
                <th style={{ ...hd, textAlign: 'right' }}>Balance Qty</th><th style={hd}>Validity</th><th style={hd}>Sourced By</th>
              </tr></thead>
              <tbody>
                {(data?.forwardContracts || [])
                  .filter(c => !q || `${c.contract_no} ${c.vendor_name} ${c.buyer_name} ${c.trim_type} ${c.trim_code}`.toLowerCase().includes(q.toLowerCase()))
                  .map(c => (
                    <tr key={c.id}>
                      <td style={{ ...cell, fontWeight: 700, color: accent }}>{c.contract_no}</td>
                      <td style={{ ...cell, fontWeight: 600 }}>{c.vendor_name}</td>
                      <td style={cell}>{c.buyer_name || 'All Buyers'}</td>
                      <td style={{ ...cell, color: bomTypeColor(c.bom_type), fontWeight: 700 }}>{c.bom_type}</td>
                      <td style={{ ...cell, fontWeight: 600 }}>{c.trim_type}</td>
                      <td style={cell}>{c.trim_code || '—'}</td>
                      <td style={{ ...cell, textAlign: 'right', fontWeight: 700 }}>₹{Number(c.contract_rate || 0).toFixed(2)}</td>
                      <td style={{ ...cell, textAlign: 'right' }}>{fmt(c.contract_qty)}</td>
                      <td style={{ ...cell, textAlign: 'right', color: 'var(--text3)' }}>{fmt(c.consumed_qty)}</td>
                      <td style={{ ...cell, textAlign: 'right', fontWeight: 700, color: '#15803d' }}>{fmt(c.balance_qty)}</td>
                      <td style={{ ...cell, fontSize: 11 }}>{c.valid_from} → {c.valid_to}</td>
                      <td style={cell}><span style={{ padding: '2px 7px', borderRadius: 4, fontSize: 10.5, fontWeight: 700, background: c.sourced_by === 'FACTORY' ? '#fef3c7' : '#dcfce7', color: c.sourced_by === 'FACTORY' ? '#b45309' : '#15803d' }}>{c.sourced_by}</span></td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 2. PRODUCT BASE BOM CHECKLIST TEMPLATES */}
      {subTab === 'templates' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <b style={{ fontSize: 12 }}>Select Garment Product:</b>
            {['TROUSER', 'SHIRT', 'HALF SLEEVE SHIRT', 'FULL SLEEVE SHIRT', 'JACKET', 'SHORTS', 'T-SHIRT', 'BLAZER'].map(p => (
              <button key={p} onClick={() => setFilterProduct(p)}
                style={{ padding: '5px 11px', borderRadius: 6, border: filterProduct === p ? `2px solid ${accent}` : '1px solid var(--border)', background: filterProduct === p ? `${accent}15` : 'var(--bg2)', color: filterProduct === p ? accent : 'var(--text)', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>
                {p}
              </button>
            ))}
          </div>

          <div style={{ padding: 12, borderRadius: 8, border: `1px solid ${accent}`, background: `${accent}08`, marginBottom: 14, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>Buyer (Blank = Base for All)</div><input style={{ ...inp, width: 165 }} placeholder="All Buyers or e.g. LIFESTYLE" value={newTpl.buyer_name} onChange={e => setNewTpl({ ...newTpl, buyer_name: e.target.value })} /></div>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>BOM Type</div>
              <select style={inp} value={newTpl.bom_type} onChange={e => setNewTpl({ ...newTpl, bom_type: e.target.value })}>
                <option value="SEWING">SEWING</option><option value="FINISHING">FINISHING</option><option value="PACKING">PACKING</option>
              </select>
            </div>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>Trim Type</div><input style={{ ...inp, width: 160 }} placeholder="Pocketing / Fly Zipper" value={newTpl.trim_type} onChange={e => setNewTpl({ ...newTpl, trim_type: e.target.value })} /></div>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>Default Spec</div><input style={{ ...inp, width: 170 }} placeholder="Default quality spec" value={newTpl.default_trim_code} onChange={e => setNewTpl({ ...newTpl, default_trim_code: e.target.value })} /></div>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>Breakdown Rule</div>
              <select style={inp} value={newTpl.cons_type} onChange={e => setNewTpl({ ...newTpl, cons_type: e.target.value })}>
                {CONS_TYPES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>Cons</div><input type="number" step="any" style={{ ...inp, width: 70 }} value={newTpl.consumption} onChange={e => setNewTpl({ ...newTpl, consumption: e.target.value })} /></div>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>Bought By</div>
              <select style={inp} value={newTpl.sourced_by} onChange={e => setNewTpl({ ...newTpl, sourced_by: e.target.value })}>
                <option value="RWPL">RISHABH</option><option value="FACTORY">FACTORY</option>
              </select>
            </div>
            <button onClick={saveTpl} style={{ height: 30, padding: '0 14px', borderRadius: 6, border: 'none', background: accent, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>+ Add to {filterProduct} Checklist</button>
          </div>

          <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={hd}>Product</th><th style={hd}>Buyer Scope</th><th style={hd}>BOM Type</th><th style={hd}>Trim Type</th>
                <th style={hd}>Default Spec</th><th style={hd}>Breakdown Rule</th><th style={{ ...hd, textAlign: 'right' }}>Cons</th>
                <th style={hd}>Unit</th><th style={{ ...hd, textAlign: 'right' }}>Std Rate</th><th style={hd}>Bought By</th><th style={hd}>Used In</th>
              </tr></thead>
              <tbody>
                {(data?.productTemplates || [])
                  .filter(t => t.product_type === filterProduct && (!q || `${t.trim_type} ${t.default_trim_code} ${t.buyer_name || ''}`.toLowerCase().includes(q.toLowerCase())))
                  .map(t => (
                    <tr key={t.id}>
                      <td style={{ ...cell, fontWeight: 700 }}>{t.product_type}</td>
                      <td style={{ ...cell, color: t.buyer_name ? accent : 'var(--text3)', fontWeight: t.buyer_name ? 700 : 400 }}>{t.buyer_name || 'Base (All Buyers)'}</td>
                      <td style={{ ...cell, color: bomTypeColor(t.bom_type), fontWeight: 700 }}>{t.bom_type}</td>
                      <td style={{ ...cell, fontWeight: 600 }}>{t.trim_type}</td>
                      <td style={cell}>{t.default_trim_code || '—'}</td>
                      <td style={cell}>{t.cons_type}</td>
                      <td style={{ ...cell, textAlign: 'right' }}>{t.consumption}</td>
                      <td style={cell}>{t.unit}</td>
                      <td style={{ ...cell, textAlign: 'right' }}>{t.default_price ? `₹${t.default_price}` : '—'}</td>
                      <td style={cell}>{t.sourced_by}</td>
                      <td style={cell}>{t.used_frequency} BOMs</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 3. PARALLEL RAW MATERIAL MASTER */}
      {subTab === 'raw' && (
        <div>
          <div style={{ padding: 12, borderRadius: 8, border: `1px solid ${accent}`, background: `${accent}08`, marginBottom: 14, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>BOM Type</div>
              <select style={inp} value={newRaw.bom_type} onChange={e => setNewRaw({ ...newRaw, bom_type: e.target.value })}>
                <option value="SEWING">SEWING</option><option value="FINISHING">FINISHING</option><option value="PACKING">PACKING</option>
              </select>
            </div>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>Trim Type</div><input style={{ ...inp, width: 140 }} placeholder="Fly Zipper / Button" value={newRaw.trim_type} onChange={e => setNewRaw({ ...newRaw, trim_type: e.target.value })} /></div>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>Material Name / Spec</div><input style={{ ...inp, width: 180 }} placeholder="Coil Zipper AA580" value={newRaw.material_name} onChange={e => setNewRaw({ ...newRaw, material_name: e.target.value })} /></div>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>Size</div><input style={{ ...inp, width: 80 }} placeholder="24L / 6.25&quot;" value={newRaw.mat_size} onChange={e => setNewRaw({ ...newRaw, mat_size: e.target.value })} /></div>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>Shade</div><input style={{ ...inp, width: 90 }} placeholder="DTM / Black" value={newRaw.mat_shade} onChange={e => setNewRaw({ ...newRaw, mat_shade: e.target.value })} /></div>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>Buyer / Brand</div><input style={{ ...inp, width: 130 }} placeholder="STYLE UNION" value={newRaw.buyer_name} onChange={e => setNewRaw({ ...newRaw, buyer_name: e.target.value, brand: e.target.value })} /></div>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>Preferred Vendor</div><input style={{ ...inp, width: 130 }} placeholder="Optional Vendor" value={newRaw.vendor_name} onChange={e => setNewRaw({ ...newRaw, vendor_name: e.target.value })} /></div>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>Breakdown Rule</div>
              <select style={inp} value={newRaw.cons_type} onChange={e => setNewRaw({ ...newRaw, cons_type: e.target.value })}>
                {CONS_TYPES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>Rate ₹</div><input type="number" step="any" style={{ ...inp, width: 75 }} value={newRaw.price} onChange={e => setNewRaw({ ...newRaw, price: e.target.value })} /></div>
            <button onClick={saveRaw} style={{ height: 30, padding: '0 14px', borderRadius: 6, border: 'none', background: accent, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>+ Add Raw Material</button>
            <button onClick={() => setRawViewMode(v => v === 'list' ? 'pivot' : 'list')} style={{ height: 30, padding: '0 14px', borderRadius: 6, border: `1.5px solid ${accent}`, background: rawViewMode === 'pivot' ? accent : 'var(--bg2)', color: rawViewMode === 'pivot' ? '#fff' : accent, fontWeight: 800, cursor: 'pointer', marginLeft: 'auto' }}>
              📊 {rawViewMode === 'pivot' ? 'Switch to Flat List' : 'Pivot Matrix (Quality × Size × Shade)'}
            </button>
          </div>

          {rawViewMode === 'pivot' ? (
            <BomPivotMatrix
              mode="master"
              lines={(data?.rawMaterials || [])
                .filter(m => (!filterBomType || m.bom_type === filterBomType) && (!q || `${m.trim_type} ${m.material_name} ${m.buyer_name || ''} ${m.mat_size || ''} ${m.mat_shade || ''}`.toLowerCase().includes(q.toLowerCase())))
                .map(m => ({ ...m, trim_code: m.material_name, qty: m.used_in_boms || 1 }))}
            />
          ) : (
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={hd}>BOM Type</th><th style={hd}>Trim Type</th><th style={hd}>Material Name / Spec</th>
                <th style={hd}>Size</th><th style={hd}>Shade</th><th style={hd}>Buyer / Brand</th>
                <th style={hd}>Breakdown Rule</th><th style={{ ...hd, textAlign: 'right' }}>Cons</th><th style={hd}>Unit</th>
                <th style={{ ...hd, textAlign: 'right' }}>Price ₹</th><th style={hd}>Bought By</th><th style={{ ...hd, textAlign: 'right' }}>Used In</th>
              </tr></thead>
              <tbody>
                {(data?.rawMaterials || [])
                  .filter(m => (!filterBomType || m.bom_type === filterBomType) && (!q || `${m.trim_type} ${m.material_name} ${m.buyer_name || ''} ${m.mat_size || ''} ${m.mat_shade || ''}`.toLowerCase().includes(q.toLowerCase())))
                  .slice(0, 150)
                  .map(m => (
                    <tr key={m.id}>
                      <td style={{ ...cell, color: bomTypeColor(m.bom_type), fontWeight: 700 }}>{m.bom_type}</td>
                      <td style={{ ...cell, fontWeight: 600 }}>{m.trim_type}</td>
                      <td style={{ ...cell, fontWeight: 600 }}>{m.material_name}</td>
                      <td style={cell}>{m.mat_size || '—'}</td>
                      <td style={cell}>{m.mat_shade || '—'}</td>
                      <td style={{ ...cell, color: accent, fontWeight: 600 }}>{m.buyer_name || '—'}</td>
                      <td style={cell}>{m.cons_type}</td>
                      <td style={{ ...cell, textAlign: 'right' }}>{m.consumption}</td>
                      <td style={cell}>{m.unit}</td>
                      <td style={{ ...cell, textAlign: 'right', fontWeight: 700 }}>{m.price != null ? `₹${m.price}` : '—'}</td>
                      <td style={cell}>{m.sourced_by}</td>
                      <td style={{ ...cell, textAlign: 'right' }}>{m.used_in_boms}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}

      {/* 4. PARALLEL TRIM CATEGORY MASTER */}
      {subTab === 'trims' && (
        <div>
          <div style={{ padding: 12, borderRadius: 8, border: `1px solid ${accent}`, background: `${accent}08`, marginBottom: 14, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>BOM Section</div>
              <select style={inp} value={newTrim.bom_type} onChange={e => setNewTrim({ ...newTrim, bom_type: e.target.value })}>
                <option value="SEWING">1. SEWING BOM</option><option value="FINISHING">2. FINISHING BOM</option><option value="PACKING">3. PACKING BOM</option>
              </select>
            </div>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>Trim Category Name *</div><input style={{ ...inp, width: 175 }} placeholder="e.g. Fly Zipper / Hang Tag" value={newTrim.trim_type} onChange={e => setNewTrim({ ...newTrim, trim_type: e.target.value, canonical_group: e.target.value })} /></div>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>Canonical Group</div><input style={{ ...inp, width: 145 }} placeholder="Group Name" value={newTrim.canonical_group} onChange={e => setNewTrim({ ...newTrim, canonical_group: e.target.value })} /></div>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>Default Breakdown Rule</div>
              <select style={inp} value={newTrim.default_cons_type} onChange={e => setNewTrim({ ...newTrim, default_cons_type: e.target.value })}>
                {CONS_TYPES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>Default Cons</div><input type="number" step="any" style={{ ...inp, width: 70 }} value={newTrim.default_consumption} onChange={e => setNewTrim({ ...newTrim, default_consumption: e.target.value })} /></div>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>Unit</div><input style={{ ...inp, width: 65 }} value={newTrim.default_unit} onChange={e => setNewTrim({ ...newTrim, default_unit: e.target.value })} /></div>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>Wastage %</div><input type="number" step="any" style={{ ...inp, width: 65 }} value={newTrim.default_wastage} onChange={e => setNewTrim({ ...newTrim, default_wastage: e.target.value })} /></div>
            <div><div style={{ fontSize: 10, fontWeight: 700 }}>Default Bought By</div>
              <select style={inp} value={newTrim.default_sourced_by} onChange={e => setNewTrim({ ...newTrim, default_sourced_by: e.target.value })}>
                <option value="RWPL">RISHABH</option><option value="FACTORY">FACTORY</option>
              </select>
            </div>
            <button onClick={saveTrimMaster} style={{ height: 30, padding: '0 14px', borderRadius: 6, border: 'none', background: accent, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>+ Add Trim Category</button>
          </div>

          <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={hd}>BOM Type</th><th style={hd}>Trim Category Name</th><th style={hd}>Canonical Group</th>
                <th style={hd}>Default Breakdown Rule</th><th style={{ ...hd, textAlign: 'right' }}>Default Cons</th>
                <th style={hd}>Unit</th><th style={{ ...hd, textAlign: 'right' }}>Wastage %</th><th style={hd}>Default Bought By</th>
              </tr></thead>
              <tbody>
                {(data?.trimMasters || [])
                  .filter(t => !q || `${t.trim_type} ${t.canonical_group} ${t.bom_type}`.toLowerCase().includes(q.toLowerCase()))
                  .map(t => (
                    <tr key={t.id}>
                      <td style={{ ...cell, color: bomTypeColor(t.bom_type), fontWeight: 800 }}>{t.bom_type}</td>
                      <td style={{ ...cell, fontWeight: 700 }}>{t.trim_type}</td>
                      <td style={cell}>{t.canonical_group}</td>
                      <td style={cell}>{t.default_cons_type}</td>
                      <td style={{ ...cell, textAlign: 'right' }}>{t.default_consumption}</td>
                      <td style={cell}>{t.default_unit}</td>
                      <td style={{ ...cell, textAlign: 'right' }}>{t.default_wastage}%</td>
                      <td style={cell}>{t.default_sourced_by}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function BomView({ Header, setView }) {
  const accent = '#7c3aed';
  const [rows, setRows] = useState(null);
  const [tab, setTab] = useState('pending');   // 'pending' | 'done' | 'masters'
  const [q, setQ] = useState('');
  const [prodFilter, setProdFilter] = useState('ALL');
  const [sel, setSel] = useState(null);        // TAS row opened
  const [detail, setDetail] = useState(null);
  const [addFor, setAddFor] = useState(null);  // TAS row being entered/edited
  const [dq, setDq] = useState('');
  const [df, setDf] = useState({ bom_type: '', sourced_by: '', trim_type: '', style: '', shade: '', size: '' });
  const [detailViewMode, setDetailViewMode] = useState('list'); // 'list' | 'pivot'
  const [showTraining, setShowTraining] = useState(false);

  const load = () => api('/bom').then(d => setRows(Array.isArray(d) ? d : [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  if (addFor) return <BomEntry tas={addFor} Header={Header}
    onBack={() => setAddFor(null)}
    onSaved={() => { setAddFor(null); load(); setTab('done'); }} />;

  const open = r => {
    setSel(r); setDetail(null); setDq('');
    setDf({ bom_type: '', sourced_by: '', trim_type: '', style: '', shade: '', size: '' });
    api(`/bom/${encodeURIComponent(r.tas_no)}`).then(setDetail).catch(() => setDetail({ lines: [] }));
  };

  const doneRows = (rows || []).filter(r => r.has_bom);
  const pendRows = (rows || []).filter(r => !r.has_bom && r.is_open_tas);
  const openRows = (rows || []).filter(r => r.is_open_tas);
  const openWithBom = openRows.filter(r => r.has_bom).length;
  const openCoveragePct = openRows.length > 0 ? Math.round((openWithBom / openRows.length) * 100) : 0;
  const totalBomValue = doneRows.reduce((a, r) => a + Number(r.bom_value || 0), 0);
  const totalSewVal = doneRows.reduce((a, r) => a + Number(r.sewing_value || 0), 0);
  const totalFinVal = doneRows.reduce((a, r) => a + Number(r.finishing_value || 0), 0);
  const totalPackVal = doneRows.reduce((a, r) => a + Number(r.packing_value || 0), 0);
  const pctOf = (v, tot) => (tot > 0 ? ((Number(v || 0) / tot) * 100).toFixed(1) : '0.0');

  const isDone = tab === 'done';
  const baseList = isDone ? doneRows : pendRows;
  const productChoices = ['ALL', ...new Set(baseList.map(r => r.product_type || 'TROUSER').filter(Boolean))];
  const t = q.trim().toLowerCase();
  const list = baseList
    .filter(r => prodFilter === 'ALL' || (r.product_type || 'TROUSER') === prodFilter)
    .filter(r => !t || [r.tas_no, r.buyer, r.factory, r.merchandiser, r.style_name, r.product_type, r.entryno].join(' ').toLowerCase().includes(t));

  const cell = { padding: '10px 12px', fontSize: 12, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
  const hd = { ...cell, color: 'var(--text3)', fontWeight: 800, fontSize: 10, letterSpacing: 0.9, background: 'var(--bg3)', textAlign: 'left', textTransform: 'uppercase' };
  const money = n => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  const num = (n, d = 2) => (n == null || n === '' ? '—' : Number(n).toLocaleString('en-IN', { maximumFractionDigits: d }));

  const printBomSheet = (mode = 'full', sheet, allLines) => {
    const active = (allLines || []).filter(l => !l.is_na);
    const win = window.open('', '_blank', 'width=1150,height=820');
    if (!win) return;
    const hidePrice = mode === 'factory_card';
    const byBomType = ['SEWING', 'FINISHING', 'PACKING'].map(bt => ({
      bt,
      rows: active.filter(l => (l.bom_type || 'SEWING') === bt)
    })).filter(g => g.rows.length > 0);

    const html = `<!doctype html><html><head><title>${sel.tas_no} - ${mode === 'factory_card' ? 'Factory Trim Card' : 'Bill of Material'}</title>
      <style>
        body { font-family: Inter, Arial, sans-serif; margin: 20px; color: #0f172a; font-size: 11px; }
        .hdr { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #4f46e5; padding-bottom: 10px; margin-bottom: 12px; }
        h1 { margin: 0; font-size: 18px; color: #4f46e5; letter-spacing: 0.5px; }
        .sub { font-size: 11px; color: #64748b; margin-top: 2px; }
        .meta { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px 16px; padding: 10px 14px; background: #f8fafc; border: 1px solid #cbd5e1; border-radius: 8px; margin-bottom: 14px; font-size: 11.5px; }
        .sec { margin-top: 16px; font-weight: 800; font-size: 12.5px; padding: 7px 12px; background: #eef2ff; color: #312e81; border: 1px solid #c7d2fe; border-radius: 6px 6px 0 0; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #cbd5e1; padding: 5px 7px; text-align: left; }
        th { background: #f1f5f9; font-size: 9.5px; text-transform: uppercase; letter-spacing: 0.5px; color: #334155; }
        .r { text-align: right; }
        img.thumb { width: 32px; height: 32px; object-fit: cover; border-radius: 4px; }
        @media print { .no-print { display: none; } }
      </style></head><body>
      <div class="no-print" style="margin-bottom:10px;text-align:right">
        <button onclick="window.print()" style="padding:7px 18px;font-weight:700;background:#4f46e5;color:#fff;border:none;border-radius:6px;cursor:pointer">🖨️ Print / Save PDF</button>
      </div>
      <div class="hdr">
        <div>
          <h1>RISHABH WORLD PRIVATE LIMITED — ${mode === 'factory_card' ? 'FACTORY PRODUCTION TRIM CARD' : 'EXECUTIVE MASTER BILL OF MATERIAL'}</h1>
          <div class="sub">3-Section Zero-Miss BOM (Sewing · Finishing · Packing)</div>
        </div>
        <div style="text-align:right;font-weight:800;font-size:15px;color:#4f46e5">${sel.tas_no}</div>
      </div>
      <div class="meta">
        <div><b>TAS No:</b> ${sel.tas_no}</div>
        <div><b>BOM No:</b> ${sheet?.bom_no || sel.entryno || '—'}</div>
        <div><b>Garment Product:</b> ${detail?.product_type || sel.product_type || '—'}</div>
        <div><b>Buyer / Company:</b> ${sel.buyer || '—'}</div>
        <div><b>Style Name:</b> ${detail?.style_name || sel.style_name || '—'}</div>
        <div><b>Factory:</b> ${sel.factory || '—'}</div>
        <div><b>Order Qty:</b> ${fmt(detail?.order_qty || sel.order_qty || 0)} Pcs</div>
        <div><b>BOM Date:</b> ${sheet?.entry_dt || sel.entry_dt || '—'}</div>
      </div>
      ${byBomType.map(g => `
        <div class="sec">${g.bt} BOM (${g.rows.length} Lines)</div>
        <table>
          <thead><tr>
            <th>Img</th><th>Trim Category</th><th>Material Spec / Quality</th><th>Mat Size</th>
            <th>Style</th><th>👕 Garment Shade</th><th>🎨 Raw Mat Shade</th><th>Garment Size</th>
            <th class="r">Cons</th><th class="r">Wast%</th><th>Unit</th><th class="r">Total Qty</th>
            ${hidePrice ? '' : '<th class="r">Rate ₹</th><th class="r">Value ₹</th>'}
            <th>Bought By</th><th>Vendor / Contract</th>
          </tr></thead>
          <tbody>
            ${g.rows.map(l => `<tr>
              <td>${l.image_url ? `<img class="thumb" src="${l.image_url}"/>` : '—'}</td>
              <td><b>${l.trim_type || ''}</b></td>
              <td>${l.trim_code || '—'}</td>
              <td>${l.mat_size || '—'}</td>
              <td>${l.style || '—'}</td>
              <td><b>${l.shade || 'ALL'}</b></td>
              <td><b>${l.mat_shade || (l.shade ? `DTM (${l.shade})` : '—')}</b></td>
              <td>${l.size || '—'}</td>
              <td class="r">${l.consumption ?? ''}</td>
              <td class="r">${l.wastage ?? ''}%</td>
              <td>${l.unit || ''}</td>
              <td class="r"><b>${fmt(l.qty)}</b></td>
              ${hidePrice ? '' : `<td class="r">${l.price != null ? Number(l.price).toFixed(2) : '—'}</td><td class="r"><b>₹${money(l.line_value)}</b></td>`}
              <td><b>${l.sourced_by || 'RWPL'}</b></td>
              <td>${[l.vendor, l.contract_no].filter(Boolean).join(' · ') || '—'}</td>
            </tr>`).join('')}
          </tbody>
        </table>
      `).join('')}
      </body></html>`;
    win.document.write(html);
    win.document.close();
  };

  if (sel) {
    const sheet = detail && detail.app;
    const all = sheet ? (sheet.lines || []) : ((detail && detail.lines) || []);
    const dt = dq.trim().toLowerCase();
    const lines = all.filter(l =>
      (!df.bom_type || (l.bom_type || 'SEWING') === df.bom_type) &&
      (!df.sourced_by || (l.sourced_by || 'RWPL') === df.sourced_by) &&
      (!df.trim_type || l.trim_type === df.trim_type) &&
      (!df.style || l.style === df.style) &&
      (!df.shade || l.shade === df.shade) &&
      (!df.size || l.size === df.size) &&
      (!dt || [l.trim_type, l.trim_code, l.style, l.shade, l.mat_shade, l.size, l.vendor, l.remark, l.bom_type].join(' ').toLowerCase().includes(dt)));

    const total = lines.reduce((a, l) => a + Number(l.line_value || 0), 0);
    const orderQty = Number(detail?.order_qty || sel.order_qty || 0);
    const perGarment = orderQty > 0 ? total / orderQty : 0;
    const rwplVal = lines.filter(l => (l.sourced_by || 'RWPL') === 'RWPL').reduce((a, l) => a + Number(l.line_value || 0), 0);
    const facVal = lines.filter(l => l.sourced_by === 'FACTORY').reduce((a, l) => a + Number(l.line_value || 0), 0);

    const choices = k => [...new Set(all.map(l => l[k]).filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
    const filtered = dt || df.bom_type || df.sourced_by || df.trim_type || df.style || df.shade || df.size;
    const fsel = { height: 32, padding: '0 8px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 7, color: 'var(--text)', fontSize: 12, outline: 'none', maxWidth: 190 };
    const drop = (k, label) => {
      const list = choices(k);
      if (list.length < 2) return null;
      return (
        <select key={k} style={fsel} value={df[k]} onChange={e => setDf({ ...df, [k]: e.target.value })}>
          <option value="">{label} · All</option>
          {list.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      );
    };

    const GC = 'minmax(150px, 1.3fr) minmax(110px, 0.9fr) 105px 115px 66px 58px 66px 56px 84px 72px 88px 88px';
    const LINE = '1px solid var(--border)';
    const td = (i, right) => ({
      padding: '7px 9px', display: 'flex', alignItems: 'center', justifyContent: right ? 'flex-end' : 'flex-start',
      textAlign: right ? 'right' : 'left', borderLeft: i ? LINE : 'none', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis'
    });
    const cols = [['Material Spec / Code', 0], ['Style', 0], ['👕 Garment Shade', 0], ['🎨 Raw Mat Shade', 0], ['Size', 0], ['Cons', 1], ['Wast %', 1], ['Unit', 0], ['Total Qty', 1], ['Rate ₹', 1], ['Value ₹', 1], ['Bought By', 0]];

    return (
      <div className="screen" style={{ paddingBottom: 32 }}>
        <ExportBar reportKey="bom-detail" title={`${sel.tas_no} - BOM`}
          sections={[{ name: 'BOM', columns: [C('bom_type', 'BOM Type'), C('trim_type', 'Trim Type'), C('trim_code', 'Trim Code'), C('style', 'Style'), C('shade', 'Garment Shade'), C('mat_shade', 'Raw Mat Shade'), C('size', 'Size'),
            C('consumption', 'Consumption', 'number'), C('wastage', 'Wastage %', 'number'), C('unit', 'Unit'), C('qty', 'Total Qty', 'number', true),
            C('price', 'Price', 'number'), C('line_value', 'Value', 'money', true), C('sourced_by', 'Bought By'), C('vendor', 'Vendor'), C('remark', 'Remarks')], rows: lines }]}
          source={{ endpoint: `/bom/${encodeURIComponent(sel.tas_no)}` }} />
        <Header subtitle="TAS · EXECUTIVE BOM VIEW" />

        {/* Executive Summary Header */}
        <div style={{
          margin: '12px 16px 0', padding: '16px 20px', borderRadius: 14,
          background: 'linear-gradient(135deg, rgba(124,58,237,0.12) 0%, rgba(2,132,199,0.06) 100%)',
          border: '1px solid rgba(124,58,237,0.28)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button onClick={() => { setSel(null); setDetail(null); }} style={{
              width: 34, height: 34, borderRadius: 8, border: '1px solid var(--border)',
              background: 'var(--bg2)', color: accent, fontSize: 18, fontWeight: 800, cursor: 'pointer'
            }}>←</button>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: 20, color: 'var(--text)' }}>{sel.tas_no}</span>
                <span style={{ padding: '3px 10px', borderRadius: 20, background: accent, color: '#fff', fontSize: 10.5, fontWeight: 800 }}>
                  {sel.product_type || 'TROUSER'}
                </span>
                {sel.entryno && <span style={{ padding: '3px 10px', borderRadius: 20, background: 'var(--bg2)', border: '1px solid var(--border)', fontSize: 11, fontWeight: 700 }}>BOM #{sel.entryno}</span>}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>
                Buyer: <b>{titleCase(sel.buyer)}</b> · Style: <b>{sel.style_name || '—'}</b> · Factory: <b>{titleCase(sel.factory) || '—'}</b> · Order: <b>{fmt(orderQty)} pcs</b>
              </div>
            </div>

            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={() => setDetailViewMode(v => v === 'list' ? 'pivot' : 'list')} style={{
                padding: '8px 16px', borderRadius: 8, border: `2px solid ${accent}`,
                background: detailViewMode === 'pivot' ? accent : 'var(--bg2)',
                color: detailViewMode === 'pivot' ? '#fff' : accent, fontSize: 12, fontWeight: 800, cursor: 'pointer'
              }}>
                📊 {detailViewMode === 'pivot' ? 'Switch to Detailed List View' : 'Pivot Matrix View (Size × Shade × Quality)'}
              </button>
              <button onClick={() => setAddFor(sel)} style={{
                padding: '8px 16px', borderRadius: 8, border: 'none',
                background: 'linear-gradient(135deg, #7c3aed, #4f46e5)', color: '#fff', fontSize: 12, fontWeight: 800, cursor: 'pointer'
              }}>
                ✏️ Edit / Upgrade BOM
              </button>
              <button onClick={() => printBomSheet('full', sheet, all)} style={{
                padding: '8px 14px', borderRadius: 8, border: '1.5px solid #0284c7',
                background: 'rgba(2,132,199,0.12)', color: '#0284c7', fontSize: 12, fontWeight: 800, cursor: 'pointer'
              }}>
                🖨️ Print Master BOM
              </button>
              <button onClick={() => printBomSheet('factory_card', sheet, all)} style={{
                padding: '8px 14px', borderRadius: 8, border: '1.5px solid #059669',
                background: 'rgba(5,150,105,0.12)', color: '#059669', fontSize: 12, fontWeight: 800, cursor: 'pointer'
              }}>
                🖨️ Print Factory Trim Card
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(175px, 1fr))', gap: 10, marginTop: 14 }}>
            {BOM_TYPES.map(bt => {
              const bRows = lines.filter(l => (l.bom_type || 'SEWING') === bt.key);
              const bVal = bRows.reduce((a, l) => a + Number(l.line_value || 0), 0);
              const bPct = total > 0 ? ((bVal / total) * 100).toFixed(1) : '0.0';
              const bPerPc = orderQty > 0 ? (bVal / orderQty).toFixed(2) : '0.00';
              const bTrims = new Set(bRows.map(l => l.trim_type)).size;
              return (
                <div key={bt.key} onClick={() => setDf({ ...df, bom_type: df.bom_type === bt.key ? '' : bt.key })}
                  style={{ padding: '11px 14px', borderRadius: 10, background: df.bom_type === bt.key ? bt.bg : 'var(--bg2)', border: `1.5px solid ${df.bom_type === bt.key ? bt.color : bt.border}`, cursor: 'pointer' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 10, fontWeight: 800, color: bt.color }}>{bt.icon} {bt.label}</span>
                    <span style={{ padding: '2px 7px', borderRadius: 10, background: bt.bg, color: bt.color, fontSize: 11, fontWeight: 800 }}>{bPct}%</span>
                  </div>
                  <div style={{ fontSize: 19, fontWeight: 800, color: 'var(--text)', marginTop: 3 }}>₹{money(bVal)}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text2)', marginTop: 3 }}>
                    <span>{bTrims} Trims ({bRows.length} ln)</span>
                    <b style={{ color: bt.color }}>₹{bPerPc}/pc</b>
                  </div>
                  <div style={{ height: 5, borderRadius: 3, background: 'var(--border)', marginTop: 5, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${bPct}%`, background: bt.color }} />
                  </div>
                </div>
              );
            })}
            <div style={{ padding: '11px 14px', borderRadius: 10, background: 'var(--bg2)', border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, fontWeight: 800, color: '#15803d' }}>
                <span>RISHABH VS FACTORY</span>
                <span>{total > 0 ? Math.round((rwplVal / total) * 100) : 0}% / {total > 0 ? Math.round((facVal / total) * 100) : 0}%</span>
              </div>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: '#15803d', marginTop: 5 }}>RWPL: ₹{money(rwplVal)}</div>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: '#b45309', marginTop: 3 }}>Factory: ₹{money(facVal)}</div>
            </div>
            <div style={{ padding: '11px 14px', borderRadius: 10, background: 'linear-gradient(135deg, rgba(124,58,237,0.16), rgba(79,70,229,0.08))', border: `2px solid ${accent}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 10, fontWeight: 800, color: accent }}>💰 TOTAL BOM COST</span>
                <span style={{ padding: '2px 7px', borderRadius: 10, background: accent, color: '#fff', fontSize: 10.5, fontWeight: 800 }}>100%</span>
              </div>
              <div style={{ fontSize: 21, fontWeight: 800, color: accent, marginTop: 3 }}>₹{money(total)}</div>
              <div style={{ fontSize: 11, color: 'var(--text)', marginTop: 3, fontWeight: 700 }}><b>₹{perGarment.toFixed(2)} / Garment</b></div>
            </div>
          </div>
        </div>

        {/* Filter Bar */}
        <div style={{ padding: '12px 16px 0', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <input value={dq} onChange={e => setDq(e.target.value)} placeholder="🔍 Search trim, spec, style, garment shade, raw mat shade, vendor…"
            style={{ ...fsel, minWidth: 280 }} />
          {['bom_type', 'sourced_by', 'trim_type', 'style', 'shade', 'size'].map(k =>
            drop(k, { bom_type: 'BOM Type', sourced_by: 'Bought By', trim_type: 'Trim Category', style: 'Style', shade: 'Garment Shade', size: 'Size' }[k])
          )}
          {filtered && (
            <button onClick={() => { setDq(''); setDf({ bom_type: '', sourced_by: '', trim_type: '', style: '', shade: '', size: '' }); }}
              style={{ height: 32, padding: '0 14px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text2)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              Reset Filters ✕
            </button>
          )}
        </div>

        {!detail && <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>Loading BOM breakdown…</div>}
        {detail && all.length === 0 && <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No BOM lines for this TAS.</div>}

        {detail && all.length > 0 && detailViewMode === 'pivot' ? (
          <BomPivotMatrix tasNo={sel.tas_no} mode="bom" lines={lines} />
        ) : (
        /* Grouped by 3 High-Level BOM Sections -> Trim Category */
        <div style={{ padding: '12px 16px 24px' }}>
          {BOM_TYPES.map(bt => {
            const secLines = lines.filter(l => (l.bom_type || 'SEWING') === bt.key);
            if (!secLines.length) return null;
            const secTotal = secLines.reduce((a, l) => a + Number(l.line_value || 0), 0);
            const byTrim = secLines.reduce((m, l) => {
              const k = l.trim_type || 'OTHER';
              (m[k] = m[k] || []).push(l);
              return m;
            }, {});

            return (
              <div key={bt.key} style={{ marginBottom: 20, borderRadius: 12, border: `1.5px solid ${bt.border}`, background: 'var(--bg2)', overflow: 'hidden' }}>
                <div style={{ padding: '10px 16px', background: bt.bg, borderBottom: `1px solid ${bt.border}`, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 16 }}>{bt.icon}</span>
                  <span style={{ fontWeight: 800, fontSize: 14, color: bt.color }}>{bt.label}</span>
                  <span style={{ fontSize: 11.5, color: 'var(--text2)' }}>
                    {Object.keys(byTrim).length} Trim Categories · {secLines.length} Expanded PO Lines
                  </span>
                  <span style={{ marginLeft: 'auto', fontWeight: 800, fontSize: 13.5, color: bt.color }}>
                    ₹{money(secTotal)} ({total > 0 ? ((secTotal / total) * 100).toFixed(1) : '0.0'}% of Total BOM)
                  </span>
                </div>

                <div style={{ padding: '10px 12px', overflowX: 'auto' }}>
                  {Object.entries(byTrim).map(([trimName, ls]) => {
                    const sub = ls.reduce((a, l) => a + Number(l.line_value || 0), 0);
                    const totQty = ls.reduce((a, l) => a + Number(l.qty || 0), 0);
                    return (
                      <div key={trimName} style={{ minWidth: 1120, marginBottom: 10, border: LINE, borderRadius: 8, overflow: 'hidden' }}>
                        <div style={{ padding: '7px 12px', background: 'var(--bg3)', display: 'flex', alignItems: 'center', gap: 10 }}>
                          {ls[0]?.image_url && <img src={ls[0].image_url} alt="" style={{ width: 24, height: 24, objectFit: 'cover', borderRadius: 4 }} />}
                          <b style={{ color: 'var(--text)', fontSize: 12.5 }}>{trimName}</b>
                          <span style={{ padding: '1px 8px', borderRadius: 10, background: 'var(--bg2)', fontSize: 10.5, color: 'var(--text2)', fontWeight: 700 }}>
                            {ls[0]?.cons_type || 'Style + Shade Wise'}
                          </span>
                          <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                            Total Qty: <b style={{ color: 'var(--text)' }}>{fmt(totQty)} {ls[0]?.unit || ''}</b> ({ls.length} line{ls.length !== 1 ? 's' : ''})
                          </span>
                          <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 800, color: bt.color }}>₹{money(sub)}</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: GC, background: 'var(--bg)', fontSize: 9.5, letterSpacing: 0.7, color: 'var(--text3)', fontWeight: 800, textTransform: 'uppercase', borderTop: LINE }}>
                          {cols.map(([label, right], i) => <span key={label} style={td(i, right)}>{label}</span>)}
                        </div>
                        {ls.map((l, i) => (
                          <div key={i} style={{ display: 'grid', gridTemplateColumns: GC, borderTop: LINE, fontSize: 12, background: i % 2 ? 'var(--bg2)' : 'transparent' }}>
                            <span style={{ ...td(0), color: 'var(--text)', fontWeight: 600 }} title={l.trim_code || ''}>{l.trim_code || '—'}</span>
                            <span style={{ ...td(1), color: 'var(--text2)' }} title={l.style || ''}>{l.style || '—'}</span>
                            <span style={{ ...td(2), color: '#0284c7', fontWeight: 700 }} title="Garment Shade">{l.shade || 'ALL'}</span>
                            <span style={{ ...td(3), color: '#6d28d9', fontWeight: 700 }} title="Raw Material Shade">{l.mat_shade || (l.shade ? `DTM (${l.shade})` : '—')}</span>
                            <span style={{ ...td(4), color: 'var(--text2)' }}>{l.size || l.mat_size || '—'}</span>
                            <span style={{ ...td(5, 1), color: 'var(--text2)' }}>{num(l.consumption, 3)}</span>
                            <span style={{ ...td(6, 1), color: 'var(--text3)' }}>{num(l.wastage, 2)}</span>
                            <span style={{ ...td(7), color: 'var(--text3)' }}>{l.unit || '—'}</span>
                            <span style={{ ...td(8, 1), fontWeight: 800, color: 'var(--text)' }}>{num(l.qty, 0)}</span>
                            <span style={{ ...td(9, 1), color: 'var(--text2)' }}>{num(l.price, 2)}</span>
                            <span style={{ ...td(10, 1), fontWeight: 700, color: accent }}>₹{money(l.line_value)}</span>
                            <span style={{ ...td(11), fontWeight: 800, color: l.sourced_by === 'FACTORY' ? '#b45309' : '#15803d' }}>{l.sourced_by || 'RWPL'}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        )}
      </div>
    );
  }

  const cols = isDone
    ? [C('tas_no', 'TAS'), C('product_type', 'Product'), C('buyer', 'Buyer'), C('style_name', 'Style'), C('factory', 'Factory'),
       C('entryno', 'BOM No'), C('entry_dt', 'BOM Date', 'date'), C('sewing_trims', 'Sewing', 'int'), C('finishing_trims', 'Finishing', 'int'),
       C('packing_trims', 'Packing', 'int'), C('line_count', 'Lines', 'int', true), C('bom_value', 'BOM Value', 'money', true)]
    : [C('tas_no', 'TAS'), C('product_type', 'Product'), C('buyer', 'Buyer'), C('style_name', 'Style'), C('factory', 'Factory'), C('merchandiser', 'Merchandiser'), C('pending_qty', 'Pending Pcs', 'int', true)];

  return (
    <div className="screen" style={{ paddingBottom: 32 }}>
      <ExportBar reportKey={isDone ? 'bom-done' : 'bom-pending'} title={`BOM - ${isDone ? 'Created' : 'Pending'}`}
        sections={[{ name: isDone ? 'BOM created' : 'BOM pending', columns: cols, rows: list }]} source={{ endpoint: '/bom' }} />
      <Header subtitle="TAS · EXECUTIVE BOM COMMAND CENTER" />

      {/* ─── TOP COMMAND CENTER HEADER & KPI TILES ─── */}
      <div style={{
        margin: '12px 16px 0', padding: '18px 20px', borderRadius: 14,
        background: 'linear-gradient(135deg, rgba(124,58,237,0.12) 0%, rgba(79,70,229,0.05) 55%, rgba(5,150,105,0.07) 100%)',
        border: '1px solid rgba(124,58,237,0.28)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
          <button onClick={() => setView('tas-daily')} style={{
            width: 34, height: 34, borderRadius: 8, border: '1px solid var(--border)',
            background: 'var(--bg2)', color: accent, fontSize: 18, fontWeight: 800, cursor: 'pointer'
          }}>←</button>
          <div>
            <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: 21, color: 'var(--text)' }}>
              ☰ Executive BOM Command Center
              <button onClick={() => setShowTraining(true)} style={{
                marginLeft: 12, padding: '5px 12px', borderRadius: 20, border: 'none',
                background: 'linear-gradient(135deg, #0284c7, #4f46e5)', color: '#fff',
                fontSize: 11.5, fontWeight: 800, cursor: 'pointer', boxShadow: '0 2px 8px rgba(2,132,199,0.35)'
              }}>
                🎥 Watch 7-Step BOM Training Guide
              </button>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>
              3-Level Architecture: <b>BOM Type (Sewing · Finishing · Packing)</b> → <b>183 Trim Categories</b> → <b>612 Detailed Raw Materials</b>
            </div>
          </div>

          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ padding: '6px 12px', borderRadius: 10, background: 'var(--bg2)', border: '1px solid rgba(79,70,229,0.35)', textAlign: 'right' }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: '#4f46e5', letterSpacing: 0.6 }}>🧵 SEWING BOM</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#4f46e5' }}>₹{money(totalSewVal)} ({pctOf(totalSewVal, totalBomValue)}%)</div>
            </div>
            <div style={{ padding: '6px 12px', borderRadius: 10, background: 'var(--bg2)', border: '1px solid rgba(2,132,199,0.35)', textAlign: 'right' }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: '#0284c7', letterSpacing: 0.6 }}>✨ FINISHING BOM</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#0284c7' }}>₹{money(totalFinVal)} ({pctOf(totalFinVal, totalBomValue)}%)</div>
            </div>
            <div style={{ padding: '6px 12px', borderRadius: 10, background: 'var(--bg2)', border: '1px solid rgba(5,150,105,0.35)', textAlign: 'right' }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: '#059669', letterSpacing: 0.6 }}>📦 PACKING BOM</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#059669' }}>₹{money(totalPackVal)} ({pctOf(totalPackVal, totalBomValue)}%)</div>
            </div>
            <div style={{ padding: '6px 14px', borderRadius: 10, background: 'linear-gradient(135deg, rgba(124,58,237,0.16), rgba(79,70,229,0.08))', border: `1.5px solid ${accent}`, textAlign: 'right' }}>
              <div style={{ fontSize: 9.5, fontWeight: 800, color: accent, letterSpacing: 0.7 }}>💰 TOTAL BOM COST (100%)</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: accent }}>₹{money(totalBomValue)}</div>
            </div>
          </div>
        </div>

        {/* 3 Main Navigation Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
          <div onClick={() => setTab('pending')} style={{
            padding: '14px 18px', borderRadius: 12, cursor: 'pointer',
            border: tab === 'pending' ? '2px solid #dc2626' : '1px solid var(--border)',
            background: tab === 'pending' ? 'rgba(220,38,38,0.10)' : 'var(--bg2)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: '#dc2626' }}>⏳ BOM PENDING (ACTION REQUIRED)</div>
              <div style={{ fontSize: 11.5, color: 'var(--text2)', marginTop: 4 }}>Click any TAS to launch Zero-Miss BOM Entry</div>
            </div>
            <div style={{ fontSize: 30, fontWeight: 800, color: '#dc2626' }}>{rows ? fmt(pendRows.length) : '…'}</div>
          </div>

          <div onClick={() => setTab('done')} style={{
            padding: '14px 18px', borderRadius: 12, cursor: 'pointer',
            border: tab === 'done' ? '2px solid #059669' : '1px solid var(--border)',
            background: tab === 'done' ? 'rgba(5,150,105,0.10)' : 'var(--bg2)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: '#059669' }}>✓ BOM CREATED & IMPORTED</div>
              <div style={{ fontSize: 11.5, color: 'var(--text2)', marginTop: 4 }}>Sewing · Finishing · Packing BOMs ready to print/edit</div>
            </div>
            <div style={{ fontSize: 30, fontWeight: 800, color: '#059669' }}>{rows ? fmt(doneRows.length) : '…'}</div>
          </div>

          <div onClick={() => setTab('masters')} style={{
            padding: '14px 18px', borderRadius: 12, cursor: 'pointer',
            border: tab === 'masters' ? `2px solid ${accent}` : '1px solid var(--border)',
            background: tab === 'masters' ? `${accent}15` : 'var(--bg2)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: accent }}>⚙️ 5 PARALLEL MASTERS & VENDORS</div>
              <div style={{ fontSize: 11.5, color: 'var(--text2)', marginTop: 4 }}>Vendors · Raw Materials · Trims · Product Checklists · Contracts</div>
            </div>
            <div style={{ fontSize: 30, fontWeight: 800, color: accent }}>5</div>
          </div>
        </div>
      </div>

      {showTraining && <BomTrainingModal onClose={() => setShowTraining(false)} />}
      {tab === 'masters' ? (
        <BomMastersManager onBack={() => setTab('pending')} />
      ) : (
        <>
          {/* Product Filter Pills + Search */}
          <div style={{ padding: '12px 16px 8px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {productChoices.map(pt => (
              <button key={pt} onClick={() => setProdFilter(pt)} style={{
                padding: '5px 12px', borderRadius: 20,
                border: prodFilter === pt ? `1.5px solid ${accent}` : '1px solid var(--border)',
                background: prodFilter === pt ? `${accent}18` : 'var(--bg2)',
                color: prodFilter === pt ? accent : 'var(--text2)', fontSize: 11, fontWeight: 800, cursor: 'pointer'
              }}>
                {pt}
              </button>
            ))}

            <input value={q} onChange={e => setQ(e.target.value)} placeholder="🔍 Search TAS / buyer / style / factory / merchandiser…"
              style={{ marginLeft: 'auto', minWidth: 280, padding: '8px 12px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 12, outline: 'none' }} />
          </div>

          <div style={{ margin: '0 16px 24px', border: '1px solid var(--border)', borderRadius: 12, background: 'var(--bg2)', overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: isDone ? 1120 : 780, borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={hd}>TAS No</th>
                <th style={hd}>Garment Product</th>
                <th style={hd}>Buyer / Company</th>
                <th style={hd}>Style Name</th>
                <th style={hd}>Factory</th>
                <th style={hd}>Merchandiser</th>
                {isDone
                  ? <>
                      <th style={hd}>BOM No</th>
                      <th style={hd}>BOM Date</th>
                      <th style={hd}>3-Section Breakdown (Sewing · Finishing · Packing)</th>
                      <th style={hd}>Rishabh / Factory</th>
                      <th style={{ ...hd, textAlign: 'right' }}>PO Lines</th>
                      <th style={{ ...hd, textAlign: 'right' }}>Total Value</th>
                    </>
                  : <>
                      <th style={{ ...hd, textAlign: 'right' }}>Order Pcs</th>
                      <th style={{ ...hd, textAlign: 'center' }}>Action</th>
                    </>}
              </tr></thead>
              <tbody>
                {!rows && <tr><td colSpan={isDone ? 12 : 8} style={{ ...cell, textAlign: 'center', color: 'var(--text3)', padding: 30 }}>Loading TAS BOMs…</td></tr>}
                {rows && list.length === 0 && (
                  <tr><td colSpan={isDone ? 12 : 8} style={{ ...cell, textAlign: 'center', color: 'var(--text3)', padding: 30 }}>
                    {isDone ? 'No matching BOMs found.' : 'Every open TAS in this filter has a BOM!'}</td></tr>
                )}
                {list.map(r => (
                  <tr key={r.tas_no} onClick={() => (isDone ? open(r) : setAddFor(r))} style={{ cursor: 'pointer' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg3)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                    <td style={{ ...cell, fontWeight: 800, color: accent }}>{r.tas_no} ›</td>
                    <td style={cell}>
                      <span style={{ padding: '2px 8px', borderRadius: 6, background: `${accent}15`, color: accent, fontSize: 10.5, fontWeight: 800 }}>
                        {r.product_type || 'TROUSER'}
                      </span>
                    </td>
                    <td style={{ ...cell, fontWeight: 600 }}>{titleCase(r.buyer) || '—'}</td>
                    <td style={{ ...cell, color: 'var(--text2)', fontWeight: 600 }}>{r.style_name || '—'}</td>
                    <td style={cell}>{titleCase(r.factory) || '—'}</td>
                    <td style={cell}>{titleCase(r.merchandiser) || '—'}</td>
                    {isDone
                      ? <>
                          <td style={{ ...cell, fontWeight: 700 }}>{r.entryno || '—'}</td>
                          <td style={cell}>{r.entry_dt || '—'}</td>
                          <td style={cell}>
                            <div style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}>
                              <span style={{ padding: '2px 7px', borderRadius: 5, background: 'rgba(79,70,229,0.12)', color: '#4f46e5', fontSize: 10.5, fontWeight: 800 }}>
                                🧵 Sew: ₹{money(r.sewing_value)} ({pctOf(r.sewing_value, r.bom_value)}%)
                              </span>
                              <span style={{ padding: '2px 7px', borderRadius: 5, background: 'rgba(2,132,199,0.12)', color: '#0284c7', fontSize: 10.5, fontWeight: 800 }}>
                                ✨ Fin: ₹{money(r.finishing_value)} ({pctOf(r.finishing_value, r.bom_value)}%)
                              </span>
                              <span style={{ padding: '2px 7px', borderRadius: 5, background: 'rgba(5,150,105,0.12)', color: '#059669', fontSize: 10.5, fontWeight: 800 }}>
                                📦 Pack: ₹{money(r.packing_value)} ({pctOf(r.packing_value, r.bom_value)}%)
                              </span>
                            </div>
                          </td>
                          <td style={cell}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#15803d' }}>RWPL: {fmt(r.rwpl_trims)}</span>
                            <span style={{ color: 'var(--text3)' }}> · </span>
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#b45309' }}>Fac: {fmt(r.factory_trims)}</span>
                          </td>
                          <td style={{ ...cell, textAlign: 'right', fontWeight: 700 }}>{fmt(r.line_count)}</td>
                          <td style={{ ...cell, textAlign: 'right', fontWeight: 800, color: accent }}>
                            <div>₹{money(r.bom_value)}</div>
                            {r.order_qty > 0 && <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 600 }}>₹{(Number(r.bom_value || 0) / Number(r.order_qty)).toFixed(2)}/pc</div>}
                          </td>
                        </>
                      : <>
                          <td style={{ ...cell, textAlign: 'right', fontWeight: 800 }}>{fmt(r.pending_qty || r.order_qty)}</td>
                          <td style={{ ...cell, textAlign: 'center' }}>
                            <span style={{ padding: '4px 10px', borderRadius: 6, background: accent, color: '#fff', fontSize: 10.5, fontWeight: 800 }}>
                              + Build BOM →
                            </span>
                          </td>
                        </>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function StageProofModal({ order, onClose, onOpenUpdate }) {
  const [stages, setStages] = useState(null);
  const [showAllStages, setShowAllStages] = useState(false);

  const orderId = (order.type === 'fabric' && !String(order.order_no).startsWith('GSP/'))
    ? `GSP/${order.order_no}`
    : order.order_no;

  useEffect(() => {
    api(`/orders/${encodeURIComponent(orderId)}/stages${order.type === 'fabric' ? '?type=fabric' : ''}`)
      .then(d => {
        const list = (Array.isArray(d) ? d : []).filter(x => !(x.stage_category || '').toLowerCase().includes('estimated tna'));
        setStages(list);
      })
      .catch(() => setStages([]));
  }, [orderId, order.type]);

  const fmtD = s => {
    if (!s) return '—';
    const p = String(s).slice(0, 10).split('-');
    return p.length === 3 ? `${p[2]}-${p[1]}-${p[0].slice(2)}` : s;
  };

  const cut = Number(order.dpr_cut || 0);
  const inp = Number(order.dpr_input || 0);
  const out = Number(order.dpr_output || 0);
  const hasDpr = cut > 0 || inp > 0 || out > 0;
  const pct = inp > 0 ? ((out * 100) / inp).toFixed(1) : '0.0';

  // Core milestones to show by default (clean & simple)
  const KEY_GARMENT_NAMES = new Set([
    'FAB ETD', 'FAB ETA', 'FAB INHOUSE',
    'PCD', 'IN LINE', 'LINE OUT',
    'FI', 'EX FACT'
  ]);

  const filteredStages = useMemo(() => {
    if (!stages) return [];
    if (order.type === 'fabric' || showAllStages) return stages;
    // Show only Garment Routing (4_x) key milestones + any completed 4_x stage + current stage
    const gStages = stages.filter(s => String(s.sort_order || '').startsWith('4_'));
    const baseList = gStages.length ? gStages : stages;
    return baseList.filter(s => {
      const nm = String(s.stage_name || '').trim().toUpperCase();
      const isCurr = nm === String(order.current_stage || '').trim().toUpperCase();
      return KEY_GARMENT_NAMES.has(nm) || s.status === 'done' || isCurr;
    }).slice().reverse();
  }, [stages, showAllStages, order.type, order.current_stage]);

  let whyText = '';
  if (order.current_stage === 'PCD') {
    whyText = `Cutting started on ${fmtD(order.pcd_dt)} (${cut.toLocaleString('en-IN')} pcs cut, 0 pcs issued to line).`;
  } else if (order.current_stage === 'IN LINE') {
    whyText = `Line Input started on ${fmtD(order.inline_dt)} (${inp.toLocaleString('en-IN')} pcs in line, ${out.toLocaleString('en-IN')} pcs output = ${pct}%, which is < 98%).`;
  } else if (order.current_stage === 'LINE OUT') {
    whyText = `Line Output reached ${pct}% (≥ 98%) on ${fmtD(order.lineout_dt)} (${out.toLocaleString('en-IN')} / ${inp.toLocaleString('en-IN')} pcs).`;
  } else if (order.current_stage === 'FAB INHOUSE' && order.fab_inhouse_dt) {
    whyText = `Fabric received inhouse at factory on ${fmtD(order.fab_inhouse_dt)} (via Factory Fab Inhouse module) → Ready for Cutting (PCD).`;
  } else if (order.last_done_stage) {
    whyText = `"${order.last_done_stage}" was completed on ${fmtD(order.last_done_dt)} → Next pending stage is "${order.current_stage}".`;
  } else {
    whyText = `Waiting for "${order.current_stage || '—'}" to be completed.`;
  }

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      backdropFilter: 'blur(2px)', zIndex: 520,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 14,
        padding: '18px 20px', width: '100%', maxWidth: 500, maxHeight: '84vh',
        display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.5)'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text)' }}>
              {order.order_no} <span style={{ fontWeight: 500, color: 'var(--text3)', fontSize: 13 }}>· {order.buyer || '—'}</span>
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--text3)', marginTop: 2 }}>
              Factory: <b style={{ color: 'var(--text2)' }}>{order.factory || '—'}</b> · Order Qty: <b style={{ color: 'var(--text)' }}>{Number(order.order_qty || 0).toLocaleString('en-IN')} {order.unit}</b>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 18, cursor: 'pointer', padding: 4 }}>✕</button>
        </div>

        {/* Simple 2-Box Summary: Last Completed vs Current Stage */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
          <div style={{ background: 'rgba(22,163,74,0.08)', border: '1px solid rgba(22,163,74,0.25)', borderRadius: 10, padding: '10px 12px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#16a34a', textTransform: 'uppercase', letterSpacing: 0.5 }}>✓ Last Completed</div>
            <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text)', marginTop: 3 }}>{order.last_done_stage || 'None'}</div>
            <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>Date: <b>{fmtD(order.last_done_dt)}</b></div>
          </div>
          <div style={{ background: 'rgba(26,79,156,0.10)', border: '1px solid rgba(26,79,156,0.30)', borderRadius: 10, padding: '10px 12px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#1A4F9C', textTransform: 'uppercase', letterSpacing: 0.5 }}>▶ Current Stage</div>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#1A4F9C', marginTop: 3 }}>{order.current_stage || '—'}</div>
            <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>
              {(hasDpr && ['PCD', 'IN LINE', 'LINE OUT'].includes(order.current_stage)) || (order.current_stage === 'FAB INHOUSE' && order.fab_inhouse_dt)
                ? <>Actual Date: <b>{fmtD(order.fab_inhouse_dt || order.stage_dt)}</b></>
                : <>Status: <b>Pending</b></>}
            </div>
          </div>
        </div>

        {/* 1-Line Reason */}
        <div style={{ fontSize: 11.5, color: 'var(--text2)', background: 'var(--bg3)', borderRadius: 8, padding: '8px 12px', marginBottom: 12, lineHeight: 1.4 }}>
          <b>Why:</b> {whyText}
        </div>

        {/* DPR Production Box — ONLY shown if Cutting / Input / Output > 0 */}
        {order.type !== 'fabric' && hasDpr && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
              Daily Production (DPR) Totals
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 10px' }}>
                <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700 }}>CUTTING (PCD)</div>
                <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text)', marginTop: 2 }}>{cut.toLocaleString('en-IN')} pcs</div>
                <div style={{ fontSize: 10, color: 'var(--teal)' }}>Date: {fmtD(order.pcd_dt)}</div>
              </div>
              <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 10px' }}>
                <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700 }}>INPUT (IN LINE)</div>
                <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text)', marginTop: 2 }}>{inp.toLocaleString('en-IN')} pcs</div>
                <div style={{ fontSize: 10, color: 'var(--teal)' }}>Date: {fmtD(order.inline_dt)}</div>
              </div>
              <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 10px' }}>
                <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700 }}>OUTPUT (LINE OUT)</div>
                <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text)', marginTop: 2 }}>
                  {out.toLocaleString('en-IN')} <span style={{ fontSize: 11, color: Number(pct) >= 98 ? '#16a34a' : '#d97706' }}>({pct}%)</span>
                </div>
                <div style={{ fontSize: 10, color: 'var(--teal)' }}>Date: {fmtD(order.lineout_dt)}</div>
              </div>
            </div>
          </div>
        )}

        {/* Clean Key Milestones Table */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {showAllStages ? 'All Routing Stages' : 'Key Garment Milestones'}
            </span>
            {order.type !== 'fabric' && stages && stages.length > filteredStages.length && (
              <button
                type="button"
                onClick={() => setShowAllStages( true )}
                style={{ background: 'none', border: 'none', color: '#1A4F9C', fontSize: 11, fontWeight: 600, cursor: 'pointer', padding: 0 }}
              >
                Show all {stages.length} stages
              </button>
            )}
            {showAllStages && (
              <button
                type="button"
                onClick={() => setShowAllStages( false )}
                style={{ background: 'none', border: 'none', color: '#1A4F9C', fontSize: 11, fontWeight: 600, cursor: 'pointer', padding: 0 }}
              >
                Show key milestones only
              </button>
            )}
          </div>

          {!stages ? (
            <div style={{ padding: 14, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>Loading…</div>
          ) : (
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1.8fr 90px 90px 75px', gap: 6, padding: '6px 10px', background: 'var(--bg3)', fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase' }}>
                <span>Stage</span><span>Plan Date</span><span>Actual Date</span><span>Status</span>
              </div>
              {filteredStages.map((s, idx) => {
                const isDone = s.status === 'done';
                const isCurr = String(s.stage_name || '').trim().toUpperCase() === String(order.current_stage || '').trim().toUpperCase();
                return (
                  <div key={s.uniqueid || idx} style={{
                    display: 'grid', gridTemplateColumns: '1.8fr 90px 90px 75px', gap: 6, padding: '6px 10px',
                    fontSize: 11.5, borderTop: '1px solid var(--border)',
                    background: isCurr ? 'rgba(26,79,156,0.12)' : 'transparent',
                    fontWeight: isCurr ? 700 : 400
                  }}>
                    <span style={{ color: isCurr ? '#1A4F9C' : (isDone ? 'var(--text)' : 'var(--text2)') }}>
                      {isDone ? '✓ ' : (isCurr ? '▶ ' : '○ ')}{s.stage_name}
                    </span>
                    <span style={{ color: 'var(--text3)' }}>{fmtD(s.planned_date)}</span>
                    <span style={{ color: isDone ? '#16a34a' : 'var(--text3)', fontWeight: isDone ? 700 : 400 }}>{fmtD(s.actual_date)}</span>
                    <span style={{ fontSize: 10, fontWeight: 700, color: isDone ? '#16a34a' : (isCurr ? '#1A4F9C' : 'var(--text3)') }}>
                      {isDone ? 'DONE' : (isCurr ? 'CURRENT' : 'PENDING')}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
          {isProcessDrivenStage(order.current_stage, order.type) ? (
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', background: 'var(--bg3)', border: '1px solid var(--border)', padding: '5px 10px', borderRadius: 6 }} title={processStageReason(order.current_stage)}>
              ⚙ Auto from Process ({processStageReason(order.current_stage)})
            </span>
          ) : (
            <button
              type="button"
              onClick={() => { onClose(); if (onOpenUpdate) onOpenUpdate(order); }}
              style={{ padding: '6px 12px', borderRadius: 7, border: '1px solid #1A4F9C', background: 'rgba(26,79,156,0.1)', color: '#1A4F9C', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}
            >
              ✎ Update Stage
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            style={{ padding: '6px 16px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text)', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// Stages that come automatically from ERP / TNA processes (DPR, Pre-Order, Costing, Shipment, Invoice)
// and must NOT be manually date-updated via the Update Stage button.
const PROCESS_DRIVEN_GARMENT_STAGES = {
  'FAB INHOUSE': 'Factory Fab Inhouse Module',
  'PCD': 'DPR Cutting Entry',
  'IN LINE': 'DPR Line Input Entry',
  'LINE OUT': 'DPR Line Output (≥ 98%)',
  'ORDER SHARING WITH SBD': 'Pre-Order Checklist (TECPAC / SPECS / BOM)',
  'ORDER SHARING WITH FACTORY': 'Pre-Order Checklist (TECPAC / SPECS / BOM)',
  'GARMENT SALES CONTRACT - BANGALDESH SUPPLIER': 'Factory Cost Sheet Process',
  'GARMENT SALES CONTRACT - BANGLADESH SUPPLIER': 'Factory Cost Sheet Process',
  'DELIVERY CONFIRMATION AND PRICE CLOSER': 'BD Cost Sheet Approval Process',
  'EX FACT': 'Shipment Update Module',
  'BCP/ONWARD DISPATCH': 'Garment Invoice Process',
};

function isProcessDrivenStage(stageName, orderType = 'garment') {
  if (orderType === 'fabric') return false;
  const k = String(stageName || '').trim().toUpperCase();
  return Object.prototype.hasOwnProperty.call(PROCESS_DRIVEN_GARMENT_STAGES, k);
}

function processStageReason(stageName) {
  const k = String(stageName || '').trim().toUpperCase();
  return PROCESS_DRIVEN_GARMENT_STAGES[k] || 'ERP Process';
}

function UpdateStageModal({ order, onClose, onSuccess }) {
  const [stages, setStages] = useState(null);
  const [selectedUid, setSelectedUid] = useState('');
  const [actualDate, setActualDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [remark, setRemark] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const orderId = (order.type === 'fabric' && !String(order.order_no).startsWith('GSP/'))
    ? `GSP/${order.order_no}`
    : order.order_no;

  useEffect(() => {
    api(`/orders/${encodeURIComponent(orderId)}/stages${order.type === 'fabric' ? '?type=fabric' : ''}`)
      .then(d => {
        // Filter out Estimated TNA and any process-driven stages (e.g. PCD, IN LINE, LINE OUT)
        const list = (Array.isArray(d) ? d : []).filter(x => {
          if ((x.stage_category || '').toLowerCase().includes('estimated tna')) return false;
          if (order.type !== 'fabric' && isProcessDrivenStage(x.stage_name, order.type)) return false;
          return true;
        });
        setStages(list);
        let match = list.find(s => s.status !== 'done' && String(s.stage_name || '').trim().toUpperCase() === String(order.current_stage || '').trim().toUpperCase());
        if (!match) match = list.find(s => s.status !== 'done');
        if (!match && list.length) match = list[0];
        if (match) setSelectedUid(String(match.uniqueid));
      })
      .catch(() => {
        setStages([]);
        setErr('Could not load stages for this order.');
      });
  }, [orderId, order.type, order.current_stage]);

  function submit(e) {
    e?.preventDefault();
    if (!selectedUid) { setErr('Please select a stage'); return; }
    if (!actualDate) { setErr('Please select an actual date'); return; }
    setSaving(true);
    setErr('');
    const [y, m, d] = actualDate.split('-');
    const ddmmyyyy = `${d}/${m}/${y}`;
    api('/stages/update', {
      method: 'POST',
      body: JSON.stringify({
        uniqueid: selectedUid,
        actual_date: ddmmyyyy,
        remark: remark || '',
        source: order.type === 'fabric' ? 'fabric' : 'garment'
      })
    }).then(res => {
      setSaving(false);
      if (res && (res.success || res.message)) {
        const stageObj = (stages || []).find(s => String(s.uniqueid) === String(selectedUid));
        onSuccess(res.message || `✓ Stage updated for ${order.order_no}`, {
          order_no: order.order_no,
          stage_name: stageObj?.stage_name || ''
        });
        onClose();
      } else {
        setErr(res?.error || 'Failed to update stage');
      }
    }).catch(e => {
      setSaving(false);
      setErr(e?.message || 'Network error');
    });
  }

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      backdropFilter: 'blur(3px)', zIndex: 500,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 16,
        padding: '22px 24px', width: '100%', maxWidth: 460, boxShadow: '0 12px 48px rgba(0,0,0,0.6)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Update Current Stage</div>
            <div style={{ fontSize: 12, color: 'var(--teal)', fontWeight: 600, marginTop: 2 }}>{order.order_no} · {order.buyer || 'Unknown Buyer'}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 20, cursor: 'pointer', padding: 4 }}>✕</button>
        </div>

        <div style={{ background: 'var(--bg3)', borderRadius: 8, padding: '10px 12px', marginBottom: 16, fontSize: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text2)', marginBottom: 4 }}>
            <span>Style: <b>{order.style || order.design_no || '—'}</b></span>
            <span>Pending: <b>{Number(order.qty || 0).toLocaleString('en-IN')} {order.unit}</b></span>
          </div>
          <div style={{ color: 'var(--text3)', fontSize: 11 }}>
            Current Stage: <span style={{ color: 'var(--gold)', fontWeight: 600 }}>{order.current_stage || 'None'}</span>
          </div>
        </div>

        {err && <div style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', padding: '8px 12px', borderRadius: 8, fontSize: 12, marginBottom: 12 }}>{err}</div>}

        {!stages ? (
          <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>Loading order stages…</div>
        ) : stages.length === 0 ? (
          <div style={{ padding: '16px 0', color: 'var(--text3)', fontSize: 12 }}>No routing stages found for this order.</div>
        ) : (
          <form onSubmit={submit}>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text3)', letterSpacing: 0.5, marginBottom: 6, textTransform: 'uppercase' }}>
                Select Stage
              </label>
              <select
                value={selectedUid}
                onChange={e => setSelectedUid(e.target.value)}
                style={{ width: '100%', padding: '9px 12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 13, outline: 'none' }}
              >
                {stages.map(s => {
                  const isDone = s.status === 'done';
                  const pl = s.planned_date ? ` (Plan: ${s.planned_date.split('-').reverse().join('-')})` : '';
                  return (
                    <option key={s.uniqueid} value={String(s.uniqueid)}>
                      {isDone ? '✓ ' : '○ '}{s.stage_name}{pl} {isDone ? '[Completed]' : ''}
                    </option>
                  );
                })}
              </select>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text3)', letterSpacing: 0.5, marginBottom: 6, textTransform: 'uppercase' }}>
                Actual Date
              </label>
              <input
                type="date"
                value={actualDate}
                onChange={e => setActualDate(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 13, outline: 'none' }}
              />
            </div>

            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text3)', letterSpacing: 0.5, marginBottom: 6, textTransform: 'uppercase' }}>
                Remark (Optional)
              </label>
              <input
                type="text"
                placeholder="Add remark or reason..."
                value={remark}
                onChange={e => setRemark(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 13, outline: 'none' }}
              />
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text3)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || !selectedUid}
                style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: 'var(--teal)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', letterSpacing: 0.5 }}
              >
                {saving ? 'Updating…' : '✓ Update Stage'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function ShortCloseModal({ order, onClose, onSuccess }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const orderId = (order.type === 'fabric' && !String(order.order_no).startsWith('GSP/'))
    ? `GSP/${order.order_no}`
    : order.order_no;

  function submit(e) {
    e?.preventDefault();
    setSaving(true);
    setErr('');
    api(`/orders/${encodeURIComponent(orderId)}/status`, {
      method: 'POST',
      body: JSON.stringify({
        statusid: 6, // 6 = 'Close'
        reason: reason || null,
        source: order.type === 'fabric' ? 'fabric' : 'garment'
      })
    }).then(res => {
      setSaving(false);
      if (res && res.success) {
        onSuccess(`✓ Order ${order.order_no} short closed successfully.`, {
          order_no: order.order_no,
          closed: true
        });
        onClose();
      } else {
        setErr(res?.error || 'Failed to short close order');
      }
    }).catch(e => {
      setSaving(false);
      setErr(e?.message || 'Network error');
    });
  }

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      backdropFilter: 'blur(3px)', zIndex: 500,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 16,
        padding: '22px 24px', width: '100%', maxWidth: 440, boxShadow: '0 12px 48px rgba(0,0,0,0.6)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 22 }}>⚠️</span>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#ef4444' }}>Short Close Order</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 20, cursor: 'pointer', padding: 4 }}>✕</button>
        </div>

        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
          <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600, marginBottom: 4 }}>
            Close {order.order_no}?
          </div>
          <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.5 }}>
            Buyer: <b>{order.buyer || '—'}</b> · Style: <b>{order.style || order.design_no || '—'}</b>
          </div>
          <div style={{ fontSize: 12, color: '#ef4444', fontWeight: 600, marginTop: 4 }}>
            Pending: {Number(order.qty || 0).toLocaleString('en-IN')} {order.unit}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8, lineHeight: 1.4 }}>
            Short closing this order will set its status to <b>Close</b> in ERP. Remaining pending quantity will no longer appear in monthly shipment targets.
          </div>
        </div>

        {err && <div style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', padding: '8px 12px', borderRadius: 8, fontSize: 12, marginBottom: 12 }}>{err}</div>}

        <form onSubmit={submit}>
          <div style={{ marginBottom: 18 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text3)', letterSpacing: 0.5, marginBottom: 6, textTransform: 'uppercase' }}>
              Reason for Short Closure (Optional)
            </label>
            <input
              type="text"
              placeholder="e.g. Buyer cancelled remaining balance, partial dispatch final..."
              value={reason}
              onChange={e => setReason(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 13, outline: 'none' }}
            />
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text3)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: '#ef4444', color: '#fff', fontSize: 12, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', letterSpacing: 0.5 }}
            >
              {saving ? 'Closing…' : 'Confirm Short Close'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Module-level persistent state for Monthly Shipment Target so drilling into Order Detail
// and coming back preserves the selected month, view mode, filters and scroll position.
let _shipmentTargetState = {
  selMonth: null,
  detailMode: 'ason',
  type: 'garment',
  gspSub: 'gsp',
  q: '',
  groupByStage: true,
  selStageFilter: 'ALL',
  collapsedStages: {},
  sort: { key: 'order_no', dir: 1 }
};

// ===== MONTHLY SHIPMENT TARGET — garment (pcs/FI date) + fabric (mtrs/ex-factory
// date) with current routing stage, filterable by month. =====
function MonthlyTargetView({ setView, Header, onOrderClick, pushBack }) {
  const accent = '#1A4F9C';
  const teal = 'var(--teal)';
  const [rows, setRows] = useState(null);
  const [selMonth, _setSelMonth] = useState(_shipmentTargetState.selMonth);   // null = month summary; else drill into a month
  const [detailMode, _setDetailMode] = useState(_shipmentTargetState.detailMode); // default 'ason' = cumulative up to the month; 'month' = that month only
  const [type, _setType] = useState(_shipmentTargetState.type);   // 'all', 'garment' (TAS), 'fabric' (GSP)
  const [gspSub, _setGspSub] = useState(_shipmentTargetState.gspSub);   // within GSP: 'gsp' (Fabric orders) or 'tas' (TAS orders using FAB ETD)
  const [q, _setQ] = useState(_shipmentTargetState.q);
  const [stageModalOrder, setStageModalOrder] = useState(null);
  const [proofModalOrder, setProofModalOrder] = useState(null);
  const [shortCloseOrder, setShortCloseOrder] = useState(null);
  const [toastMsg, setToastMsg] = useState(null);
  const [groupByStage, _setGroupByStage] = useState(_shipmentTargetState.groupByStage);          // default: grouped stage-wise with last stage at top
  const [selStageFilter, _setSelStageFilter] = useState(_shipmentTargetState.selStageFilter);     // filter to a single stage from top summary pills
  const [collapsedStages, _setCollapsedStages] = useState(_shipmentTargetState.collapsedStages);      // { [stageName]: boolean }
  const [sort, _setSort] = useState(_shipmentTargetState.sort);   // {key, dir:1|-1}; null = by target date

  const setSelMonth = val => {
    _shipmentTargetState.selMonth = typeof val === 'function' ? val(_shipmentTargetState.selMonth) : val;
    _setSelMonth(_shipmentTargetState.selMonth);
  };
  const setDetailMode = val => {
    _shipmentTargetState.detailMode = typeof val === 'function' ? val(_shipmentTargetState.detailMode) : val;
    _setDetailMode(_shipmentTargetState.detailMode);
  };
  const setType = val => {
    _shipmentTargetState.type = typeof val === 'function' ? val(_shipmentTargetState.type) : val;
    _setType(_shipmentTargetState.type);
  };
  const setGspSub = val => {
    _shipmentTargetState.gspSub = typeof val === 'function' ? val(_shipmentTargetState.gspSub) : val;
    _setGspSub(_shipmentTargetState.gspSub);
  };
  const setQ = val => {
    _shipmentTargetState.q = typeof val === 'function' ? val(_shipmentTargetState.q) : val;
    _setQ(_shipmentTargetState.q);
  };
  const setGroupByStage = val => {
    _shipmentTargetState.groupByStage = typeof val === 'function' ? val(_shipmentTargetState.groupByStage) : val;
    _setGroupByStage(_shipmentTargetState.groupByStage);
  };
  const setSelStageFilter = val => {
    _shipmentTargetState.selStageFilter = typeof val === 'function' ? val(_shipmentTargetState.selStageFilter) : val;
    _setSelStageFilter(_shipmentTargetState.selStageFilter);
  };
  const setCollapsedStages = val => {
    _shipmentTargetState.collapsedStages = typeof val === 'function' ? val(_shipmentTargetState.collapsedStages) : val;
    _setCollapsedStages(_shipmentTargetState.collapsedStages);
  };
  const setSort = val => {
    _shipmentTargetState.sort = typeof val === 'function' ? val(_shipmentTargetState.sort) : val;
    _setSort(_shipmentTargetState.sort);
  };

  // Opening a month shows a long list. Put the month heading itself at the top of the
  // screen (past the app header and the export bar) and reset the table to Order No,
  // instead of keeping the scroll position from the month grid.
  const tableScrollRef = useRef(null);
  const headingRef = useRef(null);
  useEffect(() => {
    setSelStageFilter('ALL');
    setCollapsedStages({});
    if (!selMonth) return;
    if (tableScrollRef.current) tableScrollRef.current.scrollLeft = 0;
    if (headingRef.current) headingRef.current.scrollIntoView({ block: 'start' });
  }, [selMonth]);

  const reload = (bypass = false) => {
    const url = bypass ? `/orders/shipment-target?_t=${Date.now()}` : '/orders/shipment-target';
    api(url).then(d => setRows(Array.isArray(d) ? d : [])).catch(() => setRows([]));
  };
  const showToast = msg => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 3500);
  };
  const handleUpdateSuccess = (msg, change) => {
    showToast(msg);
    if (change?.closed) {
      setRows(prev => (prev || []).filter(r => r.order_no !== change.order_no));
    } else if (change?.stage_name) {
      setRows(prev => (prev || []).map(r => r.order_no === change.order_no ? { ...r, current_stage: change.stage_name } : r));
    }
    reload(true);
  };

  useEffect(() => { reload(); }, []);
  const monthLabel = m => m === 'none' ? 'No Date' : (() => { const [y, mo] = m.split('-'); return new Date(+y, +mo - 1, 1).toLocaleString('en', { month: 'long', year: 'numeric' }); })();
  const monthKeyOf = r => (r.target_date && r.target_date > '1900-01-02' && !r.target_date.startsWith('1900')) ? r.target_date.slice(0, 7) : 'none';
  // base = type + gspSub + search filter (month handled by the drill)
  // Always exclude EX FACT (and onward dispatch stages) from Monthly Shipment Target
  const base = useMemo(() => (rows || []).filter(r => {
    const stUp = String(r.current_stage || '').trim().toUpperCase();
    if (stUp === 'EX FACT' || stUp === 'BCP/ONWARD DISPATCH' || stUp === 'WH DEL / GRN DATE') return false;
    if (type === 'garment') {
      if (r.type !== 'garment') return false;
    } else if (type === 'fabric') {
      if (gspSub === 'tas') {
        if (r.type !== 'garment_fab_etd') return false;
      } else {
        if (r.type !== 'fabric') return false;
      }
    } else if (type === 'all') {
      if (r.type !== 'garment' && r.type !== 'fabric') return false;
    }
    const s = q.trim().toLowerCase();
    if (s && !`${r.order_no} ${r.buyer || ''} ${r.factory || ''} ${r.style || ''} ${r.design_no || ''} ${r.fabric_name || ''} ${r.fabric_supplier || ''} ${r.current_stage || ''}`.toLowerCase().includes(s)) return false;
    return true;
  }), [rows, type, gspSub, q]);

  // month-wise summary
  const { msum, monthKeys, cumMap } = useMemo(() => {
    const summary = {};
    base.forEach(r => {
      const k = monthKeyOf(r);
      const g = summary[k] || (summary[k] = { count: 0, pcs: 0, mtrs: 0 });
      g.count++;
      g[r.unit] = (g[r.unit] || 0) + (Number(r.qty) || 0);
    });
    const keys = Object.keys(summary).sort((a, b) => (a === 'none' ? '9999-99' : a).localeCompare(b === 'none' ? '9999-99' : b));
    const cumulative = {};
    let _cum = { pcs: 0, mtrs: 0 };
    keys.forEach(k => {
      if (k === 'none') return;
      _cum = { pcs: _cum.pcs + summary[k].pcs, mtrs: _cum.mtrs + summary[k].mtrs };
      cumulative[k] = { ..._cum };
    });
    return { msum: summary, monthKeys: keys, cumMap: cumulative };
  }, [base]);

  // Column sorting. Numbers compare as numbers, dates as their ISO string, the rest as text;
  // blanks always sit last. Clicking a header cycles ascending, descending, default order.
  const SORT_VAL = {
    order_no: r => r.order_no || '', buyer: r => titleCase(r.buyer) || '', factory: r => titleCase(r.factory) || '',
    style: r => r.style || '', design_no: r => r.design_no || '',
    fabric_name: r => r.fabric_name || '', fabric_supplier: r => r.fabric_supplier || '',
    fabric_cut: r => (r.fabric_cut == null ? null : Number(r.fabric_cut)),
    fabric_mtr: r => (r.fabric_mtr == null ? null : Number(r.fabric_mtr)),
    order_qty: r => (r.order_qty == null ? null : Number(r.order_qty)), qty: r => Number(r.qty || 0),
    unit: r => r.unit || '', target_date: r => r.target_date || '', month: r => (r.target_date || '').slice(0, 7),
    current_stage: r => r.current_stage || '', stage_dt: r => r.stage_dt || '',
  };
  const cmp = (a, b, key) => {
    const va = SORT_VAL[key](a), vb = SORT_VAL[key](b);
    const ea = va == null || va === '', eb = vb == null || vb === '';
    if (ea || eb) return ea && eb ? 0 : (ea ? 1 : -1);      // blanks last, whichever direction
    return typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb), undefined, { numeric: true });
  };

  // detail rows for the selected month ('month' = that month only; 'ason' = cumulative up to it)
  const shown = useMemo(() => {
    if (!selMonth) return [];
    const list = base.filter(r => {
      const k = monthKeyOf(r);
      if (selMonth === 'none') return k === 'none';
      return (detailMode === 'ason') ? (k !== 'none' && k <= selMonth) : (k === selMonth);
    });
    if (sort && SORT_VAL[sort.key]) {
      const blank = r => { const v = SORT_VAL[sort.key](r); return v == null || v === ''; };
      return list.sort((a, b) => (blank(a) || blank(b) ? cmp(a, b, sort.key) : sort.dir * cmp(a, b, sort.key))
        || (a.order_no || '').localeCompare(b.order_no || '', undefined, { numeric: true }));
    }
    return list.sort((a, b) => ((a.target_date || '9999-99') + (a.order_no || '')).localeCompare((b.target_date || '9999-99') + (b.order_no || '')));
  }, [base, selMonth, detailMode, sort]);

  // Routing progression rank: higher rank = closer to shipment (appears at the TOP).
  const STAGE_RANK = {
    'WH DEL / GRN DATE': 423,
    'BCP/ONWARD DISPATCH': 422,
    'EX FACT': 421,
    'FI': 420,
    'GPT': 419,
    'LINE OUT': 418,
    'IN LINE': 417,
    'PCD': 416,
    'INDIA TRIMS IN HOUSE': 415,
    'INDIA TRIMS ETA': 414,
    'INDIA TRIMS ETD': 413,
    'CHINA TRIMS IN HOUSE': 412,
    'CHINA TRIMS ETA': 411,
    'CHINA TRIMS ETD': 410,
    'FAB INHOUSE': 409,
    'FAB ETA': 408,
    'FAB SHIPMENT DOCUMENT SHARING': 407,
    'FAB ETD': 406,
    'PP DATE': 405,
    'FABRIC LC FROM BD TO CHINA': 404,
    'DELIVERY CONFIRMATION AND PRICE CLOSER': 403,
    'GARMENT SALES CONTRACT - BANGALDESH SUPPLIER': 402,
    'GARMENT SALES CONTRACT - BANGLADESH SUPPLIER': 402,
    'ORDER SHARING WITH SBD': 401,
    'ORDER SHARING WITH FACTORY': 401,
    // Fabric routing stages (3_x)
    'FPT': 312,
    'IND PSS APPROVAL': 311,
    'BD PSS': 310,
    'INSPECTION': 309,
    'FINISHING': 308,
    'WEAVING': 307,
    'DYEING': 306,
    'YARN INHOUSE': 305,
    'LAB DIP APPROVAL': 304,
    'HANDLOOM': 303,
    'FAB PI': 302,
    'FAB PO': 301,
  };
  const stageRankOf = s => {
    const k = String(s || '').trim().toUpperCase();
    return STAGE_RANK[k] ?? 100;
  };

  // All stage groups for the selected month (sorted with LAST stage at the TOP)
  const stageGroupsAll = useMemo(() => {
    const map = new Map();
    shown.forEach(r => {
      const st = String(r.current_stage || 'NO STAGE').trim() || 'NO STAGE';
      let g = map.get(st);
      if (!g) {
        g = { stage: st, rank: stageRankOf(st), rows: [], order_pcs: 0, order_mtrs: 0, pend_pcs: 0, pend_mtrs: 0, fabric_mtr: 0 };
        map.set(st, g);
      }
      g.rows.push(r);
      g.fabric_mtr += Number(r.fabric_mtr || 0);
      if (r.unit === 'mtrs') {
        g.order_mtrs += Number(r.order_qty || 0);
        g.pend_mtrs  += Number(r.qty || 0);
      } else {
        g.order_pcs += Number(r.order_qty || 0);
        g.pend_pcs  += Number(r.qty || 0);
      }
    });
    return Array.from(map.values()).sort((a, b) => (b.rank - a.rank) || a.stage.localeCompare(b.stage));
  }, [shown]);

  const visibleStageGroups = useMemo(() => {
    if (selStageFilter === 'ALL') return stageGroupsAll;
    return stageGroupsAll.filter(g => g.stage === selStageFilter);
  }, [stageGroupsAll, selStageFilter]);

  const visibleFlatRows = useMemo(() => {
    if (selStageFilter === 'ALL') return shown;
    return shown.filter(r => (String(r.current_stage || 'NO STAGE').trim() || 'NO STAGE') === selStageFilter);
  }, [shown, selStageFilter]);

  const isTasInGsp = type === 'fabric' && gspSub === 'tas';

  // Build stage-grouped rows (Clean Stage Group Header + Stage Orders + Stage Subtotal at bottom) for PDF / Excel export
  const buildExportGroupedRows = groups => {
    const out = [];
    groups.forEach(g => {
      const ordVal = g.order_pcs || g.order_mtrs || 0;
      const pendVal = g.pend_pcs || g.pend_mtrs || 0;
      const unitStr = g.pend_pcs ? 'pcs' : (g.pend_mtrs ? 'mtr' : 'pcs');
      out.push({
        _rowType: 'group_header',
        _label: `STAGE: ${g.stage}   (${g.rows.length} ${g.rows.length === 1 ? 'Order' : 'Orders'})`,
        order_no: `STAGE: ${g.stage} (${g.rows.length} ${g.rows.length === 1 ? 'Order' : 'Orders'})`,
      });
      g.rows.forEach(r => out.push({
        ...r,
        month: r.target_date ? monthLabel(r.target_date.slice(0, 7)) : '—'
      }));
      out.push({
        _rowType: 'subtotal',
        _label: `${g.stage} TOTAL (${g.rows.length} ${g.rows.length === 1 ? 'Order' : 'Orders'})`,
        order_no: `${g.stage} TOTAL (${g.rows.length})`,
        order_qty: ordVal,
        qty: pendVal,
        ...(isTasInGsp ? { fabric_mtr: Math.round(g.fabric_mtr || 0) } : {}),
        unit: unitStr,
        current_stage: g.stage,
        dpr_cut: g.rows.reduce((s, r) => s + Number(r.dpr_cut || 0), 0),
        dpr_input: g.rows.reduce((s, r) => s + Number(r.dpr_input || 0), 0),
        dpr_output: g.rows.reduce((s, r) => s + Number(r.dpr_output || 0), 0),
      });
    });
    return out;
  };

  // All-months stage groups (Last Stage at Top) so PDF/Excel from the top-level screen also has Stage Summary & Stage-Wise Orders
  const baseStageGroups = useMemo(() => {
    const map = new Map();
    base.forEach(r => {
      const st = String(r.current_stage || 'NO STAGE').trim() || 'NO STAGE';
      let g = map.get(st);
      if (!g) {
        g = { stage: st, rank: stageRankOf(st), rows: [], order_pcs: 0, order_mtrs: 0, pend_pcs: 0, pend_mtrs: 0, fabric_mtr: 0 };
        map.set(st, g);
      }
      g.rows.push(r);
      g.fabric_mtr += Number(r.fabric_mtr || 0);
      if (r.unit === 'mtrs') {
        g.order_mtrs += Number(r.order_qty || 0);
        g.pend_mtrs  += Number(r.qty || 0);
      } else {
        g.order_pcs += Number(r.order_qty || 0);
        g.pend_pcs  += Number(r.qty || 0);
      }
    });
    return Array.from(map.values()).sort((a, b) => (b.rank - a.rank) || a.stage.localeCompare(b.stage));
  }, [base]);

  const { totalOrderQty, totalPendingQty, totalFabricMtr } = useMemo(() => {
    const orderQty = { pcs: 0, mtrs: 0 };
    const pendingQty = { pcs: 0, mtrs: 0 };
    let fabMtr = 0;
    visibleFlatRows.forEach(r => {
      orderQty[r.unit] = (orderQty[r.unit] || 0) + (Number(r.order_qty) || 0);
      pendingQty[r.unit] = (pendingQty[r.unit] || 0) + (Number(r.qty) || 0);
      fabMtr += Number(r.fabric_mtr || 0);
    });
    return { totalOrderQty: orderQty, totalPendingQty: pendingQty, totalFabricMtr: fabMtr };
  }, [visibleFlatRows]);
  // The date column holds a different thing per section: FI for garment orders, the
  // ex-factory date for GSP, FAB ETD for the TAS rows shown inside GSP.
  const dateColLabel = isTasInGsp ? 'FAB ETD' : type === 'fabric' ? 'Ex Factory' : type === 'garment' ? 'FI' : 'FI / Ex-Factory';
  const activeColor = isTasInGsp ? 'var(--teal)' : (type === 'fabric' ? 'var(--teal)' : accent);
  // Each text column keeps a readable minimum and grows with the screen; the fixed ones
  // (qty, unit, dates, actions) stay put. minWidth below = the sum, so narrow screens scroll
  // sideways instead of squeezing Buyer/Style/Design No down to a few characters.
  const GC = isTasInGsp
    ? '96px minmax(130px, 1fr) minmax(120px, 0.9fr) minmax(150px, 1.1fr) minmax(140px, 1fr) minmax(130px, 0.95fr) 86px 76px 92px 86px 46px 88px 105px minmax(140px, 1.1fr) 210px'
    : '96px minmax(140px, 1.15fr) minmax(130px, 0.95fr) minmax(170px, 1.35fr) 86px 86px 46px 88px 105px minmax(150px, 1.2fr) 210px';
  const unitLabel = u => u === 'mtrs' ? 'mtr' : u;
  const inp = { padding: '7px 10px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 12, outline: 'none' };
  const fmtDate = s => { if (!s) return '—'; const [y, m, d] = s.split('-'); return `${d}-${m}-${y.slice(2)}`; };

  return (
    <div className="screen">
      <Header subtitle="MONTHLY SHIPMENT TARGET" />
      <ExportBar reportKey="monthly-shipment-target"
        title={selMonth ? `Monthly Shipment Target - ${monthLabel(selMonth)}` : 'Monthly Shipment Target'}
        subtitle={selMonth ? `${detailMode === 'ason' ? `As on ${monthLabel(selMonth)}` : monthLabel(selMonth)}${selStageFilter !== 'ALL' ? ` | Stage: ${selStageFilter}` : ''} | Stage-Wise (Last Stage First)` : 'All Months | Stage-Wise (Last Stage First)'}
        sections={selMonth
          ? [
              { name: `${monthLabel(selMonth)} - Stage-Wise Summary (Last Stage First)`,
                columns: [C('stage', 'Stage (Last Stage -> First Stage)'), C('orders', 'Orders', 'int', true), C('order_qty', 'Order Qty', 'int', true), ...(isTasInGsp ? [C('fabric_mtr', 'Fabric Mtr', 'int', true)] : []), C('pend_qty', 'Pending Qty', 'int', true), C('unit', 'Unit')],
                rows: visibleStageGroups.map(g => ({ stage: g.stage, orders: g.rows.length, order_qty: g.order_pcs || g.order_mtrs, fabric_mtr: Math.round(g.fabric_mtr || 0), pend_qty: g.pend_pcs || g.pend_mtrs, unit: g.pend_pcs ? 'pcs' : 'mtr' })) },
              { name: `${monthLabel(selMonth)} - Stage-Wise Orders (Last Stage First)`,
                columns: [C('order_no', 'Order No'), C('buyer', 'Buyer'), C('factory', 'Factory'), C('style', 'Style'),
                          ...(isTasInGsp ? [C('fabric_name', 'Fabric Name'), C('fabric_supplier', 'Fabric Supplier')] : []),
                          C('order_qty', 'Order Qty', 'int', true),
                          ...(isTasInGsp ? [C('fabric_cut', 'Fabric Cut', 'dec2'), C('fabric_mtr', 'Fabric Mtr', 'int', true)] : []),
                          C('qty', 'Pending', 'int', true),
                          C('unit', 'Unit'), C('target_date', dateColLabel, 'date'), C('month', 'Month'),
                          C('current_stage', 'Current Stage'), C('stage_dt', 'Stage Date', 'date')],
                rows: groupByStage ? buildExportGroupedRows(visibleStageGroups) : visibleFlatRows.map(r => ({ ...r, month: r.target_date ? monthLabel(r.target_date.slice(0, 7)) : '—' })) }
            ]
          : [
              { name: 'Month Summary',
                columns: [C('month', 'Month'), C('orders', 'Orders', 'int', true), C('pcs', 'Pcs', 'int', true), C('mtrs', 'Mtrs', 'int', true)],
                rows: monthKeys.map(k => ({ month: monthLabel(k), orders: msum[k].count, pcs: msum[k].pcs, mtrs: msum[k].mtrs })) },
              { name: 'Stage-Wise Summary (Last Stage First)',
                columns: [C('stage', 'Stage (Last Stage -> First Stage)'), C('orders', 'Orders', 'int', true), C('order_qty', 'Order Qty', 'int', true), ...(isTasInGsp ? [C('fabric_mtr', 'Fabric Mtr', 'int', true)] : []), C('pend_qty', 'Pending Qty', 'int', true), C('unit', 'Unit')],
                rows: baseStageGroups.map(g => ({ stage: g.stage, orders: g.rows.length, order_qty: g.order_pcs || g.order_mtrs, fabric_mtr: Math.round(g.fabric_mtr || 0), pend_qty: g.pend_pcs || g.pend_mtrs, unit: g.pend_pcs ? 'pcs' : 'mtr' })) },
              { name: 'Stage-Wise Orders (Last Stage First)',
                columns: [C('order_no', 'Order No'), C('buyer', 'Buyer'), C('factory', 'Factory'), C('style', 'Style'),
                          ...(isTasInGsp ? [C('fabric_name', 'Fabric Name'), C('fabric_supplier', 'Fabric Supplier')] : []),
                          C('order_qty', 'Order Qty', 'int', true),
                          ...(isTasInGsp ? [C('fabric_cut', 'Fabric Cut', 'dec2'), C('fabric_mtr', 'Fabric Mtr', 'int', true)] : []),
                          C('qty', 'Pending', 'int', true),
                          C('unit', 'Unit'), C('target_date', dateColLabel, 'date'), C('month', 'Month'),
                          C('current_stage', 'Current Stage'), C('stage_dt', 'Stage Date', 'date')],
                rows: buildExportGroupedRows(baseStageGroups) }
            ]}
        source={{ endpoint: '/orders/shipment-target' }} />
      <div ref={headingRef} style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
        <button onClick={() => {
          if (selMonth) {
            setSelMonth(null);
          } else {
            setView('rw');
          }
        }} style={{ background: 'none', border: 'none', color: activeColor, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: activeColor }}>{selMonth ? monthLabel(selMonth) : 'Monthly Shipment Target'}</div>
        <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>{selMonth ? `${shown.length} orders` : `${monthKeys.length} month${monthKeys.length !== 1 ? 's' : ''}`}</div>
      </div>
      <div style={{ padding: '10px 16px 4px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {[['all', 'All'], ['garment', 'Garment (TAS)'], ['fabric', 'Fabric (GSP)']].map(([k, t]) => (
          <button key={k} onClick={() => { setType(k); setSelMonth(null); }}
            style={{
              padding: '6px 12px', borderRadius: 14, fontSize: 11, cursor: 'pointer',
              border: `1px solid ${type === k ? (k === 'fabric' ? 'var(--teal)' : accent) : 'var(--border)'}`,
              background: type === k ? (k === 'fabric' ? 'rgba(0,168,150,0.14)' : `${accent}18`) : 'transparent',
              color: type === k ? (k === 'fabric' ? 'var(--teal)' : accent) : 'var(--text3)',
              fontWeight: type === k ? 700 : 400
            }}>
            {t}
          </button>
        ))}

        {type === 'fabric' && (
          <span style={{ display: 'inline-flex', gap: 6, marginLeft: 4, paddingLeft: 10, borderLeft: '1px solid var(--border)', alignItems: 'center' }}>
            <span style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700, letterSpacing: 0.5 }}>SUBSECTION:</span>
            {[['gsp', 'GSP'], ['tas', 'TAS']].map(([sk, st]) => (
              <button key={sk} onClick={() => { setGspSub(sk); setSelMonth(null); }}
                style={{
                  padding: '5px 12px', borderRadius: 14, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  border: `1px solid ${gspSub === sk ? 'var(--teal)' : 'var(--border)'}`,
                  background: gspSub === sk ? 'var(--teal)' : 'transparent',
                  color: gspSub === sk ? '#fff' : 'var(--text2)'
                }}>
                {st}
              </button>
            ))}
          </span>
        )}

        {selMonth && selMonth !== 'none' && (
          <span style={{ display: 'inline-flex', gap: 6, marginLeft: 6, paddingLeft: 10, borderLeft: '1px solid var(--border)' }}>
            {[['month', 'This month'], ['ason', `As on ${monthLabel(selMonth)}`]].map(([k, t]) => (
              <button key={k} onClick={() => setDetailMode(k)} style={{ padding: '6px 12px', borderRadius: 14, fontSize: 11, cursor: 'pointer', border: `1px solid ${detailMode === k ? activeColor : 'var(--border)'}`, background: detailMode === k ? activeColor : 'transparent', color: detailMode === k ? '#fff' : 'var(--text3)', fontWeight: 600 }}>{t}</button>
            ))}
          </span>
        )}
        {selMonth && (
          <span style={{ display: 'inline-flex', gap: 6, marginLeft: 4, paddingLeft: 10, borderLeft: '1px solid var(--border)' }}>
            <button
              onClick={() => setGroupByStage(g => !g)}
              style={{
                padding: '6px 12px', borderRadius: 14, fontSize: 11, cursor: 'pointer', fontWeight: 700,
                border: `1px solid ${groupByStage ? activeColor : 'var(--border)'}`,
                background: groupByStage ? `${activeColor}18` : 'transparent',
                color: groupByStage ? activeColor : 'var(--text3)'
              }}
              title="Toggle stage-wise grouping (Last stage at top)"
            >
              {groupByStage ? '✓ Grouped by Stage (Last Stage Top)' : 'Group by Stage'}
            </button>
          </span>
        )}
        <input style={{ ...inp, flex: 1, minWidth: 180 }} placeholder="search order, buyer, factory, style, stage…" value={q} onChange={e => setQ(e.target.value)} />
      </div>

      {/* MONTH-WISE SUMMARY (default) — click a month to drill into its orders */}
      {!selMonth && (
        <div style={{ padding: '12px 16px 24px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
          {!rows && <div style={{ color: 'var(--text3)', fontSize: 12 }}>Loading…</div>}
          {rows && monthKeys.length === 0 && <div style={{ color: 'var(--text3)', fontSize: 12 }}>No orders.</div>}
          {monthKeys.map(k => {
            const g = msum[k];
            return (
              <div key={k} onClick={() => {
                if (pushBack) pushBack(() => setSelMonth(null));
                setSelMonth(k);
                setDetailMode('ason');
              }}
                style={{ border: '1px solid var(--border)', borderLeft: `3px solid ${k === 'none' ? 'var(--text3)' : activeColor}`, borderRadius: 10, padding: 14, cursor: 'pointer', background: 'var(--bg2)', transition: 'background 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg3)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg2)'; }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: k === 'none' ? 'var(--text3)' : activeColor }}>{monthLabel(k)}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{g.count} order{g.count !== 1 ? 's' : ''}</div>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 6 }}>
                  {g.pcs ? `${g.pcs.toLocaleString('en-IN')} pcs` : ''}{g.pcs && g.mtrs ? ' · ' : ''}{g.mtrs ? `${g.mtrs.toLocaleString('en-IN')} mtr` : ''}
                </div>
                {cumMap[k] && (
                  <div style={{ fontSize: 11, color: activeColor, marginTop: 4, fontWeight: 600 }}>
                    As on: {cumMap[k].pcs ? `${cumMap[k].pcs.toLocaleString('en-IN')} pcs` : ''}{cumMap[k].pcs && cumMap[k].mtrs ? ' · ' : ''}{cumMap[k].mtrs ? `${cumMap[k].mtrs.toLocaleString('en-IN')} mtr` : ''}
                  </div>
                )}
                <div style={{ fontSize: 9, color: 'var(--text3)', marginTop: 8, letterSpacing: 1 }}>OPEN →</div>
              </div>
            );
          })}
        </div>
      )}

      {selMonth && (
      <div ref={tableScrollRef} style={{ padding: '8px 16px 24px', overflowX: 'auto' }}>
        {/* STAGE-WISE SUMMARY PILLS (Last Stage -> First Stage) */}
        {stageGroupsAll.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, padding: '10px 12px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10, alignItems: 'center' }}>
            <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--text3)', letterSpacing: 0.7, textTransform: 'uppercase', marginRight: 4 }}>
              Stage Summary (Last → First):
            </span>
            <button
              onClick={() => setSelStageFilter('ALL')}
              style={{
                padding: '5px 11px', borderRadius: 8, fontSize: 11, cursor: 'pointer', fontWeight: 700,
                border: `1px solid ${selStageFilter === 'ALL' ? activeColor : 'var(--border)'}`,
                background: selStageFilter === 'ALL' ? activeColor : 'var(--bg3)',
                color: selStageFilter === 'ALL' ? '#fff' : 'var(--text2)'
              }}
            >
              ALL ({shown.length})
            </button>
            {stageGroupsAll.map(g => {
              const active = selStageFilter === g.stage;
              const qtyLabel = g.pend_pcs ? `${g.pend_pcs.toLocaleString('en-IN')} pcs` : `${g.pend_mtrs.toLocaleString('en-IN')} mtr`;
              return (
                <button
                  key={g.stage}
                  onClick={() => setSelStageFilter(active ? 'ALL' : g.stage)}
                  style={{
                    padding: '5px 11px', borderRadius: 8, fontSize: 11, cursor: 'pointer',
                    border: `1px solid ${active ? activeColor : 'var(--border)'}`,
                    background: active ? `${activeColor}20` : 'var(--bg)',
                    color: active ? activeColor : 'var(--text)',
                    display: 'inline-flex', alignItems: 'center', gap: 6
                  }}
                  title="Click to filter this stage"
                >
                  <span style={{ fontWeight: 800, color: active ? activeColor : 'var(--text)' }}>{g.stage}</span>
                  <span style={{ fontSize: 10.5, color: 'var(--text3)', fontWeight: 600 }}>
                    {g.rows.length} ord · <b style={{ color: activeColor }}>{qtyLabel}</b>
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <div style={{ minWidth: isTasInGsp ? 1820 : 1380 }}>
          <div style={{ display: 'grid', gridTemplateColumns: GC, gap: 8, padding: '8px 10px', background: 'var(--bg3)', color: 'var(--text)', fontWeight: 700, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', borderRadius: '6px 6px 0 0', border: '1px solid var(--border)' }}>
            {[['order_no', 'Order No'], ['buyer', 'Buyer'], ['factory', 'Factory'], ['style', 'Style'],
              ...(isTasInGsp ? [['fabric_name', 'Fabric Name'], ['fabric_supplier', 'Fabric Supplier']] : []),
              ['order_qty', 'Order Qty', 'right'],
              ...(isTasInGsp ? [['fabric_cut', 'Fabric Cut', 'right'], ['fabric_mtr', 'Fabric Mtr', 'right']] : []),
              ['qty', 'Pending', 'right'], ['unit', 'Unit', 'center'],
              ['target_date', dateColLabel], ['month', 'Month'], ['current_stage', 'Current Stage']
            ].map(([key, label, align]) => {
              const on = sort && sort.key === key;
              return (
                <span key={key} onClick={() => setSort(on ? (sort.dir === 1 ? { key, dir: -1 } : null) : { key, dir: 1 })}
                  title={on ? (sort.dir === 1 ? 'Sorted A→Z · click for Z→A' : 'Sorted Z→A · click to clear') : 'Click to sort'}
                  style={{ cursor: 'pointer', userSelect: 'none', color: on ? activeColor : 'inherit', fontWeight: on ? 800 : 'inherit',
                    display: 'flex', alignItems: 'center', gap: 3, justifyContent: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start' }}>
                  {label}<span style={{ fontSize: 8, opacity: on ? 1 : 0.35 }}>{on ? (sort.dir === 1 ? '▲' : '▼') : '⇅'}</span>
                </span>
              );
            })}
            <span style={{ textAlign: 'center' }}>Actions</span>
          </div>
          {!rows && <div style={{ padding: 24, color: 'var(--text3)', fontSize: 12 }}>Loading…</div>}
          {rows && visibleFlatRows.length === 0 && <div style={{ padding: 24, color: 'var(--text3)', fontSize: 12, border: '1px solid var(--border)', borderTop: 'none' }}>No orders for this filter.</div>}

          {groupByStage ? (
            visibleStageGroups.map(g => {
              const isCollapsed = !!collapsedStages[g.stage];
              const gOrdText = g.order_pcs ? g.order_pcs.toLocaleString('en-IN') : (g.order_mtrs ? g.order_mtrs.toLocaleString('en-IN') : '—');
              const gPendText = g.pend_pcs ? g.pend_pcs.toLocaleString('en-IN') : (g.pend_mtrs ? g.pend_mtrs.toLocaleString('en-IN') : '0');
              const gFabMtrText = Math.round(g.fabric_mtr || 0).toLocaleString('en-IN');
              const gUnit = g.pend_pcs ? 'pcs' : 'mtr';
              return (
                <React.Fragment key={g.stage}>
                  {/* STAGE GROUP HEADER ROW (Clean title only; stage totals appear at the bottom of the stage) */}
                  <div
                    onClick={() => setCollapsedStages(prev => ({ ...prev, [g.stage]: !prev[g.stage] }))}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                      background: `${activeColor}14`, borderLeft: `4px solid ${activeColor}`,
                      borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)',
                      cursor: 'pointer', userSelect: 'none'
                    }}
                    title="Click to expand / collapse stage group"
                  >
                    <span style={{ fontSize: 11, color: activeColor, fontWeight: 800 }}>{isCollapsed ? '▶' : '▼'}</span>
                    <span style={{ fontSize: 12.5, fontWeight: 800, color: activeColor, letterSpacing: 0.4 }}>{g.stage}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text2)', background: 'var(--bg2)', padding: '2px 8px', borderRadius: 10, border: '1px solid var(--border)' }}>
                      {g.rows.length} {g.rows.length === 1 ? 'Order' : 'Orders'}
                    </span>
                    {isCollapsed && (
                      <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--text2)', fontWeight: 600 }}>
                        Total: <b style={{ color: activeColor }}>{gPendText} {gUnit}</b>{isTasInGsp ? <> · Fab Mtr: <b style={{ color: activeColor }}>{gFabMtrText} mtr</b></> : null}
                      </span>
                    )}
                  </div>

                  {/* STAGE ROWS */}
                  {!isCollapsed && g.rows.map((r, i) => (
                    <div key={r.type + r.order_no + i} style={{ display: 'grid', gridTemplateColumns: GC, gap: 8, padding: '7px 10px', borderBottom: '1px solid var(--border)', borderLeft: '1px solid var(--border)', borderRight: '1px solid var(--border)', fontSize: 12, alignItems: 'center', background: i % 2 ? 'var(--bg2)' : 'transparent' }}>
                      <span
                        onClick={() => onOrderClick && onOrderClick({ tas_no: r.order_no, buyer: r.buyer, factory: r.factory, order_type: r.type === 'fabric' ? 'fabric' : 'garment' })}
                        style={{
                          fontWeight: 700,
                          color: r.type === 'fabric' ? 'var(--teal)' : (isTasInGsp ? 'var(--teal)' : accent),
                          cursor: onOrderClick ? 'pointer' : 'default',
                          textDecoration: onOrderClick ? 'underline' : 'none'
                        }}
                        title={onOrderClick ? 'Open order detail' : ''}
                      >
                        {r.order_no}
                      </span>
                      <span style={{ color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{titleCase(r.buyer) || '—'}</span>
                      <span style={{ color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{titleCase(r.factory) || '—'}</span>
                      <span style={{ color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.style || '—'}</span>
                      {isTasInGsp && <span style={{ color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.fabric_name || ''}>{r.fabric_name || '—'}</span>}
                      {isTasInGsp && <span style={{ color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.fabric_supplier || ''}>{r.fabric_supplier || '—'}</span>}
                      <span style={{ textAlign: 'right', color: 'var(--text3)', paddingRight: 4 }}>{r.order_qty != null ? Number(r.order_qty).toLocaleString('en-IN') : '—'}</span>
                      {isTasInGsp && <span style={{ textAlign: 'right', color: 'var(--text2)', paddingRight: 4 }}>{r.fabric_cut ? Number(r.fabric_cut).toFixed(2) : '—'}</span>}
                      {isTasInGsp && <span style={{ textAlign: 'right', color: 'var(--text)', fontWeight: 600, paddingRight: 4 }}>{r.fabric_mtr != null ? Math.round(Number(r.fabric_mtr)).toLocaleString('en-IN') : '—'}</span>}
                      <span style={{ textAlign: 'right', color: 'var(--text)', fontWeight: 600, paddingRight: 4 }}>{Number(r.qty || 0).toLocaleString('en-IN')}</span>
                      <span style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 11 }}>{unitLabel(r.unit)}</span>
                      <span style={{ color: r.target_date ? 'var(--text)' : 'var(--text3)' }}>{fmtDate(r.target_date)}</span>
                      <span style={{ color: 'var(--text3)' }}>{r.target_date ? monthLabel(r.target_date.slice(0, 7)) : '—'}</span>
                      <span
                        onClick={() => setProofModalOrder(r)}
                        title="Click to see stage proof"
                        style={{ color: activeColor, fontWeight: 700, cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: 'underline dotted' }}
                      >
                        {r.current_stage || '—'}
                        {r.stage_dt && <span style={{ marginLeft: 5, fontSize: 10.5, fontWeight: 600, color: 'var(--text3)' }}>({fmtDate(r.stage_dt)})</span>}
                      </span>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
                        {isProcessDrivenStage(r.current_stage, r.type) ? (
                          <span
                            style={{
                              padding: '3px 8px', borderRadius: 6, fontSize: 10.5, fontWeight: 600,
                              border: '1px solid var(--border)', background: 'var(--bg3)',
                              color: 'var(--text3)', cursor: 'not-allowed', whiteSpace: 'nowrap'
                            }}
                            title={`"${r.current_stage}" is updated automatically from ${processStageReason(r.current_stage)} — manual date update is not allowed`}
                          >
                            ⚙ Auto ({r.current_stage === 'PCD' || r.current_stage === 'IN LINE' || r.current_stage === 'LINE OUT' ? 'DPR' : r.current_stage === 'FAB INHOUSE' ? 'Factory' : 'Process'})
                          </span>
                        ) : (
                          <button
                            onClick={() => setStageModalOrder(r)}
                            style={{
                              padding: '3px 9px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                              border: `1px solid ${activeColor}`, background: `${activeColor}15`,
                              color: activeColor, cursor: 'pointer', whiteSpace: 'nowrap'
                            }}
                            title="Update current stage"
                          >
                            Update Stage
                          </button>
                        )}
                        <button
                          onClick={() => setShortCloseOrder(r)}
                          style={{
                            padding: '3px 9px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                            border: '1px solid #ef4444', background: '#ef444415',
                            color: '#ef4444', cursor: 'pointer', whiteSpace: 'nowrap'
                          }}
                          title="Short close this order: sets ERP status to Close and removes its pending qty from shipment targets"
                        >
                          ✕ Close Order
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* STAGE SUBTOTAL ROW (shown AFTER all orders of this stage) */}
                  {!isCollapsed && (
                    <div
                      style={{
                        display: 'grid', gridTemplateColumns: GC, gap: 8, padding: '7px 10px',
                        background: 'var(--bg3)', borderLeft: `4px solid ${activeColor}`,
                        borderRight: '1px solid var(--border)', borderBottom: '2px solid var(--border)',
                        alignItems: 'center', fontWeight: 700, fontSize: 12
                      }}
                    >
                      <div style={{ gridColumn: isTasInGsp ? '1 / 7' : '1 / 5', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 12, color: activeColor, fontWeight: 800, fontSize: 11.5, letterSpacing: 0.3 }}>
                        {g.stage} TOTAL ({g.rows.length} {g.rows.length === 1 ? 'Order' : 'Orders'}) :
                      </div>
                      <span style={{ textAlign: 'right', fontSize: 12, fontWeight: 700, color: 'var(--text2)', paddingRight: 4 }} title="Stage Total Order Qty">
                        {gOrdText}
                      </span>
                      {isTasInGsp && (
                        <span style={{ textAlign: 'right', fontSize: 11, color: 'var(--text3)', paddingRight: 4 }}>—</span>
                      )}
                      {isTasInGsp && (
                        <span style={{ textAlign: 'right', fontSize: 12, fontWeight: 800, color: activeColor, paddingRight: 4 }} title="Stage Total Fabric Meters">
                          {gFabMtrText}
                        </span>
                      )}
                      <span style={{ textAlign: 'right', fontSize: 12.5, fontWeight: 800, color: activeColor, paddingRight: 4 }} title="Stage Total Pending Qty">
                        {gPendText}
                      </span>
                      <span style={{ textAlign: 'center', fontSize: 11, color: 'var(--text3)', fontWeight: 600 }}>
                        {gUnit}
                      </span>
                      <span style={{ gridColumn: isTasInGsp ? '12 / 16' : '8 / 12' }}></span>
                    </div>
                  )}
                </React.Fragment>
              );
            })
          ) : (
            visibleFlatRows.map((r, i) => (
              <div key={r.type + r.order_no + i} style={{ display: 'grid', gridTemplateColumns: GC, gap: 8, padding: '7px 10px', borderBottom: '1px solid var(--border)', borderLeft: '1px solid var(--border)', borderRight: '1px solid var(--border)', fontSize: 12, alignItems: 'center', background: i % 2 ? 'var(--bg2)' : 'transparent' }}>
                <span
                  onClick={() => onOrderClick && onOrderClick({ tas_no: r.order_no, buyer: r.buyer, factory: r.factory, order_type: r.type === 'fabric' ? 'fabric' : 'garment' })}
                  style={{
                    fontWeight: 700,
                    color: r.type === 'fabric' ? 'var(--teal)' : (isTasInGsp ? 'var(--teal)' : accent),
                    cursor: onOrderClick ? 'pointer' : 'default',
                    textDecoration: onOrderClick ? 'underline' : 'none'
                  }}
                  title={onOrderClick ? 'Open order detail' : ''}
                >
                  {r.order_no}
                </span>
                <span style={{ color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{titleCase(r.buyer) || '—'}</span>
                <span style={{ color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{titleCase(r.factory) || '—'}</span>
                <span style={{ color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.style || '—'}</span>
                {isTasInGsp && <span style={{ color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.fabric_name || ''}>{r.fabric_name || '—'}</span>}
                {isTasInGsp && <span style={{ color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.fabric_supplier || ''}>{r.fabric_supplier || '—'}</span>}
                <span style={{ textAlign: 'right', color: 'var(--text3)', paddingRight: 4 }}>{r.order_qty != null ? Number(r.order_qty).toLocaleString('en-IN') : '—'}</span>
                {isTasInGsp && <span style={{ textAlign: 'right', color: 'var(--text2)', paddingRight: 4 }}>{r.fabric_cut ? Number(r.fabric_cut).toFixed(2) : '—'}</span>}
                {isTasInGsp && <span style={{ textAlign: 'right', color: 'var(--text)', fontWeight: 600, paddingRight: 4 }}>{r.fabric_mtr != null ? Math.round(Number(r.fabric_mtr)).toLocaleString('en-IN') : '—'}</span>}
                <span style={{ textAlign: 'right', color: 'var(--text)', fontWeight: 600, paddingRight: 4 }}>{Number(r.qty || 0).toLocaleString('en-IN')}</span>
                <span style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 11 }}>{unitLabel(r.unit)}</span>
                <span style={{ color: r.target_date ? 'var(--text)' : 'var(--text3)' }}>{fmtDate(r.target_date)}</span>
                <span style={{ color: 'var(--text3)' }}>{r.target_date ? monthLabel(r.target_date.slice(0, 7)) : '—'}</span>
                <span
                  onClick={() => setProofModalOrder(r)}
                  title="Click to see stage proof"
                  style={{ color: activeColor, fontWeight: 700, cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: 'underline dotted' }}
                >
                  {r.current_stage || '—'}
                  {r.stage_dt && <span style={{ marginLeft: 5, fontSize: 10.5, fontWeight: 600, color: 'var(--text3)' }}>({fmtDate(r.stage_dt)})</span>}
                </span>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
                  {isProcessDrivenStage(r.current_stage, r.type) ? (
                    <span
                      style={{
                        padding: '3px 8px', borderRadius: 6, fontSize: 10.5, fontWeight: 600,
                        border: '1px solid var(--border)', background: 'var(--bg3)',
                        color: 'var(--text3)', cursor: 'not-allowed', whiteSpace: 'nowrap'
                      }}
                      title={`"${r.current_stage}" is updated automatically from ${processStageReason(r.current_stage)} — manual date update is not allowed`}
                    >
                      ⚙ Auto ({r.current_stage === 'PCD' || r.current_stage === 'IN LINE' || r.current_stage === 'LINE OUT' ? 'DPR' : r.current_stage === 'FAB INHOUSE' ? 'Factory' : 'Process'})
                    </span>
                  ) : (
                    <button
                      onClick={() => setStageModalOrder(r)}
                      style={{
                        padding: '3px 9px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                        border: `1px solid ${activeColor}`, background: `${activeColor}15`,
                        color: activeColor, cursor: 'pointer', whiteSpace: 'nowrap'
                      }}
                      title="Update current stage"
                    >
                      Update Stage
                    </button>
                  )}
                  <button
                    onClick={() => setShortCloseOrder(r)}
                    style={{
                      padding: '3px 9px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                      border: '1px solid #ef4444', background: '#ef444415',
                      color: '#ef4444', cursor: 'pointer', whiteSpace: 'nowrap'
                    }}
                    title="Short close this order: sets ERP status to Close and removes its pending qty from shipment targets"
                  >
                    ✕ Close Order
                  </button>
                </div>
              </div>
            ))
          )}

          {visibleFlatRows.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: GC, gap: 8, padding: '8px 10px', fontWeight: 700, fontSize: 12, border: '1px solid var(--border)', borderTop: '2px solid var(--border)', background: 'var(--bg3)', alignItems: 'center' }}>
              <span style={{ fontWeight: 800 }}>GRAND TOTAL ({visibleFlatRows.length})</span>
              <span></span>
              <span></span>
              <span></span>
              {isTasInGsp && <span></span>}
              {isTasInGsp && <span></span>}
              <span style={{ textAlign: 'right', color: 'var(--text3)', paddingRight: 4, fontWeight: 600 }}>
                {totalOrderQty.pcs ? totalOrderQty.pcs.toLocaleString('en-IN') : (totalOrderQty.mtrs ? totalOrderQty.mtrs.toLocaleString('en-IN') : '—')}
              </span>
              {isTasInGsp && (
                <span style={{ textAlign: 'right', color: 'var(--text3)', paddingRight: 4 }}>—</span>
              )}
              {isTasInGsp && (
                <span style={{ textAlign: 'right', color: activeColor, paddingRight: 4, fontWeight: 800 }} title="Grand Total Fabric Meters">
                  {Math.round(totalFabricMtr || 0).toLocaleString('en-IN')}
                </span>
              )}
              <span style={{ textAlign: 'right', color: activeColor, fontWeight: 800, paddingRight: 4 }}>
                {totalPendingQty.pcs ? totalPendingQty.pcs.toLocaleString('en-IN') : (totalPendingQty.mtrs ? totalPendingQty.mtrs.toLocaleString('en-IN') : '0')}
              </span>
              <span style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 11 }}>
                {totalPendingQty.pcs && totalPendingQty.mtrs ? 'pcs/mtr' : (totalPendingQty.pcs ? 'pcs' : (totalPendingQty.mtrs ? 'mtr' : ''))}
              </span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          )}
        </div>
      </div>
      )}

      {proofModalOrder && (
        <StageProofModal
          order={proofModalOrder}
          onClose={() => setProofModalOrder(null)}
          onOpenUpdate={ord => setStageModalOrder(ord)}
        />
      )}
      {stageModalOrder && (
        <UpdateStageModal
          order={stageModalOrder}
          onClose={() => setStageModalOrder(null)}
          onSuccess={handleUpdateSuccess}
        />
      )}
      {shortCloseOrder && (
        <ShortCloseModal
          order={shortCloseOrder}
          onClose={() => setShortCloseOrder(null)}
          onSuccess={handleUpdateSuccess}
        />
      )}
      {toastMsg && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 600,
          background: 'var(--bg2)', border: '1px solid var(--teal)', color: 'var(--teal)',
          padding: '10px 18px', borderRadius: 10, fontWeight: 600, fontSize: 13,
          boxShadow: '0 8px 30px rgba(0,0,0,0.5)'
        }}>
          {toastMsg}
        </div>
      )}
    </div>
  );
}

// ===== EXPORT — Final Costing (stage 1). Add a TAS, upload cost sheet, fill the
// garment cost grid, send for approval; management/admin approves. (Ex Fact &
// Shipment stages come later — approved records will flow to the Shipment module.) =====
function ExportFlowView({ setView, Header, user }) {
  const accent = '#006a4e';
  const isApprover = !!(user && (user.isManagement || user.isAdmin));
  const [list, setList] = useState(null);
  const [tasOpts, setTasOpts] = useState([]);
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newTas, setNewTas] = useState('');
  const [costRem, setCostRem] = useState('');
  const [cost, setCost] = useState({});

  const inp = { boxSizing: 'border-box', padding: '7px 10px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 13, outline: 'none' };
  const lbl = { fontSize: 10, letterSpacing: 1, color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 4, display: 'block' };

  const loadList = () => api('/shipflow/list').then(d => setList(Array.isArray(d) ? d : [])).catch(() => setList([]));
  const loadTasOpts = () => api('/shipflow/tas-options').then(d => setTasOpts(Array.isArray(d) ? d : [])).catch(() => {});
  const openDetail = id => { setActive(id); setDetail(null); api(`/shipflow/${id}`).then(setDetail).catch(() => {}); };
  const reloadDetail = () => { if (active) api(`/shipflow/${active}`).then(setDetail).catch(() => {}); loadList(); };
  useEffect(() => { loadList(); loadTasOpts(); }, []);

  const uploadFile = (docType, file) => {
    if (!active) return;
    const reader = new FileReader();
    reader.onload = () => api('/shipflow/file', { method: 'POST', body: JSON.stringify({ ship_id: active, doc_type: docType, filename: file.name, data: reader.result }) })
      .then(() => reloadDetail()).catch(() => alert('Upload failed'));
    reader.readAsDataURL(file);
  };

  const addCosting = () => {
    if (!newTas) { alert('Pick a TAS'); return; }
    setBusy(true);
    api('/shipflow/costing', { method: 'POST', body: JSON.stringify({ tas_no: newTas, costing_remark: costRem || null }) })
      .then(r => { setBusy(false); setNewTas(''); setCostRem(''); loadTasOpts(); loadList(); if (r.id) openDetail(r.id); })
      .catch(() => { setBusy(false); alert('Failed'); });
  };
  const approve = (id, decision) => api(`/shipflow/${id}/approve`, { method: 'POST', body: JSON.stringify({ decision }) }).then(() => reloadDetail()).catch(() => alert('Failed'));

  // cost grid
  useEffect(() => {
    if (detail) {
      const c = detail.cost || {};
      const g = k => (c[k] == null ? '' : c[k]);
      setCost({ fab_price: g('fab_price'), freight: g('freight'), consumption: g('consumption'), cm: g('cm'), bd_trims: g('bd_trims'), ind_trims: g('ind_trims'), china_trims: g('china_trims'), wash: g('wash'), transp: g('transp'), exch: g('exch') });
    }
  }, [detail]);
  const nc = v => parseFloat(v) || 0;
  const cif = nc(cost.fab_price) + nc(cost.freight);
  const fabCost = cif * nc(cost.consumption);
  const cog = fabCost + nc(cost.cm) + nc(cost.bd_trims) + nc(cost.ind_trims) + nc(cost.china_trims) + nc(cost.wash) + nc(cost.transp);
  const inr = cog * nc(cost.exch);
  const computedVals = { cif, fab_cost: fabCost, cog, inr };
  const COST_COLS = [['fab_price', 'Fab Price', 0], ['freight', 'Freight', 0], ['cif', 'Cif', 1], ['consumption', 'Consumption', 0], ['fab_cost', 'Fab Cost', 1], ['cm', 'Cm', 0], ['bd_trims', 'Bd Trims', 0], ['ind_trims', 'Ind Trims', 0], ['china_trims', 'China Trims', 0], ['wash', 'Wash', 0], ['transp', 'Transp', 0], ['cog', 'Cog', 1], ['exch', 'Exch', 0], ['inr', 'Inr', 1]];
  const saveCost = () => {
    setBusy(true);
    api(`/shipflow/${active}/cost`, { method: 'POST', body: JSON.stringify(cost) })
      .then(() => { setBusy(false); reloadDetail(); })
      .catch(() => { setBusy(false); alert('Cost save failed'); });
  };

  const statusChip = r => {
    const map = { pending: ['var(--yellow)', 'Pending approval'], approved: ['var(--green)', 'Approved'], rejected: ['var(--red)', 'Rejected'] };
    const [c, t] = map[r.costing_status] || ['var(--text3)', r.costing_status];
    return <span style={{ fontSize: 10, fontWeight: 700, color: c, background: 'var(--bg3)', padding: '2px 8px', borderRadius: 10 }}>{t}</span>;
  };

  return (
    <div className="screen">
      <Header subtitle="TAS · FINAL COSTING" />
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
        <button onClick={() => setView('tas')} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: accent }}>Final Costing</div>
      </div>

      <div style={{ padding: '14px 16px', display: 'grid', gridTemplateColumns: 'minmax(260px, 340px) 1fr', gap: 16, alignItems: 'start' }}>
        {/* LEFT: add + records */}
        <div>
          <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12, marginBottom: 12, background: 'var(--bg2)' }}>
            <label style={lbl}>Add TAS to Costing</label>
            <select style={{ ...inp, width: '100%', marginBottom: 6 }} value={newTas} onChange={e => setNewTas(e.target.value)}>
              <option value="">— select TAS —</option>
              {tasOpts.map(o => <option key={o.tas_no} value={o.tas_no}>{o.tas_no} · {titleCase(o.buyer)}</option>)}
            </select>
            <input style={{ ...inp, width: '100%', marginBottom: 8 }} placeholder="Remark (optional)" value={costRem} onChange={e => setCostRem(e.target.value)} />
            <button onClick={addCosting} disabled={busy} style={{ width: '100%', padding: 9, border: 'none', borderRadius: 8, background: accent, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>+ Add</button>
          </div>
          {!list && <div style={{ color: 'var(--text3)', fontSize: 12, padding: 12 }}>Loading…</div>}
          {list && list.length === 0 && <div style={{ color: 'var(--text3)', fontSize: 12, padding: 12 }}>No costing records yet.</div>}
          {(list || []).map(r => (
            <div key={r.id} onClick={() => openDetail(r.id)}
              style={{ border: '1px solid var(--border)', borderLeft: `3px solid ${active === r.id ? accent : 'var(--border)'}`, borderRadius: 8, padding: '10px 12px', marginBottom: 8, cursor: 'pointer', background: active === r.id ? `${accent}10` : 'var(--bg2)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 700, color: 'var(--text)' }}>{r.tas_no}</span>
                {statusChip(r)}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>{titleCase(r.buyer) || '—'}{r.cost_amount != null ? ` · INR ${fmt(r.cost_amount)}` : ''}</div>
            </div>
          ))}
        </div>

        {/* RIGHT: costing editor */}
        <div>
          {!detail && <div style={{ color: 'var(--text3)', fontSize: 12, padding: 20, border: '1px dashed var(--border)', borderRadius: 10 }}>Select or add a TAS on the left.</div>}
          {detail && (
            <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 16, background: 'var(--bg2)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--text)' }}>{detail.tas_no} <span style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 400 }}>· {titleCase(detail.buyer)}</span></div>
                {statusChip(detail)}
              </div>

              <DocRow label="Cost Sheet" docType="costsheet" detail={detail} onUpload={uploadFile} />

              <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--text2)', margin: '14px 0 6px' }}>COST GRID <span style={{ fontWeight: 400, color: 'var(--text3)' }}>(grey = auto-computed)</span></div>
              <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 6 }}>
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${COST_COLS.length}, minmax(86px, 1fr))`, minWidth: 1180 }}>
                  {COST_COLS.map(([k, label]) => (
                    <div key={k} style={{ padding: '7px 8px', background: '#14213d', color: '#fff', fontSize: 10.5, fontWeight: 600, textAlign: 'center', borderRight: '1px solid rgba(255,255,255,0.08)' }}>{label}</div>
                  ))}
                  {COST_COLS.map(([k, label, computed]) => (
                    <div key={k + '_v'} style={{ padding: 5, borderRight: '1px solid var(--border)', borderTop: '1px solid var(--border)', background: computed ? 'var(--bg3)' : 'transparent' }}>
                      {computed
                        ? <div style={{ textAlign: 'right', fontSize: 12, padding: '4px 6px', color: 'var(--text2)', fontWeight: 600 }}>{(computedVals[k] || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
                        : <input type="number" value={cost[k] ?? ''} placeholder="0" onChange={e => setCost(c => ({ ...c, [k]: e.target.value }))}
                            style={{ width: '100%', boxSizing: 'border-box', border: '1px solid var(--border)', borderRadius: 4, padding: '4px 6px', textAlign: 'right', fontSize: 12, background: 'var(--bg2)', color: 'var(--text)', outline: 'none' }} />}
                    </div>
                  ))}
                </div>
              </div>
              <button onClick={saveCost} disabled={busy} style={{ marginTop: 10, padding: '8px 16px', border: 'none', borderRadius: 8, background: accent, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Save Cost Grid</button>

              <div style={{ fontSize: 12, color: 'var(--text3)', margin: '12px 0' }}>Costed INR: <b style={{ color: 'var(--text)' }}>{detail.cost_amount != null ? fmt(detail.cost_amount) : '—'}</b>{detail.costing_remark ? ` · ${detail.costing_remark}` : ''}</div>
              {detail.costing_status === 'pending' && <div style={{ fontSize: 12, color: 'var(--yellow)', marginBottom: 10 }}>⏳ Sent for approval — awaiting management/admin.</div>}
              {detail.costing_status === 'approved' && <div style={{ fontSize: 12, color: 'var(--green)', marginBottom: 10 }}>✓ Approved by {detail.approved_by_name || '—'}.</div>}
              {detail.costing_status === 'rejected' && <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 10 }}>✕ Rejected.</div>}
              {isApprover && detail.costing_status !== 'approved' && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => approve(detail.id, 'approve')} style={{ flex: 1, padding: 9, border: 'none', borderRadius: 8, background: 'var(--green)', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Approve</button>
                  <button onClick={() => approve(detail.id, 'reject')} style={{ padding: '9px 14px', border: '1px solid var(--red)', borderRadius: 8, background: 'transparent', color: 'var(--red)', fontWeight: 700, cursor: 'pointer' }}>Reject</button>
                </div>
              )}
              {!isApprover && detail.costing_status === 'pending' && <div style={{ fontSize: 11, color: 'var(--text3)' }}>Only management/admin can approve.</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ===== BD Order in Hand / Upcoming — reuses the full Order-in-Hand card grid,
// filtered to the BD bucket set (in_hand = routing-ready + Sales Order Cost Sheet
// pending; upcoming = routing not yet ready). =====
function BdOrdersView({ bucket, titleTag, setView, Header, onOrderClick, onBuyerClick, buyers }) {
  const [set, setSet] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    api(`/orders/bd-list?bucket=${bucket}`)
      .then(d => {
        if (!alive) return;
        const s = new Set((Array.isArray(d) ? d : []).map(r => String(r.order_no || '').trim().toUpperCase()));
        setSet(s);
      })
      .catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, [bucket]);
  if (err) return (
    <div className="screen">
      <Header subtitle={titleTag} />
      <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)' }}>
        <button onClick={() => setView('bd')} style={{ background: 'none', border: 'none', color: '#006a4e', fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
      </div>
      <div style={{ textAlign: 'center', padding: '50px', color: 'var(--red)' }}>Could not load.</div>
    </div>
  );
  if (!set) return (
    <div className="screen">
      <Header subtitle={titleTag} />
      <div className="loading">LOADING ORDERS…</div>
    </div>
  );
  return <OrderInHandView isTas setView={setView} Header={Header}
    onOrderClick={onOrderClick} onBuyerClick={onBuyerClick} buyersFromHealth={buyers}
    filterTasNos={set} backTo="bd" titleTag={titleTag}
    exportSource={{ endpoint: '/orders/bd-list', params: { bucket }, sections: [{ name: 'Orders', path: '', columns: BD_LIST_COLS }] }} />;
}

function DevelopmentView({ setView, Header, groupBy }) {
  const [seasonGroups, setSeasonGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderStats, setOrderStats] = useState({ count: 0, items: 0 });   // sample-order counts shown on the ORDER hub card
  // Top-level section: null = card hub | 'season' = development by season | 'order' = order-taking
  const [section, setSection] = useState(null);
  // Drill state — start directly on Seasons (no intermediate section menu)
  const [selectedSection, setSelectedSection] = useState('seasons');
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [selectedSilhouette, setSelectedSilhouette] = useState(null);
  // After clicking a season, user always sees sub-category cards first.
  // After clicking a sub-category, breakdown tabs appear (STYLE / PRODUCT / GENDER / DESIGNER).
  const [groupValue, setGroupValue] = useState(null);          // selected sub-category (e.g. "CASUAL")
  const [breakdownTab, setBreakdownTab] = useState('product'); // active tab inside the sub-category
  const [subGroupValue, setSubGroupValue] = useState(null);    // 2nd-level filter (e.g. "TOP WEAR" within CASUAL+PRODUCT)

  // Pull the sample-orders list and summarise (count of orders + total line
  // items) for the ORDER hub card. Runs once on mount; cheap query (returns
  // ~10 rows max for typical usage).
  useEffect(() => {
    api('/orders/sample-orders').then(d => {
      if (!Array.isArray(d)) return;
      const items = d.reduce((a, o) => a + (Number(o.items) || 0), 0);
      setOrderStats({ count: d.length, items });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api('/silhouettes').then(d => {
      const groups = Array.isArray(d) ? d : [];
      setSeasonGroups(groups);
      setLoading(false);
      // Deep-link via window.location.hash — e.g. #dev/AW-27/CASUAL[/silhouetteId]
      const h = (window.location.hash || '').replace(/^#/, '');
      if (h.startsWith('dev')) {
        const parts = h.split('/').slice(1).map(decodeURIComponent);
        const [seasonHint, subCatHint, silIdHint] = parts;
        if (seasonHint) {
          // Match season case-insensitively
          const found = groups.find(g => (g.season || '').toUpperCase() === seasonHint.toUpperCase());
          if (found) setSelectedSeason(found.season);
        }
        if (subCatHint) {
          setGroupValue(subCatHint.toUpperCase());
          setBreakdownTab('product');
        }
        // 4th segment = silhouette id → open style detail directly
        if (silIdHint) {
          const sid = parseInt(silIdHint);
          if (!isNaN(sid)) setSelectedSilhouette(sid);
        }
        // clear hash so it doesn't replay on refresh
        window.history.replaceState(null, '', window.location.pathname);
      }
    });
  }, []);

  const accent = 'var(--yellow)';

  if (loading) return (
    <div className="screen">
      <Header subtitle="DEVELOPMENT" />
      <div className="loading">LOADING SILHOUETTES…</div>
    </div>
  );

  // Flatten all silhouettes for filtering
  const allSilhouettes = seasonGroups.flatMap(g => g.silhouettes.map(s => ({ ...s, season: g.season })));
  const isMapped = s => (s.mapping_count || 0) > 0;
  const isOverdue = s => !isMapped(s) && (s.days_since_created || 0) > 30;
  const isAtRisk  = s => !isMapped(s) && (s.days_since_created || 0) <= 30;

  const stats = {
    seasons:    seasonGroups.length,
    silhouettes: allSilhouettes.length,
    mapped:     allSilhouettes.filter(isMapped).length,
    not_mapped: allSilhouettes.filter(s => !isMapped(s)).length,
    overdue:    allSilhouettes.filter(isOverdue).length,
    at_risk:    allSilhouettes.filter(isAtRisk).length,
    total_qty:  allSilhouettes.reduce((a, s) => a + (s.total_qty || 0), 0),
  };

  // Silhouette list for the drilled-into section
  function listForSection(sec) {
    if (sec === 'mapped')     return allSilhouettes.filter(isMapped);
    if (sec === 'not_mapped') return allSilhouettes.filter(s => !isMapped(s));
    if (sec === 'overdue')    return allSilhouettes.filter(isOverdue);
    if (sec === 'at_risk')    return allSilhouettes.filter(isAtRisk);
    return [];
  }

  // Overall totals
  const totalSilhouettes = seasonGroups.reduce((a, g) => a + g.silhouette_count, 0);
  const totalMapped      = seasonGroups.reduce((a, g) => a + g.mapped_count, 0);
  const totalQty         = seasonGroups.reduce((a, g) => a + g.total_qty, 0);

  // Active season + silhouette objects (silhouette can come from either season-drill or section list)
  const activeSeason = selectedSeason ? seasonGroups.find(g => g.season === selectedSeason) : null;
  const activeSilhouette = selectedSilhouette
    ? allSilhouettes.find(s => s.id === selectedSilhouette)
    : null;

  // ─── HUB — card menu (SEASON / ORDER), styled like the TAS menu ───
  if (section === null) {
    const cardBg = 'rgba(91,194,199,0.04)';
    const hubCard = (label, value, sub, onClick, c = accent) => (
      <div onClick={onClick}
        style={{ padding: '20px 28px', border: '1px solid var(--border)', borderRadius: 10, cursor: 'pointer', textAlign: 'center', minWidth: 200, background: cardBg, transition: 'all 0.2s' }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = c; e.currentTarget.style.background = `${c}14`; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = cardBg; }}>
        <div style={{ color: c, fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 32 }}>{value}</div>
        <div style={{ color: c, fontSize: 12.5, fontWeight: 700, letterSpacing: 1.4, marginTop: 5 }}>{label}</div>
        <div style={{ color: 'var(--text3)', fontSize: 11, marginTop: 6 }}>{sub}</div>
        <div style={{ color: 'var(--text3)', fontSize: 9, marginTop: 10, letterSpacing: 1 }}>OPEN →</div>
      </div>
    );
    return (
      <div className="screen">
        <Header subtitle="DEVELOPMENT" />
        <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
          <button onClick={() => setView('rw')} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: accent }}>Development</div>
        </div>
        <div style={{ marginTop: 28, padding: '0 16px', display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
          {hubCard('SEASONS', fmt(stats.seasons), seasonGroups.map(g => g.season).join(' · ') || 'view seasons', () => setSection('season'), 'var(--teal)')}
          {hubCard('ORDER', fmt(orderStats.count), orderStats.count ? `${orderStats.items} item${orderStats.items !== 1 ? 's' : ''} · view orders` : 'order taking · new', () => setSection('order'), 'var(--gold)')}
        </div>
      </div>
    );
  }

  // ─── ORDER section — order-taking module (to be built next) ───
  if (section === 'order') {
    return <OrderTakingView Header={Header} onBack={() => setSection(null)} />;
  }

  return (
    <div className="screen">
      <Header subtitle="DEVELOPMENT" />
      <ExportBar reportKey="development-silhouettes" title="Development — Silhouettes" subtitle={[selectedSeason ? `season: ${selectedSeason}` : '', selectedSection ? `section: ${selectedSection}` : ''].filter(Boolean).join(' | ')} sections={[{ name: 'Silhouettes', columns: DEV_COLS, rows: (selectedSection && selectedSection !== 'seasons' ? listForSection(selectedSection) : allSilhouettes) || [] }]}
      source={{ endpoint: '/silhouettes', explode: { path: 'silhouettes', inherit: ['season'] },
        filters: [
          ...(selectedSeason ? [{ t: 'eq', k: 'season', v: selectedSeason }] : []),
          ...(selectedSection === 'mapped'     ? [{ t: 'gt', k: 'mapping_count', v: 0 }]                             : []),
          ...(selectedSection === 'not_mapped' ? [{ t: 'not', f: { t: 'gt', k: 'mapping_count', v: 0 } }]            : []),
        ] }} />

      {/* Header row */}
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
        <button
          onClick={() => {
            if (selectedSilhouette) setSelectedSilhouette(null);
            else if (subGroupValue)  setSubGroupValue(null);
            else if (groupValue)     setGroupValue(null);
            else if (selectedSeason) { setSelectedSeason(null); setGroupValue(null); setSubGroupValue(null); }
            else setSection(null);
          }}
          style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}
        >←</button>
        <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: accent }}>
          {activeSilhouette ? activeSilhouette.name
            : activeSeason ? activeSeason.season
            : selectedSection ? ({ seasons: 'Seasons', mapped: 'Mapped Silhouettes', not_mapped: 'Not Mapped Silhouettes', overdue: 'Overdue Development', at_risk: 'At Risk' }[selectedSection] || 'Development')
            : 'Development'}
        </div>
        <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>
          {activeSilhouette
            ? `${activeSilhouette.mapping_count} mapping${activeSilhouette.mapping_count !== 1 ? 's' : ''} · ${fmt(activeSilhouette.total_qty)} qty`
            : activeSeason
              ? `${activeSeason.silhouette_count} / ${fmt(activeSeason.planned_styles_total || 0)} styles ready · ${fmt(activeSeason.planned_qty_total || activeSeason.total_qty)} planned qty`
              : `${stats.seasons} seasons · ${stats.silhouettes} silhouettes · ${fmt(stats.total_qty)} qty`}
        </div>
      </div>

      <div style={{ padding: '20px 16px' }}>
        {seasonGroups.length === 0 && <div style={{ padding: 30, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>No silhouettes found.</div>}

        {/* ─── LEVEL 3: SILHOUETTE MAPPING DETAIL ─── */}
        {activeSilhouette && (() => {
          const s = activeSilhouette;
          const meta = [
            ['Season', s.season],
            ['Gender', s.gender],
            ['Product', s.product],
            ['Sub Category', s.sub_category],
            ['Designer', s.designer],
          ];
          return (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8, marginBottom: 18 }}>
                {meta.map(([k, v]) => (
                  <div key={k} style={{ background: 'rgba(227,193,94,0.04)', border: '1px solid var(--border)', borderRadius: 6, padding: 10 }}>
                    <div style={{ fontSize: 9, color: 'var(--text3)', letterSpacing: 1.5 }}>{k.toUpperCase()}</div>
                    <div style={{ fontSize: 13, color: 'var(--text)', marginTop: 2 }}>{v || '—'}</div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 11, color: accent, letterSpacing: 2, marginBottom: 10 }}>MAPPING ENTRIES ({s.mappings.length})</div>
              {s.mappings.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)', fontSize: 12, background: 'var(--bg2)', borderRadius: 8, border: '1px solid var(--border)' }}>
                  No development mappings yet for this silhouette.
                </div>
              ) : (
                <div style={{ background: 'var(--bg2)', borderRadius: 8, border: '1px solid var(--border)', padding: '10px 14px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ color: 'var(--text3)', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                        <th style={{ padding: '8px 6px', fontWeight: 500 }}>Entry</th>
                        <th style={{ padding: '8px 6px', fontWeight: 500 }}>Date</th>
                        <th style={{ padding: '8px 6px', fontWeight: 500 }}>Designer</th>
                        <th style={{ padding: '8px 6px', fontWeight: 500 }}>Sample Type</th>
                        <th style={{ padding: '8px 6px', fontWeight: 500 }}>Customer</th>
                        <th style={{ padding: '8px 6px', fontWeight: 500 }}>Sizes</th>
                        <th style={{ padding: '8px 6px', fontWeight: 500, textAlign: 'right' }}>Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.mappings.map(m => (
                        <tr key={m.mapping_id} style={{ borderBottom: '1px solid var(--grid-row-border)', color: 'var(--text2)' }}>
                          <td style={{ padding: '10px 6px', color: 'var(--text)', fontWeight: 500 }}>{m.entryno}</td>
                          <td style={{ padding: '10px 6px' }}>{m.entry_dt ? new Date(m.entry_dt).toLocaleDateString('en-IN') : '—'}</td>
                          <td style={{ padding: '10px 6px' }}>{m.designer || '—'}</td>
                          <td style={{ padding: '10px 6px' }}>{m.sample_type || '—'}</td>
                          <td style={{ padding: '10px 6px' }}>{m.customer || '—'}</td>
                          <td style={{ padding: '10px 6px', color: 'var(--text3)' }}>{m.sizes || '—'}</td>
                          <td style={{ padding: '10px 6px', textAlign: 'right', color: 'var(--gold)', fontWeight: 600 }}>{fmt(m.qty || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          );
        })()}

        {/* ─── LEVEL 2: tabs + content for the selected season ─── */}
        {activeSeason && !activeSilhouette && (() => {
          // Tabs that appear AFTER clicking a sub-category
          const breakdownTabs = [
            { key: 'style',    label: 'STYLE-WISE' },
            { key: 'product',  label: 'PRODUCT-WISE' },
            { key: 'gender',   label: 'GENDER-WISE' },
            { key: 'designer', label: 'DESIGNER-WISE' },
          ];

          // Silhouette LIST view — table format (one row per silhouette)
          const renderSilhouetteCards = (sils) => (
            <div style={{ background: 'var(--bg2)', borderRadius: 8, border: '1px solid var(--border)', overflow: 'hidden' }}>
              {sils.length === 0 && (
                <div style={{ padding: 30, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>No silhouettes</div>
              )}
              {sils.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ color: 'var(--text3)', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                      <th style={{ padding: '10px 14px', fontWeight: 500 }}>Style</th>
                      <th style={{ padding: '10px 14px', fontWeight: 500 }}>Product</th>
                      <th style={{ padding: '10px 14px', fontWeight: 500 }}>Gender</th>
                      <th style={{ padding: '10px 14px', fontWeight: 500 }}>Designer</th>
                      <th style={{ padding: '10px 14px', fontWeight: 500, textAlign: 'right' }}>Mappings</th>
                      <th style={{ padding: '10px 14px', fontWeight: 500, textAlign: 'right' }}>Qty</th>
                      <th style={{ padding: '10px 14px', fontWeight: 500 }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sils.map(s => {
                      const mapped = (s.mapping_count || 0) > 0;
                      return (
                        <tr key={s.id}
                          onClick={() => setSelectedSilhouette(s.id)}
                          style={{ borderBottom: '1px solid var(--grid-row-border)', color: 'var(--text2)', cursor: 'pointer' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(91,194,199,0.06)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <td style={{ padding: '10px 14px', color: 'var(--teal)', fontWeight: 500 }}>{s.name}</td>
                          <td style={{ padding: '10px 14px' }}>{titleCase(s.product) || '—'}</td>
                          <td style={{ padding: '10px 14px' }}>{s.gender || '—'}</td>
                          <td style={{ padding: '10px 14px' }}>{s.designer || '—'}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', color: mapped ? 'var(--green)' : 'var(--text3)' }}>{s.mapping_count || 0}</td>
                          <td style={{ padding: '10px 14px', textAlign: 'right', color: 'var(--gold)' }}>{fmt(s.total_qty || 0)}</td>
                          <td style={{ padding: '10px 14px', color: mapped ? 'var(--green)' : 'var(--yellow)', fontSize: 11, fontWeight: 600 }}>
                            {mapped ? '✓ MAPPED' : 'NOT MAPPED'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          );

          // Generic group-card renderer.
          //   When dim === 'sub_category', the BIG number on each card is the
          //   PLANNED style count from forecasting (inv_forecaste_det.noofstyle),
          //   not just the master silhouette count.
          const renderGroupCards = (sourceList, dim, onClickKey) => {
            const map = new Map();
            for (const s of sourceList) {
              const k = (s[dim] || '—').toString().trim() || '—';
              if (!map.has(k)) map.set(k, { key: k, count: 0, qty: 0, mapped: 0 });
              const g = map.get(k);
              g.count += 1;
              g.qty   += (s.total_qty || 0);
              g.mapped += (s.mapping_count > 0 ? 1 : 0);
            }
            const groups = [...map.values()].sort((a, b) => b.count - a.count);
            const plan = (activeSeason && activeSeason.plan_by_subcategory) || {};
            return (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 }}>
                {groups.map(g => {
                  const isSubCat = dim === 'sub_category';
                  const p = isSubCat ? plan[g.key] : null;
                  const planned = p ? (p.planned_styles || 0) : 0;
                  const ready   = g.count;
                  const ageMonths = p ? (p.months_since_planned || 0) : 0;
                  // Attention rule (6-month timeline):
                  //   complete (green)         → ready ≥ planned
                  //   critical (red, attention) → planned > 0 AND age ≥ 6 months AND ratio < 100%
                  //   warning  (yellow)        → planned > 0 AND age ≥ 3 months AND ratio < 100%
                  //   on-track (cyan)          → planned > 0 AND age < 3 months
                  const ratio = planned > 0 ? (ready / planned) : 1;
                  const complete       = isSubCat && planned > 0 && ratio >= 1;
                  const needsAttention = isSubCat && !complete && planned > 0 && ageMonths >= 6;
                  const inProgress     = isSubCat && !complete && !needsAttention && planned > 0 && ageMonths >= 3;
                  const attentionColor = complete       ? 'var(--green)'
                                       : needsAttention ? 'var(--red)'
                                       : inProgress    ? 'var(--yellow)'
                                       : 'var(--teal)';
                  const attentionBg   = complete       ? 'rgba(82,194,120,0.05)'
                                      : needsAttention ? 'rgba(232,114,124,0.06)'
                                      : 'rgba(91,194,199,0.04)';
                  return (
                    <div key={g.key}
                      onClick={() => onClickKey(g.key)}
                      style={{
                        padding: '18px 24px', borderRadius: 10, cursor: 'pointer',
                        border: `1px solid ${needsAttention ? attentionColor : 'var(--border)'}`,
                        borderLeft: `4px solid ${attentionColor}`,
                        background: attentionBg,
                        textAlign: 'center', transition: 'all 0.2s', minWidth: 200
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = attentionColor; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = needsAttention ? attentionColor : 'var(--border)'; e.currentTarget.style.borderLeftColor = attentionColor; }}
                    >
                      <div style={{ color: attentionColor, fontSize: 11, letterSpacing: 2, marginBottom: 8, fontWeight: 600 }}>{g.key}</div>
                      {isSubCat && p ? (
                        <>
                          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', gap: 6 }}>
                            <div style={{ color: attentionColor, fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 32 }}>{fmt(ready)}</div>
                            <div style={{ color: 'var(--text3)', fontSize: 14 }}>/</div>
                            <div style={{ color: 'var(--text3)', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 24 }}>{fmt(planned)}</div>
                          </div>
                          <div style={{ color: 'var(--text3)', fontSize: 10, marginTop: 4 }}>READY / PLANNED STYLES</div>
                          {/* progress bar */}
                          <div style={{ height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 2, marginTop: 10, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${Math.min(100, ratio * 100)}%`, background: attentionColor, borderRadius: 2 }} />
                          </div>
                          <div style={{ color: 'var(--text3)', fontSize: 10, marginTop: 6, letterSpacing: 0.5 }}>
                            {complete
                              ? `✓ COMPLETE`
                              : needsAttention
                                ? `⚠ NEEDS ATTENTION · planned ${ageMonths}mo ago · ${Math.round(ratio*100)}%`
                                : inProgress
                                  ? `${Math.round(ratio*100)}% done · planned ${ageMonths}mo ago`
                                  : `${Math.round(ratio*100)}% done · planned ${ageMonths}mo ago`}
                          </div>
                          <div style={{ color: 'var(--text3)', fontSize: 9, marginTop: 8, letterSpacing: 1 }}>OPEN →</div>
                        </>
                      ) : (
                        <>
                          <div style={{ color: attentionColor, fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 30 }}>{g.count}</div>
                          <div style={{ color: 'var(--text3)', fontSize: 11, marginTop: 6 }}>{g.mapped} mapped · {fmt(g.qty)} qty</div>
                          <div style={{ color: 'var(--text3)', fontSize: 9, marginTop: 10, letterSpacing: 1 }}>OPEN →</div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          };

          // 1st-level filter — by selected sub-category (e.g. CASUAL)
          const subCatList = groupValue
            ? activeSeason.silhouettes.filter(s => ((s.sub_category || '—').toString().trim() || '—') === groupValue)
            : activeSeason.silhouettes;
          // 2nd-level filter — by selected secondary group (e.g. TOP WEAR within CASUAL)
          const dimMap = { product: 'product', gender: 'gender', designer: 'designer' };
          const secondaryDim = dimMap[breakdownTab];
          const finalList = (groupValue && subGroupValue && secondaryDim)
            ? subCatList.filter(s => ((s[secondaryDim] || '—').toString().trim() || '—') === subGroupValue)
            : subCatList;

          return (
            <>
              {/* Breadcrumb when drilled into a sub-category */}
              {groupValue && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <button
                    onClick={() => { if (subGroupValue) setSubGroupValue(null); else setGroupValue(null); }}
                    style={{ background: 'none', border: 'none', color: accent, fontSize: 18, cursor: 'pointer', padding: 0 }}
                  >←</button>
                  <div style={{ fontSize: 13, color: accent, letterSpacing: 1 }}>
                    {groupValue}{subGroupValue ? ` · ${subGroupValue}` : ''}
                  </div>
                  <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>
                    {finalList.length} silhouettes
                  </div>
                </div>
              )}

              {/* Tab bar — appears AFTER clicking a sub-category */}
              {groupValue && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                  {breakdownTabs.map(t => (
                    <button key={t.key}
                      onClick={() => { setBreakdownTab(t.key); setSubGroupValue(null); }}
                      style={{
                        padding: '8px 16px', borderRadius: 20, fontSize: 11, letterSpacing: 1.5, fontWeight: 600,
                        background: breakdownTab === t.key ? `${accent}26` : 'transparent',
                        border: `1px solid ${breakdownTab === t.key ? accent : 'var(--border)'}`,
                        color: breakdownTab === t.key ? accent : 'var(--text3)',
                        cursor: 'pointer', transition: 'all 0.15s'
                      }}
                    >{t.label}</button>
                  ))}
                </div>
              )}

              {/* Content rendering */}
              {/* Level 1: no sub-category → show sub-category cards (always) */}
              {!groupValue && renderGroupCards(activeSeason.silhouettes, 'sub_category', key => {
                setGroupValue(key);
                setBreakdownTab('product');
                setSubGroupValue(null);
              })}

              {/* Level 2a: sub-category selected, STYLE-WISE tab → silhouettes */}
              {groupValue && breakdownTab === 'style' && renderSilhouetteCards(subCatList)}

              {/* Level 2b: sub-category selected, group tab (PRODUCT/GENDER/DESIGNER) without secondary drill → group cards */}
              {groupValue && breakdownTab !== 'style' && !subGroupValue
                && renderGroupCards(subCatList, secondaryDim, key => setSubGroupValue(key))}

              {/* Level 2c: secondary group selected → silhouettes filtered by both */}
              {groupValue && breakdownTab !== 'style' && subGroupValue
                && renderSilhouetteCards(finalList)}
            </>
          );
        })()}


        {/* ─── LEVEL 1a: SECTION CARDS (TAS-style 3-up + 2-up) ─── */}
        {!activeSeason && !selectedSection && (() => {
          const subCard = (key, label, value, sub, c) => (
            <div key={key}
              onClick={() => key === 'seasons' ? setSelectedSection('seasons') : setSelectedSection(key)}
              style={{
                padding: '20px 28px', borderRadius: 10, cursor: 'pointer',
                border: '1px solid var(--border)', background: 'rgba(91,194,199,0.04)',
                textAlign: 'center', minWidth: 170, transition: 'all 0.2s'
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = c; e.currentTarget.style.background = `${c}15`; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'rgba(91,194,199,0.04)'; }}
            >
              <div style={{ color: c, fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 30 }}>{value}</div>
              <div style={{ color: c, fontSize: 12.5, fontWeight: 700, letterSpacing: 1.4, marginTop: 5 }}>{label}</div>
              <div style={{ color: 'var(--text3)', fontSize: 11, marginTop: 6 }}>{sub}</div>
              <div style={{ color: 'var(--text3)', fontSize: 9, marginTop: 10, letterSpacing: 1 }}>OPEN →</div>
            </div>
          );
          const accentCard = (key, label, value, sub, c) => (
            <div key={key}
              onClick={() => setSelectedSection(key)}
              style={{
                flex: '1 1 200px', maxWidth: 280,
                padding: '14px 16px', border: '1px solid var(--border)', borderRadius: 8,
                textAlign: 'center', background: `${c}10`,
                borderLeft: `3px solid ${c}`, cursor: 'pointer', transition: 'all 0.2s'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = `${c}1f`; e.currentTarget.style.borderColor = c; }}
              onMouseLeave={e => { e.currentTarget.style.background = `${c}10`; e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.borderLeftColor = c; }}
            >
              <div style={{ color: c, fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 32 }}>{value}</div>
              <div style={{ color: c, fontSize: 11, letterSpacing: 3, marginTop: 2 }}>{label}</div>
              <div style={{ color: 'var(--text3)', fontSize: 10, marginTop: 4 }}>{sub}</div>
              <div style={{ color: 'var(--text3)', fontSize: 9, marginTop: 4, letterSpacing: 1 }}>VIEW DETAILS →</div>
            </div>
          );
          return (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14, marginBottom: 22 }}>
                {subCard('seasons',    'SEASONS',    fmt(stats.seasons),    `${fmt(stats.silhouettes)} silhouettes`, 'var(--teal)')}
                {subCard('mapped',     'MAPPED',     fmt(stats.mapped),     `${fmt(stats.total_qty)} qty in development`, 'var(--green)')}
                {subCard('not_mapped', 'NOT MAPPED', fmt(stats.not_mapped), 'awaiting development', 'var(--yellow)')}
              </div>
              <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
                {accentCard('overdue', 'OVERDUE', fmt(stats.overdue), `>30 days unmapped`, 'var(--red)')}
                {accentCard('at_risk', 'AT RISK', fmt(stats.at_risk), `≤30 days unmapped`, 'var(--yellow)')}
              </div>
            </>
          );
        })()}

        {/* ─── LEVEL 1b: SEASON cards (when SEASONS section drilled into) ─── */}
        {selectedSection === 'seasons' && !activeSeason && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
            {seasonGroups.map(g => {
              const planned = g.planned_styles_total || 0;
              const ready   = g.silhouette_count || 0;
              return (
                <div key={g.season}
                  onClick={() => setSelectedSeason(g.season)}
                  style={{
                    padding: '20px 28px', borderRadius: 10, cursor: 'pointer',
                    border: '1px solid var(--border)',
                    background: 'rgba(91,194,199,0.04)',
                    textAlign: 'center', transition: 'all 0.2s', minWidth: 170
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--teal)'; e.currentTarget.style.background = 'rgba(91,194,199,0.10)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'rgba(91,194,199,0.04)'; }}
                >
                  {/* Season name first — large, prominent */}
                  <div style={{ color: 'var(--teal)', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 30, fontWeight: 500 }}>{g.season}</div>
                  {/* Numbers below */}
                  {planned > 0 ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', gap: 6, marginTop: 12 }}>
                      <div style={{ color: 'var(--text)', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22 }}>{fmt(ready)}</div>
                      <div style={{ color: 'var(--text3)', fontSize: 13 }}>/</div>
                      <div style={{ color: 'var(--text3)', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 18 }}>{fmt(planned)}</div>
                    </div>
                  ) : (
                    <div style={{ color: 'var(--text)', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, marginTop: 12 }}>{fmt(ready)}</div>
                  )}
                  <div style={{ color: 'var(--text3)', fontSize: 10, marginTop: 4, letterSpacing: 1 }}>
                    {planned > 0 ? `READY / PLANNED STYLES` : `${g.mapped_count} mapped · ${fmt(g.total_qty)} qty`}
                  </div>
                  <div style={{ color: 'var(--text3)', fontSize: 9, marginTop: 10, letterSpacing: 1 }}>OPEN →</div>
                </div>
              );
            })}
          </div>
        )}

        {/* ─── LEVEL 1c: silhouette LIST for filtered sections (mapped/not_mapped/overdue/at_risk) ─── */}
        {selectedSection && selectedSection !== 'seasons' && !activeSilhouette && (() => {
          const list = listForSection(selectedSection);
          return (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
              {list.length === 0 && <div style={{ gridColumn: '1/-1', padding: 30, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>No silhouettes in this bucket.</div>}
              {list.map(s => (
                <div key={s.id}
                  onClick={() => setSelectedSilhouette(s.id)}
                  style={{
                    padding: '20px 24px', borderRadius: 10, cursor: 'pointer',
                    border: '1px solid var(--border)', background: 'rgba(91,194,199,0.04)',
                    textAlign: 'center', transition: 'all 0.2s', minWidth: 170
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--teal)'; e.currentTarget.style.background = 'rgba(91,194,199,0.10)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'rgba(91,194,199,0.04)'; }}
                >
                  <div style={{ color: 'var(--teal)', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, lineHeight: 1.15 }}>{s.name}</div>
                  <div style={{ color: 'var(--text3)', fontSize: 10, marginTop: 6 }}>{s.season} · {titleCase(s.product) || '—'}</div>
                  <div style={{ color: 'var(--text3)', fontSize: 10, marginTop: 4 }}>{s.designer || '—'}</div>
                  <div style={{ color: isMapped(s) ? 'var(--green)' : (s.days_since_created > 30 ? 'var(--red)' : 'var(--yellow)'), fontSize: 11, marginTop: 10 }}>
                    {isMapped(s) ? `${s.mapping_count} mapping · ${fmt(s.total_qty)} qty` : `${s.days_since_created || 0}d unmapped`}
                  </div>
                  <div style={{ color: 'var(--text3)', fontSize: 9, marginTop: 8, letterSpacing: 1 }}>OPEN →</div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

// ============================================================
// ORDER IN HAND view — default TASK-wise + tabs MONTH/BUYER/BRAND/MERCHANDISER
// ============================================================
// Module-level cache so the user's tab + drill-down survive a navigation
// to OrderDetail and back (component otherwise unmounts and resets to defaults).
// Keyed by isTas so TAS and GSP keep their own positions independently.
const _orderInHandState = {
  tas:    { tab: 'task', drillBy: null, searchQ: '', cacheData: null, cacheBreach: null, fetchedAt: 0 },
  gsp:    { tab: 'task', drillBy: null, searchQ: '', cacheData: null, cacheBreach: null, fetchedAt: 0 },
};
function OrderInHandView({ isTas, setView, Header, onOrderClick, onBuyerClick, buyersFromHealth, criticalOnly, bucket, filterTasNos, backTo, titleTag, exportSource, initialTab, factoryOnly }) {
  const _stateKey = isTas ? 'tas' : 'gsp';
  const _saved = _orderInHandState[_stateKey];
  const [tab, setTabRaw] = useState(initialTab || _saved.tab);

  // Instant render from module cache if available (eliminates loading screen on return)
  const norm = s => String(s || '').trim().toUpperCase();
  const applyFilters = (rawList, bSet) => {
    let rows = Array.isArray(rawList) ? rawList : [];
    const stageBehind = o => (o.current_stage_delay != null) && (o.current_stage_delay > 0);
    if (criticalOnly) {
      if (bucket === 'critical')
        rows = rows.filter(o => !(o.Delay_days < 0) && (isTas ? bSet.has(norm(o.tas_no)) : stageBehind(o)));
      else if (bucket === 'delayed')
        rows = rows.filter(o => o.Delay_days < 0);
      else
        rows = rows.filter(o => o.Delay_days < 0);
    }
    if (filterTasNos) {
      const set = filterTasNos instanceof Set ? filterTasNos : new Set((filterTasNos || []).map(norm));
      rows = rows.filter(o => set.has(norm(o.tas_no)));
    }
    return rows;
  };

  const hasFreshCache = _saved.cacheData && (Date.now() - _saved.fetchedAt < 60000);
  const [orders, setOrders] = useState(() => hasFreshCache ? applyFilters(_saved.cacheData, _saved.cacheBreach || new Set()) : []);
  const [loading, setLoading] = useState(!hasFreshCache);
  // FY filter removed from Order in Hand — always show all years
  const [yearFilter, setYearFilter] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo]     = useState('');
  const [searchQ, setSearchQRaw] = useState(_saved.searchQ);
  const [drillBy, setDrillByRaw] = useState(_saved.drillBy);
  // Layout toggle: card grid vs single-column vertical list.
  // GSP opens vertical by default (its rows carry more columns than a card shows well);
  // TAS keeps the card grid. Either can still be switched with the toggle.
  const [vertical, setVertical] = useState(!isTas);
  // Mirror state into the module cache on every change so a remount restores it.
  const setTab     = v => { _orderInHandState[_stateKey].tab     = typeof v === 'function' ? v(tab) : v;     setTabRaw(v); };
  const setDrillBy = v => { _orderInHandState[_stateKey].drillBy = typeof v === 'function' ? v(drillBy) : v; setDrillByRaw(v); };
  const setSearchQ = v => { _orderInHandState[_stateKey].searchQ = typeof v === 'function' ? v(searchQ) : v; setSearchQRaw(v); };

  useEffect(() => {
    // If we already rendered fresh cached data, don't show full loading flicker
    if (!hasFreshCache) {
      setLoading(true);
    }
    const pBreach = (criticalOnly && bucket === 'critical' && isTas)
      ? api('/orders/tas-critical').then(d => new Set((Array.isArray(d) ? d : []).map(x => norm(x.tas_no)))).catch(() => new Set())
      : Promise.resolve(new Set());
    Promise.all([api(`/orders?type=${isTas ? 'garment' : 'fabric'}`), pBreach])
      .then(([d, breachSet]) => {
        _orderInHandState[_stateKey].cacheData = d;
        _orderInHandState[_stateKey].cacheBreach = breachSet;
        _orderInHandState[_stateKey].fetchedAt = Date.now();
        setOrders(applyFilters(d, breachSet));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [isTas, criticalOnly, bucket, filterTasNos]);

  // FY filter
  function inFY(dateVal, fyKey) {
    if (!dateVal) return false;
    const d = new Date(dateVal);
    const startY = 2000 + parseInt(fyKey.slice(0, 2));
    const start = new Date(startY, 3, 1);
    const end   = new Date(startY + 1, 2, 31, 23, 59, 59);
    return d >= start && d <= end;
  }
  function inCustom(dateVal) {
    if (!dateVal) return false;
    const d = new Date(dateVal);
    if (customFrom && d < new Date(customFrom)) return false;
    if (customTo) {
      const end = new Date(customTo); end.setHours(23,59,59);
      if (d > end) return false;
    }
    return !customFrom && !customTo ? true : true;
  }
  const yearScoped = yearFilter === 'all' ? orders
                   : yearFilter === 'custom'
                     ? (customFrom || customTo
                         ? orders.filter(o => inCustom(o.enquiry_date || o.delivery_dt))
                         : orders)
                     : orders.filter(o => inFY(o.enquiry_date || o.delivery_dt, yearFilter));

  // Search filter
  const q = searchQ.trim().toLowerCase();
  const filteredAll = !q ? yearScoped : yearScoped.filter(o => {
    const hay = `${o.tas_no || ''} ${titleCase(o.factory) || ''} ${titleCase(o.merchandiser) || ''} ${titleCase(o.buyer) || ''} ${titleCase(o.style_name) || ''}`.toLowerCase();
    return hay.includes(q);
  });

  // Drill-down filter
  const list = drillBy ? filteredAll.filter(o => {
    if (drillBy.by === 'month')        return monthKeyFor(o) === drillBy.value;
    if (drillBy.by === 'merchandiser') return (o.merchandiser || '—') === drillBy.value;
    if (drillBy.by === 'buyer')        return (o.buyer || '—') === drillBy.value;
    if (drillBy.by === 'brand')        return (o.brand || '—') === drillBy.value;
    if (drillBy.by === 'style')        return (o.style_name || '—') === drillBy.value;
    if (drillBy.by === 'stage')        return (o.current_pending_stage || '—') === drillBy.value;
    if (drillBy.by === 'factory')      return (o.factory || '—') === drillBy.value;
    return true;
  }) : filteredAll;

  function monthKeyFor(o) {
    const d = o.delivery_dt && new Date(o.delivery_dt);
    if (!d || isNaN(d)) return '—';
    return d.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
  }

  // Aggregations
  const groupBy = (key, sortFn) => {
    const map = new Map();
    for (const o of filteredAll) {
      const k = key(o) || '—';
      if (!map.has(k)) map.set(k, { key: k, count: 0, qty: 0, value: 0, overdue: 0, _sortKey: null });
      const g = map.get(k);
      g.count++;
      g.qty   += (o.bal_qty || o.orderqty || 0);
      g.value += (o.order_value || 0);
      if (o.Delay_days < 0) g.overdue++;
      // For month sort: track earliest delivery date in the group
      if (o.delivery_dt) {
        const t = new Date(o.delivery_dt).getTime();
        if (!g._sortKey || t < g._sortKey) g._sortKey = t;
      }
    }
    const arr = [...map.values()];
    if (sortFn) arr.sort(sortFn);
    else arr.sort((a, b) => b.count - a.count);
    return arr;
  };

  // Chronological sort for month groups (Jan→Dec, oldest year first)
  const monthSort = (a, b) => {
    if (a.key === '—') return 1;
    if (b.key === '—') return -1;
    return (a._sortKey || 0) - (b._sortKey || 0);
  };

  const accent = isTas ? 'var(--gold)' : 'var(--teal)';

  if (loading) return (
    <div className="screen">
      <Header subtitle={titleTag || (isTas ? 'TAS · ORDER IN HAND' : 'GSP · ORDER IN HAND')} />
      <div className="loading">LOADING ORDERS…</div>
    </div>
  );

  // Render the TAS grid
  const renderTaskGrid = (rows) => {
    if (vertical) {
      const th = { padding: '8px 8px', textAlign: 'right', background: 'var(--bg2)', color: 'var(--text3)', fontSize: 10, letterSpacing: 0.6, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
      const td = { padding: '8px 8px', textAlign: 'right', borderBottom: '1px solid var(--border)', fontSize: 12, whiteSpace: 'nowrap' };
      return (
        <div style={{ padding: '8px 12px 24px', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {rows.length === 0 ? <div style={{ padding: 30, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>No orders for this filter</div> : (
            <table className="responsive-order-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={{ ...th, textAlign: 'left' }}>{isTas ? 'TAS' : 'GSP'}</th>
                <th style={{ ...th, textAlign: 'left' }}>BUYER</th>
                <th style={{ ...th, textAlign: 'left' }}>FACTORY</th>
                <th className="hide-on-mobile" style={th}>{isTas ? 'QTY' : 'MTRS'}</th>
                <th style={th}>%</th>
                <th style={th}>DELAY</th>
                <th className="hide-on-mobile" style={th}>VALUE</th>
              </tr></thead>
              <tbody>
                {rows.map(o => {
                  const c = o.health === 'critical' || o.health === 'overdue' ? 'var(--red)' : o.health === 'at_risk' ? 'var(--yellow)' : 'var(--green)';
                  const overdue = o.Delay_days != null && o.Delay_days < 0;
                  const dayLbl = o.Delay_days == null ? '—' : overdue ? `${Math.abs(o.Delay_days)}d late` : o.Delay_days === 0 ? 'Today' : `${o.Delay_days}d`;
                  return (
                    <tr key={o.iid + '_' + o.tas_no} onClick={() => onOrderClick(o)} style={{ cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ ...td, textAlign: 'left', color: 'var(--text)', fontWeight: 600, borderLeft: `3px solid ${c}` }}>
                        <div>{o.tas_no}</div>
                        <div className="show-on-mobile-inline" style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 400 }}>{(o.bal_qty || o.orderqty || 0).toLocaleString()} {isTas ? 'pcs' : 'm'}</div>
                      </td>
                      <td style={{ ...td, textAlign: 'left', color: 'var(--text2)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>{titleCase(o.buyer) || '—'}</td>
                      <td style={{ ...td, textAlign: 'left', color: 'var(--text3)', maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis' }}>{titleCase(o.factory) || '—'}</td>
                      <td className="hide-on-mobile" style={{ ...td, color: 'var(--text2)' }}>{(o.bal_qty || o.orderqty || 0).toLocaleString()}</td>
                      <td style={{ ...td, color: 'var(--text3)', fontWeight: 600 }}>{o.completion_pct || 0}%</td>
                      <td style={{ ...td, color: overdue ? 'var(--red)' : 'var(--green)', fontWeight: 600 }}>{dayLbl}</td>
                      <td className="hide-on-mobile" style={{ ...td, color: 'var(--gold)' }}>{o.order_value > 0 ? fmtCr(o.order_value) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      );
    }
    return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12, padding: '8px 16px 24px' }}>
      {rows.length === 0 && <div style={{ gridColumn: '1/-1', padding: 30, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>No orders for this filter</div>}
      {rows.map(o => {
        const c = o.health === 'critical' || o.health === 'overdue' ? 'var(--red)'
                : o.health === 'at_risk' ? 'var(--yellow)' : 'var(--green)';
        const overdue = o.Delay_days < 0;
        const dayLbl = overdue ? `${Math.abs(o.Delay_days)}d overdue`
                     : o.Delay_days === 0 ? 'Due today'
                     : o.Delay_days <= 7 ? `${o.Delay_days}d left` : `${o.Delay_days}d`;
        return (
          <div key={o.iid + '_' + o.tas_no}
               onClick={() => onOrderClick(o)}
               style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderLeft: `3px solid ${c}`, borderRadius: 8, padding: 12, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 8 }}
               onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg3)'; e.currentTarget.style.borderColor = c; }}
               onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg2)'; e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.borderLeftColor = c; }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 600 }}>{o.tas_no}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{titleCase(o.buyer)}</div>
              </div>
              <div style={{ fontSize: 9, padding: '3px 7px', borderRadius: 3, background: overdue ? 'var(--red-bg)' : 'var(--green-bg)', color: overdue ? 'var(--red)' : 'var(--green)', whiteSpace: 'nowrap' }}>{dayLbl}</div>
            </div>
            <div>
              <div style={{ height: 4, background: 'var(--border)', borderRadius: 2 }}>
                <div style={{ height: '100%', background: c, width: `${o.completion_pct || 0}%`, borderRadius: 2 }} />
              </div>
              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3, textAlign: 'right' }}>{o.completion_pct || 0}%</div>
            </div>
            <div style={{ fontSize: 10, color: 'var(--text3)', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 100 }}>🏭 {titleCase(o.factory) || '—'}</span>
              <span>📦 {(o.bal_qty || o.orderqty || 0).toLocaleString()}</span>
              {o.order_value > 0 && <span style={{ color: 'var(--gold)', marginLeft: 'auto' }}>{fmtCr(o.order_value)}</span>}
            </div>
          </div>
        );
      })}
    </div>
    );
  };

  // Render group cards — card grid by default, or a compact table in vertical mode.
  const renderGroupCards = (groups, by) => {
    if (vertical) {
      const th = { padding: '8px 10px', textAlign: 'right', background: 'var(--bg2)', color: 'var(--text3)', fontSize: 10, letterSpacing: 0.6, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
      const td = { padding: '9px 10px', textAlign: 'right', borderBottom: '1px solid var(--border)', fontSize: 12, whiteSpace: 'nowrap' };
      return (
        <div style={{ padding: '8px 16px 24px', overflowX: 'auto' }}>
          {groups.length === 0 ? <div style={{ padding: 30, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>No groups</div> : (
            <table style={{ width: '100%', minWidth: 520, borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={{ ...th, textAlign: 'left' }}>{String(by).toUpperCase()}</th>
                <th style={th}>{isTas ? 'TAS' : 'GSP'}</th>
                <th style={th}>{isTas ? 'PCS' : 'MTRS'}</th>
                <th style={th}>OVERDUE</th>
                <th style={th}>VALUE</th>
              </tr></thead>
              <tbody>
                {groups.map(g => (
                  <tr key={g.key} onClick={factoryOnly ? undefined : () => setDrillBy({ by, value: g.key })}
                      style={{ cursor: factoryOnly ? 'default' : 'pointer' }}
                      onMouseEnter={factoryOnly ? undefined : (e => e.currentTarget.style.background = 'var(--bg3)')}
                      onMouseLeave={factoryOnly ? undefined : (e => e.currentTarget.style.background = 'transparent')}>
                    <td style={{ ...td, textAlign: 'left', color: 'var(--text)', fontWeight: 600, borderLeft: `3px solid ${accent}` }}>{g.key}</td>
                    <td style={{ ...td, color: 'var(--text2)' }}>{g.count}</td>
                    <td style={{ ...td, color: 'var(--text2)' }}>{g.qty.toLocaleString()}</td>
                    <td style={{ ...td, color: g.overdue > 0 ? 'var(--red)' : 'var(--text3)' }}>{g.overdue}</td>
                    <td style={{ ...td, color: 'var(--gold)' }}>{fmtCr(g.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }
    return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, padding: '8px 16px 24px' }}>
      {groups.length === 0 && <div style={{ gridColumn: '1/-1', padding: 30, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>No groups</div>}
      {groups.map(g => (
        <div key={g.key}
             onClick={factoryOnly ? undefined : () => setDrillBy({ by, value: g.key })}
             style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderLeft: `3px solid ${accent}`, borderRadius: 8, padding: 14, cursor: factoryOnly ? 'default' : 'pointer', display: 'flex', flexDirection: 'column', gap: 6 }}
             onMouseEnter={factoryOnly ? undefined : (e => { e.currentTarget.style.background = 'var(--bg3)'; e.currentTarget.style.borderColor = accent; })}
             onMouseLeave={factoryOnly ? undefined : (e => { e.currentTarget.style.background = 'var(--bg2)'; e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.borderLeftColor = accent; })}>
          <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 600 }}>{g.key}</div>
          {!factoryOnly && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text3)' }}>
            <span>{g.count} {isTas ? 'TAS' : 'GSP'}</span>
            <span>{g.qty.toLocaleString()} {isTas ? 'pcs' : 'mtrs'}</span>
          </div>
          )}
          {!factoryOnly && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
            <span style={{ color: g.overdue > 0 ? 'var(--red)' : 'var(--text3)' }}>{g.overdue} overdue</span>
            <span style={{ color: 'var(--gold)' }}>{fmtCr(g.value)}</span>
          </div>
          )}
        </div>
      ))}
    </div>
    );
  };

  // If user drilled into a group → show TAS grid for that group
  if (drillBy) {
    return (
      <div className="screen">
        <Header subtitle={`${isTas ? 'TAS' : 'GSP'} · ${drillBy.by.toUpperCase()}`} />
        <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
          <button onClick={() => setDrillBy(null)} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: accent }}>{drillBy.value}</div>
          <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>{list.length} {isTas ? 'TAS' : 'GSP'}</div>
        </div>
        {renderTaskGrid([...list].sort(compareTas))}
      </div>
    );
  }

  const tabs = [
    { key: 'task',         label: isTas ? 'TAS-WISE' : 'GSP-WISE' },
    { key: 'style',        label: 'STYLE-WISE'       },
    { key: 'month',        label: 'MONTH-WISE'       },
    { key: 'buyer',        label: 'BUYER-WISE'       },
    { key: 'brand',        label: 'BRAND-WISE'       },
    { key: 'merchandiser', label: 'MERCHANDISER-WISE'},
    { key: 'factory',      label: 'FACTORY-WISE'      },
    // Stage-wise grouping — both TAS and GSP. Each order carries
    // current_pending_stage from /orders payload (TAS via _pendingMap,
    // GSP via _gspPendingMap).
    { key: 'stage',        label: 'STAGE-WISE' }
  ];

  return (
    <div className="screen">
      <Header subtitle={
        titleTag
          ? titleTag
          : criticalOnly
            ? (isTas ? `TAS · ${bucket === 'delayed' ? 'DELAYED' : 'CRITICAL'}` : `GSP · ${bucket === 'delayed' ? 'DELAYED' : 'CRITICAL'}`)
            : (isTas ? 'TAS · ORDER IN HAND' : 'GSP · ORDER IN HAND')
      } />
      <ExportBar reportKey={titleTag ? 'bd-orders' : criticalOnly ? `${isTas ? 'tas' : 'gsp'}-${bucket === 'delayed' ? 'delayed' : 'critical'}` : `${isTas ? 'tas' : 'gsp'}-order-in-hand`} title={titleTag || (criticalOnly ? `${isTas ? 'TAS' : 'GSP'} ${bucket === 'delayed' ? 'Delayed' : 'Critical'} Orders` : `${isTas ? 'TAS' : 'GSP'} Order in Hand`)} subtitle={[tab && tab !== 'tas' && tab !== 'gsp' && tab !== 'all' ? `group: ${tab}` : '', drillBy ? `${drillBy.by}: ${drillBy.value}` : '', searchQ ? `search: ${searchQ}` : ''].filter(Boolean).join(' | ')} sections={[{ name: 'Orders', columns: ORDER_COLS, rows: drillBy ? list : filteredAll }]} source={exportSource || (filterTasNos ? null : {
        endpoint: '/orders', params: { type: isTas ? 'garment' : 'fabric' },
        filters: [
          // Critical = a key/given stage is overdue (stage behind) AND not yet past delivery date;
          // Delayed = delivery date passed. Overdue orders go to Delayed only.
          ...(criticalOnly && bucket === 'critical'
                ? [{ t: 'gt', k: 'current_stage_delay', v: 0 }, { t: 'not', f: { t: 'lt', k: 'Delay_days', v: 0 } }] : []),
          ...(criticalOnly && bucket === 'delayed'
                ? [{ t: 'lt', k: 'Delay_days', v: 0 }] : []),
          ...(searchQ ? [{ t: 'search', q: searchQ, keys: ['tas_no', 'factory', 'merchandiser', 'buyer', 'style_name'] }] : []),
          ...(drillBy && ['buyer', 'merchandiser', 'factory', 'brand'].includes(drillBy.by)
                ? [{ t: 'eq', k: drillBy.by, v: drillBy.value }] : []),
        ],
      })} />
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
        <button onClick={() => setView(backTo || (isTas ? 'tas' : 'gsp'))} style={{ background: 'none', border: 'none', color: criticalOnly ? (bucket === 'delayed' ? 'var(--yellow)' : 'var(--red)') : accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: criticalOnly ? (bucket === 'delayed' ? 'var(--yellow)' : 'var(--red)') : accent }}>
          {factoryOnly ? 'Factory' : criticalOnly ? (bucket === 'delayed' ? '⏱ Delayed Orders' : '⚠ Critical Orders') : 'Order in Hand'}
        </div>
        <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>{factoryOnly ? `${groupBy(o => o.factory || '—').length} factories` : `${filteredAll.length} ${isTas ? 'TAS' : 'GSP'}`}</div>
      </div>

      {/* Grouping tabs */}
      {!factoryOnly && (
      <div style={{ padding: '10px 16px 4px', display: 'flex', gap: 6, flexWrap: 'wrap', borderBottom: '1px solid var(--border)' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); setDrillBy(null); }}
            style={{
              background: tab === t.key ? `${accent}26` : 'transparent',
              border: `1px solid ${tab === t.key ? accent : 'var(--border)'}`,
              color: tab === t.key ? accent : 'var(--text3)',
              padding: '5px 12px', borderRadius: 14, fontSize: 11, cursor: 'pointer', letterSpacing: 1
            }}>{t.label}</button>
        ))}
        {/* Layout toggle — vertical (single column) vs card grid. Sits after STAGE-WISE. */}
        <button onClick={() => setVertical(v => !v)}
          title={vertical ? 'Switch to grid layout' : 'Switch to vertical layout'}
          style={{
            background: vertical ? `${accent}26` : 'transparent',
            border: `1px solid ${vertical ? accent : 'var(--border)'}`,
            color: vertical ? accent : 'var(--text3)',
            padding: '5px 12px', borderRadius: 14, fontSize: 11, cursor: 'pointer', letterSpacing: 1
          }}>≣ VERTICAL</button>
      </div>
      )}

      {/* Search box */}
      <div style={{ padding: '8px 16px 4px', position: 'relative' }}>
        <input
          type="text" value={searchQ} onChange={e => setSearchQ(e.target.value)}
          placeholder={isTas ? "Search TAS, factory, buyer, merchandiser, style…" : "Search GSP, buyer, brand, merchandiser…"}
          style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 12, outline: 'none' }}
          onFocus={e => e.target.style.borderColor = accent}
          onBlur={e => e.target.style.borderColor = 'var(--border)'}
        />
      </div>

      {/* FY filter removed from Order in Hand */}

      {/* Tab content */}
      {tab === 'task'         && renderTaskGrid([...filteredAll].sort(compareTas))}
      {tab === 'style'        && renderGroupCards(groupBy(o => o.style_name || '—'), 'style')}
      {tab === 'month'        && renderGroupCards(groupBy(monthKeyFor, monthSort), 'month')}
      {tab === 'buyer'        && renderGroupCards(groupBy(o => o.buyer || '—'), 'buyer')}
      {tab === 'brand'        && renderGroupCards(groupBy(o => o.brand || '—'), 'brand')}
      {tab === 'merchandiser' && renderGroupCards(groupBy(o => o.merchandiser || '—'), 'merchandiser')}
      {tab === 'factory'      && renderGroupCards(groupBy(o => o.factory || '—'), 'factory')}
      {tab === 'stage'        && renderGroupCards(groupBy(o => o.current_pending_stage || '—'), 'stage')}
    </div>
  );
}

// ============================================================
// TAS FABRIC ROUTING — TAS (garment) orders currently pending at a FABRIC routing
// stage (ERP Form 8056 / sp_GridReport_8056). A self-fetching KPI card for the GSP
// War Room + a full drill-down list. CurrentDelay > 0 = overdue.
// ============================================================
// GSP (fabric) orders pending at a fabric routing stage — same format as TAS Fabric Routing.
function GspRoutingView({ setView, Header, onOrderClick, embedded }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  useEffect(() => { api('/orders/gsp-fabric-routing').then(d => setRows(Array.isArray(d) ? d : [])).catch(() => setRows([])); }, []);
  if (rows === null) return (<div className={embedded ? '' : 'screen'}>{!embedded && <Header subtitle="GSP · ROUTING" />}<div className="loading">LOADING GSP ROUTING…</div></div>);
  const ql = q.trim().toLowerCase();
  const list = ql ? rows.filter(r => [r.GspNo, r.customer, r.fabric_name, r.CurrentPendingStage].some(v => String(v || '').toLowerCase().includes(ql))) : rows;
  const overdue = rows.filter(r => (r.CurrentDelay || 0) > 0).length;
  const mtrs = rows.reduce((s, r) => s + (r.meters || 0), 0);
  const cell = { padding: '8px 10px', fontSize: 12, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
  const hd = { ...cell, color: 'var(--text3)', fontSize: 10, letterSpacing: 1, textAlign: 'left', position: 'sticky', top: 0, background: 'var(--bg2)' };
  const stat = (label, value, color) => (
    <div style={{ flex: '1 1 120px', textAlign: 'center', padding: '10px 8px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg2)' }}>
      <div style={{ color: color || 'var(--text)', fontSize: 24, fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>{value}</div>
      <div style={{ color: 'var(--text3)', fontSize: 10, letterSpacing: 1, marginTop: 2 }}>{label}</div>
    </div>
  );
  return (
    <div className={embedded ? '' : 'screen'}>
      {!embedded && <Header subtitle="GSP · ROUTING" />}
      <ExportBar reportKey="gsp-fabric-routing" title="GSP Routing" subtitle={q ? `search: ${q}` : ''} sections={[{ name: 'Routing', columns: GSP_ROUTING_COLS, rows: list }]} source={{ endpoint: '/orders/gsp-fabric-routing', filters: q ? [{ t: 'search', q, keys: ['GspNo', 'customer', 'fabric_name', 'CurrentPendingStage'] }] : [] }} />
      {!embedded && (
        <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
          <button onClick={() => setView('gsp')} style={{ background: 'none', border: 'none', color: 'var(--teal)', fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: 'var(--teal)' }}>GSP Routing — {rows.length} orders</div>
        </div>
      )}
      <div style={{ padding: '14px 16px 6px', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {stat('ORDERS', fmt(rows.length), 'var(--teal)')}
        {stat('OVERDUE', fmt(overdue), overdue ? 'var(--red)' : 'var(--text)')}
        {stat('METERS PENDING', fmt(Math.round(mtrs)))}
      </div>
      <div style={{ padding: '6px 16px 10px' }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search GSP / customer / fabric / stage…"
          style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 13, outline: 'none' }} />
      </div>
      <div style={{ margin: '0 16px 24px', border: '1px solid var(--border)', borderRadius: 8, overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 980, borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={hd}>GSP</th><th style={hd}>Customer</th><th style={hd}>Fabric</th><th style={hd}>Pending Stage</th>
              <th style={hd}>Stage Est. Date</th><th style={{ ...hd, textAlign: 'right' }}>Meters</th><th style={{ ...hd, textAlign: 'right' }}>Delay</th><th style={hd}>New Delivery</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={8} style={{ ...cell, textAlign: 'center', color: 'var(--text3)' }}>No GSP orders in fabric routing.</td></tr>}
            {list.map((r, i) => (
              <tr key={r.GspNo + i} onClick={() => onOrderClick && onOrderClick(r)} style={{ cursor: onOrderClick ? 'pointer' : 'default' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <td style={{ ...cell, fontWeight: 700, color: 'var(--teal)' }}>{r.GspNo}</td>
                <td style={cell}>{titleCase(r.customer) || '—'}</td>
                <td style={cell}>{r.fabric_name || '—'}</td>
                <td style={cell}>{r.CurrentPendingStage || '—'}</td>
                <td style={cell}>{r.stage_eta || '—'}</td>
                <td style={{ ...cell, textAlign: 'right' }}>{fmt(Math.round(r.meters || 0))}</td>
                <td style={{ ...cell, textAlign: 'right', color: (r.CurrentDelay || 0) > 0 ? 'var(--red)' : 'var(--text3)', fontWeight: (r.CurrentDelay || 0) > 0 ? 700 : 400 }}>{(r.CurrentDelay || 0) > 0 ? `${r.CurrentDelay}d late` : '—'}</td>
                <td style={cell}>{r.new_delivery || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// One Fabric Routing section with two sub-tabs: TAS Fabric Routing + GSP Routing.
function FabricRoutingTabsView({ setView, Header, onOrderClick }) {
  const [sub, setSub] = useState('tas');
  const teal = 'var(--teal)';
  return (
    <div className="screen">
      <Header subtitle="GSP · FABRIC ROUTING" />
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => setView('gsp')} style={{ background: 'none', border: 'none', color: teal, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: teal }}>Fabric Routing</div>
      </div>
      <div style={{ padding: '0 20px 12px', display: 'flex', gap: 6, flexWrap: 'wrap', borderBottom: '1px solid var(--border)' }}>
        {[['tas', 'TAS'], ['gsp', 'GSP']].map(([k, t]) => (
          <button key={k} onClick={() => setSub(k)}
            style={{ padding: '6px 14px', borderRadius: 14, fontSize: 11, fontWeight: 700, cursor: 'pointer', border: `1px solid ${sub === k ? teal : 'var(--border)'}`, background: sub === k ? teal : 'var(--bg2)', color: sub === k ? '#fff' : 'var(--text2)' }}>{t}</button>
        ))}
      </div>
      {sub === 'tas' && <TasFabricRoutingView embedded setView={setView} Header={Header} onOrderClick={onOrderClick} />}
      {sub === 'gsp' && <GspRoutingView embedded setView={setView} Header={Header} onOrderClick={onOrderClick} />}
    </div>
  );
}

function FabricRoutingCard({ onOpen }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let alive = true;
    api('/orders/tas-fabric-routing').then(d => {
      if (!alive) return;
      const rows = Array.isArray(d) ? d : [];
      setStats({
        count:   rows.length,
        overdue: rows.filter(r => (r.CurrentDelay || 0) > 0).length,
        mtrs:    rows.reduce((s, r) => s + (r.meters || 0), 0),
      });
    }).catch(() => alive && setStats({ count: 0, overdue: 0, mtrs: 0 }));
    return () => { alive = false; };
  }, []);
  return (
    <div
      onClick={onOpen}
      style={{
        flex: '1 1 200px', maxWidth: 280,
        padding: '14px 16px', border: '1px solid var(--border)', borderRadius: 8,
        textAlign: 'center', background: 'rgba(91,194,199,0.06)',
        borderLeft: '3px solid #5BC2C7', cursor: 'pointer', transition: 'all 0.2s'
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--teal-bg)'; e.currentTarget.style.borderColor = 'var(--teal)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(91,194,199,0.06)'; e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.borderLeftColor = 'var(--teal)'; }}
    >
      <div style={{ color: 'var(--teal)', fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: 32 }}>
        {stats ? fmt(stats.count) : '…'}
      </div>
      <div style={{ color: 'var(--teal)', fontSize: 11, letterSpacing: 2, marginTop: 2 }}>FABRIC ROUTING</div>
      <div style={{ color: 'var(--text3)', fontSize: 10, marginTop: 4 }}>
        {stats ? `${fmt(Math.round(stats.mtrs))} mtrs pending` : ' '}
      </div>
      <div style={{ color: stats && stats.overdue ? 'var(--red)' : 'var(--text2)', fontSize: 10, marginTop: 2 }}>
        {stats && stats.overdue ? `${stats.overdue} overdue` : 'stuck in fabric stage'}
      </div>
      <div style={{ color: 'var(--text3)', fontSize: 9, marginTop: 4, letterSpacing: 1 }}>VIEW DETAILS →</div>
    </div>
  );
}

function TasFabricRoutingView({ setView, Header, onOrderClick, embedded }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  useEffect(() => {
    api('/orders/tas-fabric-routing').then(d => setRows(Array.isArray(d) ? d : [])).catch(() => setRows([]));
  }, []);

  if (rows === null) {
    return (<div className={embedded ? '' : 'screen'}>{!embedded && <Header subtitle="TAS · FABRIC ROUTING" />}<div className="loading">LOADING FABRIC ROUTING…</div></div>);
  }

  const ql = q.trim().toLowerCase();
  const list = ql
    ? rows.filter(r => [r.TasNo, r.customer, r.merchandiser, r.CurrentPendingStage, r.fabric_name]
        .some(v => String(v || '').toLowerCase().includes(ql)))
    : rows;
  const overdue = rows.filter(r => (r.CurrentDelay || 0) > 0).length;
  const mtrs = rows.reduce((s, r) => s + (r.meters || 0), 0);

  const cell = { padding: '8px 10px', fontSize: 12, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
  const hd   = { ...cell, color: 'var(--text3)', fontSize: 10, letterSpacing: 1, textAlign: 'left', position: 'sticky', top: 0, background: 'var(--bg2)' };
  const stat = (label, value, color) => (
    <div style={{ flex: '1 1 120px', textAlign: 'center', padding: '10px 8px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg2)' }}>
      <div style={{ color: color || 'var(--text)', fontSize: 24, fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>{value}</div>
      <div style={{ color: 'var(--text3)', fontSize: 10, letterSpacing: 1, marginTop: 2 }}>{label}</div>
    </div>
  );

  return (
    <div className={embedded ? '' : 'screen'}>
      {!embedded && <Header subtitle="TAS · FABRIC ROUTING" />}
      <ExportBar reportKey="tas-fabric-routing" title="TAS Fabric Routing" subtitle={q ? `search: ${q}` : ''} sections={[{ name: 'Routing', columns: FABRIC_ROUTING_COLS, rows: list }]} source={{ endpoint: '/orders/tas-fabric-routing', filters: q ? [{ t: 'search', q, keys: ['TasNo', 'customer', 'fabric_name', 'CurrentPendingStage'] }] : [] }} />

      {/* Back row */}
      {!embedded && (
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
        <button onClick={() => setView('gsp')} style={{ background: 'none', border: 'none', color: 'var(--teal)', fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: 'var(--teal)' }}>TAS Fabric Routing — {rows.length} orders</div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>Garment orders currently pending at a fabric stage</div>
        </div>
      </div>
      )}

      {/* Summary strip */}
      <div style={{ padding: '14px 16px 6px', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {stat('ORDERS', fmt(rows.length), 'var(--teal)')}
        {stat('OVERDUE', fmt(overdue), overdue ? 'var(--red)' : 'var(--text)')}
        {stat('METERS PENDING', fmt(Math.round(mtrs)))}
      </div>

      {/* Search */}
      <div style={{ padding: '6px 16px 10px' }}>
        <input
          value={q} onChange={e => setQ(e.target.value)}
          placeholder="Search TAS / customer / stage…"
          style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: 13, outline: 'none' }}
        />
      </div>

      {/* Table (scrolls horizontally on mobile) */}
      <div style={{ margin: '0 16px 24px', border: '1px solid var(--border)', borderRadius: 8, overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 960, borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={hd}>TAS</th>
              <th style={hd}>Customer</th>
              <th style={hd}>Fabric</th>
              <th style={hd}>Pending Stage</th>
              <th style={hd}>Stage Est. Date</th>
              <th style={{ ...hd, textAlign: 'right' }}>Meters</th>
              <th style={{ ...hd, textAlign: 'right' }}>Delay</th>
              <th style={hd}>New Delivery</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan={8} style={{ ...cell, textAlign: 'center', color: 'var(--text3)' }}>No orders</td></tr>
            )}
            {list.map((r, i) => (
              <tr key={r.TasNo + '_' + i}
                onClick={() => onOrderClick && onOrderClick({ tas_no: r.TasNo, buyer: r.customer, order_type: 'garment' })}
                style={{ cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(91,194,199,0.06)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <td style={{ ...cell, color: 'var(--teal)', fontWeight: 600 }}>{r.TasNo}</td>
                <td style={{ ...cell, color: 'var(--text)' }}>{titleCase(r.customer)}</td>
                <td style={{ ...cell, color: 'var(--text2)' }}>{r.fabric_name || '—'}</td>
                <td style={{ ...cell, color: 'var(--text2)' }}>{r.CurrentPendingStage || '—'}</td>
                <td style={{ ...cell, color: 'var(--text2)' }}>{r.stage_eta || '—'}</td>
                <td style={{ ...cell, textAlign: 'right', color: 'var(--text2)' }}>{fmt(Math.round(r.meters || 0))}</td>
                <td style={{ ...cell, textAlign: 'right', color: (r.CurrentDelay || 0) > 0 ? 'var(--red)' : 'var(--text3)', fontWeight: (r.CurrentDelay || 0) > 0 ? 600 : 400 }}>
                  {(r.CurrentDelay || 0) > 0 ? `${r.CurrentDelay}d late` : 'on track'}
                </td>
                <td style={{ ...cell, color: 'var(--text2)' }}>{r.NewDeliveryDate || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================
// SALES view — buyer-wise sales summary + drill-down to invoices
// ============================================================
function SalesView(props) {
  if (!props.isTas) return <GspSalesView {...props} />;
  return <TasSalesView {...props} />;
}

// Horizontal-scroll wrapper for wide content on mobile.
function ScrollX({ children, min = 520 }) {
  return (
    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <div style={{ minWidth: min }}>{children}</div>
    </div>
  );
}

// ============================================================
// GSP Sales — uses Form 5569 "Fabric Sales Invoice"
//   Two tabs: BUYER-WISE / MONTH-WISE
//   Click a row → drill to invoice list (qty in mtrs)
// ============================================================
function GspSalesView({ setView, Header }) {
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salesTab, setSalesTab] = useState('buyer'); // 'buyer' | 'month' | 'design' | 'today'
  const [asOnTab, setAsOnTab] = useState('buyer');   // sub-tab inside AS ON TODAY
  const [asToday, setAsToday] = useState(null);       // YTD comparison payload
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [buyersPrev, setBuyersPrev] = useState(null);    // previous FY buyer-wise (YoY)
  const [monthsPrev, setMonthsPrev] = useState(null);    // previous FY by-month (YoY)
  const [designs, setDesigns] = useState(null);          // current FY by-design (quality)
  const [designsPrev, setDesignsPrev] = useState(null);  // previous FY (for YoY)
  // Buyer-wise drill for a clicked Design or Month row. { kind:'design'|'month', title, design?, from?, to? }
  const [buyerDrill, setBuyerDrill] = useState(null);
  const [buyerDrillRows, setBuyerDrillRows] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [invLoading, setInvLoading] = useState(false);
  const [yearFilter, setYearFilter] = useState(currentFY());
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo]     = useState('');

  const yearQS = (() => {
    if (yearFilter === 'all') return '';
    if (yearFilter === 'custom') {
      const parts = [];
      if (customFrom) parts.push(`from=${customFrom}`);
      if (customTo)   parts.push(`to=${customTo}`);
      return parts.length ? '&' + parts.join('&') : '';
    }
    const startY = 2000 + parseInt(yearFilter.slice(0, 2));
    return `&from=${startY}-04-01&to=${startY + 1}-03-31`;
  })();
  // Previous FY range (YoY) — only when a specific FY is selected.
  const prevYearQS = (yearFilter !== 'all' && yearFilter !== 'custom')
    ? (() => { const s = 2000 + parseInt(yearFilter.slice(0, 2)) - 1; return `&from=${s}-04-01&to=${s + 1}-03-31`; })()
    : null;

  useEffect(() => {
    setLoading(true);
    const empty = Promise.resolve([]);
    const emptySummary = Promise.resolve({ buyers: [] });
    Promise.all([
      api('/sales/fabric-summary?_=1'  + yearQS),
      api('/sales/fabric-by-month?_=1' + yearQS),
      api('/sales/fabric-by-quality?_=1' + yearQS),
      prevYearQS ? api('/sales/fabric-by-quality?_=1' + prevYearQS) : empty,
      prevYearQS ? api('/sales/fabric-summary?_=1'  + prevYearQS) : emptySummary,
      prevYearQS ? api('/sales/fabric-by-month?_=1' + prevYearQS) : empty,
    ]).then(([s, m, dz, dzp, sp, mp]) => {
      setData(s); setMonths(Array.isArray(m) ? m : []);
      setDesigns(Array.isArray(dz) ? dz : []); setDesignsPrev(Array.isArray(dzp) ? dzp : []);
      setBuyersPrev(Array.isArray(sp?.buyers) ? sp.buyers : []); setMonthsPrev(Array.isArray(mp) ? mp : []);
      setLoading(false);
    });
  }, [yearFilter, customFrom, customTo]);

  useEffect(() => {
    if (!selectedBuyer && !selectedMonth) { setInvoices(null); return; }
    setInvLoading(true);
    let qs = yearQS;
    // When a buyer is clicked from inside a Month/Design drill, scope its invoices to
    // that drill's period (month → that month; design → its FY).
    if (selectedBuyer && buyerDrill && buyerDrill.curFrom) {
      qs = `&from=${buyerDrill.curFrom}&to=${buyerDrill.curTo}`;
    } else if (selectedBuyer && buyerDrill && buyerDrill.curQS) {
      qs = buyerDrill.curQS;
    } else if (selectedMonth) {
      const { yr, mo } = selectedMonth;
      const from = `${yr}-${String(mo).padStart(2,'0')}-01`;
      const last = new Date(yr, mo, 0).getDate();
      const to   = `${yr}-${String(mo).padStart(2,'0')}-${String(last).padStart(2,'0')}`;
      qs = `&from=${from}&to=${to}`;
    }
    const buyerQS = selectedBuyer ? `&buyer=${encodeURIComponent(selectedBuyer)}` : '';
    api(`/sales/fabric-invoices?_=1${buyerQS}${qs}`)
      .then(d => { setInvoices(Array.isArray(d) ? d : []); setInvLoading(false); });
  }, [selectedBuyer, selectedMonth, buyerDrill, yearFilter, customFrom, customTo]);

  // Buyer-wise YoY breakdown for a clicked Design or Month. Fetches current AND
  // previous period, merges by buyer → [{ buyer, cur, prev }] so the drill shows the
  // same FY-vs-FY comparison the main tab shows, but per buyer.
  useEffect(() => {
    if (!buyerDrill) { setBuyerDrillRows(null); return; }
    setBuyerDrillRows(null);
    const empty = Promise.resolve(null);
    const urlFor = (which) => {
      if (buyerDrill.kind === 'design') {
        const qs = which === 'cur' ? (buyerDrill.curQS || '') : buyerDrill.prevQS;
        return qs != null ? `/sales/fabric-quality-buyers?_=1&design=${encodeURIComponent(buyerDrill.design)}${qs}` : null;
      }
      const from = which === 'cur' ? buyerDrill.curFrom : buyerDrill.prevFrom;
      const to   = which === 'cur' ? buyerDrill.curTo   : buyerDrill.prevTo;
      return from ? `/sales/fabric-summary?_=1&from=${from}&to=${to}` : null;
    };
    const norm = d => {
      const rows = buyerDrill.kind === 'design' ? (Array.isArray(d) ? d : []) : (Array.isArray(d?.buyers) ? d.buyers : []);
      return rows.map(b => ({ ...b, avg_rate: b.avg_rate != null ? b.avg_rate : (b.total_qty > 0 ? b.total_amount / b.total_qty : 0) }));
    };
    const curUrl = urlFor('cur'), prevUrl = urlFor('prev');
    Promise.all([curUrl ? api(curUrl) : empty, prevUrl ? api(prevUrl) : empty]).then(([c, p]) => {
      const cur = norm(c), prev = norm(p);
      const map = new Map();
      cur.forEach(b => map.set(b.buyer, { buyer: b.buyer, cur: b, prev: null }));
      prev.forEach(b => { if (!map.has(b.buyer)) map.set(b.buyer, { buyer: b.buyer, cur: null, prev: b }); else map.get(b.buyer).prev = b; });
      const merged = [...map.values()].sort((a, b) => (+(b.cur?.total_amount || 0)) - (+(a.cur?.total_amount || 0)));
      setBuyerDrillRows(merged);
    }).catch(() => setBuyerDrillRows([]));
  }, [buyerDrill, yearFilter, customFrom, customTo]);

  // AS ON TODAY: year-to-date comparison (this FY Apr 1 → today vs last FY Apr 1 →
  // the same date last year) for buyer / month / design. Fetched once on entering the tab.
  useEffect(() => {
    if (salesTab !== 'today') return;
    setAsToday(null);
    const now = new Date();
    const pad = n => String(n).padStart(2, '0');
    const cy = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1; // FY start year
    const mmdd = `${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    const curFrom = `${cy}-04-01`, curTo = `${now.getFullYear()}-${mmdd}`;
    const prevFrom = `${cy - 1}-04-01`, prevTo = `${now.getFullYear() - 1}-${mmdd}`;
    const S = (f, t) => `/sales/fabric-summary?_=1&from=${f}&to=${t}`;
    const Q = (f, t) => `/sales/fabric-by-quality?_=1&from=${f}&to=${t}`;
    const M = (f, t) => `/sales/fabric-by-month?_=1&from=${f}&to=${t}`;
    Promise.all([
      api(S(curFrom, curTo)), api(S(prevFrom, prevTo)),
      api(Q(curFrom, curTo)), api(Q(prevFrom, prevTo)),
      api(M(curFrom, curTo)), api(M(prevFrom, prevTo)),
    ]).then(([bc, bp, dc, dp, mc, mp]) => setAsToday({
      buyerCur: (bc && bc.buyers) || [], buyerPrev: (bp && bp.buyers) || [],
      designCur: Array.isArray(dc) ? dc : [], designPrev: Array.isArray(dp) ? dp : [],
      monthCur: Array.isArray(mc) ? mc : [], monthPrev: Array.isArray(mp) ? mp : [],
      curLabel: `${String(cy).slice(2)}-${String(cy + 1).slice(2)}`,
      prevLabel: `${String(cy - 1).slice(2)}-${String(cy).slice(2)}`,
      asOf: `${pad(now.getDate())} ${now.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}`,
      fyStart: cy, curFrom, curTo, prevFrom, prevTo,
    })).catch(() => setAsToday({ buyerCur: [], buyerPrev: [], designCur: [], designPrev: [], monthCur: [], monthPrev: [] }));
  }, [salesTab]);

  const filterChips = (
    <div style={{ padding: '10px 16px 4px', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      <span style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: 1, marginRight: 4 }}>FY</span>
      {[['all','ALL'],['2627','26-27'],['2526','25-26']].map(([y, label]) => (
        <button key={y} onClick={() => setYearFilter(y)}
          style={{
            background: yearFilter === y ? 'var(--teal-bg)' : 'transparent',
            border: `1px solid ${yearFilter === y ? 'var(--teal)' : 'var(--border)'}`,
            color: yearFilter === y ? 'var(--teal)' : 'var(--text3)',
            padding: '4px 12px', borderRadius: 12, fontSize: 11, cursor: 'pointer', letterSpacing: 1
          }}>{label}</button>
      ))}
    </div>
  );

  // YoY delta between a current and previous value → { t: "+12.3%", col }.
  const pct = (cur, prev) => {
    const c = +cur || 0, p = +prev || 0;
    if (p <= 0) return { t: '—', col: 'var(--text3)' };
    const d = (c - p) / p * 100;
    return { t: `${d >= 0 ? '+' : ''}${d.toFixed(1)}%`, col: d >= 0 ? 'var(--green)' : 'var(--red)' };
  };

  // Buyer-wise drill for a clicked month → this month vs the SAME month last year.
  const openMonthDrill = (yr, mo) => {
    const pad = n => String(n).padStart(2, '0');
    const monName = new Date(2000, mo - 1, 1).toLocaleDateString('en-IN', { month: 'short' });
    const compare = yearFilter !== 'all' && yearFilter !== 'custom';
    setBuyerDrill({
      kind: 'month', title: `${monName} ${yr}`,
      curFrom: `${yr}-${pad(mo)}-01`, curTo: `${yr}-${pad(mo)}-${pad(new Date(yr, mo, 0).getDate())}`,
      prevFrom: compare ? `${yr - 1}-${pad(mo)}-01` : null,
      prevTo:   compare ? `${yr - 1}-${pad(mo)}-${pad(new Date(yr - 1, mo, 0).getDate())}` : null,
      curLabel: `${monName} ${yr}`, prevLabel: compare ? `${monName} ${yr - 1}` : null,
    });
  };

  // Buyer-wise drill for a clicked design → current FY vs previous FY.
  const openDesignDrill = (key) => {
    const compare = yearFilter !== 'all' && yearFilter !== 'custom';
    const curY  = compare ? `${yearFilter.slice(0, 2)}-${yearFilter.slice(2)}` : 'Current';
    const prevY = compare ? `${parseInt(yearFilter.slice(0, 2)) - 1}-${yearFilter.slice(0, 2)}` : '';
    setBuyerDrill({
      kind: 'design', title: `Design ${key}`, design: key,
      curQS: yearQS, prevQS: compare ? prevYearQS : null,
      curLabel: compare ? `FY ${curY}` : 'Current', prevLabel: compare ? `FY ${prevY}` : null,
    });
  };

  if (loading) return (
    <div className="screen">
      <Header subtitle="GSP · SALES" />
      <div className="loading">LOADING SALES…</div>
    </div>
  );

  const { totals, buyers } = data || {};

  // Drill-down: buyer-wise YoY comparison for a clicked Design or Month.
  // (When a buyer inside it is clicked, selectedBuyer takes over → invoice list below.)
  if (buyerDrill && !selectedBuyer) {
    const rows = buyerDrillRows || [];
    const hasCompare = !!buyerDrill.prevLabel;
    const nf = n => (+n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
    const mtrsOf = e => e ? `${nf(e.total_qty)} mtrs` : '';
    const totCur = rows.reduce((s, b) => s + (+(b.cur?.total_amount) || 0), 0);
    const totPrev = rows.reduce((s, b) => s + (+(b.prev?.total_amount) || 0), 0);
    return (
      <div className="screen">
        <Header subtitle={`GSP SALES · ${buyerDrill.title}`} />
        <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
          <button onClick={() => setBuyerDrill(null)} style={{ background: 'none', border: 'none', color: 'var(--teal)', fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
          <div>
            <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: 'var(--teal)' }}>{buyerDrill.title}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>Buyer-wise {hasCompare ? 'comparison' : 'sales'}</div>
          </div>
          <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>{rows.length} buyers</div>
        </div>
        {filterChips}
        <div className="card" style={{ marginTop: 4 }}>
          {buyerDrillRows === null ? (
            <div className="loading">Loading…</div>
          ) : rows.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--text3)' }}>No sales</div>
          ) : hasCompare ? (
            <ScrollX>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px 70px', gap: 8, padding: '6px 8px', borderBottom: '1px solid var(--border)', fontSize: 10, color: 'var(--text3)', letterSpacing: 1, textTransform: 'uppercase' }}>
                <div>Buyer</div><div style={{ textAlign: 'right' }}>{buyerDrill.curLabel}</div><div style={{ textAlign: 'right' }}>{buyerDrill.prevLabel}</div><div style={{ textAlign: 'right' }}>Δ %</div>
              </div>
              {rows.map((b, i) => {
                const cA = +(b.cur?.total_amount) || 0, pA = +(b.prev?.total_amount) || 0;
                const both = b.cur && b.prev;
                const dAmt = pct(cA, pA);
                const dMtr = pct(b.cur?.total_qty, b.prev?.total_qty);
                return (
                  <div key={b.buyer + i}
                       onClick={() => b.buyer && setSelectedBuyer(b.buyer)}
                       style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px 70px', gap: 8, alignItems: 'center', padding: '10px 8px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                       onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                       onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600 }}>{titleCase(b.buyer)} <span style={{ color: 'var(--teal)', fontSize: 10, fontWeight: 400 }}>›</span></div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 13, color: 'var(--teal)', fontWeight: 600 }}>{b.cur ? fmtCr(cA) : '—'}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{mtrsOf(b.cur)}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 13, color: 'var(--text3)', fontWeight: 500 }}>{b.prev ? fmtCr(pA) : '—'}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{mtrsOf(b.prev)}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: dAmt.col }}>{both ? dAmt.t : '—'}</div>
                      <div style={{ fontSize: 10, fontWeight: 600, marginTop: 2, color: dMtr.col }}>{both ? dMtr.t : ''}</div>
                    </div>
                  </div>
                );
              })}
              {(() => {
                const totCurM = rows.reduce((s, b) => s + (+(b.cur?.total_qty) || 0), 0);
                const totPrevM = rows.reduce((s, b) => s + (+(b.prev?.total_qty) || 0), 0);
                const dA = pct(totCur, totPrev), dM = pct(totCurM, totPrevM);
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px 70px', gap: 8, padding: '10px 8px', background: 'var(--grid-total-bg)', fontWeight: 700 }}>
                    <div style={{ fontSize: 13, color: 'var(--text)', letterSpacing: 1 }}>TOTAL</div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 13, color: 'var(--teal)' }}>{fmtCr(totCur)}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 600, marginTop: 2 }}>{nf(totCurM)} mtrs</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 13, color: 'var(--text3)' }}>{fmtCr(totPrev)}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 600, marginTop: 2 }}>{nf(totPrevM)} mtrs</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: dA.col }}>{dA.t}</div>
                      <div style={{ fontSize: 10, fontWeight: 700, marginTop: 2, color: dM.col }}>{dM.t}</div>
                    </div>
                  </div>
                );
              })()}
            </ScrollX>
          ) : (
            rows.map((b, i) => (
              <div key={b.buyer + i} style={{ padding: '10px 8px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 600 }}>{titleCase(b.buyer)}</div>
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>{b.cur?.invoice_count || 0} inv · {mtrsOf(b.cur)}</div>
                </div>
                <div style={{ fontSize: 13, color: 'var(--teal)', fontWeight: 600 }}>{fmtCr(b.cur?.total_amount)}</div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // Drill-down: invoice list for one buyer or month
  if (selectedBuyer || selectedMonth) {
    const drillTitle = selectedBuyer || (selectedMonth?.label || 'Month');
    const closeDrill = () => { setSelectedBuyer(null); setSelectedMonth(null); };
    return (
      <div className="screen">
        <Header subtitle={`GSP SALES · ${drillTitle}`} />
        <ExportBar reportKey="gsp-sales-invoices" title={`GSP Sales Invoices — ${drillTitle}`} sections={[{ name: 'Invoices', columns: GSP_INV_COLS, rows: invoices || [] }]} />
        <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
          <button onClick={closeDrill} style={{ background: 'none', border: 'none', color: 'var(--teal)', fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: 'var(--teal)' }}>{drillTitle}</div>
          <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>{(invoices || []).length} invoices</div>
        </div>
        {filterChips}
        {invLoading ? <div className="loading">Loading invoices…</div> : (
          <div style={{ padding: '8px 12px 24px' }}>
            {(invoices || []).length === 0 ? (
              <div style={{ padding: 30, textAlign: 'center', color: 'var(--text3)' }}>No invoices found</div>
            ) : (
              <div className="grid-wrap" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '72vh', border: '1px solid var(--border)', borderRadius: 6 }}>
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 12, minWidth: 760 }}>
                  <thead>
                    <tr>
                      {[
                        ['Invoice No','left'],['Invoice Dt','left'],['Buyer','left'],
                        ['Brand','left'],['GSP No','left'],
                        ['Items','right'],['Qty (mtrs)','right'],['Rate','right'],['Cost/mtr','right'],
                        ['Amount','right'],['GP','right'],['GP %','right'],['Est %','right']
                      ].map(([h, a]) => (
                        <th key={h} style={{
                          position: 'sticky', top: 0, zIndex: 1,
                          background: 'var(--grid-head-bg)', color: 'var(--grid-head-text)',
                          padding: '10px', textAlign: a, borderBottom: '1px solid var(--grid-head-border)',
                          fontWeight: 600, fontSize: 11, letterSpacing: 0.4, whiteSpace: 'nowrap'
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(invoices || []).map((inv, i) => {
                      const zebra = i % 2 === 0 ? 'var(--grid-row)' : 'var(--grid-row-alt)';
                      const cell = { padding: '9px 10px', borderBottom: '1px solid var(--grid-row-border)', whiteSpace: 'nowrap' };
                      return (
                        <tr key={inv.id} style={{ background: zebra }}>
                          <td style={{ ...cell, color: 'var(--text)', fontWeight: 600 }}>{inv.invoiceno || '—'}</td>
                          <td style={{ ...cell, color: 'var(--text2)' }}>{inv.invoicedate ? new Date(inv.invoicedate).toLocaleDateString('en-IN', { day:'2-digit', month:'2-digit', year:'numeric' }) : '—'}</td>
                          <td style={{ ...cell, color: 'var(--text)' }}>{inv.buyer || '—'}</td>
                          <td style={{ ...cell, color: 'var(--text2)' }}>{inv.brand || '—'}</td>
                          <td style={{ ...cell, color: 'var(--text2)' }}>{inv.gspno || '—'}</td>
                          <td style={{ ...cell, textAlign: 'right', color: 'var(--text3)' }}>{inv.line_items}</td>
                          <td style={{ ...cell, textAlign: 'right', color: 'var(--text)' }}>{(inv.qty || 0).toLocaleString(undefined,{maximumFractionDigits:1})}</td>
                          <td style={{ ...cell, textAlign: 'right', color: 'var(--text2)' }}>{fmtRate(inv.Inv_rate)}</td>
                          {/* Cost / GP come from the invoice's own cost grid
                              (inv_garmentsaleschallan_det2), falling back to the sales
                              order costing. Never-costed lines show "—" rather than a
                              fake 100% GP. A partly-costed invoice shows GP over its
                              costed lines only. */}
                          <td style={{ ...cell, textAlign: 'right', color: 'var(--text2)' }}
                              title={inv.uncosted_lines ? `${inv.uncosted_lines} of ${inv.line_items} lines not costed` : ''}>
                            {fmtRate(inv.Cost_per_unit)}
                          </td>
                          <td style={{ ...cell, textAlign: 'right', color: 'var(--teal)', fontWeight: 600 }}>{fmtCr(inv.amount)}</td>
                          <td style={{ ...cell, textAlign: 'right', color: inv.ProfitAmount == null ? 'var(--text3)' : pctClass(inv.gp_pct), fontWeight: 600 }}>
                            {inv.ProfitAmount == null ? '—' : fmtCr(inv.ProfitAmount)}
                          </td>
                          <td style={{ ...cell, textAlign: 'right', color: inv.gp_pct == null ? 'var(--text3)' : pctClass(inv.gp_pct), fontWeight: 600 }}
                              title={inv.uncosted_lines ? `GP over costed lines only (${inv.uncosted_lines} of ${inv.line_items} uncosted)` : ''}>
                            {fmtPct(inv.gp_pct)}{inv.uncosted_lines && inv.gp_pct != null ? '*' : ''}
                          </td>
                          {/* Est % = invoice amount vs the SALES ORDER cost estimate.
                              GP % above is vs the invoice's own costing, where price is
                              set as cost+margin, so it just returns that margin. This
                              column is the one that shows real over/under performance. */}
                          <td style={{ ...cell, textAlign: 'right', color: inv.est_gp_pct == null ? 'var(--text3)' : pctClass(inv.est_gp_pct), fontWeight: 600 }}
                              title={inv.Est_cost_per_unit ? `vs order cost ${fmtRate(inv.Est_cost_per_unit)}/mtr` : 'Order not costed'}>
                            {fmtPct(inv.est_gp_pct)}
                          </td>
                        </tr>
                      );
                    })}
                    {(() => {
                      const totQty = (invoices||[]).reduce((s,i)=>s+(i.qty||0),0);
                      const totAmt = (invoices||[]).reduce((s,i)=>s+(i.amount||0),0);
                      // GP totals are taken over COSTED lines only, so the % is honest.
                      const totGP      = (invoices||[]).reduce((s,i)=>s+(i.ProfitAmount||0),0);
                      const totCostAmt = (invoices||[]).reduce((s,i)=>s+(i.costed_amount||0),0);
                      const totCost    = (invoices||[]).reduce((s,i)=>s+(i.cost||0),0);
                      const totCostQty = (invoices||[]).reduce((s,i)=>s+(i.costed_qty||0),0);
                      const gpPct      = totCostAmt > 0 ? (totGP * 100) / totCostAmt : null;
                      const avgCost    = totCostQty > 0 ? totCost / totCostQty : null;
                      const totEstCost = (invoices||[]).reduce((s,i)=>s+(i.est_cost||0),0);
                      const totEstAmt  = (invoices||[]).reduce((s,i)=>s+(i.est_amount||0),0);
                      const estPct     = totEstAmt > 0 ? ((totEstAmt - totEstCost) * 100) / totEstAmt : null;
                      const cell = { padding: '10px 10px', borderTop: '2px solid var(--grid-head-border)', whiteSpace: 'nowrap', fontSize: 12 };
                      return (
                        <tr style={{ background: 'var(--grid-total-bg)' }}>
                          <td style={{ ...cell, color: 'var(--text)', fontWeight: 700, letterSpacing: 1 }}>TOTAL</td>
                          <td style={cell}></td><td style={cell}></td><td style={cell}></td><td style={cell}></td><td style={cell}></td>
                          <td style={{ ...cell, textAlign: 'right', color: 'var(--text)', fontWeight: 700 }}>{totQty.toLocaleString(undefined,{maximumFractionDigits:1})}</td>
                          <td style={cell}></td>
                          <td style={{ ...cell, textAlign: 'right', color: 'var(--text2)', fontWeight: 700 }}>{fmtRate(avgCost)}</td>
                          <td style={{ ...cell, textAlign: 'right', color: 'var(--teal)', fontWeight: 700 }}>{fmtCr(totAmt)}</td>
                          <td style={{ ...cell, textAlign: 'right', color: gpPct == null ? 'var(--text3)' : pctClass(gpPct), fontWeight: 700 }}>{totCostAmt > 0 ? fmtCr(totGP) : '—'}</td>
                          <td style={{ ...cell, textAlign: 'right', color: gpPct == null ? 'var(--text3)' : pctClass(gpPct), fontWeight: 700 }}
                              title={totCostAmt > 0 ? `GP on ${fmtCr(totCostAmt)} of ${fmtCr(totAmt)} costed sales` : 'No costed lines'}>
                            {fmtPct(gpPct)}
                          </td>
                          <td style={{ ...cell, textAlign: 'right', color: estPct == null ? 'var(--text3)' : pctClass(estPct), fontWeight: 700 }}
                              title={totEstAmt > 0 ? `vs order estimate on ${fmtCr(totEstAmt)} of sales` : 'No order costing'}>
                            {fmtPct(estPct)}
                          </td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Summary view: stats + tabs
  return (
    <div className="screen">
      <Header subtitle="GSP · SALES" />
      <ExportBar reportKey={`gsp-sales-${salesTab}`} title={`GSP Sales — ${salesTab === 'buyer' ? 'Buyer-wise' : salesTab === 'month' ? 'Month-wise' : salesTab === 'design' ? 'Design-wise' : 'As on today'}`} subtitle={`FY ${yearFilter}`} sections={[{ name: 'Sales', columns: SALES_GSP_COLS, rows: (salesTab === 'today'
        ? (asToday ? (asOnTab === 'buyer' ? asToday.buyerCur : asOnTab === 'design' ? asToday.designCur : asToday.monthCur) : [])
        : ({ buyer: buyers, month: months, design: designs }[salesTab] || [])).map(salesRow) }]}
      source={{
        endpoint: salesTab === 'today'
          ? { buyer: '/sales/fabric-summary', month: '/sales/fabric-by-month', design: '/sales/fabric-by-quality' }[asOnTab]
          : { buyer: '/sales/fabric-summary', month: '/sales/fabric-by-month', design: '/sales/fabric-by-quality' }[salesTab],
        // As-on-today uses $today/$fy_start macros so a schedule stays current every run.
        params: salesTab === 'today'
          ? { from: '$fy_start', to: '$today' }
          : qsToParams(yearQS),
        path: (salesTab === 'today' ? asOnTab : salesTab) === 'buyer' ? 'buyers' : '',
        derive: SALES_DERIVE,
      }} />
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
        <button onClick={() => setView('gsp')} style={{ background: 'none', border: 'none', color: 'var(--teal)', fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: 'var(--teal)' }}>GSP Sales</div>
      </div>
      {filterChips}

      <div style={{ display: 'flex', gap: 12, padding: 16, justifyContent: 'space-around', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 140px', textAlign: 'center', padding: '12px 8px', border: '1px solid var(--border)', borderRadius: 8, background: 'rgba(91,194,199,0.04)' }}>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: 'var(--teal)' }}>{fmtCr(totals?.this_month)}</div>
          <div style={{ fontSize: 9, color: 'var(--text3)', letterSpacing: 1, marginTop: 2 }}>THIS MONTH</div>
        </div>
        <div style={{ flex: '1 1 140px', textAlign: 'center', padding: '12px 8px', border: '1px solid var(--border)', borderRadius: 8 }}>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: 'var(--text3)' }}>{fmtCr(totals?.last_month)}</div>
          <div style={{ fontSize: 9, color: 'var(--text3)', letterSpacing: 1, marginTop: 2 }}>LAST MONTH</div>
        </div>
        <div style={{ flex: '1 1 140px', textAlign: 'center', padding: '12px 8px', border: '1px solid var(--border)', borderRadius: 8 }}>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: 'var(--gold)' }}>{fmtCr(totals?.total_amount)}</div>
          <div style={{ fontSize: 9, color: 'var(--text3)', letterSpacing: 1, marginTop: 2 }}>{yearFilter === 'all' ? 'ALL TIME' : `FY ${yearFilter.slice(0,2)}-${yearFilter.slice(2)}`} · {totals?.total_invoices || 0} INV</div>
        </div>
      </div>

      <div style={{ padding: '4px 16px 0', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {[{key:'buyer',label:'BUYER-WISE'},{key:'month',label:'MONTH-WISE'},{key:'design',label:'DESIGN-WISE'},{key:'today',label:'AS ON TODAY'}].map(t => (
          <button key={t.key} onClick={() => setSalesTab(t.key)}
            style={{
              background: salesTab === t.key ? 'var(--teal-bg)' : 'transparent',
              border: `1px solid ${salesTab === t.key ? 'var(--teal)' : 'var(--border)'}`,
              color: salesTab === t.key ? 'var(--teal)' : 'var(--text3)',
              padding: '5px 12px', borderRadius: 14, fontSize: 11, cursor: 'pointer', letterSpacing: 1
            }}>{t.label}</button>
        ))}
      </div>

      {salesTab === 'buyer' && (() => {
        const hasCompare = prevYearQS != null;
        const curY  = hasCompare ? `${yearFilter.slice(0,2)}-${yearFilter.slice(2)}` : 'Current';
        const prevY = hasCompare ? `${(parseInt(yearFilter.slice(0,2))-1)}-${yearFilter.slice(0,2)}` : 'Previous';
        const mtrs = q => `${(+((q && q.total_qty) || 0)).toLocaleString(undefined,{maximumFractionDigits:0})} mtrs`;

        if (!hasCompare) {
          return (
            <div className="card" style={{ marginTop: 4 }}>
              <div className="card-title">💰 Buyer-wise Sales</div>
              {(buyers || []).length === 0 && <div style={{ padding: 20, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>No data</div>}
              {(buyers || []).slice(0, 40).map(b => (
                <div key={titleCase(b.buyer) || 'unknown'}
                     onClick={() => b.buyer && setSelectedBuyer(b.buyer)}
                     style={{ padding: '10px 8px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', cursor: b.buyer ? 'pointer' : 'default' }}
                     onMouseEnter={e => b.buyer && (e.currentTarget.style.background = 'rgba(255,255,255,0.03)')}
                     onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 600, letterSpacing: 0.5 }}>{titleCase(b.buyer) || '(unknown)'}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>{b.invoice_count} invoices · {mtrs(b)}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}><div style={{ fontSize: 13, color: 'var(--teal)' }}>{fmtCr(b.total_amount)}</div></div>
                </div>
              ))}
            </div>
          );
        }

        const bmap = new Map();
        (buyers || []).forEach(b => bmap.set(b.buyer, { key: b.buyer, cur: b, prev: null }));
        (buyersPrev || []).forEach(b => {
          if (!bmap.has(b.buyer)) bmap.set(b.buyer, { key: b.buyer, cur: null, prev: b });
          else bmap.get(b.buyer).prev = b;
        });
        const bmerged = [...bmap.values()].sort((a, b) => (+(b.cur?.total_amount || 0)) - (+(a.cur?.total_amount || 0)));

        return (
          <div className="card" style={{ marginTop: 4 }}>
            <div className="card-title">💰 Buyer-wise Sales (YoY)</div>
            <ScrollX>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px 70px', gap: 8, padding: '6px 8px', borderBottom: '1px solid var(--border)', fontSize: 10, color: 'var(--text3)', letterSpacing: 1, textTransform: 'uppercase' }}>
              <div>Buyer</div><div style={{ textAlign: 'right' }}>FY {curY}</div><div style={{ textAlign: 'right' }}>FY {prevY}</div><div style={{ textAlign: 'right' }}>Δ %</div>
            </div>
            {bmerged.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>No data</div>}
            {bmerged.map(row => {
              const cur = row.cur, prev = row.prev;
              const curAmt = cur ? +cur.total_amount || 0 : 0;
              const prevAmt = prev ? +prev.total_amount || 0 : 0;
              const delta = prevAmt > 0 ? ((curAmt - prevAmt) / prevAmt) * 100 : null;
              const deltaCol = delta == null ? 'var(--text3)' : delta >= 0 ? 'var(--green)' : 'var(--red)';
              return (
                <div key={row.key || 'unknown'}
                     onClick={() => row.key && setSelectedBuyer(row.key)}
                     style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px 70px', gap: 8, alignItems: 'center', padding: '10px 8px', borderBottom: '1px solid var(--border)', cursor: row.key ? 'pointer' : 'default' }}
                     onMouseEnter={e => row.key && (e.currentTarget.style.background = 'rgba(255,255,255,0.03)')}
                     onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 600, letterSpacing: 0.5 }}>{titleCase(row.key) || '(unknown)'}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>{cur ? `${cur.invoice_count} inv · ${mtrs(cur)}` : '—'}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, color: 'var(--teal)', fontWeight: 600 }}>{cur ? fmtCr(curAmt) : '—'}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{cur ? mtrs(cur) : ''}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, color: 'var(--text3)', fontWeight: 500 }}>{prev ? fmtCr(prevAmt) : '—'}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{prev ? mtrs(prev) : ''}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: pct(curAmt, prevAmt).col }}>{cur && prev ? pct(curAmt, prevAmt).t : '—'}</div>
                    <div style={{ fontSize: 10, fontWeight: 600, marginTop: 2, color: pct(cur?.total_qty, prev?.total_qty).col }}>{cur && prev ? pct(cur.total_qty, prev.total_qty).t : ''}</div>
                  </div>
                </div>
              );
            })}
            </ScrollX>
          </div>
        );
      })()}

      {salesTab === 'month' && (() => {
        const hasCompare = prevYearQS != null;
        const mtrs = q => `${(+((q && q.total_qty) || 0)).toLocaleString(undefined,{maximumFractionDigits:0})} mtrs`;

        if (!hasCompare) {
          return (
            <div className="card" style={{ marginTop: 4 }}>
              <div className="card-title">📅 Month-wise Sales</div>
              {(months || []).length === 0 && <div style={{ padding: 20, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>No data</div>}
              {(months || []).map(m => {
                const label = new Date(m.yr, m.mo - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
                return (
                  <div key={`${m.yr}-${m.mo}`}
                       onClick={() => openMonthDrill(m.yr, m.mo)}
                       style={{ padding: '10px 8px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}
                       onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                       onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 600, letterSpacing: 0.5 }}>{label}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>{m.invoice_count} invoices · {mtrs(m)}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}><div style={{ fontSize: 13, color: 'var(--teal)' }}>{fmtCr(m.total_amount)}</div></div>
                  </div>
                );
              })}
            </div>
          );
        }

        // YoY: align by month-of-year (Apr→Mar FY order); compare same month across the two FYs.
        const curY  = `${yearFilter.slice(0,2)}-${yearFilter.slice(2)}`;
        const prevY = `${(parseInt(yearFilter.slice(0,2))-1)}-${yearFilter.slice(0,2)}`;
        const mmap = new Map();
        (months || []).forEach(m => mmap.set(m.mo, { mo: m.mo, cur: m, prev: null }));
        (monthsPrev || []).forEach(m => {
          if (!mmap.has(m.mo)) mmap.set(m.mo, { mo: m.mo, cur: null, prev: m });
          else mmap.get(m.mo).prev = m;
        });
        const fySeq = mo => (mo >= 4 ? mo - 4 : mo + 8);
        const mmerged = [...mmap.values()].sort((a, b) => fySeq(a.mo) - fySeq(b.mo));

        return (
          <div className="card" style={{ marginTop: 4 }}>
            <div className="card-title">📅 Month-wise Sales (YoY)</div>
            <ScrollX>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px 70px', gap: 8, padding: '6px 8px', borderBottom: '1px solid var(--border)', fontSize: 10, color: 'var(--text3)', letterSpacing: 1, textTransform: 'uppercase' }}>
              <div>Month</div><div style={{ textAlign: 'right' }}>FY {curY}</div><div style={{ textAlign: 'right' }}>FY {prevY}</div><div style={{ textAlign: 'right' }}>Δ %</div>
            </div>
            {mmerged.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>No data</div>}
            {mmerged.map(row => {
              const cur = row.cur, prev = row.prev;
              const monName = new Date(2000, row.mo - 1, 1).toLocaleDateString('en-IN', { month: 'short' });
              const curAmt = cur ? +cur.total_amount || 0 : 0;
              const prevAmt = prev ? +prev.total_amount || 0 : 0;
              const delta = prevAmt > 0 ? ((curAmt - prevAmt) / prevAmt) * 100 : null;
              const deltaCol = delta == null ? 'var(--text3)' : delta >= 0 ? 'var(--green)' : 'var(--red)';
              const drill = cur ? () => openMonthDrill(cur.yr, cur.mo) : undefined;
              return (
                <div key={row.mo}
                     onClick={drill}
                     style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px 70px', gap: 8, alignItems: 'center', padding: '10px 8px', borderBottom: '1px solid var(--border)', cursor: cur ? 'pointer' : 'default' }}
                     onMouseEnter={e => cur && (e.currentTarget.style.background = 'rgba(255,255,255,0.03)')}
                     onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 600, letterSpacing: 0.5 }}>{monName}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>{cur ? `${cur.invoice_count} inv · ${mtrs(cur)}` : '—'}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, color: 'var(--teal)', fontWeight: 600 }}>{cur ? fmtCr(curAmt) : '—'}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{cur ? mtrs(cur) : ''}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, color: 'var(--text3)', fontWeight: 500 }}>{prev ? fmtCr(prevAmt) : '—'}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{prev ? mtrs(prev) : ''}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: pct(curAmt, prevAmt).col }}>{cur && prev ? pct(curAmt, prevAmt).t : '—'}</div>
                    <div style={{ fontSize: 10, fontWeight: 600, marginTop: 2, color: pct(cur?.total_qty, prev?.total_qty).col }}>{cur && prev ? pct(cur.total_qty, prev.total_qty).t : ''}</div>
                  </div>
                </div>
              );
            })}
            </ScrollX>
          </div>
        );
      })()}

      {salesTab === 'design' && (() => {
        // Design-wise (fabric quality) — YoY when a specific FY is chosen.
        const hasCompare = prevYearQS != null;
        const map = new Map();
        (designs || []).forEach(r => map.set(r.key, { key: r.key, cur: r, prev: null }));
        (designsPrev || []).forEach(r => {
          if (!map.has(r.key)) map.set(r.key, { key: r.key, cur: null, prev: r });
          else map.get(r.key).prev = r;
        });
        const merged = [...map.values()].sort((a, b) => (+(b.cur?.total_amount || 0)) - (+(a.cur?.total_amount || 0)));
        const curY  = hasCompare ? `${yearFilter.slice(0,2)}-${yearFilter.slice(2)}` : 'Current';
        const prevY = hasCompare ? `${(parseInt(yearFilter.slice(0,2))-1)}-${yearFilter.slice(0,2)}` : 'Previous';

        return (
          <div className="card" style={{ marginTop: 4 }}>
            <div className="card-title">🧵 Design-wise — Avg Selling Rate {hasCompare ? '(YoY)' : ''}</div>

            <ScrollX min={hasCompare ? 520 : 0}>
            {hasCompare && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px 70px', gap: 8, padding: '6px 8px', borderBottom: '1px solid var(--border)', fontSize: 10, color: 'var(--text3)', letterSpacing: 1, textTransform: 'uppercase' }}>
                <div>Design</div>
                <div style={{ textAlign: 'right' }}>FY {curY}</div>
                <div style={{ textAlign: 'right' }}>FY {prevY}</div>
                <div style={{ textAlign: 'right' }}>Δ %</div>
              </div>
            )}

            {merged.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>No data</div>}

            {merged.map(row => {
              const cur = row.cur, prev = row.prev;
              const curRate  = cur  ? +cur.avg_rate  || 0 : 0;
              const prevRate = prev ? +prev.avg_rate || 0 : 0;
              const delta    = prevRate > 0 ? ((curRate - prevRate) / prevRate) * 100 : null;
              const deltaCol = delta == null ? 'var(--text3)' : delta >= 0 ? 'var(--green)' : 'var(--red)';
              const mtrs = q => `${(+((q && q.total_qty) || 0)).toLocaleString(undefined,{maximumFractionDigits:0})} mtrs`;

              if (!hasCompare) {
                return (
                  <div key={row.key}
                       onClick={() => row.key && openDesignDrill(row.key)}
                       style={{ padding: '10px 8px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}
                       onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                       onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 600, letterSpacing: 0.5 }}>{row.key}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>{cur?.invoice_count || 0} inv · {mtrs(cur)}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 13, color: 'var(--gold)', fontWeight: 600 }}>₹{Math.round(curRate).toLocaleString('en-IN')} <span style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 400 }}>/ mtr</span></div>
                      <div style={{ fontSize: 10, color: 'var(--teal)', marginTop: 2 }}>{fmtCr(cur?.total_amount)}</div>
                    </div>
                  </div>
                );
              }

              return (
                <div key={row.key}
                     onClick={() => row.key && openDesignDrill(row.key)}
                     style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px 70px', gap: 8, alignItems: 'center', padding: '10px 8px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                     onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                     onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 600, letterSpacing: 0.5 }}>{row.key}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>{cur ? `${cur.invoice_count} inv · ${mtrs(cur)}` : '—'}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, color: 'var(--gold)', fontWeight: 600 }}>{cur ? `₹${Math.round(curRate).toLocaleString('en-IN')}` : '—'}</div>
                    <div style={{ fontSize: 10, color: 'var(--teal)', marginTop: 2 }}>{cur ? fmtCr(cur.total_amount) : ''}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1 }}>{cur ? mtrs(cur) : ''}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, color: 'var(--text3)', fontWeight: 500 }}>{prev ? `₹${Math.round(prevRate).toLocaleString('en-IN')}` : '—'}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{prev ? fmtCr(prev.total_amount) : ''}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1 }}>{prev ? mtrs(prev) : ''}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: pct(curRate, prevRate).col }}>{cur && prev ? pct(curRate, prevRate).t : '—'}</div>
                    <div style={{ fontSize: 10, fontWeight: 600, marginTop: 2, color: pct(cur?.total_amount, prev?.total_amount).col }}>{cur && prev ? pct(cur.total_amount, prev.total_amount).t : ''}</div>
                    <div style={{ fontSize: 10, fontWeight: 600, marginTop: 1, color: pct(cur?.total_qty, prev?.total_qty).col }}>{cur && prev ? pct(cur.total_qty, prev.total_qty).t : ''}</div>
                  </div>
                </div>
              );
            })}
            </ScrollX>
          </div>
        );
      })()}

      {salesTab === 'today' && (() => {
        if (!asToday) return <div className="loading">LOADING…</div>;
        const A = asToday;
        const mergeBy = (cur, prev, keyFn) => {
          const map = new Map();
          (cur || []).forEach(r => map.set(keyFn(r), { key: keyFn(r), cur: r, prev: null }));
          (prev || []).forEach(r => { const k = keyFn(r); if (!map.has(k)) map.set(k, { key: k, cur: null, prev: r }); else map.get(k).prev = r; });
          return [...map.values()];
        };
        let merged, nameHeader, labelOf;
        if (asOnTab === 'buyer') { merged = mergeBy(A.buyerCur, A.buyerPrev, r => r.buyer); nameHeader = 'Buyer'; labelOf = row => titleCase(row.key); }
        else if (asOnTab === 'design') { merged = mergeBy(A.designCur, A.designPrev, r => r.key); nameHeader = 'Design'; labelOf = row => row.key; }
        else { merged = mergeBy(A.monthCur, A.monthPrev, r => r.mo); nameHeader = 'Month'; labelOf = row => new Date(2000, row.key - 1, 1).toLocaleDateString('en-IN', { month: 'short' }); }
        if (asOnTab === 'month') { const seq = mo => (mo >= 4 ? mo - 4 : mo + 8); merged.sort((a, b) => seq(a.key) - seq(b.key)); }
        else merged.sort((a, b) => (+(b.cur?.total_amount || 0)) - (+(a.cur?.total_amount || 0)));

        // Month/Design rows drill into the buyer-wise summary (YTD-scoped). Buyer rows don't.
        const canDrill = asOnTab !== 'buyer';
        const drillRow = (row) => {
          const pad = n => String(n).padStart(2, '0');
          if (asOnTab === 'design') {
            setBuyerDrill({
              kind: 'design', title: `Design ${row.key}`, design: row.key,
              curQS: `&from=${A.curFrom}&to=${A.curTo}`, prevQS: `&from=${A.prevFrom}&to=${A.prevTo}`,
              curLabel: `FY ${A.curLabel}`, prevLabel: `FY ${A.prevLabel}`,
            });
          } else { // month → that month this year vs same month last year
            const mo = row.key, yr = mo >= 4 ? A.fyStart : A.fyStart + 1;
            const monName = new Date(2000, mo - 1, 1).toLocaleDateString('en-IN', { month: 'short' });
            setBuyerDrill({
              kind: 'month', title: `${monName} ${yr}`,
              curFrom: `${yr}-${pad(mo)}-01`, curTo: `${yr}-${pad(mo)}-${pad(new Date(yr, mo, 0).getDate())}`,
              prevFrom: `${yr - 1}-${pad(mo)}-01`, prevTo: `${yr - 1}-${pad(mo)}-${pad(new Date(yr - 1, mo, 0).getDate())}`,
              curLabel: `${monName} ${yr}`, prevLabel: `${monName} ${yr - 1}`,
            });
          }
        };
        const nf = n => (+n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
        const mtrsOf = e => e ? `${nf(e.total_qty)} mtrs` : '';
        const rateOf = e => e ? (e.avg_rate != null ? +e.avg_rate : (e.total_qty > 0 ? e.total_amount / e.total_qty : 0)) : 0;
        const rateStr = e => e ? `₹${Math.round(rateOf(e)).toLocaleString('en-IN')}/mtr` : '';
        const totCurA = merged.reduce((s, r) => s + (+(r.cur?.total_amount) || 0), 0);
        const totPrevA = merged.reduce((s, r) => s + (+(r.prev?.total_amount) || 0), 0);
        const totCurM = merged.reduce((s, r) => s + (+(r.cur?.total_qty) || 0), 0);
        const totPrevM = merged.reduce((s, r) => s + (+(r.prev?.total_qty) || 0), 0);
        const dTA = pct(totCurA, totPrevA), dTM = pct(totCurM, totPrevM);

        return (
          <div className="card" style={{ marginTop: 4 }}>
            <div className="card-title">📆 As on {A.asOf} — year-to-date comparison</div>
            <div style={{ display: 'flex', gap: 6, padding: '2px 8px 10px', flexWrap: 'wrap' }}>
              {[{ k: 'buyer', l: 'BUYER' }, { k: 'month', l: 'MONTH' }, { k: 'design', l: 'DESIGN' }].map(t => (
                <button key={t.k} onClick={() => setAsOnTab(t.k)}
                  style={{ background: asOnTab === t.k ? 'var(--teal-bg)' : 'transparent', border: `1px solid ${asOnTab === t.k ? 'var(--teal)' : 'var(--border)'}`, color: asOnTab === t.k ? 'var(--teal)' : 'var(--text3)', padding: '4px 12px', borderRadius: 12, fontSize: 11, cursor: 'pointer', letterSpacing: 1 }}>{t.l}</button>
              ))}
            </div>
            <ScrollX>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px 70px', gap: 8, padding: '6px 8px', borderBottom: '1px solid var(--border)', fontSize: 10, color: 'var(--text3)', letterSpacing: 1, textTransform: 'uppercase' }}>
                <div>{nameHeader}</div><div style={{ textAlign: 'right' }}>FY {A.curLabel}</div><div style={{ textAlign: 'right' }}>FY {A.prevLabel}</div><div style={{ textAlign: 'right' }}>Δ %</div>
              </div>
              {merged.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>No data</div>}
              {merged.map((row, i) => {
                const cA = +(row.cur?.total_amount) || 0, pA = +(row.prev?.total_amount) || 0;
                const both = row.cur && row.prev;
                const dA = pct(cA, pA), dM = pct(row.cur?.total_qty, row.prev?.total_qty), dR = pct(rateOf(row.cur), rateOf(row.prev));
                return (
                  <div key={String(row.key) + i}
                       onClick={canDrill ? () => drillRow(row) : undefined}
                       style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px 70px', gap: 8, alignItems: 'center', padding: '10px 8px', borderBottom: '1px solid var(--border)', cursor: canDrill ? 'pointer' : 'default' }}
                       onMouseEnter={e => canDrill && (e.currentTarget.style.background = 'var(--bg3)')}
                       onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                    <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600 }}>{labelOf(row)}{canDrill && <span style={{ color: 'var(--teal)', fontSize: 10, fontWeight: 400 }}> ›</span>}</div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 13, color: 'var(--teal)', fontWeight: 600 }}>{row.cur ? fmtCr(cA) : '—'}</div>
                      <div style={{ fontSize: 10, color: 'var(--gold)', marginTop: 2 }}>{rateStr(row.cur)}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1 }}>{mtrsOf(row.cur)}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 13, color: 'var(--text3)', fontWeight: 500 }}>{row.prev ? fmtCr(pA) : '—'}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{rateStr(row.prev)}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1 }}>{mtrsOf(row.prev)}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: dA.col }}>{both ? dA.t : '—'}</div>
                      <div style={{ fontSize: 10, fontWeight: 600, marginTop: 2, color: dR.col }}>{both ? dR.t : ''}</div>
                      <div style={{ fontSize: 10, fontWeight: 600, marginTop: 1, color: dM.col }}>{both ? dM.t : ''}</div>
                    </div>
                  </div>
                );
              })}
              {(() => {
                const rCur = totCurM > 0 ? totCurA / totCurM : 0, rPrev = totPrevM > 0 ? totPrevA / totPrevM : 0;
                const dTR = pct(rCur, rPrev);
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px 70px', gap: 8, padding: '10px 8px', background: 'var(--grid-total-bg)', fontWeight: 700 }}>
                    <div style={{ fontSize: 13, color: 'var(--text)', letterSpacing: 1 }}>TOTAL</div>
                    <div style={{ textAlign: 'right' }}><div style={{ fontSize: 13, color: 'var(--teal)' }}>{fmtCr(totCurA)}</div><div style={{ fontSize: 10, color: 'var(--gold)', fontWeight: 700, marginTop: 2 }}>₹{Math.round(rCur).toLocaleString('en-IN')}/mtr</div><div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 600, marginTop: 1 }}>{nf(totCurM)} mtrs</div></div>
                    <div style={{ textAlign: 'right' }}><div style={{ fontSize: 13, color: 'var(--text3)' }}>{fmtCr(totPrevA)}</div><div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700, marginTop: 2 }}>₹{Math.round(rPrev).toLocaleString('en-IN')}/mtr</div><div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 600, marginTop: 1 }}>{nf(totPrevM)} mtrs</div></div>
                    <div style={{ textAlign: 'right' }}><div style={{ fontSize: 13, fontWeight: 700, color: dTA.col }}>{dTA.t}</div><div style={{ fontSize: 10, fontWeight: 700, marginTop: 2, color: dTR.col }}>{dTR.t}</div><div style={{ fontSize: 10, fontWeight: 700, marginTop: 1, color: dTM.col }}>{dTM.t}</div></div>
                  </div>
                );
              })()}
            </ScrollX>
          </div>
        );
      })()}
    </div>
  );
}

function TasSalesView({ isTas, setView, Header }) {
  const [data, setData] = useState(null);              // current FY /sales/summary
  const [dataPrev, setDataPrev] = useState(null);      // previous FY /sales/summary
  const [months, setMonths] = useState(null);          // current FY by-month
  const [monthsPrev, setMonthsPrev] = useState(null);  // previous FY by-month
  const [merchs, setMerchs] = useState(null);          // current FY by-merchandiser
  const [merchsPrev, setMerchsPrev] = useState(null);  // previous FY
  const [subcats, setSubcats] = useState(null);        // current FY by-subcategory
  const [subcatsPrev, setSubcatsPrev] = useState(null); // previous FY
  const [loading, setLoading] = useState(true);
  const [salesTab, setSalesTab] = useState('buyer'); // 'buyer' | 'month' | 'merch' | 'subcat'
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null); // {yr, mo, label}
  const [selectedMerch, setSelectedMerch] = useState(null);
  const [selectedSubcat, setSelectedSubcat] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [invLoading, setInvLoading] = useState(false);
  const [costDetail, setCostDetail] = useState(null); // {tasno, summary, stages, loading} — opens GP drilldown modal
  function openCostSheet(inv) {
    if (!inv?.tas_no) return;
    setCostDetail({ tasno: inv.tas_no, summary: {
      buyer: inv.buyer, Merchandiser: inv.merchendiser, Factory: null,
    }, stages: null, loading: true });
    api('/sales/cost-detail/' + encodeURIComponent(inv.tas_no))
      .then(d => setCostDetail(s => s && s.tasno === inv.tas_no
        ? { ...s, stages: Array.isArray(d) ? d : [], loading: false }
        : s));
  }
  const [yearFilter, setYearFilter] = useState(currentFY()); // default = current FY
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo]     = useState('');

  // Build the from/to query string for the chosen year filter
  const yearQS = (() => {
    if (yearFilter === 'all') return '';
    if (yearFilter === 'custom') {
      const parts = [];
      if (customFrom) parts.push(`from=${customFrom}`);
      if (customTo)   parts.push(`to=${customTo}`);
      return parts.length ? '&' + parts.join('&') : '';
    }
    // FY key like '2627' → Apr 1 2026 to Mar 31 2027
    const startY = 2000 + parseInt(yearFilter.slice(0, 2));
    return `&from=${startY}-04-01&to=${startY + 1}-03-31`;
  })();

  useEffect(() => {
    setLoading(true);
    // For YoY compare, also fetch previous-FY versions of every list when a specific FY is selected.
    let prevYearQS = '';
    if (yearFilter !== 'all' && yearFilter !== 'custom') {
      const startY = 2000 + parseInt(yearFilter.slice(0, 2));
      prevYearQS = `&from=${startY - 1}-04-01&to=${startY}-03-31`;
    }
    const empty = Promise.resolve(null);
    Promise.all([
      api('/sales/summary?_=1'                     + yearQS),
      api('/sales/by-month?_=1'                    + yearQS),
      api('/sales/by-group?group=merchandiser&_=1' + yearQS),
      api('/sales/by-group?group=subcategory&_=1'  + yearQS),
      prevYearQS ? api('/sales/summary?_=1'                     + prevYearQS) : empty,
      prevYearQS ? api('/sales/by-month?_=1'                    + prevYearQS) : empty,
      prevYearQS ? api('/sales/by-group?group=merchandiser&_=1' + prevYearQS) : empty,
      prevYearQS ? api('/sales/by-group?group=subcategory&_=1'  + prevYearQS) : empty
    ]).then(([s, m, mch, sub, sP, mP, mchP, subP]) => {
      setData(s);
      setMonths(Array.isArray(m)   ? m   : []);
      setMerchs(Array.isArray(mch) ? mch : []);
      setSubcats(Array.isArray(sub) ? sub : []);
      setDataPrev(sP);
      setMonthsPrev(Array.isArray(mP)   ? mP   : []);
      setMerchsPrev(Array.isArray(mchP) ? mchP : []);
      setSubcatsPrev(Array.isArray(subP) ? subP : []);
      setLoading(false);
    });
  }, [yearFilter, customFrom, customTo]);

  useEffect(() => {
    if (!selectedBuyer && !selectedMonth && !selectedMerch && !selectedSubcat) { setInvoices(null); return; }
    setInvLoading(true);
    // Respect the FY filter for every drill-down (sub-category included) so the
    // invoice list matches the avg rate / amount the user clicked on.
    let qs = yearQS;
    if (selectedMonth) {
      const { yr, mo } = selectedMonth;
      const from = `${yr}-${String(mo).padStart(2,'0')}-01`;
      const last = new Date(yr, mo, 0).getDate();
      const to   = `${yr}-${String(mo).padStart(2,'0')}-${String(last).padStart(2,'0')}`;
      qs = `&from=${from}&to=${to}`;
    }
    const filters = [];
    if (selectedBuyer)  filters.push(`buyer=${encodeURIComponent(selectedBuyer)}`);
    if (selectedMerch)  filters.push(`merchandiser=${encodeURIComponent(selectedMerch)}`);
    if (selectedSubcat) filters.push(`subcategory=${encodeURIComponent(selectedSubcat)}`);
    const filterQS = filters.length ? '&' + filters.join('&') : '';
    api(`/sales/invoices?_=1${filterQS}${qs}`)
      .then(d => { setInvoices(Array.isArray(d) ? d : []); setInvLoading(false); });
  }, [selectedBuyer, selectedMonth, selectedMerch, selectedSubcat, yearFilter, customFrom, customTo]);

  // Filter chips component (Indian FY + custom)
  const filterChips = (
    <div style={{ padding: '10px 16px 4px', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      <span style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: 1, marginRight: 4 }}>FY</span>
      {[['all','ALL'],['2627','26-27'],['2526','25-26']].map(([y, label]) => (
        <button
          key={y}
          onClick={() => setYearFilter(y)}
          style={{
            background: yearFilter === y ? 'var(--teal-bg)' : 'transparent',
            border: `1px solid ${yearFilter === y ? 'var(--teal)' : 'var(--border)'}`,
            color: yearFilter === y ? 'var(--teal)' : 'var(--text3)',
            padding: '4px 12px', borderRadius: 12, fontSize: 11, cursor: 'pointer', letterSpacing: 1
          }}
        >
          {label}
        </button>
      ))}
      {yearFilter === 'custom' && (
        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
            style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)', fontSize: 11, padding: '3px 6px', outline: 'none' }} />
          <span style={{ color: 'var(--text3)', fontSize: 11 }}>→</span>
          <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
            style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)', fontSize: 11, padding: '3px 6px', outline: 'none' }} />
        </span>
      )}
    </div>
  );

  if (loading) return (
    <div className="screen">
      <Header subtitle={isTas ? 'TAS · SALES' : 'GSP · SALES'} />
      <div className="loading">LOADING SALES…</div>
    </div>
  );

  const { totals, buyers } = data || {};

  // Drill-down: invoice list for one buyer / month / merchandiser / sub-category
  if (selectedBuyer || selectedMonth || selectedMerch || selectedSubcat) {
    const drillTitle = selectedBuyer || selectedMerch || selectedSubcat || (selectedMonth?.label || 'Month');
    const drillSub   = `SALES · ${drillTitle}`;
    const closeDrill = () => { setSelectedBuyer(null); setSelectedMonth(null); setSelectedMerch(null); setSelectedSubcat(null); };

    // Drill-down summary so user sees overall qty / weighted-avg rate /
    // amount / GP% without navigating back to the previous screen.
    // Avg rate uses sales_value (qty × rate) ÷ qty — same formula as backend
    // Inv_rate, so per-row and summary stay in lockstep.
    const drillSummary = (() => {
      const list = invoices || [];
      const totalQty    = list.reduce((s, i) => s + (Number(i.qty)          || 0), 0);
      const totalAmt    = list.reduce((s, i) => s + (Number(i.amount)       || 0), 0);
      const salesValue  = list.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.Inv_rate) || 0), 0);
      const totalProfit = list.reduce((s, i) => s + (Number(i.ProfitAmount) || 0), 0);
      const avgRate     = totalQty > 0 ? salesValue / totalQty : 0;
      const gpPct       = totalAmt > 0 ? (totalProfit / totalAmt) * 100 : null;
      return { totalQty, totalAmt, avgRate, gpPct, gpAmt: totalProfit };
    })();
    const chipBox = {
      flex: '1 1 110px', minWidth: 110, padding: '10px 12px',
      border: '1px solid var(--border)', borderRadius: 6, background: 'var(--grid-row)'
    };
    const chipLbl = { fontSize: 9, color: 'var(--text3)', letterSpacing: 2, marginBottom: 4 };
    const chipVal = { fontSize: 16, color: 'var(--text)', fontWeight: 600, fontFamily: 'Inter, sans-serif', fontWeight: 600 };

    return (
      <div className="screen">
        <Header subtitle={drillSub} />
        <ExportBar reportKey="tas-sales-invoices" title={`TAS Sales Invoices — ${drillSub}`} sections={[{ name: 'Invoices', columns: TAS_INV_COLS, rows: invoices || [] }]} />
        <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
          <button onClick={closeDrill} style={{ background: 'none', border: 'none', color: 'var(--teal)', fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: 'var(--teal)' }}>{drillTitle}</div>
          <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>{(invoices || []).length} invoices</div>
        </div>

        {!invLoading && (invoices || []).length > 0 && (
          <div style={{ display: 'flex', gap: 10, padding: '12px 20px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
            <div style={chipBox}>
              <div style={chipLbl}>QTY</div>
              <div style={chipVal}>{drillSummary.totalQty.toLocaleString('en-IN')}</div>
            </div>
            <div style={chipBox}>
              <div style={chipLbl}>AVG RATE</div>
              <div style={{ ...chipVal, color: 'var(--yellow)' }}>₹{Math.round(drillSummary.avgRate).toLocaleString('en-IN')}</div>
              <div style={{ fontSize: 9, color: 'var(--text2)', marginTop: 2 }}>amount ÷ qty</div>
            </div>
            <div style={chipBox}>
              <div style={chipLbl}>AMOUNT</div>
              <div style={{ ...chipVal, color: 'var(--teal)' }}>{fmtCr(drillSummary.totalAmt)}</div>
            </div>
            {drillSummary.gpPct != null && (
              <div style={chipBox}>
                <div style={chipLbl}>GP %</div>
                <div style={{ ...chipVal, color: pctClass(drillSummary.gpPct) }}>{fmtPct(drillSummary.gpPct)}</div>
                <div style={{ fontSize: 9, color: 'var(--text2)', marginTop: 2 }}>
                  {drillSummary.gpAmt != null ? `${fmtCr(drillSummary.gpAmt)} profit` : 'profit ÷ amount'}
                </div>
              </div>
            )}
          </div>
        )}

        {filterChips}
        {invLoading ? <div className="loading">Loading invoices…</div> : (
          <div style={{ padding: '8px 12px 24px' }}>
            {(invoices || []).length === 0 ? (
              <div style={{ padding: 30, textAlign: 'center', color: 'var(--text3)' }}>No invoices found</div>
            ) : (
              <div className="grid-wrap" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '72vh', border: '1px solid var(--border)', borderRadius: 6 }}>
                <table className="data-grid" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 12, minWidth: 880 }}>
                  <thead>
                    <tr>
                      {[
                        ['Invoice No',  'left'],
                        ['Invoice Dt',  'left'],
                        ['Buyer',       'left'],
                        ['TAS',         'left'],
                        ['Style(s)',    'left'],
                        ['Shade(s)',    'left'],
                        ['Merch.',      'left'],
                        ['Qty',         'right'],
                        ['Rate',        'right'],
                        ['Cost/pc',     'right'],
                        ['Amount',      'right'],
                        ['GP ₹',        'right'],
                        ['GP %',        'right'],
                      ].map(([h, a]) => (
                        <th key={h} style={{
                          position: 'sticky', top: 0, zIndex: 1,
                          background: 'var(--grid-head-bg)', color: 'var(--grid-head-text)',
                          padding: '10px 10px', textAlign: a,
                          borderBottom: '1px solid var(--grid-head-border)', fontWeight: 600, fontSize: 11,
                          letterSpacing: 0.4, whiteSpace: 'nowrap'
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(invoices || []).map((inv, i) => {
                      const zebra = i % 2 === 0 ? 'var(--grid-row)' : 'var(--grid-row-alt)';
                      const cell = { padding: '9px 10px', borderBottom: '1px solid var(--grid-row-border)', whiteSpace: 'nowrap' };
                      return (
                        <tr key={inv.invoiceno} style={{ background: zebra }}>
                          <td style={{ ...cell, color: 'var(--text)', fontWeight: 600 }}>{inv.invoiceno}</td>
                          <td style={{ ...cell, color: 'var(--text2)' }}>{new Date(inv.invoicedate).toLocaleDateString('en-IN', { day:'2-digit', month:'2-digit', year:'numeric' })}</td>
                          <td style={{ ...cell, color: 'var(--text2)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }} title={inv.buyer}>{inv.buyer || '—'}</td>
                          <td style={{ ...cell, color: 'var(--text)' }}>{inv.tas_no || '—'}</td>
                          <td style={{ ...cell, color: 'var(--text2)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }} title={inv.styles}>{inv.styles || '—'}</td>
                          <td style={{ ...cell, color: 'var(--text2)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }} title={inv.shades}>{inv.shades || '—'}</td>
                          <td style={{ ...cell, color: 'var(--text2)' }}>{inv.merchendiser || '—'}</td>
                          <td style={{ ...cell, textAlign: 'right', color: 'var(--text)' }}>{(inv.qty || 0).toLocaleString()}</td>
                          <td style={{ ...cell, textAlign: 'right', color: 'var(--text2)' }}>{inv.Inv_rate ? `₹${Math.round(inv.Inv_rate)}` : '—'}</td>
                          {(() => {
                            // Cost/pc = (amount − ProfitAmount) / qty. Fall back to rate*(1−gp%) if
                            // ProfitAmount missing. Blank when we truly can't derive it.
                            const cost = (inv.qty > 0 && inv.ProfitAmount != null)
                              ? (inv.amount - inv.ProfitAmount) / inv.qty
                              : (inv.Inv_rate && inv.gp_pct != null ? inv.Inv_rate * (1 - inv.gp_pct/100) : null);
                            return (
                              <td style={{ ...cell, textAlign: 'right', color: 'var(--text2)' }}>
                                {cost != null && !isNaN(cost) ? `₹${Math.round(cost)}` : '—'}
                              </td>
                            );
                          })()}
                          <td style={{ ...cell, textAlign: 'right', color: 'var(--teal)', fontWeight: 600 }}>{fmtCr(inv.amount)}</td>
                          <td onClick={() => openCostSheet(inv)}
                              title={inv.tas_no ? `Open cost sheet for ${inv.tas_no}` : ''}
                              style={{ ...cell, textAlign: 'right', color: pctClass(inv.ProfitAmount), fontWeight: 600,
                                       cursor: inv.tas_no ? 'pointer' : 'default',
                                       textDecoration: inv.tas_no ? 'underline dotted' : 'none' }}>
                            {inv.ProfitAmount != null ? fmtCr(inv.ProfitAmount) : '—'}
                          </td>
                          <td onClick={() => openCostSheet(inv)}
                              title={inv.tas_no ? `Open cost sheet for ${inv.tas_no}` : ''}
                              style={{ ...cell, textAlign: 'right', color: pctClass(inv.gp_pct), fontWeight: 600,
                                       cursor: inv.tas_no ? 'pointer' : 'default',
                                       textDecoration: inv.tas_no ? 'underline dotted' : 'none' }}>
                            {fmtPct(inv.gp_pct)}
                          </td>
                        </tr>
                      );
                    })}
                    {/* Total row — qty + volume-weighted avg rate + total amount + total GP */}
                    {(() => {
                      const totalQty    = (invoices||[]).reduce((s, i) => s + (i.qty||0), 0);
                      const totalAmt    = (invoices||[]).reduce((s, i) => s + (i.amount||0), 0);
                      const totalProfit = (invoices||[]).reduce((s, i) => s + (i.ProfitAmount||0), 0);
                      const avgRate     = totalQty > 0 ? totalAmt / totalQty : 0;
                      const avgCost     = totalQty > 0 ? (totalAmt - totalProfit) / totalQty : 0;
                      const gpPct       = totalAmt > 0 ? (totalProfit / totalAmt) * 100 : null;
                      const cell = { padding: '10px 10px', borderTop: '2px solid var(--grid-head-border)', whiteSpace: 'nowrap', fontSize: 12 };
                      return (
                        <tr style={{ background: 'var(--grid-total-bg)' }}>
                          <td style={{ ...cell, color: 'var(--text)', fontWeight: 700, letterSpacing: 1 }}>TOTAL</td>
                          <td style={cell}></td><td style={cell}></td><td style={cell}></td><td style={cell}></td><td style={cell}></td><td style={cell}></td>
                          <td style={{ ...cell, textAlign: 'right', color: 'var(--text)', fontWeight: 700 }}>{totalQty.toLocaleString()}</td>
                          <td style={{ ...cell, textAlign: 'right', color: 'var(--gold)', fontWeight: 700 }}>₹{Math.round(avgRate)}</td>
                          <td style={{ ...cell, textAlign: 'right', color: 'var(--text2)', fontWeight: 700 }}>₹{Math.round(avgCost)}</td>
                          <td style={{ ...cell, textAlign: 'right', color: 'var(--teal)', fontWeight: 700 }}>{fmtCr(totalAmt)}</td>
                          <td style={{ ...cell, textAlign: 'right', color: pctClass(totalProfit), fontWeight: 700 }}>{fmtCr(totalProfit)}</td>
                          <td style={{ ...cell, textAlign: 'right', color: pctClass(gpPct), fontWeight: 700 }}>{fmtPct(gpPct)}</td>
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        {costDetail && <TasCostDetailModal detail={costDetail} onClose={() => setCostDetail(null)} />}
      </div>
    );
  }

  // Summary view: top stats + buyer list
  return (
    <div className="screen">
      <Header subtitle={isTas ? 'TAS · SALES' : 'GSP · SALES'} />
      <ExportBar reportKey={`${isTas ? 'tas' : 'gsp'}-sales-${salesTab}`} title={`${isTas ? 'TAS' : 'GSP'} Sales — ${salesTab === 'buyer' ? 'Buyer-wise' : salesTab === 'month' ? 'Month-wise' : salesTab === 'merch' ? 'Merchandiser-wise' : 'Sub-category'}`} subtitle={`FY ${yearFilter}`} sections={[{ name: 'Sales', columns: SALES_TAS_COLS, rows: ({ buyer: buyers, month: months, merch: merchs, subcat: subcats }[salesTab] || []).map(salesRow) }]}
      source={{
        endpoint: { buyer: '/sales/summary', month: '/sales/by-month', merch: '/sales/by-group', subcat: '/sales/by-group' }[salesTab],
        params: { ...qsToParams(yearQS), ...(salesTab === 'merch' ? { group: 'merchandiser' } : salesTab === 'subcat' ? { group: 'subcategory' } : {}) },
        path: salesTab === 'buyer' ? 'buyers' : '', derive: SALES_DERIVE,
      }} />
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
        <button onClick={() => setView(isTas ? 'tas' : 'gsp')} style={{ background: 'none', border: 'none', color: 'var(--teal)', fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: 'var(--teal)' }}>{isTas ? 'TAS Sales' : 'GSP Sales'}</div>
      </div>

      {filterChips}

      {/* Top stats row */}
      <div style={{ display: 'flex', gap: 12, padding: 16, justifyContent: 'space-around', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 140px', textAlign: 'center', padding: '12px 8px', border: '1px solid var(--border)', borderRadius: 8, background: 'rgba(91,194,199,0.04)' }}>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: 'var(--teal)' }}>{fmtCr(totals?.this_month)}</div>
          <div style={{ fontSize: 9, color: 'var(--text3)', letterSpacing: 1, marginTop: 2 }}>THIS MONTH</div>
        </div>
        <div style={{ flex: '1 1 140px', textAlign: 'center', padding: '12px 8px', border: '1px solid var(--border)', borderRadius: 8 }}>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: 'var(--text3)' }}>{fmtCr(totals?.last_month)}</div>
          <div style={{ fontSize: 9, color: 'var(--text3)', letterSpacing: 1, marginTop: 2 }}>LAST MONTH</div>
        </div>
        <div style={{ flex: '1 1 140px', textAlign: 'center', padding: '12px 8px', border: '1px solid var(--border)', borderRadius: 8 }}>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: 'var(--gold)' }}>{fmtCr(totals?.total_amount)}</div>
          <div style={{ fontSize: 9, color: 'var(--text3)', letterSpacing: 1, marginTop: 2 }}>ALL TIME · {totals?.total_invoices || 0} INV</div>
        </div>
      </div>

      {/* Tab toggle */}
      <div style={{ padding: '4px 16px 0', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {[
          { key: 'buyer',  label: 'BUYER-WISE'        },
          { key: 'month',  label: 'MONTH-WISE'        },
          { key: 'merch',  label: 'MERCHANDISER-WISE' },
          { key: 'subcat', label: 'SUB-CATEGORY'      }
        ].map(t => (
          <button key={t.key} onClick={() => setSalesTab(t.key)}
            style={{
              background: salesTab === t.key ? 'var(--teal-bg)' : 'transparent',
              border: `1px solid ${salesTab === t.key ? 'var(--teal)' : 'var(--border)'}`,
              color: salesTab === t.key ? 'var(--teal)' : 'var(--text3)',
              padding: '5px 12px', borderRadius: 14, fontSize: 11, cursor: 'pointer', letterSpacing: 1
            }}>{t.label}</button>
        ))}
      </div>

      {salesTab === 'buyer' && (
        <div className="card" style={{ marginTop: 4 }}>
          <div className="card-title">💰 Buyer-wise Sales</div>
          {(buyers || []).length === 0 && <div style={{ padding: 20, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>No data</div>}
          {(buyers || []).slice(0, 30).map((b) => (
            <div key={titleCase(b.buyer)}
                 onClick={() => setSelectedBuyer(b.buyer)}
                 style={{ padding: '10px 8px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}
                 onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                 onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 600, letterSpacing: 0.5 }}>{titleCase(b.buyer)}</div>
                <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>
                  {b.invoice_count} invoices · {(b.total_qty || 0).toLocaleString()} pcs
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 13, color: 'var(--teal)' }}>{fmtCr(b.total_amount)}</div>
                <div style={{ fontSize: 10, color: pctClass(b.avg_gp_pct), marginTop: 2 }}>GP {fmtPct(b.avg_gp_pct)}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {salesTab === 'month' && (
        <div className="card" style={{ marginTop: 4 }}>
          <div className="card-title">📅 Month-wise Sales</div>
          {(months || []).length === 0 && <div style={{ padding: 20, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>No data</div>}
          {(months || []).map((m) => {
            const label = m.month_label || `${new Date(m.yr, m.mo - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}`;
            return (
              <div key={`${m.yr}-${m.mo}`}
                   onClick={() => setSelectedMonth({ yr: m.yr, mo: m.mo, label })}
                   style={{ padding: '10px 8px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}
                   onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                   onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 600, letterSpacing: 0.5 }}>{label}</div>
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>
                    {m.invoice_count} invoices · {(m.total_qty || 0).toLocaleString()} pcs
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 13, color: 'var(--teal)' }}>{fmtCr(m.total_amount)}</div>
                  <div style={{ fontSize: 10, color: pctClass(m.avg_gp_pct), marginTop: 2 }}>GP {fmtPct(m.avg_gp_pct)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {salesTab === 'merch' && (
        <div className="card" style={{ marginTop: 4 }}>
          <div className="card-title">👤 Merchandiser-wise Sales</div>
          {(merchs || []).length === 0 && <div style={{ padding: 20, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>No data</div>}
          {(merchs || []).map(m => (
            <div key={titleCase(m.key)}
                 onClick={() => m.key !== '(unknown)' && setSelectedMerch(m.key)}
                 style={{ padding: '10px 8px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', cursor: m.key === '(unknown)' ? 'default' : 'pointer' }}
                 onMouseEnter={e => m.key !== '(unknown)' && (e.currentTarget.style.background = 'rgba(255,255,255,0.03)')}
                 onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 600, letterSpacing: 0.5 }}>{titleCase(m.key)}</div>
                <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>
                  {m.invoice_count} invoices · {(m.total_qty || 0).toLocaleString()} pcs
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 13, color: 'var(--teal)' }}>{fmtCr(m.total_amount)}</div>
                <div style={{ fontSize: 10, color: pctClass(m.avg_gp_pct), marginTop: 2 }}>GP {fmtPct(m.avg_gp_pct)}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {salesTab === 'subcat' && (() => {
        const hasCompare = (subcatsPrev || []).length > 0 && yearFilter !== 'all' && yearFilter !== 'custom';
        // Merge current + previous by key
        const map = new Map();
        (subcats || []).forEach(r => map.set(r.key, { key: r.key, cur: r, prev: null }));
        (subcatsPrev || []).forEach(r => {
          if (!map.has(r.key)) map.set(r.key, { key: r.key, cur: null, prev: r });
          else map.get(r.key).prev = r;
        });
        const merged = [...map.values()].sort((a,b) => (+(b.cur?.total_amount||0)) - (+(a.cur?.total_amount||0)));
        const curY  = yearFilter !== 'all' && yearFilter !== 'custom' ? `${yearFilter.slice(0,2)}-${yearFilter.slice(2)}` : 'Current';
        const prevY = yearFilter !== 'all' && yearFilter !== 'custom'
          ? `${(parseInt(yearFilter.slice(0,2))-1)}-${yearFilter.slice(0,2)}`
          : 'Previous';

        return (
          <div className="card" style={{ marginTop: 4 }}>
            <div className="card-title">🏷 Sub-Category — Avg Selling Rate {hasCompare ? '(YoY)' : ''}</div>

            <ScrollX min={hasCompare ? 520 : 0}>
            {/* Column header */}
            {hasCompare && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 100px 70px', gap: 8, padding: '6px 8px', borderBottom: '1px solid var(--border)', fontSize: 10, color: 'var(--text3)', letterSpacing: 1, textTransform: 'uppercase' }}>
                <div>Sub-Category</div>
                <div style={{ textAlign: 'right' }}>FY {curY}</div>
                <div style={{ textAlign: 'right' }}>FY {prevY}</div>
                <div style={{ textAlign: 'right' }}>Δ %</div>
              </div>
            )}

            {merged.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>No data</div>}

            {merged.map(row => {
              const cur     = row.cur;
              const prev    = row.prev;
              const curRate  = cur  ? +cur.avg_rate  || 0 : 0;
              const prevRate = prev ? +prev.avg_rate || 0 : 0;
              const delta    = prevRate > 0 ? ((curRate - prevRate) / prevRate) * 100 : null;
              const deltaCol = delta == null ? 'var(--text3)' : delta >= 0 ? 'var(--green)' : 'var(--red)';

              if (!hasCompare) {
                // Single-period view (ALL or custom range)
                return (
                  <div key={titleCase(row.key)}
                       onClick={() => row.key !== '(unknown)' && setSelectedSubcat(row.key)}
                       style={{ padding: '10px 8px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', cursor: row.key === '(unknown)' ? 'default' : 'pointer' }}
                       onMouseEnter={e => row.key !== '(unknown)' && (e.currentTarget.style.background = 'rgba(255,255,255,0.03)')}
                       onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 600, letterSpacing: 0.5 }}>{titleCase(row.key)}</div>
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>
                        {cur?.invoice_count || 0} invoices · {(+(cur?.total_qty)||0).toLocaleString()} pcs
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 13, color: 'var(--gold)', fontWeight: 600 }}>₹{Math.round(curRate).toLocaleString('en-IN')} <span style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 400 }}>/ pc</span></div>
                      <div style={{ fontSize: 10, color: 'var(--teal)', marginTop: 2 }}>{fmtCr(cur?.total_amount)}</div>
                      <div style={{ fontSize: 10, color: pctClass(cur?.avg_gp_pct), marginTop: 2 }}>GP {fmtPct(cur?.avg_gp_pct)}</div>
                    </div>
                  </div>
                );
              }

              // Comparison view — per-cell click sends the user to drill-down
              // pre-filtered to the year of the clicked column, so the avg
              // price they see matches what they tapped.
              const isUnknown = row.key === '(unknown)';
              const prevYearFilter = yearFilter !== 'all' && yearFilter !== 'custom'
                ? `${(parseInt(yearFilter.slice(0,2))-1)}${yearFilter.slice(0,2)}`
                : null;
              const drillCurrent = (e) => {
                if (isUnknown) return;
                e.stopPropagation();
                // Already on current FY — just open drill-down
                setSelectedSubcat(row.key);
              };
              const drillPrev = (e) => {
                if (isUnknown || !prev || !prevYearFilter) return;
                e.stopPropagation();
                setYearFilter(prevYearFilter);   // switch FY before drilling
                setSelectedSubcat(row.key);
              };
              const cellHover = { transition: 'background 0.15s', borderRadius: 4, padding: '4px 6px', margin: '-4px -6px' };
              return (
                <div key={titleCase(row.key)}
                     style={{ display: 'grid', gridTemplateColumns: '1fr 100px 100px 70px', gap: 8, alignItems: 'center', padding: '10px 8px', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ minWidth: 0, cursor: isUnknown ? 'default' : 'pointer' }}
                       onClick={drillCurrent}
                       title={isUnknown ? '' : `Open ${titleCase(row.key)} for current FY`}>
                    <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 600, letterSpacing: 0.5 }}>{titleCase(row.key)}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>
                      {cur ? `${cur.invoice_count} inv · ${(+cur.total_qty||0).toLocaleString()} pcs` : '—'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', cursor: isUnknown || !cur ? 'default' : 'pointer', ...cellHover }}
                       onClick={drillCurrent}
                       title={isUnknown || !cur ? '' : `Drill into FY ${curY}`}
                       onMouseEnter={e => !isUnknown && cur && (e.currentTarget.style.background = 'var(--gold-bg)')}
                       onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                    <div style={{ fontSize: 13, color: 'var(--gold)', fontWeight: 600 }}>{cur ? `₹${Math.round(curRate).toLocaleString('en-IN')}` : '—'}</div>
                    <div style={{ fontSize: 10, color: 'var(--teal)', marginTop: 2 }}>{cur ? fmtCr(cur.total_amount) : ''}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1 }}>{cur ? `${(+cur.total_qty||0).toLocaleString('en-IN')} pcs` : ''}</div>
                  </div>
                  <div style={{ textAlign: 'right', cursor: isUnknown || !prev ? 'default' : 'pointer', ...cellHover }}
                       onClick={drillPrev}
                       title={isUnknown || !prev ? '' : `Drill into FY ${prevY}`}
                       onMouseEnter={e => !isUnknown && prev && (e.currentTarget.style.background = 'rgba(136,136,136,0.12)')}
                       onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                    <div style={{ fontSize: 13, color: 'var(--text3)', fontWeight: 500 }}>{prev ? `₹${Math.round(prevRate).toLocaleString('en-IN')}` : '—'}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{prev ? fmtCr(prev.total_amount) : ''}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1 }}>{prev ? `${(+prev.total_qty||0).toLocaleString('en-IN')} pcs` : ''}</div>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 13, color: deltaCol, fontWeight: 600 }}>
                    {delta == null ? '—' : `${delta >= 0 ? '+' : ''}${delta.toFixed(1)}%`}
                  </div>
                </div>
              );
            })}
            </ScrollX>
          </div>
        );
      })()}

      {costDetail && <TasCostDetailModal detail={costDetail} onClose={() => setCostDetail(null)} />}
    </div>
  );
}

// ============================================================
// COST COMPARISON view — TAS-wise: Est.Cost vs Sales_Cont vs Invoice
// ============================================================

// FY options for the Cost Comparison toggle. Each option carries the
// from/to dates the backend expects. Indian FY runs Apr 1 → Mar 31.
function fyOptions() {
  const now = new Date();
  const curStart = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const yy = y => String(y).slice(2);
  const make = (startYear) => ({
    key: `${startYear}`,
    label: `FY ${yy(startYear)}-${yy(startYear + 1)}`,
    from: `${startYear}-04-01`,
    to:   `${startYear + 1}-03-31`
  });
  return [
    { ...make(curStart),     current: true },
    { ...make(curStart - 1)              },
    { ...make(curStart - 2)              },
    // "All time" — pass a very wide window because the underlying SP defaults
    // to current FY when dates are empty.
    { key: 'all', label: 'All Time', from: '2015-04-01', to: `${curStart + 2}-03-31` }
  ];
}

function fyTabs({ selected, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 6, padding: '4px 16px 8px', overflowX: 'auto' }}>
      {fyOptions().map(opt => {
        const active = opt.key === selected;
        return (
          <button
            key={opt.key}
            onClick={() => onChange(opt)}
            style={{
              padding: '6px 12px',
              background: active ? 'var(--yellow)' : 'transparent',
              color:      active ? 'var(--bg2)' : 'var(--text2)',
              border: '1px solid ' + (active ? 'var(--yellow)' : 'var(--border)'),
              borderRadius: 6,
              fontSize: 11,
              letterSpacing: 0.5,
              fontWeight: active ? 700 : 500,
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >{opt.label}</button>
        );
      })}
    </div>
  );
}

function CostComparisonView(props) {
  if (!props.isTas) return <GspCostView {...props} />;
  return <TasCostView {...props} />;
}

function GspCostView({ setView, Header }) {
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState('');
  const [fy, setFy] = useState(() => fyOptions().find(o => o.current));
  const [detail, setDetail] = useState(null); // { gspno, summary, header, lines, loading }

  useEffect(() => {
    setLoading(true);
    const qs = `?from=${fy.from}&to=${fy.to}`;
    api('/sales/gsp-cost' + qs).then(d => { setList(Array.isArray(d) ? d : []); setLoading(false); });
  }, [fy.key]);

  function openDetail(r) {
    setDetail({ gspno: r.GspNo, summary: r, header: null, stages: null, loading: true });
    api('/sales/gsp-cost-detail/' + encodeURIComponent(r.GspNo))
      .then(d => setDetail(s => s && s.gspno === r.GspNo
        ? {
            ...s,
            header: d?.header || null,
            stages: Array.isArray(d?.stages) ? d.stages : [],
            loading: false
          }
        : s));
  }

  if (loading) return (
    <div className="screen">
      <Header subtitle="GSP · COST COMPARISON" />
      <div className="loading">LOADING COST DATA…</div>
    </div>
  );

  const q = searchQ.trim().toLowerCase();
  const filtered = !q ? list : list.filter(r => {
    const hay = `${r.GspNo || ''} ${r.buyer || ''} ${r.brand || ''} ${r.design || ''}`.toLowerCase();
    return hay.includes(q);
  });

  // Average per-GSP stage margin % (mirrors TAS: avg of est_pct / inv_pct / pnl_pct).
  // est_pct = planned margin from ord_fabcost.marginper
  // inv_pct = realised margin = (inv_rate - cogs) / cogs * 100
  // pnl_pct = inv_pct - est_pct (change in margin from estimate to invoice)
  const avgOf = key => {
    const v = filtered.map(r => r[key]).filter(x => x != null && !isNaN(x));
    return v.length ? v.reduce((a,b)=>a+b,0) / v.length : null;
  };
  const avgEst = avgOf('est_pct');
  const avgInv = avgOf('inv_pct');
  const avgPnl = (avgInv != null && avgEst != null) ? avgInv - avgEst : null;
  const fmtPct = v => v == null ? '—' : (v >= 0 ? '+' : '') + v.toFixed(2) + '%';
  const pctColor = v => v == null ? 'var(--text3)' : v >= 0 ? 'var(--green)' : 'var(--red)';
  const stats = [
    { label: 'EST %', value: fmtPct(avgEst), color: pctColor(avgEst) },
    { label: 'INV %', value: fmtPct(avgInv), color: pctColor(avgInv) },
    { label: 'P&L %', value: fmtPct(avgPnl), color: pctColor(avgPnl) }
  ];

  return (
    <div className="screen">
      <Header subtitle="GSP · COST COMPARISON" />
      <ExportBar reportKey="gsp-cost-comparison" title="GSP Cost Comparison" subtitle={`FY ${fy.label || fy.key}${searchQ ? ` | search: ${searchQ}` : ''}`} sections={[{ name: 'Cost', columns: GSP_COST_COLS, rows: filtered }]} source={{ endpoint: '/sales/gsp-cost', params: { from: fy.from, to: fy.to }, filters: searchQ ? [{ t: 'search', q: searchQ, keys: ['GspNo', 'buyer', 'brand'] }] : [] }} />
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
        <button onClick={() => setView('gsp')} style={{ background: 'none', border: 'none', color: 'var(--yellow)', fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: 'var(--yellow)' }}>Cost Comparison</div>
        <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>{filtered.length} GSP</div>
      </div>

      <div style={{ display: 'flex', gap: 10, padding: 16, justifyContent: 'space-around', flexWrap: 'wrap' }}>
        {stats.map(s => (
          <div key={s.label} style={{ flex: '1 1 130px', textAlign: 'center', padding: '10px 6px', border: '1px solid var(--border)', borderRadius: 8 }}>
            <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 20, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 9, color: 'var(--text3)', letterSpacing: 1, marginTop: 2 }}>AVG {s.label}</div>
          </div>
        ))}
      </div>

      {fyTabs({ selected: fy.key, onChange: setFy })}

      <div style={{ padding: '0 16px 8px', position: 'relative' }}>
        <span style={{ position: 'absolute', left: 26, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', fontSize: 12 }}>🔍</span>
        <input
          type="text"
          placeholder="Search anything — GSP, buyer, brand, design…"
          value={searchQ}
          onChange={e => setSearchQ(e.target.value)}
          style={{ width: '100%', boxSizing: 'border-box', padding: '8px 32px 8px 32px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 12, outline: 'none' }}
          onFocus={e => e.target.style.borderColor = 'var(--yellow)'}
          onBlur={e => e.target.style.borderColor = 'var(--border)'}
        />
        {searchQ && (
          <span
            onClick={() => setSearchQ('')}
            style={{ position: 'absolute', right: 26, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', cursor: 'pointer', fontSize: 12 }}
          >✕</span>
        )}
      </div>

      <div style={{
        margin: '0 12px 16px',
        overflow: 'auto',
        maxHeight: 'calc(100vh - 290px)',
        border: '1px solid var(--border)',
        borderRadius: 6
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr style={{ color: 'var(--text3)', textAlign: 'left' }}>
              {[
                { label: 'GSP',     align: 'left'  },
                { label: 'Buyer',   align: 'left'  },
                { label: 'Factory', align: 'left'  },
                { label: 'Est %',   align: 'right' },
                { label: 'Inv %',   align: 'right' },
                { label: 'P&L %',   align: 'right' }
              ].map(col => (
                <th key={col.label} style={{
                  padding: '10px 8px', fontWeight: 600, textAlign: col.align,
                  position: 'sticky', top: 0, zIndex: 10,
                  background: 'var(--grid-head-bg)',
                  borderBottom: '1px solid var(--grid-head-border)',
                  color: 'var(--grid-head-text)', letterSpacing: 0.4, fontSize: 11, whiteSpace: 'nowrap'
                }}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 200).map((r, i) => {
              const est = r.est_pct, inv = r.inv_pct;
              const pnl = (est != null && inv != null) ? inv - est : null;
              const fmtPctCell = v => v == null ? '—' : (v > 0 ? '+' : '') + v.toFixed(2) + '%';
              const pctC = v => v == null ? 'var(--text3)' : v >= 0 ? 'var(--green)' : 'var(--red)';
              const zebra = i % 2 === 0 ? 'var(--grid-row)' : 'var(--grid-row-alt)';
              const cell = { padding: '9px 8px', borderBottom: '1px solid var(--grid-row-border)', whiteSpace: 'nowrap' };
              return (
                <tr key={r.GspNo}
                    onClick={() => openDetail(r)}
                    style={{ background: zebra, color: 'var(--text2)', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                    onMouseLeave={e => e.currentTarget.style.background = zebra}>
                  <td style={{ ...cell, color: 'var(--text)', fontWeight: 600 }}>{r.GspNo}</td>
                  <td style={cell}>{r.buyer}</td>
                  <td style={{ ...cell, color: 'var(--text3)' }}>{r.brand || '—'}</td>
                  <td style={{ ...cell, textAlign: 'right', color: pctC(est) }}>{fmtPctCell(est)}</td>
                  <td style={{ ...cell, textAlign: 'right', color: pctC(inv) }}>{fmtPctCell(inv)}</td>
                  <td style={{ ...cell, textAlign: 'right', color: pctC(pnl), fontWeight: 700 }}>{fmtPctCell(pnl)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length > 200 && <div style={{ padding: 10, fontSize: 10, color: 'var(--text3)', textAlign: 'center' }}>Showing 200 of {filtered.length}</div>}
      </div>
      {detail && <GspCostDetailModal detail={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

// Modal that shows the GSP cost-grid breakdown as a Component × Stage pivot (like TAS).
function GspCostDetailModal({ detail, onClose }) {
  const { gspno, summary, header, stages, loading } = detail;
  // 2-decimal formatting everywhere — no rounding.
  const fmtMoney = v => v == null ? '—' : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtNum   = v => v == null ? '—' : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtPct   = v => v == null ? '—' : Number(v).toFixed(2) + '%';
  const fmtUsd   = v => v == null ? '—' : '$' + Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const pctColor = v => v == null ? 'var(--text3)' : v >= 0 ? 'var(--green)' : 'var(--red)';

  const stageMap = {};
  (stages || []).forEach(s => { stageMap[s.stage] = s; });
  const stageOrder = ['SO', 'Invoice'];
  const presentStages = stageOrder.filter(s => stageMap[s]);

  // Component rows — mirrors the form's cost grid in vertical layout.
  const compRows = [
    { key: 'dollarprice', label: 'Fab Price ($)', fmt: fmtUsd  },
    { key: 'opcper',      label: 'Opc %',         fmt: fmtPct  },
    { key: 'opccom',      label: 'Opc',           fmt: fmtNum  },
    { key: 'netprice',    label: 'Net Price ($)', fmt: fmtUsd  },
    { key: 'xchrate',     label: 'Exchange',      fmt: fmtNum  },
    { key: 'inr',         label: 'INR',           fmt: fmtMoney },
    { key: 'finper',      label: 'Fin Cost %',    fmt: fmtPct  },
    { key: 'fincost',     label: 'Fin Cost',      fmt: fmtMoney },
    { key: 'bcd',         label: 'BCD',           fmt: fmtMoney },
    { key: 'cif',         label: 'CIF',           fmt: fmtMoney },
    { key: 'transport',   label: 'Transport',     fmt: fmtMoney },
    // COGS = INR + Fin Cost + BCD + CIF + Transport. Comm (agent commission) is
    // charged ON TOP and is NOT inside COGS, so it sits below the COGS line —
    // otherwise the stack above COGS doesn't add up to it.
    { key: 'cogs',        label: 'COGS',          fmt: fmtMoney, bold: true },
    { key: 'comm',        label: 'Comm (on COGS)', fmt: fmtMoney },
    { key: 'sell_rate',   label: 'Sell Rate',     fmt: fmtMoney },
    { key: 'margin_pct',  label: 'Margin %',      fmt: fmtPct, bold: true, isPct: true }
  ];

  return (
    <div onClick={onClose}
         style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 50,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()}
           style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8,
                    maxWidth: 900, width: '100%', maxHeight: '88vh', overflow: 'auto',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.6)' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)',
                      display: 'flex', alignItems: 'center', gap: 12 }}>
          <div>
            <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: 'var(--yellow)' }}>{gspno}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
              {summary?.buyer || header?.buyer || '—'} · {summary?.brand || header?.brand || '—'}
            </div>
          </div>
          <button onClick={onClose}
                  style={{ marginLeft: 'auto', background: 'none', border: 'none',
                           color: 'var(--text3)', fontSize: 22, cursor: 'pointer' }}>✕</button>
        </div>

        {loading && <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>Loading detail…</div>}

        {!loading && presentStages.length === 0 &&
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>No cost-grid data for this GSP.</div>}

        {!loading && presentStages.length > 0 && (
          <div style={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ color: 'var(--text2)' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', position: 'sticky', top: 0,
                               background: 'var(--bg3)', borderBottom: '2px solid #E3C15E',
                               fontWeight: 600, letterSpacing: 0.5 }}>Component</th>
                  {presentStages.map(s => (
                    <th key={s} style={{ padding: '10px 12px', textAlign: 'right', position: 'sticky', top: 0,
                                         background: 'var(--bg3)', borderBottom: '2px solid #E3C15E',
                                         fontWeight: 600, letterSpacing: 0.5 }}>{s}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {compRows.map(r => (
                  <tr key={r.key} style={{ borderBottom: '1px solid var(--grid-row-border)', color: 'var(--text2)' }}>
                    <td style={{ padding: '8px 12px', color: 'var(--text)', fontWeight: r.bold ? 700 : 400 }}>{r.label}</td>
                    {presentStages.map(s => {
                      const v = stageMap[s]?.[r.key];
                      const color = r.isPct ? pctColor(v) : (r.bold ? 'var(--text)' : 'var(--text2)');
                      return (
                        <td key={s} style={{ padding: '8px 12px', textAlign: 'right',
                                             color, fontWeight: r.bold ? 700 : 400 }}>
                          {r.fmt(v)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function TasCostView({ isTas, setView, Header }) {
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState('');
  const [fy, setFy] = useState(() => fyOptions().find(o => o.current));
  const [detail, setDetail] = useState(null); // { tasno, summary, stages: [...] | null, loading }

  useEffect(() => {
    setLoading(true);
    const qs = `?from=${fy.from}&to=${fy.to}`;
    api('/sales/cost' + qs).then(d => { setList(Array.isArray(d) ? d : []); setLoading(false); });
  }, [fy.key]);

  function openDetail(r) {
    setDetail({ tasno: r.Tas_No, summary: r, stages: null, loading: true });
    api('/sales/cost-detail/' + encodeURIComponent(r.Tas_No))
      .then(d => setDetail(s => s && s.tasno === r.Tas_No
        ? { ...s, stages: Array.isArray(d) ? d : [], loading: false }
        : s));
  }

  if (loading) return (
    <div className="screen">
      <Header subtitle={isTas ? 'TAS · COST COMPARISON' : 'GSP · COST COMPARISON'} />
      <div className="loading">LOADING COST DATA…</div>
    </div>
  );

  // Search across TAS, buyer, factory, merchandiser
  const q = searchQ.trim().toLowerCase();
  const filtered = !q ? list : list.filter(r => {
    const hay = `${r.Tas_No || ''} ${r.buyer || ''} ${r.factory || ''} ${r.merchandiser || ''}`.toLowerCase();
    return hay.includes(q);
  });

  // Aggregate avg margins
  const stages = [
    { key: 'est', label: 'Est.Cost'   },
    { key: 'sc',  label: 'Sales_Cont' },
    { key: 'inv', label: 'Invoice'    }
  ];
  const avg = stages.reduce((acc, s) => {
    const vals = filtered.map(r => r[`${s.key}_pct`]).filter(v => v != null && !isNaN(v));
    acc[s.key] = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
    return acc;
  }, {});

  return (
    <div className="screen">
      <Header subtitle={isTas ? 'TAS · COST COMPARISON' : 'GSP · COST COMPARISON'} />
      <ExportBar reportKey={isTas ? 'tas-cost-comparison' : 'gsp-cost-comparison'} title={`${isTas ? 'TAS' : 'GSP'} Cost Comparison`} subtitle={`FY ${fy.label || fy.key}${searchQ ? ` | search: ${searchQ}` : ''}`} sections={[{ name: 'Cost', columns: TAS_COST_COLS, rows: filtered }]} source={{ endpoint: '/sales/cost', params: { from: fy.from, to: fy.to }, filters: searchQ ? [{ t: 'search', q: searchQ, keys: ['Tas_No', 'buyer', 'factory'] }] : [] }} />
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
        <button onClick={() => setView(isTas ? 'tas' : 'gsp')} style={{ background: 'none', border: 'none', color: 'var(--yellow)', fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: 'var(--yellow)' }}>Cost Comparison</div>
        <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>{filtered.length} TAS</div>
      </div>

      {/* Avg margins by stage */}
      <div style={{ display: 'flex', gap: 10, padding: 16, justifyContent: 'space-around', flexWrap: 'wrap' }}>
        {stages.map(s => (
          <div key={s.key} style={{ flex: '1 1 130px', textAlign: 'center', padding: '10px 6px', border: '1px solid var(--border)', borderRadius: 8 }}>
            <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 20, color: pctClass(avg[s.key]) }}>{fmtPct(avg[s.key])}</div>
            <div style={{ fontSize: 9, color: 'var(--text3)', letterSpacing: 1, marginTop: 2 }}>AVG {s.label.toUpperCase()}</div>
          </div>
        ))}
      </div>

      {fyTabs({ selected: fy.key, onChange: setFy })}

      {/* Universal search — TAS / buyer / factory / merchandiser */}
      <div style={{ padding: '0 16px 8px', position: 'relative' }}>
        <span style={{ position: 'absolute', left: 26, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', fontSize: 12 }}>🔍</span>
        <input
          type="text"
          placeholder="Search anything — TAS, buyer, factory, merchandiser…"
          value={searchQ}
          onChange={e => setSearchQ(e.target.value)}
          style={{ width: '100%', boxSizing: 'border-box', padding: '8px 32px 8px 32px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 12, outline: 'none' }}
          onFocus={e => e.target.style.borderColor = 'var(--yellow)'}
          onBlur={e => e.target.style.borderColor = 'var(--border)'}
        />
        {searchQ && (
          <span
            onClick={() => setSearchQ('')}
            style={{ position: 'absolute', right: 26, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', cursor: 'pointer', fontSize: 12 }}
          >✕</span>
        )}
      </div>

      {/* TAS-wise pivot table — scrollable container so thead sticks while rows scroll */}
      <div style={{
        margin: '0 12px 16px',
        overflow: 'auto',
        maxHeight: 'calc(100vh - 290px)', // leave room for header + back row + stats + search + bottom nav
        border: '1px solid var(--border)',
        borderRadius: 6
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr style={{ color: 'var(--text3)', textAlign: 'left' }}>
              {[
                { label: 'TAS',     align: 'left'  },
                { label: 'Buyer',   align: 'left'  },
                { label: 'Factory', align: 'left'  },
                { label: 'Est %',   align: 'right' },
                { label: 'SC %',    align: 'right' },
                { label: 'Inv %',   align: 'right' },
                { label: 'P&L %',   align: 'right' }
              ].map(col => (
                <th key={col.label} style={{
                  padding: '10px 8px', fontWeight: 600, textAlign: col.align,
                  position: 'sticky', top: 0, zIndex: 10,
                  background: 'var(--grid-head-bg)',
                  borderBottom: '1px solid var(--grid-head-border)',
                  color: 'var(--grid-head-text)', letterSpacing: 0.4, fontSize: 11, whiteSpace: 'nowrap'
                }}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 200).map((r, i) => {
              const drift = (r.inv_pct != null && r.est_pct != null) ? (r.inv_pct - r.est_pct) : null;
              const zebra = i % 2 === 0 ? 'var(--grid-row)' : 'var(--grid-row-alt)';
              const cell = { padding: '9px 8px', borderBottom: '1px solid var(--grid-row-border)', whiteSpace: 'nowrap' };
              return (
                <tr key={r.Tas_No}
                    onClick={() => openDetail(r)}
                    style={{ background: zebra, color: 'var(--text2)', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                    onMouseLeave={e => e.currentTarget.style.background = zebra}>
                  <td style={{ ...cell, color: 'var(--text)', fontWeight: 600 }}>{r.Tas_No}</td>
                  <td style={cell}>{r.buyer}</td>
                  <td style={{ ...cell, color: 'var(--text3)' }}>{r.factory}</td>
                  <td style={{ ...cell, textAlign: 'right', color: pctClass(r.est_pct) }}>{fmtPct(r.est_pct)}</td>
                  <td style={{ ...cell, textAlign: 'right', color: pctClass(r.sc_pct)  }}>{fmtPct(r.sc_pct)}</td>
                  <td style={{ ...cell, textAlign: 'right', color: pctClass(r.inv_pct) }}>{fmtPct(r.inv_pct)}</td>
                  <td style={{ ...cell, textAlign: 'right', color: drift == null ? 'var(--text3)' : drift >= 0 ? 'var(--green)' : 'var(--red)' }}>
                    {drift == null ? '—' : (drift > 0 ? '+' : '') + drift.toFixed(1) + '%'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length > 200 && <div style={{ padding: 10, fontSize: 10, color: 'var(--text3)', textAlign: 'center' }}>Showing 200 of {filtered.length}</div>}
      </div>

      {detail && <TasCostDetailModal detail={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

// Modal that shows the full 3-stage cost breakdown grid for one TAS.
// Rows = the cost components (Fab, Freight, CIF, …, Profit %). Columns = cost stages.
function TasCostDetailModal({ detail, onClose }) {
  const { tasno, summary, stages, loading } = detail;
  // Order stages as Est → SC → Invoice so the eye moves left-to-right through the lifecycle.
  const stageOrder = ['1. Est.Cost', '2. Sales_Cont', '3. Invoice'];
  const stageLabel = { '1. Est.Cost': 'Est.Cost', '2. Sales_Cont': 'Sales_Cont', '3. Invoice': 'Invoice' };
  const byStage = {};
  (stages || []).forEach(s => { byStage[s.Cost_Stage] = s; });
  const presentStages = stageOrder.filter(s => byStage[s]);

  const rows = [
    { key: 'Fab',            label: 'Fab'            },
    { key: 'Freight',        label: 'Freight'        },
    { key: 'Cif',            label: 'CIF'            },
    { key: 'Consumption',    label: 'Consumption'    },
    { key: 'Fabcost',        label: 'Fab Cost'       },
    { key: 'Cm',             label: 'CM'             },
    { key: 'Bd_Trims',       label: 'BD Trims'       },
    { key: 'Ind_Trims',      label: 'Ind Trims'      },
    { key: 'China_Trims',    label: 'China Trims'    },
    { key: 'Wash',           label: 'Wash'           },
    { key: 'Transport',      label: 'Transport'      },
    { key: 'Cog',            label: 'COG',           bold: true },
    { key: 'Exchange',       label: 'Exchange Rate'  },
    { key: 'Inr',            label: 'INR'            },
    { key: 'So_Rate',        label: 'SO Rate'        },
    { key: 'Margin',         label: 'Margin'         },
    { key: 'Profit_Percent', label: 'Profit %',      pct: true, bold: true }
  ];

  const fmt = (v, pct) => {
    if (v == null) return '—';
    if (pct) return (+v).toFixed(2) + '%';
    return Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 });
  };
  const pctColor = v => v == null ? 'var(--text3)' : v >= 20 ? 'var(--green)' : v >= 10 ? 'var(--yellow)' : 'var(--red)';

  return (
    <div onClick={onClose}
         style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '20px 20px 96px 20px' /* extra bottom room so modal never sits under the .bottom-nav */ }}>
      <div onClick={e => e.stopPropagation()}
           style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8,
                    maxWidth: 720, width: '100%', maxHeight: 'calc(100vh - 116px)',
                    display: 'flex', flexDirection: 'column',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.6)' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)',
                      display: 'flex', alignItems: 'center', gap: 12 }}>
          <div>
            <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: 'var(--yellow)' }}>{tasno}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
              {summary?.buyer} · {summary?.factory} · {summary?.merchandiser || '—'}
            </div>
          </div>
          <button onClick={onClose}
                  style={{ marginLeft: 'auto', background: 'none', border: 'none',
                           color: 'var(--text3)', fontSize: 22, cursor: 'pointer' }}>✕</button>
        </div>

        {loading && <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>Loading detail…</div>}

        {!loading && presentStages.length === 0 &&
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>No cost-stage data for this TAS.</div>}

        {!loading && presentStages.length > 0 && (
          <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ color: 'var(--text2)' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', position: 'sticky', top: 0,
                               background: 'var(--bg3)', borderBottom: '2px solid #E3C15E',
                               fontWeight: 600, letterSpacing: 0.5 }}>Component</th>
                  {presentStages.map(s => (
                    <th key={s} style={{ padding: '10px 12px', textAlign: 'right', position: 'sticky', top: 0,
                                         background: 'var(--bg3)', borderBottom: '2px solid #E3C15E',
                                         fontWeight: 600, letterSpacing: 0.5 }}>{stageLabel[s]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(rdef => (
                  <tr key={rdef.key} style={{ borderBottom: '1px solid var(--grid-row-border)', color: 'var(--text2)' }}>
                    <td style={{ padding: '8px 12px', color: 'var(--text)',
                                 fontWeight: rdef.bold ? 700 : 400 }}>{rdef.label}</td>
                    {presentStages.map(s => {
                      const v = byStage[s]?.[rdef.key];
                      const color = rdef.pct ? pctColor(v) : 'var(--text)';
                      return (
                        <td key={s} style={{ padding: '8px 12px', textAlign: 'right',
                                             color, fontWeight: rdef.bold ? 700 : 400 }}>
                          {fmt(v, rdef.pct)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// ============================================================
// PRODUCTION DETAILS view — TAS only.
// Production-floor status for every pending TAS, sourced from ERP report
// sp_report_5633 (Cutting → Input → Output → Ready, per TAS × shade) via
// GET /api/orders/production-report. Each card aggregates a TAS's shades into
// the production funnel + a Ready% bar; expand → shade-wise breakdown.
// /orders?type=garment is also pulled to enrich each TAS with merchandiser,
// current pending stage + stage delay (the report has no merchandiser/stage).
// Tabs group by FACTORY / BUYER / MERCHANDISER / STATUS.
// ============================================================
function ProductionDetailsView({ setView, Header, onOrderClick, backTo = 'tas' }) {
  const [rows, setRows] = useState([]);           // raw sp_report_5633 rows (TAS×shade)
  const [meta, setMeta] = useState({});           // tas_no -> /orders row (merchandiser, stage, delay)
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('day');          // default to day (Today's Production)
  const [drillBy, setDrillBy] = useState(null);   // { by, value }
  const [searchQ, setSearchQ] = useState('');
  const [openTas, setOpenTas] = useState(null);   // tas_no whose shade breakdown is expanded
  // DATE-WISE tab state
  const [dateSummary, setDateSummary] = useState(null);  // [{prod_date,tas_cnt,d_cut,...}]
  const [dateDetail, setDateDetail] = useState({});      // { 'YYYY-MM-DD': [rows] }
  const [selectedDate, setSelectedDate] = useState(null);
  const [prodMonth, setProdMonth] = useState(null);      // 'YYYY-MM' — drilled month in the By-Month tab
  const [dayTasOpen, setDayTasOpen] = useState(null);    // tas_no whose production card is shown INLINE in the day detail
  const [dateLoading, setDateLoading] = useState(false);

  // DAY-WISE state & controls
  const todayIso = useMemo(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }, []);
  const [selectedDay, setSelectedDay] = useState(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  });
  const [expandedFac, setExpandedFac] = useState(null);
  const [daySearch, setDaySearch] = useState('');

  const FACTORY_HISTORICAL_AVG = {
    'MOMO': 7482,
    'BDBS': 3384,
    'FASHION SUMMIT': 2753,
    'SEWTECH FASHIONS LTD': 2262,
    'ALIZA FASHION': 2232,
    'BASIC SHIRTS LIMITE': 1749,
    'DEOTI': 1716,
    'ENERGYPAC': 1518,
    'CROSSWEAR INDUSTRIES LTD.': 1330,
    'DK Global Fashion Wear Ltd': 1005,
    'COLOUR & CO': 765,
    'ROSE DRESSES': 593,
    'ADVANCE ATTIRE LTD': 539,
  };
  const [facAvgMap, setFacAvgMap] = useState(FACTORY_HISTORICAL_AVG);

  useEffect(() => {
    api('/orders/production-factory-averages')
      .then(d => {
        if (Array.isArray(d) && d.length > 0) {
          const m = { ...FACTORY_HISTORICAL_AVG };
          d.forEach(r => {
            if (r.factory && r.avg_output != null) m[r.factory.trim()] = Math.round(Number(r.avg_output));
          });
          setFacAvgMap(m);
        }
      })
      .catch(() => {});
  }, []);

  const handleBack = () => {
    setView(backTo || 'tas');
  };

  const accent = 'var(--gold)';
  const num = v => Number(v) || 0;

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api('/orders/production-report'),
      api('/orders?type=garment'),
    ]).then(([rep, ord]) => {
      setRows(Array.isArray(rep) ? rep : []);
      const m = {};
      (Array.isArray(ord) ? ord : []).forEach(o => { if (o.tas_no) m[o.tas_no] = o; });
      setMeta(m);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Lazy-load the date-wise summary when Day-Wise or By-Month tab is opened.
  useEffect(() => {
    if ((tab === 'day' || tab === 'date') && dateSummary == null && !dateLoading) {
      setDateLoading(true);
      api('/orders/production-daily')
        .then(d => {
          const arr = Array.isArray(d) ? d : [];
          setDateSummary(arr);
          setDateLoading(false);
          if (arr.length > 0 && !arr.some(x => x.prod_date === todayIso)) {
            setSelectedDay(arr[0].prod_date);
          }
        })
        .catch(() => { setDateSummary([]); setDateLoading(false); });
    }
  }, [tab, dateSummary, dateLoading, todayIso]);

  // Load day detail for selectedDay whenever in Day-Wise tab
  useEffect(() => {
    if (tab === 'day' && selectedDay && !dateDetail[selectedDay]) {
      api(`/orders/production-daily?date=${selectedDay}`)
        .then(d => setDateDetail(m => ({ ...m, [selectedDay]: Array.isArray(d) ? d : [] })))
        .catch(() => setDateDetail(m => ({ ...m, [selectedDay]: [] })));
    }
  }, [tab, selectedDay, dateDetail]);

  const stepDay = (delta) => {
    const parts = (selectedDay || todayIso).split('-').map(Number);
    const dt = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2] + delta));
    const yyyy = dt.getUTCFullYear();
    const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(dt.getUTCDate()).padStart(2, '0');
    const iso = `${yyyy}-${mm}-${dd}`;
    setSelectedDay(iso);
    setExpandedFac(null);
  };

  // Drill into one production day → fetch that day's TAS×shade movements.
  function openDay(dateIso) {
    setSelectedDate(dateIso);
    setDayTasOpen(null);
    if (!dateDetail[dateIso]) {
      api(`/orders/production-daily?date=${dateIso}`)
        .then(d => setDateDetail(m => ({ ...m, [dateIso]: Array.isArray(d) ? d : [] })))
        .catch(() => setDateDetail(m => ({ ...m, [dateIso]: [] })));
    }
  }

  // 'YYYY-MM-DD' → 'Mon, 15 Jun 26'
  const fmtDateIso = iso => {
    if (!iso) return '—';
    const dt = new Date(iso + 'T00:00:00');
    return dt.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: '2-digit' });
  };

  // BASE = every PENDING TAS (from /orders, statusid=1). The sp_report_5633
  // production figures are LEFT-joined onto each pending order by tas_no, so
  // pending orders with no cutting yet still appear as "Not Started" (0%).
  // Build the report aggregation map (one entry per TAS × all its shades).
  const buildTasList = () => {
    const repMap = new Map();
    for (const r of rows) {
      const tas = String(r.tasno || '').trim();
      if (!tas) continue;
      if (!repMap.has(tas)) {
        repMap.set(tas, {
          factory: r.Factory || null, buyer: r.Buyer || null,
          order: 0, cut: 0, input: 0, output: 0, ready: 0, shades: [], allFull: true,
          lastUpdated: r['Last Updated'] || null,
          t_cut: 0, t_input: 0, t_output: 0, t_ready: 0,   // today's movement
        });
      }
      const g = repMap.get(tas);
      const order = num(r.orderqty), cut = num(r['Total Cutting']), inp = num(r['Total Input']),
            out = num(r['Total Output']), rdy = num(r['Ready Qty']);
      const tc = num(r.today_cut), ti = num(r.today_input), to = num(r.today_output), tr = num(r.today_ready);
      g.order += order; g.cut += cut; g.input += inp; g.output += out; g.ready += rdy;
      g.t_cut += tc; g.t_input += ti; g.t_output += to; g.t_ready += tr;
      if (String(r.Status || '').toLowerCase() !== 'full') g.allFull = false;
      g.shades.push({ shade: r.shade || '—', order, cut, input: inp, output: out, ready: rdy, status: r.Status || '',
        t_cut: tc, t_input: ti, t_output: to, t_ready: tr });
    }
    // Drive off the pending-order list; attach production where the report has it.
    return Object.values(meta).map(o => {
      const rep = repMap.get(String(o.tas_no || '').trim());
      const base = {
        tas_no: o.tas_no,
        merchandiser: titleCase(o.merchandiser) || '—',
        stage: o.current_pending_stage || null,
        stage_delay: o.current_stage_delay ?? null,
        style: o.style_name || '',
      };
      if (rep) {
        return {
          ...base,
          factory: titleCase(rep.factory) || titleCase(o.factory) || '—',
          buyer: rep.buyer || titleCase(o.buyer) || '—',
          order: rep.order, cut: rep.cut, input: rep.input, output: rep.output, ready: rep.ready,
          t_cut: rep.t_cut, t_input: rep.t_input, t_output: rep.t_output, t_ready: rep.t_ready,
          shades: rep.shades, lastUpdated: rep.lastUpdated, started: true,
          status: rep.allFull ? 'Full' : 'Part',
          ready_pct: rep.order > 0 ? Math.round((rep.ready / rep.order) * 100) : 0,
        };
      }
      // Pending order with no production started yet.
      const ord = num(o.orderqty) || num(o.bal_qty) || 0;
      return {
        ...base,
        factory: titleCase(o.factory) || '—',
        buyer: titleCase(o.buyer) || '—',
        order: ord, cut: 0, input: 0, output: 0, ready: 0,
        t_cut: 0, t_input: 0, t_output: 0, t_ready: 0,
        shades: [], lastUpdated: null, started: false,
        status: 'Not Started', ready_pct: 0,
      };
    });
  };

  const allTas = buildTasList();

  // Search across TAS / factory / buyer / merchandiser / style.
  const q = searchQ.trim().toLowerCase();
  const filtered = !q ? allTas : allTas.filter(t => {
    const hay = `${t.tas_no} ${titleCase(t.factory)} ${titleCase(t.buyer)} ${t.merchandiser} ${t.style}`.toLowerCase();
    return hay.includes(q);
  });

  const keyForTab = {
    factory:      t => titleCase(t.factory) || '—',
    buyer:        t => titleCase(t.buyer) || '—',
    merchandiser: t => t.merchandiser || '—',
    status:       t => t.status,
  };

  // Group helper — counts TAS + sums order/ready, derives group Ready%.
  const groupBy = (keyFn) => {
    const map = new Map();
    for (const t of filtered) {
      const k = keyFn(t) || '—';
      if (!map.has(k)) map.set(k, { key: k, count: 0, order: 0, ready: 0 });
      const g = map.get(k);
      g.count++; g.order += t.order; g.ready += t.ready;
    }
    return [...map.values()]
      .map(g => ({ ...g, pct: g.order > 0 ? Math.round((g.ready / g.order) * 100) : 0 }))
      .sort((a, b) => a.pct - b.pct || b.order - a.order);
  };

  // List shown for ALL or after drilling into a group — least-ready first, but
  // orders already IN production (some cutting) rank above Not-Started ones at
  // the same 0%, since they're the ones actively needing a push.
  const visibleList = (() => {
    let list = filtered;
    if (drillBy) list = list.filter(t => (keyForTab[drillBy.by]?.(t) || '—') === drillBy.value);
    return [...list].sort((a, b) =>
      a.ready_pct - b.ready_pct ||
      (b.started ? 1 : 0) - (a.started ? 1 : 0) ||
      b.order - a.order);
  })();

  const tabs = [
    { key: 'day',          label: "Today's Production" },
    { key: 'all',          label: 'All Orders' },
    { key: 'factory',      label: 'By Factory' },
    { key: 'buyer',        label: 'By Buyer' },
    { key: 'merchandiser', label: 'By Merchant' },
    { key: 'status',       label: 'By Status' },
    { key: 'date',         label: 'By Month' },
  ];

  const pctColor = p => p >= 80 ? 'var(--green)' : p >= 40 ? 'var(--yellow)' : 'var(--red)';

  // The 5 stages of garment production, in flow order. Reused for the overall
  // summary strip and each order card so the whole screen reads the same way.
  const STAGES = [
    { key: 'order',  label: 'ORDER',  color: 'var(--text2)' },
    { key: 'cut',    label: 'CUT',    color: 'var(--teal)' },
    { key: 'input',  label: 'INPUT',  color: 'var(--teal)' },
    { key: 'output', label: 'OUTPUT', color: 'var(--gold)' },
    { key: 'ready',  label: 'READY',  color: 'var(--green)' },
  ];
  // Horizontal Order→Cut→Input→Output→Ready pipeline. big=hero (header), else card.
  const stagePipeline = (d, big) => {
    const order = d.order || 0;
    const pct = v => order > 0 ? Math.round((v / order) * 100) : 0;
    const numSize = big ? 15 : 12, labSize = big ? 9.5 : 8;
    return (
      <div style={{ display: 'flex', alignItems: 'stretch', gap: big ? 4 : 2 }}>
        {STAGES.map((s, i) => {
          const v = d[s.key] || 0;
          const p = s.key === 'order' ? 100 : pct(v);
          return (
            <React.Fragment key={s.key}>
              <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
                <div style={{ fontSize: labSize, letterSpacing: 1, color: 'var(--text3)' }}>{s.label}</div>
                <div style={{ fontSize: numSize, fontWeight: 600, color: s.color, fontFamily: 'Inter, sans-serif', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fmt(v)}</div>
                <div style={{ fontSize: labSize, color: s.key === 'order' ? 'transparent' : 'var(--text3)' }}>{p}%</div>
                <div style={{ height: 3, background: 'var(--border)', borderRadius: 2, marginTop: 4 }}>
                  <div style={{ height: '100%', width: `${Math.min(100, p)}%`, background: s.color, borderRadius: 2 }} />
                </div>
              </div>
              {i < STAGES.length - 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text3)', fontSize: big ? 14 : 11, opacity: 0.5, flexShrink: 0, padding: '0 4px', userSelect: 'none' }}>›</div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  if (loading) return (
    <div className="screen">
      <Header subtitle="TAS · PRODUCTION DETAILS" />
      <div className="loading">LOADING PRODUCTION…</div>
    </div>
  );

  // TAS card — production funnel + Ready% bar; expand → shade-wise breakdown.
  const renderTasCard = (t) => {
    const isOpen = openTas === t.tas_no;
    const c = t.started ? pctColor(t.ready_pct) : 'var(--text3)';
    const statusC = t.status === 'Full' ? 'var(--green)' : t.status === 'Part' ? 'var(--yellow)' : 'var(--text3)';
    const movedToday = ((t.t_cut || 0) + (t.t_input || 0) + (t.t_output || 0) + (t.t_ready || 0)) !== 0;
    const balPcs = Math.max(0, (t.order || 0) - (t.output || 0));  // pcs still to sew out
    return (
      <div key={t.tas_no} id={`tas-${t.tas_no}`}
           style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderLeft: `3px solid ${c}`, borderRadius: 8 }}>
        <div onClick={() => setOpenTas(isOpen ? null : t.tas_no)}
             style={{ padding: '12px 14px', cursor: 'pointer' }}>
          {/* Title row: TAS + status/badges on the left, Ready% + chevron on the right */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 15, color: 'var(--text)', fontWeight: 600 }}>{t.tas_no}</span>
                <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 10, background: `${statusC}22`, color: statusC, letterSpacing: 0.5 }}>{t.status.toUpperCase()}</span>
                {t.stage_delay != null && t.stage_delay > 0 &&
                  <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 10, background: 'var(--red-bg)', color: 'var(--red)' }}>{t.stage_delay}d behind</span>}
                {movedToday && <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 10, background: 'rgba(82,194,120,0.16)', color: 'var(--green)' }}>● TODAY</span>}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {titleCase(t.buyer)} · 🏭 {titleCase(t.factory)}{t.merchandiser && t.merchandiser !== '—' ? ` · ${t.merchandiser}` : ''}
              </div>
            </div>
            <div style={{ textAlign: 'right', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div>
                <div style={{ fontSize: 20, fontWeight: 600, color: c, fontFamily: 'Inter, sans-serif', lineHeight: 1 }}>{t.ready_pct}%</div>
                <div style={{ fontSize: 8, letterSpacing: 1.5, color: 'var(--text3)', marginTop: 2 }}>READY</div>
              </div>
              <div style={{ fontSize: 16, color: 'var(--text3)' }}>{isOpen ? '▾' : '▸'}</div>
            </div>
          </div>

          {/* Today's movement + footnote */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
            {movedToday && (
              <span style={{ fontSize: 10, color: 'var(--green)' }}>
                <b style={{ letterSpacing: 0.5 }}>TODAY</b>&nbsp; ✂ {fmt(t.t_cut)} · ⮕ {fmt(t.t_input)} · ⬇ {fmt(t.t_output)}{(t.t_ready || 0) !== 0 ? ` · ✅ ${fmt(t.t_ready)}` : ''}
              </span>
            )}
            <span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: movedToday ? 'auto' : 0 }}>
              {balPcs > 0 ? <b style={{ color: 'var(--text2)' }}>{fmt(balPcs)} to sew</b> : null}{balPcs > 0 ? ' · ' : ''}{t.shades.length} shade{t.shades.length !== 1 ? 's' : ''}{t.lastUpdated ? ` · updated ${t.lastUpdated}` : ''}
            </span>
          </div>
        </div>

        {isOpen && (
          <div style={{ padding: '0 12px 12px', borderTop: '1px solid var(--border)' }}>
            {t.shades.length === 0 ? (
              <div style={{ padding: 12, fontSize: 11, color: 'var(--text3)' }}>
                Production not started — no cutting/input/output recorded yet for this TAS.
              </div>
            ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginTop: 8 }}>
              <thead>
                <tr style={{ color: 'var(--text3)', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                  <th style={{ padding: '6px 6px', fontWeight: 500 }}>Shade</th>
                  <th style={{ padding: '6px 6px', fontWeight: 500, textAlign: 'right' }}>Order</th>
                  <th style={{ padding: '6px 6px', fontWeight: 500, textAlign: 'right' }}>Cut</th>
                  <th style={{ padding: '6px 6px', fontWeight: 500, textAlign: 'right' }}>Input</th>
                  <th style={{ padding: '6px 6px', fontWeight: 500, textAlign: 'right' }}>Output</th>
                  <th style={{ padding: '6px 6px', fontWeight: 500, textAlign: 'right' }}>Ready</th>
                  <th style={{ padding: '6px 6px', fontWeight: 500, textAlign: 'right' }}>Balance</th>
                  <th style={{ padding: '6px 6px', fontWeight: 500, textAlign: 'right', color: 'var(--green)' }}>Today (Out)</th>
                  <th style={{ padding: '6px 6px', fontWeight: 500 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {t.shades.map((s, i) => {
                  const full = String(s.status || '').toLowerCase() === 'full';
                  const bal = Math.max(0, (s.order || 0) - (s.output || 0));
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid var(--grid-row-border)', color: 'var(--text2)' }}>
                      <td style={{ padding: '7px 6px', color: 'var(--text)' }}>{titleCase(s.shade)}</td>
                      <td style={{ padding: '7px 6px', textAlign: 'right' }}>{fmt(s.order)}</td>
                      <td style={{ padding: '7px 6px', textAlign: 'right' }}>{fmt(s.cut)}</td>
                      <td style={{ padding: '7px 6px', textAlign: 'right' }}>{fmt(s.input)}</td>
                      <td style={{ padding: '7px 6px', textAlign: 'right' }}>{fmt(s.output)}</td>
                      <td style={{ padding: '7px 6px', textAlign: 'right', color: s.ready >= s.order && s.order > 0 ? 'var(--green)' : 'var(--text2)' }}>{fmt(s.ready)}</td>
                      <td style={{ padding: '7px 6px', textAlign: 'right', color: bal > 0 ? 'var(--text)' : 'var(--green)' }}>{bal > 0 ? fmt(bal) : '0'}</td>
                      <td style={{ padding: '7px 6px', textAlign: 'right', color: (s.t_output || 0) > 0 ? 'var(--green)' : 'var(--text3)' }}>{(s.t_output || 0) !== 0 ? `+${fmt(s.t_output)}` : '—'}</td>
                      <td style={{ padding: '7px 6px', color: full ? 'var(--green)' : 'var(--yellow)', fontWeight: 600 }}>{(s.status || '—').toUpperCase()}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--border)', color: 'var(--text)', fontWeight: 600 }}>
                  <td style={{ padding: '8px 6px', color: accent }}>TOTAL</td>
                  <td style={{ padding: '8px 6px', textAlign: 'right' }}>{fmt(t.order)}</td>
                  <td style={{ padding: '8px 6px', textAlign: 'right' }}>{fmt(t.cut)}</td>
                  <td style={{ padding: '8px 6px', textAlign: 'right' }}>{fmt(t.input)}</td>
                  <td style={{ padding: '8px 6px', textAlign: 'right' }}>{fmt(t.output)}</td>
                  <td style={{ padding: '8px 6px', textAlign: 'right', color: c }}>{fmt(t.ready)}</td>
                  <td style={{ padding: '8px 6px', textAlign: 'right' }}>{fmt(balPcs)}</td>
                  <td style={{ padding: '8px 6px', textAlign: 'right', color: 'var(--green)' }}>{(t.t_output || 0) !== 0 ? `+${fmt(t.t_output)}` : '—'}</td>
                  <td style={{ padding: '8px 6px', color: c }}>{t.ready_pct}%</td>
                </tr>
              </tfoot>
            </table>
            )}
            {onOrderClick && meta[t.tas_no] && (
              <div onClick={() => onOrderClick(meta[t.tas_no])}
                   style={{ marginTop: 10, textAlign: 'center', fontSize: 11, color: accent, cursor: 'pointer', letterSpacing: 1 }}>
                OPEN FULL ORDER →
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Group cards (for FACTORY / BUYER / MERCHANDISER / STATUS tabs).
  const renderGroupCards = (groups, by) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, padding: '8px 16px 24px' }}>
      {groups.length === 0 && <div style={{ gridColumn: '1/-1', padding: 30, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>No groups</div>}
      {groups.map(g => {
        const c = pctColor(g.pct);
        return (
          <div key={g.key}
               onClick={() => setDrillBy({ by, value: g.key })}
               style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderLeft: `3px solid ${c}`, borderRadius: 8, padding: 14, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 6 }}
               onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg3)'; }}
               onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg2)'; }}>
            <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 600 }}>{g.key}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text3)' }}>
              <span>{g.count} TAS</span>
              <span>{fmt(g.order)} pcs</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
              <span style={{ color: c }}>{g.pct}% ready</span>
              <span style={{ color: 'var(--text3)' }}>{fmt(g.ready)} ready</span>
            </div>
            <div style={{ height: 4, background: 'var(--border)', borderRadius: 2 }}>
              <div style={{ height: '100%', width: `${Math.min(100, g.pct)}%`, background: c, borderRadius: 2 }} />
            </div>
          </div>
        );
      })}
    </div>
  );

  // DAY-WISE view — filter by date, previous/next, daily avg, factory-wise output, total output
  const renderDayWiseView = () => {
    // Overall metrics across all recorded production days for daily average production pcs
    const validDays = (dateSummary || []).filter(d => (d.d_output || 0) > 0 || (d.d_cut || 0) > 0 || (d.d_input || 0) > 0 || (d.d_ready || 0) > 0);
    const totalDaysCount = validDays.length;
    const grandTotalOutput = validDays.reduce((acc, d) => acc + (d.d_output || 0), 0);
    const grandTotalCutting = validDays.reduce((acc, d) => acc + (d.d_cut || 0), 0);
    const avgOutputPerDay = totalDaysCount > 0 ? Math.round(grandTotalOutput / totalDaysCount) : 0;
    const avgCutPerDay = totalDaysCount > 0 ? Math.round(grandTotalCutting / totalDaysCount) : 0;
    const last7Days = validDays.slice(0, 7);
    const avg7DayOutput = last7Days.length > 0 ? Math.round(last7Days.reduce((acc, d) => acc + (d.d_output || 0), 0) / last7Days.length) : 0;

    // Data for the selected day
    const dayRows = dateDetail[selectedDay] || [];
    const isLoadingDay = !dateDetail[selectedDay] && dateLoading;

    const filteredRows = daySearch.trim()
      ? dayRows.filter(r => {
          const q = daySearch.trim().toLowerCase();
          return (
            (r.tasno && r.tasno.toLowerCase().includes(q)) ||
            (r.Style && r.Style.toLowerCase().includes(q)) ||
            (r.shade && r.shade.toLowerCase().includes(q)) ||
            (r.Factory && r.Factory.toLowerCase().includes(q))
          );
        })
      : dayRows;

    const dayTotals = dayRows.reduce((a, r) => ({
      cut: a.cut + (r.d_cut || 0),
      input: a.input + (r.d_input || 0),
      output: a.output + (r.d_output || 0),
      ready: a.ready + (r.d_ready || 0),
    }), { cut: 0, input: 0, output: 0, ready: 0 });

    const dayTasCount = new Set(dayRows.map(r => r.tasno).filter(Boolean)).size;
    const diffFromAvg = dayTotals.output - avgOutputPerDay;
    const pctFromAvg = avgOutputPerDay > 0 ? Math.round((diffFromAvg / avgOutputPerDay) * 100) : 0;

    // Factory-wise aggregation
    const facMap = new Map();
    for (const r of filteredRows) {
      const fName = (r.Factory || 'Unassigned Factory').trim();
      if (!facMap.has(fName)) {
        facMap.set(fName, {
          name: fName,
          cut: 0, input: 0, output: 0, ready: 0,
          tasSet: new Set(),
          styles: new Set(),
          rows: []
        });
      }
      const f = facMap.get(fName);
      f.cut += r.d_cut || 0;
      f.input += r.d_input || 0;
      f.output += r.d_output || 0;
      f.ready += r.d_ready || 0;
      if (r.tasno) f.tasSet.add(r.tasno);
      if (r.Style) f.styles.add(r.Style);
      f.rows.push(r);
    }
    const factoryList = Array.from(facMap.values()).sort((a, b) => b.output - a.output || b.input - a.input || b.cut - a.cut);

    const isToday = selectedDay === todayIso;

    return (
      <div style={{ paddingBottom: 32 }}>
        {/* --- 1. DAY-WISE FILTER & PREVIOUS / NEXT NAVIGATION --- */}
        <div style={{
          padding: '12px 16px', background: 'var(--bg2)', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap'
        }}>
          {/* Controls: Prev, Date Picker, Next, Today */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button
              onClick={() => stepDay(-1)}
              style={{
                padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border)',
                background: 'var(--bg)', color: 'var(--text)', fontSize: 12, fontWeight: 600,
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
              }}
              title="Previous Day"
            >
              <span>◀</span>
              <span>Previous</span>
            </button>

            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: '3px 10px'
            }}>
              <span style={{ fontSize: 13 }}>📅</span>
              <input
                type="date"
                value={selectedDay}
                onChange={e => { if (e.target.value) { setSelectedDay(e.target.value); setExpandedFac(null); } }}
                style={{
                  border: 'none', background: 'transparent', color: 'var(--text)',
                  fontSize: 13, fontWeight: 700, fontFamily: 'inherit', outline: 'none', cursor: 'pointer'
                }}
              />
            </div>

            <button
              onClick={() => stepDay(1)}
              style={{
                padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border)',
                background: 'var(--bg)', color: 'var(--text)',
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 5,
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
              }}
              title="Next Day"
            >
              <span>Next</span>
              <span>▶</span>
            </button>

            {!isToday && (
              <button
                onClick={() => { setSelectedDay(todayIso); setExpandedFac(null); }}
                style={{
                  padding: '6px 14px', borderRadius: 6, border: '1px solid var(--teal)',
                  background: 'rgba(0,106,78,0.1)', color: 'var(--teal)', fontSize: 12, fontWeight: 700, cursor: 'pointer'
                }}
              >
                Today
              </button>
            )}

            <div style={{ marginLeft: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>
                {new Date(selectedDay + 'T12:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
              {isToday && (
                <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 10, background: 'var(--green)', color: '#fff' }}>
                  TODAY
                </span>
              )}
            </div>
          </div>

          {/* Quick Active Days Strip */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600 }}>Active Days:</span>
            {(dateSummary || []).slice(0, 5).map(d => (
              <button
                key={d.prod_date}
                onClick={() => { setSelectedDay(d.prod_date); setExpandedFac(null); }}
                style={{
                  padding: '4px 8px', borderRadius: 12,
                  border: `1px solid ${selectedDay === d.prod_date ? accent : 'var(--border)'}`,
                  background: selectedDay === d.prod_date ? `${accent}22` : 'var(--bg)',
                  color: selectedDay === d.prod_date ? accent : 'var(--text2)',
                  fontSize: 11, fontWeight: selectedDay === d.prod_date ? 700 : 500,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4
                }}
              >
                <span>{new Date(d.prod_date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
                <span style={{ color: 'var(--green)', fontWeight: 700 }}>{fmt(d.d_output || 0)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* --- 2. SUMMARY KPI TILES (TOTAL OUTPUT & AVERAGE PRODUCTION PCS) --- */}
        <div style={{ padding: '16px 16px 8px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 14 }}>
          {/* TOTAL OUTPUT */}
          <div style={{ padding: '14px 16px', borderRadius: 8, background: 'var(--bg2)', border: '1px solid var(--border)', borderLeft: '4px solid var(--green)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', letterSpacing: 0.8, textTransform: 'uppercase' }}>
              Total Output
            </div>
            <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--green)', fontFamily: 'Inter, sans-serif', marginTop: 4, lineHeight: 1.1 }}>
              {fmt(dayTotals.output)} <span style={{ fontSize: 13, fontWeight: 600 }}>pcs</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
              {dayTotals.input > 0 ? `${Math.round((dayTotals.output / dayTotals.input) * 100)}% conversion vs line input (${fmt(dayTotals.input)} pcs)` : 'Total floor output stitched today'}
            </div>
          </div>

          {/* AVERAGE PRODUCTION PCS */}
          <div style={{ padding: '14px 16px', borderRadius: 8, background: 'var(--bg2)', border: '1px solid var(--border)', borderLeft: '4px solid var(--gold)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', letterSpacing: 0.8, textTransform: 'uppercase' }}>
                Average Production Pcs
              </div>
              {pctFromAvg !== 0 && (
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 10,
                  background: pctFromAvg > 0 ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                  color: pctFromAvg > 0 ? 'var(--green)' : 'var(--red)'
                }}>
                  {pctFromAvg > 0 ? `▲ +${pctFromAvg}%` : `▼ ${pctFromAvg}%`} vs avg
                </span>
              )}
            </div>
            <div style={{ fontSize: 26, fontWeight: 800, color: accent, fontFamily: 'Inter, sans-serif', marginTop: 4, lineHeight: 1.1 }}>
              {fmt(avgOutputPerDay)} <span style={{ fontSize: 13, fontWeight: 600 }}>pcs / day</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
              7-day moving avg: <strong style={{ color: 'var(--text2)' }}>{fmt(avg7DayOutput)} pcs</strong> · {totalDaysCount} active days
            </div>
          </div>

          {/* CUTTING & LINE INPUT */}
          <div style={{ padding: '14px 16px', borderRadius: 8, background: 'var(--bg2)', border: '1px solid var(--border)', borderLeft: '4px solid var(--teal)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', letterSpacing: 0.8, textTransform: 'uppercase' }}>
              Cutting & Line Input
            </div>
            <div style={{ display: 'flex', gap: 14, alignItems: 'baseline', marginTop: 4 }}>
              <div>
                <span style={{ fontSize: 11, color: 'var(--text3)' }}>Cut: </span>
                <span style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)' }}>{fmt(dayTotals.cut)}</span>
              </div>
              <div>
                <span style={{ fontSize: 11, color: 'var(--text3)' }}>Input: </span>
                <span style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)' }}>{fmt(dayTotals.input)}</span>
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
              Finished Ready: <strong style={{ color: 'var(--text2)' }}>{fmt(dayTotals.ready)} pcs</strong>
            </div>
          </div>

          {/* ACTIVE FLOOR SUMMARY */}
          <div style={{ padding: '14px 16px', borderRadius: 8, background: 'var(--bg2)', border: '1px solid var(--border)', borderLeft: '4px solid #0284c7' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', letterSpacing: 0.8, textTransform: 'uppercase' }}>
              Floor Summary
            </div>
            <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--text)', fontFamily: 'Inter, sans-serif', marginTop: 4, lineHeight: 1.1 }}>
              {factoryList.length} <span style={{ fontSize: 13, fontWeight: 600 }}>factories</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
              {dayTasCount} TAS orders · {dayRows.length} shade movements
            </div>
          </div>
        </div>

        {/* Filter search for day-wise */}
        {dayRows.length > 0 && (
          <div style={{ padding: '6px 16px 12px' }}>
            <input
              type="text"
              value={daySearch}
              onChange={e => setDaySearch(e.target.value)}
              placeholder="Filter by Factory, TAS number, style or shade…"
              style={{
                width: '100%', boxSizing: 'border-box', padding: '7px 12px',
                background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6,
                color: 'var(--text)', fontSize: 12, outline: 'none'
              }}
              onFocus={e => e.target.style.borderColor = accent}
              onBlur={e => e.target.style.borderColor = 'var(--border)'}
            />
          </div>
        )}

        {/* --- 3. FACTORY-WISE OUTPUT BREAKDOWN --- */}
        <div style={{ padding: '0 16px' }}>
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{
              padding: '12px 16px', borderBottom: '1px solid var(--border)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 16 }}>🏭</span>
                <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>
                  Factory-Wise Output Breakdown
                </div>
                <span style={{ fontSize: 11, color: 'var(--text3)' }}>({factoryList.length} active factories)</span>
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--green)' }}>
                Total Output: {fmt(dayTotals.output)} pcs
              </div>
            </div>

            {isLoadingDay ? (
              <div className="loading" style={{ padding: 32 }}>LOADING PRODUCTION DATA…</div>
            ) : factoryList.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
                <div>No production movement recorded for {new Date(selectedDay + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}.</div>
                {(dateSummary || []).length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <button
                      onClick={() => { setSelectedDay(dateSummary[0].prod_date); setExpandedFac(null); }}
                      style={{
                        padding: '6px 16px', borderRadius: 6, border: 'none',
                        background: accent, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer'
                      }}
                    >
                      Jump to Latest Production Day ({fmtDateIso(dateSummary[0].prod_date)}) ›
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, tableLayout: 'fixed', minWidth: 920 }}>
                  <colgroup>
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '13%' }} />
                    <col style={{ width: '11%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '11%' }} />
                    <col style={{ width: '5%' }} />
                  </colgroup>
                  <thead>
                    <tr style={{ background: 'var(--bg3)', color: 'var(--text3)', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left' }}>Factory</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right' }}>Daily Avg</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700 }}>Today vs Avg</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--green)', fontWeight: 700 }}>Today Output</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right' }}>Orders</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right' }}>Cut</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right' }}>Input</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right' }}>Ready</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right' }}>Output Share</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center' }}>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {factoryList.map((f, idx) => {
                      const sharePct = dayTotals.output > 0 ? Math.round((f.output / dayTotals.output) * 100) : 0;
                      const isExpanded = expandedFac === f.name;
                      const fAvg = facAvgMap[f.name] || (f.name.includes('FASHION SUMMIT') ? 2753 : f.name.includes('ALIZA') ? 2232 : f.name.includes('BASIC') ? 1749 : f.name.includes('ENERGY') ? 1518 : f.name.includes('ROSE') ? 593 : 0);
                      const pctOfAvg = fAvg > 0 ? Math.round((f.output / fAvg) * 100) : 0;
                      const diffPct = pctOfAvg - 100;
                      return (
                        <React.Fragment key={f.name}>
                          <tr
                            onClick={() => setExpandedFac(isExpanded ? null : f.name)}
                            style={{
                              borderBottom: '1px solid var(--grid-row-border)',
                              cursor: 'pointer',
                              background: isExpanded ? 'var(--bg3)' : idx % 2 === 0 ? 'var(--bg)' : 'transparent'
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                            onMouseLeave={e => e.currentTarget.style.background = isExpanded ? 'var(--bg3)' : idx % 2 === 0 ? 'var(--bg)' : 'transparent'}
                          >
                            <td style={{ padding: '10px 12px', fontWeight: 700, color: 'var(--text)', verticalAlign: 'middle', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              🏭 {f.name}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--text2)', verticalAlign: 'middle', fontWeight: 600 }}>
                              {fAvg > 0 ? `${fmt(fAvg)} pcs` : '—'}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', verticalAlign: 'middle' }}>
                              {fAvg > 0 ? (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
                                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>
                                    {pctOfAvg}%
                                  </span>
                                  <span style={{
                                    fontSize: 10, fontWeight: 700, padding: '2px 5px', borderRadius: 8,
                                    background: diffPct >= 0 ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                                    color: diffPct >= 0 ? 'var(--green)' : 'var(--red)',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {diffPct >= 0 ? `▲ +${diffPct}%` : `▼ ${diffPct}%`}
                                  </span>
                                </div>
                              ) : (
                                <span style={{ color: 'var(--text3)' }}>—</span>
                              )}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--green)', fontWeight: 800, fontSize: 13, verticalAlign: 'middle' }}>
                              {fmt(f.output)}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--text2)', verticalAlign: 'middle' }}>
                              {f.tasSet.size} TAS
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--text2)', verticalAlign: 'middle' }}>
                              {f.cut ? fmt(f.cut) : '—'}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--text2)', verticalAlign: 'middle' }}>
                              {f.input ? fmt(f.input) : '—'}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--text2)', verticalAlign: 'middle' }}>
                              {f.ready ? fmt(f.ready) : '—'}
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', verticalAlign: 'middle' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
                                <div style={{ width: 50, height: 6, borderRadius: 3, background: 'var(--border)', overflow: 'hidden' }}>
                                  <div style={{ width: `${Math.min(100, sharePct)}%`, height: '100%', background: 'var(--green)' }} />
                                </div>
                                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', minWidth: 32, textAlign: 'right' }}>{sharePct}%</span>
                              </div>
                            </td>
                            <td style={{ padding: '10px 12px', textAlign: 'center', color: accent, fontWeight: 700, verticalAlign: 'middle' }}>
                              {isExpanded ? '▲ Hide' : '▼ View'}
                            </td>
                          </tr>

                          {/* Expanded TAS breakdown for this factory */}
                          {isExpanded && (
                            <tr>
                              <td colSpan={10} style={{ padding: '8px 16px 16px', background: 'var(--bg)' }}>
                                <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, tableLayout: 'fixed' }}>
                                    <colgroup>
                                      <col style={{ width: '18%' }} />
                                      <col style={{ width: '22%' }} />
                                      <col style={{ width: '20%' }} />
                                      <col style={{ width: '10%' }} />
                                      <col style={{ width: '10%' }} />
                                      <col style={{ width: '10%' }} />
                                      <col style={{ width: '10%' }} />
                                    </colgroup>
                                    <thead>
                                      <tr style={{ background: 'var(--bg2)', color: 'var(--text3)', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>TAS Order</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Style</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Shade</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'right' }}>Cut</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'right' }}>Input</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--green)' }}>Output</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'right' }}>Ready</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {f.rows.map((r, rIdx) => {
                                        const ord = meta[String(r.tasno || '').trim()] || Object.values(meta).find(o => String(o.tas_no || '').trim() === String(r.tasno || '').trim());
                                        const clickable = !!ord;
                                        return (
                                          <tr
                                            key={rIdx}
                                            onClick={() => { if (clickable) { setOpenTas(ord.tas_no); setTab('all'); setSearchQ(ord.tas_no); } }}
                                            style={{ borderBottom: '1px solid var(--grid-row-border)', cursor: clickable ? 'pointer' : 'default' }}
                                            onMouseEnter={e => { if (clickable) e.currentTarget.style.background = 'var(--bg2)'; }}
                                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                                          >
                                            <td style={{ padding: '8px 12px', fontWeight: 700, color: clickable ? accent : 'var(--text)', verticalAlign: 'middle' }}>
                                              {r.tasno} {clickable ? '›' : ''}
                                            </td>
                                            <td style={{ padding: '8px 12px', color: 'var(--text2)', verticalAlign: 'middle', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.Style || '—'}</td>
                                            <td style={{ padding: '8px 12px', color: 'var(--text2)', verticalAlign: 'middle', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.shade || '—'}</td>
                                            <td style={{ padding: '8px 12px', textAlign: 'right', verticalAlign: 'middle' }}>{r.d_cut ? fmt(r.d_cut) : '—'}</td>
                                            <td style={{ padding: '8px 12px', textAlign: 'right', verticalAlign: 'middle' }}>{r.d_input ? fmt(r.d_input) : '—'}</td>
                                            <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--green)', fontWeight: 700, verticalAlign: 'middle' }}>{r.d_output ? fmt(r.d_output) : '—'}</td>
                                            <td style={{ padding: '8px 12px', textAlign: 'right', verticalAlign: 'middle' }}>{r.d_ready ? fmt(r.d_ready) : '—'}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    {(() => {
                      const totalActiveAvg = factoryList.reduce((acc, f) => {
                        const fAvg = facAvgMap[f.name] || (f.name.includes('FASHION SUMMIT') ? 2753 : f.name.includes('ALIZA') ? 2232 : f.name.includes('BASIC') ? 1749 : f.name.includes('ENERGY') ? 1518 : f.name.includes('ROSE') ? 593 : 0);
                        return acc + fAvg;
                      }, 0);
                      const totalPctOfAvg = totalActiveAvg > 0 ? Math.round((dayTotals.output / totalActiveAvg) * 100) : 0;
                      const totalDiffPct = totalPctOfAvg - 100;
                      return (
                        <tr style={{ background: 'var(--bg3)', borderTop: '2px solid var(--border)', fontWeight: 800 }}>
                          <td style={{ padding: '12px', color: 'var(--text)', verticalAlign: 'middle' }}>
                            TOTAL ({factoryList.length} Factories)
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right', color: 'var(--text)', verticalAlign: 'middle' }}>
                            {totalActiveAvg > 0 ? `${fmt(totalActiveAvg)} pcs` : '—'}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right', verticalAlign: 'middle' }}>
                            {totalActiveAvg > 0 ? (
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
                                <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--text)' }}>
                                  {totalPctOfAvg}%
                                </span>
                                <span style={{
                                  fontSize: 10, fontWeight: 700, padding: '2px 5px', borderRadius: 8,
                                  background: totalDiffPct >= 0 ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                                  color: totalDiffPct >= 0 ? 'var(--green)' : 'var(--red)',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {totalDiffPct >= 0 ? `▲ +${totalDiffPct}%` : `▼ ${totalDiffPct}%`}
                                </span>
                              </div>
                            ) : '—'}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right', color: 'var(--green)', fontSize: 14, verticalAlign: 'middle' }}>
                            {fmt(dayTotals.output)} pcs
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right', color: 'var(--text)', verticalAlign: 'middle' }}>
                            {dayTasCount} TAS
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right', color: 'var(--text)', verticalAlign: 'middle' }}>
                            {fmt(dayTotals.cut)}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right', color: 'var(--text)', verticalAlign: 'middle' }}>
                            {fmt(dayTotals.input)}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right', color: 'var(--text)', verticalAlign: 'middle' }}>
                            {fmt(dayTotals.ready)}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right', color: 'var(--text3)', verticalAlign: 'middle' }}>
                            100%
                          </td>
                          <td style={{ verticalAlign: 'middle' }} />
                        </tr>
                      );
                    })()}
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // DATE-WISE view — list of production days; click a day → its TAS×shade moves.
  const renderDateView = () => {
    if (selectedDate) {
      const rowsAll = dateDetail[selectedDate];
      // Scope to PENDING TAS only — the same set as the Order-in-Hand tab (meta).
      const pendingSet = new Set(Object.keys(meta).map(k => String(k).trim()));
      const rowsD = rowsAll ? rowsAll.filter(r => pendingSet.has(String(r.tasno || '').trim())) : rowsAll;
      const pt = (rowsD || []).reduce((a, r) => ({ c: a.c + (r.d_cut || 0), i: a.i + (r.d_input || 0), o: a.o + (r.d_output || 0), r: a.r + (r.d_ready || 0) }), { c: 0, i: 0, o: 0, r: 0 });
      return (
        <div>
          <div style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
            <button onClick={() => { setSelectedDate(null); setDayTasOpen(null); }} style={{ background: 'none', border: 'none', color: accent, fontSize: 20, cursor: 'pointer', padding: 0 }}>←</button>
            <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 18, color: accent }}>{fmtDateIso(selectedDate)}</div>
            {rowsD && rowsD.length > 0 && <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>{rowsD.length} pending TAS · ✂ {fmt(pt.c)} · ⮕ {fmt(pt.i)} · ⬇ {fmt(pt.o)} · ✅ {fmt(pt.r)}</div>}
          </div>
          {!rowsAll ? <div className="loading">LOADING…</div>
            : rowsD.length === 0 ? <div style={{ padding: 30, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>No pending-order movement on this day.</div>
              : (
                <div style={{ padding: '8px 16px 24px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead>
                      <tr style={{ color: 'var(--text3)', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                        <th style={{ padding: '6px 6px', fontWeight: 500 }}>TAS</th>
                        <th style={{ padding: '6px 6px', fontWeight: 500 }}>Shade</th>
                        <th style={{ padding: '6px 6px', fontWeight: 500, textAlign: 'right' }}>Cut</th>
                        <th style={{ padding: '6px 6px', fontWeight: 500, textAlign: 'right' }}>Input</th>
                        <th style={{ padding: '6px 6px', fontWeight: 500, textAlign: 'right', color: 'var(--green)' }}>Output</th>
                        <th style={{ padding: '6px 6px', fontWeight: 500, textAlign: 'right' }}>Ready</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rowsD.map((r, i) => {
                        const ord = meta[String(r.tasno || '').trim()] || Object.values(meta).find(o => String(o.tas_no || '').trim() === String(r.tasno || '').trim());
                        const clickable = !!ord;
                        const openProd = () => { if (!ord) return; const k = ord.tas_no; setOpenTas(k); setDayTasOpen(prev => prev === k ? null : k); };
                        return (
                        <tr key={i}
                            onClick={openProd}
                            style={{ borderBottom: '1px solid var(--grid-row-border)', color: 'var(--text2)', cursor: clickable ? 'pointer' : 'default' }}
                            onMouseEnter={e => { if (clickable) e.currentTarget.style.background = 'var(--bg2)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                          <td style={{ padding: '6px 6px', color: clickable ? accent : 'var(--text)', fontWeight: clickable ? 600 : 400 }}>
                            {r.tasno}{clickable ? ' ›' : ''}<div style={{ fontSize: 9, color: 'var(--text3)', fontWeight: 400 }}>{titleCase(r.Factory)}</div>
                          </td>
                          <td style={{ padding: '6px 6px' }}>{titleCase(r.shade)}</td>
                          <td style={{ padding: '6px 6px', textAlign: 'right' }}>{r.d_cut ? fmt(r.d_cut) : '—'}</td>
                          <td style={{ padding: '6px 6px', textAlign: 'right' }}>{r.d_input ? fmt(r.d_input) : '—'}</td>
                          <td style={{ padding: '6px 6px', textAlign: 'right', color: r.d_output > 0 ? 'var(--green)' : 'var(--text3)' }}>{r.d_output ? fmt(r.d_output) : '—'}</td>
                          <td style={{ padding: '6px 6px', textAlign: 'right' }}>{r.d_ready ? fmt(r.d_ready) : '—'}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
          {dayTasOpen && (() => {
            // Only THIS day's production for the clicked TAS (from the day rows).
            const dayRows = (rowsAll || []).filter(r => String(r.tasno || '').trim() === dayTasOpen);
            if (!dayRows.length) return null;
            const info = allTas.find(x => String(x.tas_no || '').trim() === dayTasOpen);
            const tt = dayRows.reduce((a, r) => ({ c: a.c + (r.d_cut || 0), i: a.i + (r.d_input || 0), o: a.o + (r.d_output || 0), r: a.r + (r.d_ready || 0) }), { c: 0, i: 0, o: 0, r: 0 });
            const th = { padding: '6px 6px', fontWeight: 500 };
            const td = { padding: '6px 6px', textAlign: 'right' };
            return (
              <div style={{ padding: '4px 16px 20px' }}>
                <div style={{ fontSize: 11, color: accent, letterSpacing: 0.5, margin: '8px 2px 4px', fontWeight: 600 }}>
                  {dayTasOpen} · {fmtDateIso(selectedDate)}{info ? ` · ${titleCase(info.buyer)} · ${titleCase(info.factory)}` : ''}
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <thead>
                    <tr style={{ color: 'var(--text3)', textAlign: 'right', borderBottom: '1px solid var(--border)' }}>
                      <th style={{ ...th, textAlign: 'left' }}>Shade</th>
                      <th style={th}>Cut</th><th style={th}>Input</th>
                      <th style={{ ...th, color: 'var(--green)' }}>Output</th><th style={th}>Ready</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dayRows.map((s, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--grid-row-border)', color: 'var(--text2)' }}>
                        <td style={{ padding: '6px 6px', color: 'var(--text)' }}>{titleCase(s.shade)}</td>
                        <td style={td}>{s.d_cut ? fmt(s.d_cut) : '—'}</td>
                        <td style={td}>{s.d_input ? fmt(s.d_input) : '—'}</td>
                        <td style={{ ...td, color: (s.d_output || 0) > 0 ? 'var(--green)' : 'var(--text3)' }}>{s.d_output ? fmt(s.d_output) : '—'}</td>
                        <td style={td}>{s.d_ready ? fmt(s.d_ready) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid var(--border)', color: 'var(--text)', fontWeight: 600 }}>
                      <td style={{ padding: '7px 6px', color: accent }}>TOTAL</td>
                      <td style={td}>{fmt(tt.c)}</td>
                      <td style={td}>{fmt(tt.i)}</td>
                      <td style={{ ...td, color: 'var(--green)' }}>{fmt(tt.o)}</td>
                      <td style={td}>{fmt(tt.r)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            );
          })()}
        </div>
      );
    }
    if (dateLoading || dateSummary == null) return <div className="loading">LOADING…</div>;
    if (dateSummary.length === 0) return <div style={{ padding: 30, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>No production history.</div>;

    // 'YYYY-MM' → 'Jul 2026'
    const fmtMonth = ym => new Date(ym + '-01T00:00:00').toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });

    // ── MONTH LEVEL ── group the daily summary into months (dateSummary is DESC,
    // so months come out newest-first). Click a month → drill into its day cards.
    if (!prodMonth) {
      const months = [], mi = {};
      for (const d of dateSummary) {
        const ym = String(d.prod_date).slice(0, 7);
        if (mi[ym] == null) { mi[ym] = months.length; months.push({ ym, days: 0, d_cut: 0, d_input: 0, d_output: 0, d_ready: 0 }); }
        const g = months[mi[ym]];
        g.days++; g.d_cut += d.d_cut || 0; g.d_input += d.d_input || 0; g.d_output += d.d_output || 0; g.d_ready += d.d_ready || 0;
      }
      months.sort((a, b) => (a.ym > b.ym ? 1 : a.ym < b.ym ? -1 : 0)); // oldest month first (ascending)
      return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12, padding: '8px 16px 24px' }}>
          {months.map(mo => (
            <div key={mo.ym}
                 onClick={() => setProdMonth(mo.ym)}
                 style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderLeft: '3px solid var(--gold)', borderRadius: 8, padding: 14, cursor: 'pointer' }}
                 onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'} onMouseLeave={e => e.currentTarget.style.background = 'var(--bg2)'}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div style={{ fontSize: 15, color: 'var(--text)', fontWeight: 600 }}>{fmtMonth(mo.ym)}</div>
                <div style={{ fontSize: 10, color: 'var(--text3)' }}>{mo.days} day{mo.days !== 1 ? 's' : ''} ›</div>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 11, color: 'var(--text3)', marginTop: 8 }}>
                <span>✂ {fmt(mo.d_cut)}</span>
                <span>⮕ {fmt(mo.d_input)}</span>
                <span style={{ color: 'var(--green)' }}>⬇ {fmt(mo.d_output)}</span>
                <span>✅ {fmt(mo.d_ready)}</span>
              </div>
            </div>
          ))}
        </div>
      );
    }

    // ── DATE LEVEL ── days within the chosen month; click a day → its detail.
    const monthDays = dateSummary.filter(d => String(d.prod_date).slice(0, 7) === prodMonth)
      .sort((a, b) => (a.prod_date > b.prod_date ? 1 : a.prod_date < b.prod_date ? -1 : 0)); // oldest day first
    const mtot = monthDays.reduce((a, d) => ({ c: a.c + (d.d_cut || 0), i: a.i + (d.d_input || 0), o: a.o + (d.d_output || 0), r: a.r + (d.d_ready || 0) }), { c: 0, i: 0, o: 0, r: 0 });
    return (
      <div>
        <div style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
          <button onClick={() => setProdMonth(null)} style={{ background: 'none', border: 'none', color: accent, fontSize: 20, cursor: 'pointer', padding: 0 }}>←</button>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 18, color: accent }}>{fmtMonth(prodMonth)}</div>
          <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>{monthDays.length} days · ✂ {fmt(mtot.c)} · ⮕ {fmt(mtot.i)} · ⬇ {fmt(mtot.o)} · ✅ {fmt(mtot.r)}</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12, padding: '8px 16px 24px' }}>
          {monthDays.map(d => (
            <div key={d.prod_date}
                 onClick={() => openDay(d.prod_date)}
                 style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderLeft: '3px solid var(--gold)', borderRadius: 8, padding: 14, cursor: 'pointer' }}
                 onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'} onMouseLeave={e => e.currentTarget.style.background = 'var(--bg2)'}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 600 }}>{fmtDateIso(d.prod_date)}</div>
                <div style={{ fontSize: 10, color: 'var(--text3)' }}>{d.tas_cnt} TAS</div>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 11, color: 'var(--text3)', marginTop: 8 }}>
                <span>✂ {fmt(d.d_cut)}</span>
                <span>⮕ {fmt(d.d_input)}</span>
                <span style={{ color: 'var(--green)' }}>⬇ {fmt(d.d_output)}</span>
                <span>✅ {fmt(d.d_ready)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Drilled into a group → list its TAS.
  if (drillBy) {
    return (
      <div className="screen">
        <Header subtitle={`TAS · PRODUCTION · ${drillBy.by.toUpperCase()}`} />
        <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
          <button onClick={() => setDrillBy(null)} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: accent }}>{drillBy.value}</div>
          <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>{visibleList.length} TAS</div>
        </div>
        <div style={{ padding: '8px 16px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {visibleList.map(renderTasCard)}
        </div>
      </div>
    );
  }

  // Header totals — overall production pipeline across the pending set.
  const totOrder  = filtered.reduce((a, t) => a + t.order, 0);
  const totCut    = filtered.reduce((a, t) => a + (t.cut || 0), 0);
  const totInput  = filtered.reduce((a, t) => a + (t.input || 0), 0);
  const totOutput = filtered.reduce((a, t) => a + (t.output || 0), 0);
  const totReady  = filtered.reduce((a, t) => a + t.ready, 0);
  const totPct = totOrder > 0 ? Math.round((totReady / totOrder) * 100) : 0;

  // Today's production movement (deltas vs yesterday's snapshot), summed.
  const tToday = filtered.reduce((a, t) => ({
    cut: a.cut + (t.t_cut || 0), input: a.input + (t.t_input || 0),
    output: a.output + (t.t_output || 0), ready: a.ready + (t.t_ready || 0),
  }), { cut: 0, input: 0, output: 0, ready: 0 });
  const nToday = filtered.filter(t => (t.t_cut || 0) + (t.t_input || 0) + (t.t_output || 0) + (t.t_ready || 0) !== 0).length;
  const anyToday = (tToday.cut + tToday.input + tToday.output + tToday.ready) !== 0;

  return (
    <div className="screen">
      <Header subtitle="TAS · PRODUCTION DETAILS" />
      <ExportBar
        reportKey={tab === 'day' ? 'tas-production-day' : 'tas-production'}
        title={tab === 'day' ? `TAS Production - Day Wise (${selectedDay})` : "TAS Production Details"}
        subtitle={[tab !== 'all' ? `group: ${tab}` : '', drillBy ? `${drillBy.by}: ${drillBy.value}` : '', searchQ ? `search: ${searchQ}` : ''].filter(Boolean).join(' | ')}
        sections={tab === 'day'
          ? [
              {
                name: `Factory Output (${selectedDay})`,
                columns: [
                  C('name', 'Factory'),
                  C('daily_avg', 'Daily Avg', 'int', true),
                  C('today_vs_avg', 'Today vs Avg %', 'pct'),
                  C('output', 'Today Output', 'int', true),
                  C('tasCount', 'TAS Orders', 'int', true),
                  C('cut', 'Cut', 'int', true),
                  C('input', 'Input', 'int', true),
                  C('ready', 'Ready', 'int', true),
                  C('share_pct', 'Output Share %', 'pct')
                ],
                rows: dateDetail[selectedDay] ? (() => {
                  const dayOutTot = (dateDetail[selectedDay] || []).reduce((acc, r) => acc + (r.d_output || 0), 0);
                  return Array.from((dateDetail[selectedDay] || []).reduce((m, r) => {
                    const f = (r.Factory || 'Unassigned').trim();
                    if (!m.has(f)) {
                      const fAvg = facAvgMap[f] || (f.includes('FASHION SUMMIT') ? 2753 : f.includes('ALIZA') ? 2232 : f.includes('BASIC') ? 1749 : f.includes('ENERGY') ? 1518 : f.includes('ROSE') ? 593 : 0);
                      m.set(f, { name: f, daily_avg: fAvg, today_vs_avg: 0, output: 0, tasCount: 0, _tas: new Set(), cut: 0, input: 0, ready: 0, share_pct: 0 });
                    }
                    const g = m.get(f);
                    g.cut += (r.d_cut || 0);
                    g.input += (r.d_input || 0);
                    g.output += (r.d_output || 0);
                    g.ready += (r.d_ready || 0);
                    if (r.tasno) g._tas.add(r.tasno);
                    g.tasCount = g._tas.size;
                    g.today_vs_avg = g.daily_avg > 0 ? Math.round((g.output / g.daily_avg) * 100) : 0;
                    g.share_pct = dayOutTot > 0 ? Math.round((g.output / dayOutTot) * 100) : 0;
                    return m;
                  }, new Map()).values());
                })() : []
              },
              {
                name: `Orders (${selectedDay})`,
                columns: [C('Factory', 'Factory'), C('tasno', 'TAS'), C('Style', 'Style'), C('shade', 'Shade'), C('d_cut', 'Cut', 'int', true), C('d_input', 'Input', 'int', true), C('d_output', 'Output', 'int', true), C('d_ready', 'Ready', 'int', true)],
                rows: dateDetail[selectedDay] || []
              }
            ]
          : [{ name: 'Orders', columns: PROD_COLS, rows: visibleList }]
        }
        source={{
          endpoint: tab === 'day' ? `/orders/production-daily?date=${selectedDay}` : '/orders/production-list',
          filters: [
            ...(searchQ ? [{ t: 'search', q: searchQ, keys: ['tas_no', 'buyer', 'factory', 'merchandiser', 'style_name'] }] : []),
            ...(drillBy && ['factory', 'buyer', 'merchandiser', 'status'].includes(drillBy.by)
                  ? [{ t: 'eq', k: drillBy.by, v: drillBy.value }] : []),
          ]
        }}
      />
      {/* Title bar */}
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
        <button
          onClick={handleBack}
          style={{
            background: 'none', border: 'none', color: accent, fontSize: 22,
            cursor: 'pointer', padding: 0, lineHeight: 1, display: 'flex', alignItems: 'center'
          }}
          title="Back to Garment TAS"
        >
          ←
        </button>
        <div>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: accent }}>
            {tab === 'day' ? "Today's Production" : "Production"}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>
            {tab === 'day' ? "Floor output and daily movement totals" : `Live floor status · ${filtered.length} pending orders`}
          </div>
        </div>
        {tab !== 'day' && (
          <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
            <div style={{ fontSize: 24, fontWeight: 600, color: pctColor(totPct), fontFamily: 'Inter, sans-serif', lineHeight: 1 }}>{totPct}%</div>
            <div style={{ fontSize: 9, letterSpacing: 1.5, color: 'var(--text3)', marginTop: 2 }}>OVERALL READY</div>
          </div>
        )}
      </div>

      {/* Overall production pipeline (the at-a-glance hero) — ONLY shown on All Orders or Grouped tabs */}
      {tab !== 'day' && !drillBy && (
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', background: 'var(--bg2)' }}>
          {stagePipeline({ order: totOrder, cut: totCut, input: totInput, output: totOutput, ready: totReady }, true)}
        </div>
      )}

      {/* TODAY'S PRODUCTION banner — appears on other tabs so user can jump to Today's Production */}
      {tab !== 'day' && anyToday && (
        <div
          onClick={() => { setTab('day'); setSelectedDay(todayIso); }}
          style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)', background: 'rgba(82,194,120,0.06)', cursor: 'pointer', transition: 'background 0.2s' }}
          title="Click to view full Day-Wise production details for Today"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 11, letterSpacing: 2, color: 'var(--green)', fontWeight: 600 }}>TODAY'S PRODUCTION</div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13, color: 'var(--text2)' }}>
              <span>✂ Cut <b style={{ color: 'var(--text)' }}>{fmt(tToday.cut)}</b></span>
              <span>⮕ Input <b style={{ color: 'var(--text)' }}>{fmt(tToday.input)}</b></span>
              <span>⬇ Output <b style={{ color: 'var(--green)' }}>{fmt(tToday.output)}</b></span>
              <span>✅ Ready <b style={{ color: 'var(--text)' }}>{fmt(tToday.ready)}</b></span>
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 11, color: 'var(--text3)' }}>{nToday} TAS updated today</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--green)' }}>View Day-Wise ›</span>
            </div>
          </div>
        </div>
      )}



      {/* Search */}
      {tab !== 'date' && tab !== 'day' && (
      <div style={{ padding: '8px 16px 4px' }}>
        <input
          type="text" value={searchQ} onChange={e => setSearchQ(e.target.value)}
          placeholder="Search TAS, buyer, merchandiser, factory, style…"
          style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 12, outline: 'none' }}
          onFocus={e => e.target.style.borderColor = accent}
          onBlur={e => e.target.style.borderColor = 'var(--border)'}
        />
      </div>
      )}

      {/* Content */}
      {tab === 'day' ? renderDayWiseView()
        : tab === 'date' ? renderDateView()
        : tab === 'all' ? (
          <div style={{ padding: '8px 16px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {visibleList.length === 0 && <div style={{ padding: 30, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>No production data for pending TAS.</div>}
            {visibleList.map(renderTasCard)}
          </div>
        ) : (
          renderGroupCards(groupBy(keyForTab[tab]), tab)
        )}
    </div>
  );
}

// PRE-ORDER PENDING view — TAS only.
// List of TAS where any 1_* (pre-order) routing stage has actual_dt missing.
// Click a TAS card → expand inline editor: each pending pre-order stage gets
// a date input + Save. Saving calls /api/orders/:tasno/preorder-stage.
// ============================================================
function PreorderPendingView({ setView, Header }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState('');
  const [openTas, setOpenTas] = useState(null); // tas_no currently expanded
  const [stageData, setStageData] = useState({}); // { [tas_no]: { loading, stages, saving:{}, savedMsg } }
  const [mode, setMode] = useState('update'); // 'update' = editable, 'view' = read-only schedule

  useEffect(() => {
    setLoading(true);
    api('/orders?type=garment')
      .then(d => {
        const rows = Array.isArray(d) ? d : [];
        setOrders(rows.filter(o => (o.preorder_pending_count || 0) > 0).sort(compareTas));
        setLoading(false);
      });
  }, []);

  const accent = 'var(--teal)';

  const q = searchQ.trim().toLowerCase();
  const list = !q ? orders : orders.filter(o => {
    const hay = `${o.tas_no || ''} ${titleCase(o.factory) || ''} ${titleCase(o.merchandiser) || ''} ${titleCase(o.buyer) || ''} ${titleCase(o.style_name) || ''}`.toLowerCase();
    return hay.includes(q);
  });

  function loadStages(tas_no) {
    setStageData(s => ({ ...s, [tas_no]: { ...(s[tas_no] || {}), loading: true } }));
    api(`/orders/${encodeURIComponent(tas_no)}/stages`)
      .then(d => {
        const all = Array.isArray(d) ? d : [];
        // 1_* preorder stages only — sorted by orders ascending (1_1, 1_2, ...)
        const preorder = all.filter(s => String(s.sort_order || '').startsWith('1_'))
                            .sort((a, b) => String(a.sort_order).localeCompare(String(b.sort_order)));
        // Seed inputDate only from actual_date (not planned). Estimated date is shown
        // separately on its own line so users don't accidentally save the estimate as actual.
        const initial = preorder.map(s => ({
          ...s,
          inputDate: s.actual_date ? String(s.actual_date).slice(0, 10) : ''
        }));
        setStageData(s => ({ ...s, [tas_no]: { loading: false, stages: initial } }));
      })
      .catch(err => setStageData(s => ({ ...s, [tas_no]: { loading: false, error: err.message || 'failed' } })));
  }

  function setInputDate(tas_no, stageId, val) {
    let stageRef = null;
    setStageData(s => {
      const cur = s[tas_no] || { stages: [] };
      const stages = cur.stages.map(st => {
        if (st.id !== stageId) return st;
        stageRef = { ...st, inputDate: val };
        return stageRef;
      });
      return { ...s, [tas_no]: { ...cur, stages } };
    });
    // Auto-save once the input has a valid YYYY-MM-DD value (type=date emits onChange only
    // on a complete valid date) — and also on clear (val === '').
    if (val === '' || /^\d{4}-\d{2}-\d{2}$/.test(val)) {
      // queue after state flush so saveStage sees the latest stages array
      setTimeout(() => saveStage(tas_no, { ...(stageRef || {}), id: stageId, inputDate: val }), 0);
    }
  }

  function saveStage(tas_no, stage) {
    const id = stage.id;
    setStageData(s => ({ ...s, [tas_no]: { ...(s[tas_no] || {}), saving: { ...((s[tas_no] && s[tas_no].saving) || {}), [id]: true } } }));
    api(`/orders/${encodeURIComponent(tas_no)}/preorder-stage`, {
      method: 'POST',
      body: JSON.stringify({ stageId: id, actualDate: stage.inputDate || null })
    }).then(r => {
      setStageData(s => {
        const cur = s[tas_no];
        const stages = cur.stages.map(st => st.id === id
          ? { ...st, actual_date: stage.inputDate || null,
                     applicable: r.newValues?.applicable ?? st.applicable, status: stage.inputDate ? 'done' : 'pending' }
          : st);
        return { ...s, [tas_no]: { ...cur, stages, saving: { ...cur.saving, [id]: false }, savedAt: { ...(cur.savedAt || {}), [id]: Date.now() } } };
      });
      // Bump the count on the master list so the card vanishes once all stages are done
      setOrders(prev => prev.map(o => {
        if (o.tas_no !== tas_no) return o;
        const cur = stageData[tas_no];
        const remaining = (cur?.stages || []).filter(st => {
          if (st.id === id) return !stage.inputDate;
          return !st.actual_date;
        }).length;
        return { ...o, preorder_pending_count: remaining };
      }));
    }).catch(err => {
      setStageData(s => ({ ...s, [tas_no]: { ...(s[tas_no] || {}), saving: { ...((s[tas_no] && s[tas_no].saving) || {}), [id]: false }, error: err.message || 'save failed' } }));
    });
  }

  if (loading) return (
    <div className="screen">
      <Header subtitle="TAS · PRE-ORDER PENDING" />
      <div className="loading">LOADING…</div>
    </div>
  );

  return (
    <div className="screen">
      <Header subtitle="TAS · PRE-ORDER PENDING" />
      <ExportBar reportKey="tas-preorder-pending" title="TAS Pre-Order Pending" subtitle={searchQ ? `search: ${searchQ}` : ''} sections={[{ name: 'Pending', columns: PREORDER_COLS, rows: list }]}
      source={{ endpoint: '/orders', params: { type: 'garment' },
        filters: [
          { t: 'gt', k: 'preorder_pending_count', v: 0 },
          ...(searchQ ? [{ t: 'search', q: searchQ, keys: ['tas_no', 'buyer', 'style_name', 'preorder_pending_names'] }] : []),
        ] }} />
      <div style={{ padding: '12px 20px 8px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
        <button onClick={() => setView('tas')} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: accent }}>Pre-Order Pending</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
            {mode === 'view'
              ? 'Read-only view of pre-order stage estimates and actuals.'
              : 'Click a TAS to expand. Pick the actual date for each completed stage — it saves automatically.'}
          </div>
        </div>
        <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>{list.length} TAS</div>
      </div>

      <div style={{ padding: '10px 16px 4px', display: 'flex', gap: 6, borderBottom: '1px solid var(--border)' }}>
        {[{key:'update',label:'UPDATE'},{key:'view',label:'VIEW'}].map(t => (
          <button key={t.key} onClick={() => setMode(t.key)}
            style={{
              background: mode === t.key ? `${accent}26` : 'transparent',
              border: `1px solid ${mode === t.key ? accent : 'var(--border)'}`,
              color: mode === t.key ? accent : 'var(--text3)',
              padding: '5px 14px', borderRadius: 14, fontSize: 11, cursor: 'pointer', letterSpacing: 1
            }}>{t.label}</button>
        ))}
      </div>

      <div style={{ padding: '8px 16px 4px' }}>
        <input
          type="text" value={searchQ} onChange={e => setSearchQ(e.target.value)}
          placeholder="Search TAS, buyer, factory, style…"
          style={{ width: '100%', boxSizing: 'border-box', padding: '8px 12px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 12, outline: 'none' }}
          onFocus={e => e.target.style.borderColor = accent}
          onBlur={e => e.target.style.borderColor = 'var(--border)'}
        />
      </div>

      <div style={{ padding: '8px 16px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {list.length === 0 && <div style={{ padding: 30, textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>All pre-order stages have actual dates ✓</div>}
        {list.map(o => {
          const isOpen = openTas === o.tas_no;
          const sd = stageData[o.tas_no];
          return (
            <div key={o.iid + '_' + o.tas_no}
                 style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderLeft: `3px solid ${accent}`, borderRadius: 8 }}>
              {/* Header row — click to expand */}
              <div onClick={() => { setOpenTas(isOpen ? null : o.tas_no); if (!isOpen && !sd) loadStages(o.tas_no); }}
                   style={{ padding: 12, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, color: 'var(--text)', fontWeight: 600 }}>{o.tas_no}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{titleCase(o.buyer)} · {titleCase(o.style_name) || '—'}</div>
                  <div style={{ fontSize: 10, color: 'var(--yellow)', marginTop: 4 }}>
                    ⏳ {o.preorder_pending_count} pending: {o.preorder_pending_names || '—'}
                  </div>
                </div>
                <div style={{ fontSize: 18, color: 'var(--text3)' }}>{isOpen ? '▾' : '▸'}</div>
              </div>

              {/* Expanded editor */}
              {isOpen && (
                <div style={{ padding: '0 12px 12px', borderTop: '1px solid var(--border)' }}>
                  {!sd || sd.loading
                    ? <div style={{ padding: 12, fontSize: 11, color: 'var(--text3)' }}>Loading stages…</div>
                    : sd.error
                      ? <div style={{ padding: 12, fontSize: 11, color: 'var(--red)' }}>Error: {sd.error}</div>
                      : (sd.stages || []).length === 0
                        ? <div style={{ padding: 12, fontSize: 11, color: 'var(--text3)' }}>No pre-order stages on this TAS.</div>
                        : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                            {/* Column headings — small uppercase labels so the user knows each column */}
                            {mode === 'view' ? (
                              <div className="preorder-head-view">
                                <div>Stage</div>
                                <div style={{ textAlign: 'center' }}>Estimated</div>
                                <div style={{ textAlign: 'right' }}>Actual</div>
                              </div>
                            ) : (
                              <div className="preorder-head-update">
                                <div>Stage</div>
                                <div style={{ textAlign: 'center' }}>Est. Date</div>
                                <div style={{ textAlign: 'center' }}>Actual Date</div>
                              </div>
                            )}
                            {sd.stages.map(st => {
                              const done = !!st.actual_date;
                              const saving = sd.saving?.[st.id];
                              const justSaved = sd.savedAt?.[st.id] && (Date.now() - sd.savedAt[st.id] < 3000);
                              return (
                                mode === 'view' ? (
                                  <div key={st.id} className="preorder-row-view" style={{ background: done ? 'rgba(82,194,120,0.05)' : 'transparent' }}>
                                    <div className="preorder-stage-name">{st.stage_name || st.sort_order}</div>
                                    <div className="preorder-est">est: {st.planned_date ? new Date(st.planned_date).toLocaleDateString('en-IN') : '—'}</div>
                                    <div className="preorder-actual" style={{ color: done ? 'var(--green)' : 'var(--text3)' }}>actual: {st.actual_date ? new Date(st.actual_date).toLocaleDateString('en-IN') : '—'}</div>
                                  </div>
                                ) : (
                                  <div key={st.id} className="preorder-row-update" style={{ background: done ? 'rgba(82,194,120,0.05)' : 'transparent' }}>
                                    <div className="preorder-stage-name">{st.stage_name || st.sort_order}</div>
                                    <div className="preorder-est">{st.planned_date ? new Date(st.planned_date).toLocaleDateString('en-IN') : '—'}</div>
                                    <input type="date" value={st.inputDate} onChange={e => setInputDate(o.tas_no, st.id, e.target.value)}
                                           style={{
                                             width: '100%', padding: '5px 8px', background: 'var(--bg)',
                                             border: `1px solid ${saving ? accent : justSaved ? 'var(--green)' : 'var(--border)'}`,
                                             borderRadius: 4, color: 'var(--text)', fontSize: 11, outline: 'none',
                                             transition: 'border-color 0.3s'
                                           }} />
                                  </div>
                                )
                              );
                            })}
                          </div>
                        )
                  }
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// OTIF view — TAS On-Time In-Full report (month-wise).
// Three sub-tabs (ALL / INFULL / ONTIME) showing the same monthly
// aggregation with different column slices.
//   InFull % = SUM(delivered_qty) / SUM(order_qty) × 100   (qty-weighted)
//   OnTime % = OnTime_count / (OnTime + Delayed) × 100     (count-based)
// ============================================================
function OtifView({ setView, Header, isTas = true }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all'); // 'all' | 'infull' | 'ontime'
  const [drillMonth, setDrillMonth] = useState(null); // {yr, mo, label} when drilled in
  const orderLabel = isTas ? 'TAS' : 'GSP';
  const apiPath    = isTas ? '/orders/otif' : '/orders/gsp-otif';
  const backView   = isTas ? 'tas' : 'gsp';

  useEffect(() => {
    setLoading(true);
    api(apiPath).then(d => { setData(d); setLoading(false); });
  }, [apiPath]);

  const accent = 'var(--green)';

  if (loading) return (
    <div className="screen">
      <Header subtitle={`${orderLabel} · OTIF`} />
      <div className="loading">LOADING OTIF…</div>
    </div>
  );

  const summary = data?.summary || {};
  const monthly = data?.monthly || [];
  const allRows = data?.rows    || [];
  const fyLabel = summary.fy_start
    ? `${new Date(summary.fy_start).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })} – ${new Date(summary.fy_end).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}`
    : 'Current FY';

  const monthLabel = (yr, mo) => new Date(yr, mo - 1, 1).toLocaleString('en-IN', { month: 'short' }) + ' ' + String(yr).slice(2);

  const pctColor = pct => pct == null ? 'var(--text3)' : pct >= 95 ? 'var(--green)' : pct >= 80 ? 'var(--yellow)' : 'var(--red)';

  // Totals across all months for footer row
  const totals = monthly.reduce((s, m) => ({
    order_qty:     s.order_qty     + (m.order_qty || 0),
    delivered_qty: s.delivered_qty + (m.delivered_qty || 0),
    on_time:       s.on_time       + (m.on_time || 0),
    delayed:       s.delayed       + (m.delayed || 0)
  }), { order_qty: 0, delivered_qty: 0, on_time: 0, delayed: 0 });
  const totInFullPct = totals.order_qty ? +(totals.delivered_qty * 100 / totals.order_qty).toFixed(2) : 0;
  const totOnTimeDen = totals.on_time + totals.delayed;
  const totOnTimePct = totOnTimeDen ? +(totals.on_time * 100 / totOnTimeDen).toFixed(2) : 0;

  // Each tab defines its column set (header + cell renderer)
  const cols = tab === 'infull' ? [
    { h: 'Month',         a: 'left',  cell: m => monthLabel(m.yr, m.mo),                            tot: 'TOTAL' },
    { h: 'Order Qty',     a: 'right', cell: m => (m.order_qty     || 0).toLocaleString(),           tot: totals.order_qty.toLocaleString() },
    { h: 'Delivered Qty', a: 'right', cell: m => (m.delivered_qty || 0).toLocaleString(),           tot: totals.delivered_qty.toLocaleString() },
    { h: 'InFull %',      a: 'right', cell: m => `${(+m.infull_pct).toFixed(2)}%`, color: m => pctColor(+m.infull_pct), tot: `${totInFullPct.toFixed(2)}%`, totColor: pctColor(totInFullPct) }
  ] : tab === 'ontime' ? [
    { h: 'Month',    a: 'left',  cell: m => monthLabel(m.yr, m.mo),                          tot: 'TOTAL' },
    { h: 'OnTime',   a: 'right', cell: m => (m.on_time || 0).toLocaleString(),               tot: totals.on_time.toLocaleString(),  totColor: 'var(--green)' },
    { h: 'Delayed',  a: 'right', cell: m => (m.delayed || 0).toLocaleString(),               tot: totals.delayed.toLocaleString(),  totColor: 'var(--red)' },
    { h: 'OnTime %', a: 'right', cell: m => `${(+m.ontime_pct).toFixed(2)}%`, color: m => pctColor(+m.ontime_pct), tot: `${totOnTimePct.toFixed(2)}%`, totColor: pctColor(totOnTimePct) }
  ] : [
    { h: 'Month',         a: 'left',  cell: m => monthLabel(m.yr, m.mo),                            tot: 'TOTAL' },
    { h: 'Order Qty',     a: 'right', cell: m => (m.order_qty     || 0).toLocaleString(),           tot: totals.order_qty.toLocaleString() },
    { h: 'Delivered Qty', a: 'right', cell: m => (m.delivered_qty || 0).toLocaleString(),           tot: totals.delivered_qty.toLocaleString() },
    { h: 'InFull %',      a: 'right', cell: m => `${(+m.infull_pct).toFixed(2)}%`, color: m => pctColor(+m.infull_pct), tot: `${totInFullPct.toFixed(2)}%`, totColor: pctColor(totInFullPct) },
    { h: 'OnTime',        a: 'right', cell: m => (m.on_time || 0).toLocaleString(),                 tot: totals.on_time.toLocaleString(),  totColor: 'var(--green)' },
    { h: 'Delayed',       a: 'right', cell: m => (m.delayed || 0).toLocaleString(),                 tot: totals.delayed.toLocaleString(),  totColor: 'var(--red)' },
    { h: 'OnTime %',      a: 'right', cell: m => `${(+m.ontime_pct).toFixed(2)}%`, color: m => pctColor(+m.ontime_pct), tot: `${totOnTimePct.toFixed(2)}%`, totColor: pctColor(totOnTimePct) }
  ];

  // Drill-down: per-TAS detail rows for the selected month
  if (drillMonth) {
    const mLabel = monthLabel(drillMonth.yr, drillMonth.mo);
    const detailRows = allRows.filter(r => {
      if (!r.invoice_dt) return false;
      const d = new Date(r.invoice_dt);
      return d.getUTCFullYear() === drillMonth.yr && (d.getUTCMonth() + 1) === drillMonth.mo;
    });
    return (
      <div className="screen">
        <Header subtitle={`${orderLabel} · OTIF · ${mLabel}`} />
        <ExportBar reportKey={`${orderLabel.toLowerCase()}-otif-month`} title={`${orderLabel} OTIF — ${mLabel}`} sections={[{ name: mLabel, columns: OTIF_ROW_COLS, rows: detailRows }]}
        source={{ endpoint: apiPath, path: 'rows',
          filters: [{ t: 'between', k: 'invoice_dt',
            from: `${drillMonth.yr}-${String(drillMonth.mo).padStart(2, '0')}-01`,
            to:   new Date(drillMonth.yr, drillMonth.mo, 0).toISOString().slice(0, 10) }] }} />
        <div style={{ padding: '12px 20px 8px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
          <button onClick={() => setDrillMonth(null)} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
          <div>
            <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: accent }}>OTIF — {mLabel}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{detailRows.length} invoiced {orderLabel}</div>
          </div>
        </div>
        <div style={{ padding: '8px 12px 24px' }}>
          {detailRows.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--text3)' }}>No data</div>
          ) : (
            <div className="grid-wrap" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '70vh', border: '1px solid var(--border)', borderRadius: 6 }}>
              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 12, minWidth: 880 }}>
                <thead>
                  <tr>
                    {[
                      [orderLabel,'left'],['Buyer','left'],['Style','left'],
                      ['Order Dt','left'],['Delivery Dt','left'],['Invoice Dt','left'],
                      ['Order Qty','right'],['Disp Qty','right'],['Fill %','right'],
                      ['Days Late','right'],['Status','center']
                    ].map(([h, a]) => (
                      <th key={h} style={{
                        position: 'sticky', top: 0, zIndex: 1,
                        background: 'var(--grid-head-bg)', color: 'var(--grid-head-text)',
                        padding: '10px', textAlign: a, borderBottom: '1px solid var(--grid-head-border)',
                        fontWeight: 600, fontSize: 11, letterSpacing: 0.4, whiteSpace: 'nowrap'
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {detailRows.map((r, i) => {
                    const zebra = i % 2 === 0 ? 'var(--grid-row)' : 'var(--grid-row-alt)';
                    const cell  = { padding: '9px 10px', borderBottom: '1px solid var(--grid-row-border)', whiteSpace: 'nowrap' };
                    const fillPct   = r.fill_rate != null ? Math.round(r.fill_rate * 100) : null;
                    const fillColor = fillPct == null ? 'var(--text3)' : fillPct >= 100 ? 'var(--green)' : fillPct >= 95 ? 'var(--yellow)' : 'var(--red)';
                    const lateColor = r.days_late > 0 ? 'var(--red)' : r.days_late === 0 ? 'var(--text3)' : 'var(--green)';
                    const statusBg  = r.otif ? 'var(--green-bg)' : 'var(--red-bg)';
                    const statusFg  = r.otif ? 'var(--green)' : 'var(--red)';
                    return (
                      <tr key={r.tas_no} style={{ background: zebra }}>
                        <td style={{ ...cell, color: 'var(--text)', fontWeight: 600 }}>{r.tas_no}</td>
                        <td style={{ ...cell, color: 'var(--text)' }}>{r.buyer || '—'}</td>
                        <td style={{ ...cell, color: 'var(--text2)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.style_name}>{r.style_name || '—'}</td>
                        <td style={{ ...cell, color: 'var(--text2)' }}>{r.order_dt    ? new Date(r.order_dt   ).toLocaleDateString('en-IN', { day:'2-digit', month:'2-digit', year:'numeric' }) : '—'}</td>
                        <td style={{ ...cell, color: 'var(--text2)' }}>{r.delivery_dt ? new Date(r.delivery_dt).toLocaleDateString('en-IN', { day:'2-digit', month:'2-digit', year:'numeric' }) : '—'}</td>
                        <td style={{ ...cell, color: 'var(--text2)' }}>{r.invoice_dt  ? new Date(r.invoice_dt ).toLocaleDateString('en-IN', { day:'2-digit', month:'2-digit', year:'numeric' }) : '—'}</td>
                        <td style={{ ...cell, textAlign: 'right', color: 'var(--text)' }}>{(r.orderqty || 0).toLocaleString()}</td>
                        <td style={{ ...cell, textAlign: 'right', color: 'var(--text)' }}>{(r.dspqty   || 0).toLocaleString()}</td>
                        <td style={{ ...cell, textAlign: 'right', color: fillColor, fontWeight: 600 }}>{fillPct == null ? '—' : `${fillPct}%`}</td>
                        <td style={{ ...cell, textAlign: 'right', color: lateColor }}>{r.days_late > 0 ? `+${r.days_late}` : (r.days_late === 0 ? 'on time' : `${r.days_late}`)}</td>
                        <td style={{ ...cell, textAlign: 'center' }}>
                          <span style={{ padding: '3px 8px', background: statusBg, color: statusFg, borderRadius: 10, fontSize: 10, letterSpacing: 1 }}>
                            {r.otif ? '✓ OTIF' : '✕'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="screen">
      <Header subtitle={`${orderLabel} · OTIF`} />
      <ExportBar reportKey={`${orderLabel.toLowerCase()}-otif`} title={`${orderLabel} OTIF`} subtitle={`FY ${fyLabel} | tab: ${tab}`} sections={[{ name: 'Monthly', columns: OTIF_MONTH_COLS, rows: monthly.map(m => ({ ...m, month: monthLabel(m.yr, m.mo) })) }, { name: 'Orders', columns: OTIF_ROW_COLS, rows: allRows }]} source={{ endpoint: apiPath, sections: [{ name: 'Monthly', path: 'monthly', columns: OTIF_MONTH_COLS_SRV }, { name: 'Orders', path: 'rows', columns: OTIF_ROW_COLS }] }} />
      <div style={{ padding: '12px 20px 8px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
        <button onClick={() => setView(backView)} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: accent }}>OTIF — On-Time In-Full</div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>FY: {fyLabel} · Click a month to view details</div>
        </div>
      </div>

      {/* Sub-tabs: ALL / INFULL / ONTIME */}
      <div style={{ padding: '12px 16px 4px', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {[{key:'all',label:'ALL'},{key:'infull',label:'INFULL'},{key:'ontime',label:'ONTIME'}].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              background: tab === t.key ? 'var(--green-bg)' : 'transparent',
              border: `1px solid ${tab === t.key ? accent : 'var(--border)'}`,
              color: tab === t.key ? accent : 'var(--text3)',
              padding: '5px 14px', borderRadius: 14, fontSize: 11, cursor: 'pointer', letterSpacing: 1
            }}>{t.label}</button>
        ))}
      </div>

      <div style={{ padding: '8px 12px 24px' }}>
        {monthly.length === 0 ? (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--text3)' }}>No invoiced {orderLabel} in this period</div>
        ) : (
          <div className="grid-wrap" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '70vh', border: '1px solid var(--border)', borderRadius: 6 }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 12, minWidth: tab === 'all' ? 720 : 480 }}>
              <thead>
                <tr>
                  {cols.map((c, i) => (
                    <th key={i} style={{
                      position: 'sticky', top: 0, zIndex: 1,
                      background: 'var(--grid-head-bg)', color: 'var(--grid-head-text)',
                      padding: '10px', textAlign: c.a, borderBottom: '1px solid var(--grid-head-border)',
                      fontWeight: 600, fontSize: 11, letterSpacing: 0.4, whiteSpace: 'nowrap'
                    }}>{c.h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {monthly.map((m, i) => {
                  const zebra = i % 2 === 0 ? 'var(--grid-row)' : 'var(--grid-row-alt)';
                  const cell = { padding: '9px 10px', borderBottom: '1px solid var(--grid-row-border)', whiteSpace: 'nowrap' };
                  return (
                    <tr key={`${m.yr}-${m.mo}`}
                        onClick={() => setDrillMonth({ yr: m.yr, mo: m.mo })}
                        style={{ background: zebra, cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                        onMouseLeave={e => e.currentTarget.style.background = zebra}>
                      {cols.map((c, j) => {
                        const val = c.cell(m);
                        const color = c.color ? c.color(m) : (j === 0 ? 'var(--text)' : 'var(--text)');
                        const weight = j === 0 || c.color ? 600 : 400;
                        return (
                          <td key={j} style={{ ...cell, textAlign: c.a, color, fontWeight: weight }}>
                            {j === 0 ? <span>{val} <span style={{ color: 'var(--teal)', fontSize: 10, fontWeight: 400 }}>›</span></span> : val}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {/* Totals row */}
                <tr style={{ background: 'var(--grid-total-bg)' }}>
                  {cols.map((c, j) => (
                    <td key={j} style={{
                      padding: '10px 10px', borderTop: '2px solid var(--grid-head-border)',
                      whiteSpace: 'nowrap', fontSize: 12, fontWeight: 700,
                      textAlign: c.a, color: c.totColor || (j === 0 ? 'var(--text)' : 'var(--text)'),
                      letterSpacing: j === 0 ? 1 : 0
                    }}>{c.tot}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// Module-level cache so re-mounts of Dashboard (e.g. user navigating to
// MY ORDERS and back) show the previous payload INSTANTLY while a fresh
// fetch runs in the background. The component remounts on every nav switch
// because App.js conditionally renders <Dashboard /> per `tab` — without this
// cache, every visit shows "LOADING WAR ROOM…" for 300-700ms.
let _dashCache = { data: null, season: null, style: null, forecast: null, t: 0 };
const DASH_TTL_MS = 60 * 1000; // 60s — beyond that, force a fresh load before showing stale

export default function Dashboard({ user, onLogout, onBuyerClick, onOrderClick, view: viewProp, setView: setViewProp, pushBack }) {
  const cachedFresh = _dashCache.data && (Date.now() - _dashCache.t < DASH_TTL_MS);
  const [data, setData] = useState(cachedFresh ? _dashCache.data : null);
  const [loading, setLoading] = useState(!cachedFresh);
  const [forecastCount, setForecastCount] = useState(_dashCache.forecast);
  const [seasonCount, setSeasonCount] = useState(_dashCache.season);
  const [styleCount, setStyleCount] = useState(_dashCache.style);
  const [bdSummary, setBdSummary] = useState(_dashCache.bd || null);
  const [localView, setLocalView] = useState('home'); // fallback if parent doesn't pass view
  const view    = viewProp    !== undefined ? viewProp    : localView;
  const setView = setViewProp !== undefined ? setViewProp : setLocalView;
  const [showPwModal, setShowPwModal] = useState(false);
  const [showScore, setShowScore] = useState(false);
  const [themeName, setThemeName] = useState(() => localStorage.getItem('dj_theme') || 'light');

  // Esc = Back. Every screen draws its own ← button and each knows where it goes
  // (a sub-view, then the section, then home), so Esc just triggers the visible one.
  // While typing, Esc leaves the field instead of navigating away.
  useEffect(() => {
    const onKey = e => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const t = e.target;
      const tag = (t && t.tagName) || '';
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(tag) || (t && t.isContentEditable)) { if (t.blur) t.blur(); return; }
      const back = Array.from(document.querySelectorAll('button'))
        .find(b => b.textContent.trim() === '←' && b.offsetParent !== null);
      if (back) { e.preventDefault(); back.click(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ── Quick-search (TAS / GSP / buyer) on the home view ──
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  useEffect(() => {
    const q = searchQ.trim();
    if (q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    const t = setTimeout(() => {
      api(`/orders/search?q=${encodeURIComponent(q)}&limit=10`)
        .then(r => { setSearchResults(Array.isArray(r) ? r : []); setSearching(false); })
        .catch(() => setSearching(false));
    }, 250); // debounce
    return () => clearTimeout(t);
  }, [searchQ]);

  useEffect(() => {
    // ALWAYS fetch fresh in the background — but if we already have cached
    // data we don't show a loading state, the tiles just update in place.
    let _retry = 0;
    const loadDash = () => api('/dashboard').then(d => {
      if (!d || !d.health) throw new Error('empty dashboard');
      setData(d);
      setLoading(false);
      _dashCache.data = d; _dashCache.t = Date.now();
    }).catch(() => {
      // Transient API blip (e.g. server restart) must NOT leave the user stuck
      // on "LOADING WAR ROOM" forever. Retry a few times, then fall back to any
      // cached payload so the screen always renders.
      if (_retry++ < 5) { setTimeout(loadDash, 1500); return; }
      if (_dashCache.data) { setData(_dashCache.data); setLoading(false); }
      else setLoading(false); // give up gracefully — show empty shell, not infinite spinner
    });
    loadDash();
    // Prefetch Order in Hand data in background so cache is warm by the time
    // the user clicks ORDER IN HAND (TAS list cold-load is ~1.7s otherwise).
    api('/orders?type=garment').catch(() => {});
    api('/orders?type=fabric').catch(() => {});
    api('/silhouettes').then(d => {
      if (!Array.isArray(d)) return;
      const season = d.length;
      // Show TOTAL PLANNED styles from forecasting (sum of noofstyle across all
      // seasons + sub-categories). Falls back to silhouette count if no plan data.
      const plannedTotal = d.reduce((s, g) => s + (g.planned_styles_total || 0), 0);
      const silhouetteTotal = d.reduce((s, g) => s + (g.silhouette_count || 0), 0);
      const style    = plannedTotal > 0 ? plannedTotal : silhouetteTotal;
      const forecast = silhouetteTotal;
      setSeasonCount(season); setStyleCount(style); setForecastCount(forecast);
      _dashCache.season = season; _dashCache.style = style; _dashCache.forecast = forecast;
    }).catch(() => {});
    api('/orders/bd-summary').then(d => { setBdSummary(d); _dashCache.bd = d; }).catch(() => {});
  }, []);

  if (loading) return (
    <>
      <div className="header">
        <div><div className="header-logo">RISHAB</div><div className="header-sub">Rishab World Private Limited</div></div>
        <div style={{ fontSize: 12, color: 'var(--text3)' }}>{user.name}</div>
      </div>
      <div className="loading">LOADING WAR ROOM...</div>
    </>
  );

  const { health, buyers } = data;
  const scoreClass = health.score < 35 ? 'critical' : health.score < 60 ? 'warning' : 'good';

  // Header (always shown). Shared button style keeps the header controls the same
  // height and vertically centred so the row lines up (fixed height + inline-flex).
  const hBtn = {
    height: 24, boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    background: 'none', border: '1px solid var(--border2)', color: 'var(--text3)',
    fontSize: 9, padding: '0 9px', borderRadius: 4, cursor: 'pointer', letterSpacing: 1, lineHeight: 1,
  };
  const Header = ({ subtitle }) => (
    <div className="header">
      <div><div className="header-logo">RISHAB</div><div className="header-sub">Rishab World Private Limited</div></div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 11, color: 'var(--text3)' }}>{user.name}</div>
        <div style={{ fontSize: 9, letterSpacing: 2, color: 'var(--text3)' }}>{subtitle || 'WAR ROOM'}</div>
        <div style={{ display: 'flex', gap: 6, marginTop: 4, justifyContent: 'flex-end', alignItems: 'center' }}>
          <NotificationBell onOrderClick={onOrderClick} onNavigate={v => setView(v)} btnStyle={hBtn} />
          {canSchedule() && <button onClick={() => setView('schedules')} style={hBtn} title="Scheduled reports, WhatsApp outbox, history">⏰ REPORTS</button>}
          <button onClick={() => setThemeName(toggleTheme())} style={hBtn} title="Switch light/dark theme">{themeName === 'dark' ? '☀ LIGHT' : '🌙 DARK'}</button>
          <button onClick={() => setShowPwModal(true)} style={hBtn} title="Change password">PASSWORD</button>
          <button onClick={onLogout} style={hBtn}>LOGOUT</button>
        </div>
      </div>
      {showPwModal && <ChangePasswordModal user={user} onClose={() => setShowPwModal(false)} />}
    </div>
  );

  if (view === 'schedules') return <SchedulesView Header={Header} setView={setView} />;
  if (view === 'size-ratio') return <SizeRatioView Header={Header} setView={setView} />;
  if (view === 'bom') return <BomView Header={Header} setView={setView} />;

  // ===== TAS / GSP MENU SCREEN: 3 sub-cards (Orders / Sales / Outstanding) =====
  if (view === 'tas' || view === 'gsp') {
    const isTas = view === 'tas';
    const accent = isTas ? 'var(--gold)' : 'var(--teal)';
    const cardBg = isTas ? 'rgba(212,185,120,0.04)' : 'rgba(183,155,201,0.04)';
    const cardBgHover = isTas ? 'var(--gold-bg)' : 'rgba(183,155,201,0.10)';
    const tagPrefix = isTas ? 'tas' : 'gsp';

    const subCard = (target, label, value, sub, accentLocal = accent, amount = null) => (
      <div
        key={target}
        onClick={() => setView(target)}
        style={{
          padding: '18px 20px', border: '1px solid var(--border)', borderRadius: 10, cursor: 'pointer',
          textAlign: 'center', flex: '1 1 160px', minWidth: 150, maxWidth: 260, height: 168, boxSizing: 'border-box',
          display: 'flex', flexDirection: 'column', justifyContent: 'center', transition: 'all 0.2s',
          background: cardBg
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = accentLocal; e.currentTarget.style.background = cardBgHover; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = cardBg; }}
      >
        <div style={{ color: accentLocal, fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 30 }}>{value}</div>
        <div style={{ color: accentLocal, fontSize: 12.5, fontWeight: 700, letterSpacing: 1.4, marginTop: 5 }}>{label}</div>
        <div style={{ color: 'var(--text3)', fontSize: 11, marginTop: 6 }}>{sub}</div>
        {/* Always reserve the amount line (blank when none) so every card is the same height. */}
        <div style={{ color: 'var(--text2)', fontSize: 11, marginTop: 2, minHeight: 15 }}>{amount != null ? amount : ' '}</div>
        <div style={{ color: 'var(--text3)', fontSize: 9, marginTop: 10, letterSpacing: 1 }}>OPEN →</div>
      </div>
    );

    return (
      <div className="screen">
        <Header subtitle={isTas ? 'TAS' : 'GSP'} />
        <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
          <button onClick={() => setView('rw')} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
          <div>
            <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: accent }}>
              {isTas ? `Garment (TAS) — ${health.tas_count || 0} orders` : `Fabric (GSP) — ${health.gsp_count || 0} orders`}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>
              {isTas ? `${fmt(health.pending_pcs)} pcs pending` : `${fmt(health.pending_mtrs)} mtrs pending`}
            </div>
          </div>
        </div>

        <div style={{ marginTop: 28, padding: '0 16px', display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
          {canSee(isTas ? 'screen.tas.order_in_hand' : 'screen.gsp.order_in_hand') && subCard(
            `${tagPrefix}-orders`,
            'ORDER IN HAND',
            fmt(isTas ? (health.tas_count || 0) : (health.gsp_count || 0)),
            isTas
              ? `${fmt(health.tas_total_pcs || 0)} pcs pending`
              : `${fmt(health.gsp_total_mtrs || 0)} mtrs pending`,
            accent,
            fmtCr(isTas ? health.tas_value : health.gsp_value)
          )}
          {canSee(isTas ? 'screen.tas.sales' : 'screen.gsp.sales') && (isTas
            ? subCard(`tas-sales`, 'SALES',
                fmt(health.sales_invoices || 0),
                `${fmt(health.sales_qty || 0)} pcs invoiced`,
                'var(--teal)',
                fmtCr(health.sales_amount))
            : subCard(`gsp-sales`, 'SALES',
                fmt(health.gsp_sales_invoices || 0),
                `${fmt(Math.round(health.gsp_sales_qty || 0))} mtrs invoiced`,
                'var(--teal)',
                fmtCr(health.gsp_sales_amount)))}
          {canSee(isTas ? 'screen.tas.cost_comparison' : 'screen.gsp.cost_comparison') && subCard(`${tagPrefix}-outstanding`, 'COST COMPARISON', '%',
            isTas ? 'Est · Sales · Invoice GP' : 'SO vs Invoice margin', 'var(--yellow)')}
          {isTas && canSee('screen.tas.production') && subCard(
            'tas-production',
            'TAS PRODUCTION',
            fmt(health.tas_count || 0),
            'Cutting · Input · Output · Ready',
            '#0284c7'
          )}
          {/* DAILY ACTIVITY — TAS only: Critical, Delayed, Size Ratio */}
          {isTas && canSee('screen.tas.daily_activity') && subCard('tas-daily', 'DAILY ACTIVITY', fmt((health.tas_critical || 0) + (health.tas_delayed || 0)),
            `${fmt(health.tas_critical || 0)} critical · ${fmt(health.tas_delayed || 0)} delayed`, 'var(--red)')}
        </div>

        {/* GSP-only cards */}
        {!isTas && canSee('screen.gsp.fabric_routing') && (
          <div style={{ padding: '20px 16px 4px', display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <FabricRoutingCard onOpen={() => setView('fabric-routing')} />
          </div>
        )}
      </div>
    );
  }

  // ===== TAS DAILY ACTIVITY — Critical, Delayed and Size Ratio =====
  if (view === 'tas-daily') {
    const card = (target, label, value, sub, color, amount) => (
      <div key={label} onClick={target ? () => setView(target) : undefined}
        style={{
          padding: '18px 20px', border: '1px solid var(--border)', borderLeft: `3px solid ${color}`, borderRadius: 10, cursor: target ? 'pointer' : 'default',
          textAlign: 'center', flex: '1 1 160px', minWidth: 150, maxWidth: 260, height: 168, boxSizing: 'border-box',
          display: 'flex', flexDirection: 'column', justifyContent: 'center', transition: 'all 0.2s', background: 'var(--bg2)'
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg3)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg2)'; }}
      >
        <div style={{ color, fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 30 }}>{value}</div>
        <div style={{ color, fontSize: 12.5, fontWeight: 700, letterSpacing: 1.4, marginTop: 5 }}>{label}</div>
        <div style={{ color: 'var(--text3)', fontSize: 11, marginTop: 6 }}>{sub}</div>
        <div style={{ color: 'var(--text2)', fontSize: 11, marginTop: 2, minHeight: 15 }}>{amount != null ? amount : ' '}</div>
        <div style={{ color: 'var(--text3)', fontSize: 9, marginTop: 10, letterSpacing: 1 }}>{target ? 'OPEN →' : 'COMING SOON'}</div>
      </div>
    );
    return (
      <div className="screen">
        <Header subtitle="TAS · DAILY ACTIVITY" />
        <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
          <button onClick={() => setView('tas')} style={{ background: 'none', border: 'none', color: 'var(--gold)', fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: 'var(--gold)' }}>Daily Activity</div>
        </div>
        <div style={{ marginTop: 28, padding: '0 16px', display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
          {canSee('screen.daily.critical') && card('tas-critical', 'CRITICAL', fmt(health.tas_critical || 0), `${fmt(health.tas_critical_pcs || 0)} pcs · stage overdue`, 'var(--red)', fmtCr(health.tas_critical_value))}
          {canSee('screen.daily.delayed') && card('tas-delayed', 'DELAYED', fmt(health.tas_delayed || 0), `${fmt(health.tas_delayed_pcs || 0)} pcs · delivery passed`, 'var(--yellow)', fmtCr(health.tas_delayed_value))}
          {canSee('screen.daily.size_ratio') && card('size-ratio', 'SIZE RATIO', '⇲', 'map pcs by size', 'var(--teal)')}
          {canSee('screen.daily.bom') && card('bom', 'BOM', '☰', 'bill of material', '#8b5cf6')}
          {card('tas-safta', 'SAFTA UPDATE', '🛂', 'duty-free coo update', '#0284c7')}
          {card('tas-routing-update', 'ROUTING UPDATE', '📅', 'manual stage actuals', '#6366f1')}
        </div>
      </div>
    );
  }

  // ===== TAS FABRIC ROUTING — TAS orders pending in fabric routing (Form 8056) =====
  if (view === 'tas-fabric-routing') {
    return <TasFabricRoutingView setView={setView} Header={Header} onOrderClick={onOrderClick} />;
  }

  // ===== FABRIC ROUTING — one section, two sub-tabs (TAS fabric routing + GSP routing) =====
  if (view === 'fabric-routing') {
    return <FabricRoutingTabsView setView={setView} Header={Header} onOrderClick={onOrderClick} />;
  }

  // ===== TAS-SALES / GSP-SALES — buyer-wise sales summary =====
  if (view === 'tas-sales' || view === 'gsp-sales') {
    const isTas = view === 'tas-sales';
    return <SalesView isTas={isTas} setView={setView} Header={Header} />;
  }

  // ===== TAS-OUTSTANDING / GSP-OUTSTANDING — Cost Comparison (3 stages per TAS) =====
  if (view === 'tas-outstanding' || view === 'gsp-outstanding') {
    const isTas = view === 'tas-outstanding';
    return <CostComparisonView isTas={isTas} setView={setView} Header={Header} />;
  }

  // ===== ORDER IN HAND: grouping tabs (Task / Month / Buyer / Brand / Merchandiser) =====
  if (view === 'tas-orders' || view === 'gsp-orders') {
    const isTas = view === 'tas-orders';
    return <OrderInHandView
      isTas={isTas}
      setView={setView}
      Header={Header}
      onOrderClick={onOrderClick}
      onBuyerClick={onBuyerClick}
      buyersFromHealth={buyers}
    />;
  }

  // ===== MONTHLY SHIPMENT TARGET — garment + fabric shipment plan table =====
  if (view === 'shipment-target') {
    return <MonthlyTargetView setView={setView} Header={Header} onOrderClick={onOrderClick} pushBack={pushBack} />;
  }

  // ===== FACTORY — factory-wise cards only (no Order-in-Hand tabs/branding) =====
  if (view === 'factory') {
    return <FactoryView setView={setView} Header={Header} />;
  }

  // ===== FACTORY cost sheets (placeholders — report details pending) =====
  if (view === 'factory-sales-cost' || view === 'factory-final-cost') {
    const isFinal = view === 'factory-final-cost';
    return (
      <div className="screen">
        <Header subtitle={isFinal ? 'FINAL COST SHEET' : 'SALES CONTRACT COST SHEET'} />
        <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
          <button onClick={() => setView('factory')} style={{ background: 'none', border: 'none', color: '#1A4F9C', fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
          <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: isFinal ? '#b5651d' : '#006a4e' }}>{isFinal ? 'Final Cost Sheet' : 'Sales Contract Cost Sheet'}</div>
        </div>
        <div style={{ textAlign: 'center', padding: '70px 24px', color: 'var(--text3)', fontSize: 13, lineHeight: 1.6 }}>
          {isFinal ? '📑' : '🧾'} {isFinal ? 'Final Cost Sheet' : 'Sales Contract Cost Sheet'}<br />
          Report coming soon — share the ERP report and I'll wire it here.
        </div>
      </div>
    );
  }

  // ===== CRITICAL drill-down: Delay_days < -7 =====
  if (view === 'tas-critical' || view === 'gsp-critical') {
    const isTas = view === 'tas-critical';
    return <OrderInHandView
      isTas={isTas}
      setView={setView}
      Header={Header}
      onOrderClick={onOrderClick}
      onBuyerClick={onBuyerClick}
      buyersFromHealth={buyers}
      criticalOnly={true}
      bucket="critical"
      backTo={isTas ? 'tas-daily' : undefined}
    />;
  }

  // ===== TAS PRE-ORDER PENDING — list + inline editor for 1_* routing stages =====
  if (view === 'tas-preorder') {
    return <PreorderPendingView setView={setView} Header={Header} />;
  }

  // ===== TAS PRODUCTION DETAILS — stage-wise tracker for all pending TAS =====
  if (view === 'tas-production') {
    return <ProductionDetailsView setView={setView} Header={Header} onOrderClick={onOrderClick} backTo="tas" />;
  }

  // ===== SHIPMENT — export shipment entry (TAS) =====
  if (view === 'tas-shipment') {
    return <ShipmentView setView={setView} Header={Header} />;
  }

  // ===== EXPORT — 3-stage workflow (Final Costing → Ex Fact → Shipment Update) =====
  if (view === 'tas-export') {
    return <ExportFlowView setView={setView} Header={Header} user={user} />;
  }

  // ===== TAS OTIF / GSP OTIF — On-Time In-Full report =====
  if (view === 'tas-otif' || view === 'gsp-otif') {
    return <OtifView isTas={view === 'tas-otif'} setView={setView} Header={Header} />;
  }

  // ===== DELAYED drill-down: Delay_days between -7 and -1 =====
  if (view === 'tas-delayed' || view === 'gsp-delayed') {
    const isTas = view === 'tas-delayed';
    return <OrderInHandView
      isTas={isTas}
      setView={setView}
      Header={Header}
      onOrderClick={onOrderClick}
      onBuyerClick={onBuyerClick}
      buyersFromHealth={buyers}
      criticalOnly={true}
      bucket="delayed"
      backTo={isTas ? 'tas-daily' : undefined}
    />;
  }

  // ===== LEGACY DRILL-DOWN (kept for reference, not reachable now) =====
  if (view === 'tas-orders-classic' || view === 'gsp-orders-classic') {
    const isTas = view === 'tas-orders-classic';
    const accent = isTas ? 'var(--gold)' : 'var(--teal)';
    const list = isTas
      ? buyers.filter(b => b.tas_count > 0).sort((a, b) => (b.tas_overdue - a.tas_overdue) || (b.pending_pcs - a.pending_pcs))
      : buyers.filter(b => b.gsp_count > 0).sort((a, b) => (b.gsp_overdue - a.gsp_overdue) || (b.pending_mtrs - a.pending_mtrs));
    const maxOver = Math.max(...list.map(b => isTas ? b.tas_overdue : b.gsp_overdue), 1);

    return (
      <div className="screen">
        <Header subtitle={isTas ? 'TAS · ORDERS' : 'GSP · ORDERS'} />

        {/* Back row — returns to TAS/GSP menu */}
        <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
          <button onClick={() => setView(isTas ? 'tas' : 'gsp')} style={{ background: 'none', border: 'none', color: accent, fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
          <div>
            <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: accent }}>
              {isTas ? `Garment (TAS) — ${health.tas_count || 0} orders` : `Fabric (GSP) — ${health.gsp_count || 0} orders`}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>
              {isTas ? `${fmt(health.pending_pcs)} pcs pending` : `${fmt(health.pending_mtrs)} mtrs pending`}
            </div>
          </div>
        </div>

        {/* Buyer-wise list */}
        <div className="card" style={{ marginTop: 12 }}>
          <div className="card-title">🔥 Buyer Fire Map</div>
          {list.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'center', padding: 20 }}>No {isTas ? 'TAS' : 'GSP'} orders</div>
          ) : list.map(b => {
            const overdue = isTas ? b.tas_overdue : b.gsp_overdue;
            const count   = isTas ? b.tas_count   : b.gsp_count;
            const pending = isTas ? b.pending_pcs : b.pending_mtrs;
            const unit    = isTas ? 'pcs' : 'mtrs';
            return (
              <div
                key={(isTas ? 'tas_' : 'gsp_') + b.buyer}
                style={{ marginBottom: 12, cursor: 'pointer', padding: '6px 8px', borderRadius: 6, transition: 'background 0.15s' }}
                onClick={() => onBuyerClick && onBuyerClick(b.buyer, isTas ? 'garment' : 'fabric')}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                  <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600 }} title={titleCase(b.buyer)}>{titleCase(b.buyer)}</div>
                  <div style={{ fontSize: 11, color: overdue > 0 ? 'var(--red)' : 'var(--text3)' }}>
                    {count} orders {overdue > 0 ? `· ${overdue} overdue` : ''}<span style={{ color: accent, marginLeft: 8 }}>→</span>
                  </div>
                </div>
                <div style={{ height: 5, borderRadius: 3, background: 'var(--border)' }}>
                  <div style={{ height: '100%', borderRadius: 3, background: overdue > 0 ? 'var(--red)' : accent, width: `${(overdue / maxOver) * 100}%` }} />
                </div>
                {pending > 0 && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 3 }}>{fmt(pending)} {unit}</div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ===== TAS STATUS — status / delivered / invoice list =====
  if (view === 'tas-status') {
    return <TasStatusView setView={setView} Header={Header} onOrderClick={onOrderClick} pushBack={pushBack} />;
  }

  // ===== STAGE & CAPTAIN LIFECYCLE MATRIX =====
  if (view === 'stage-matrix') {
    return <StageMatrixView setView={setView} Header={Header} />;
  }

  // ===== SAFTA UPDATE (RW DAILY ACTIVITY) =====
  if (view === 'tas-safta') {
    return <SaftaUpdateView setView={setView} Header={Header} />;
  }

  // ===== MANUAL ROUTING UPDATE (RW DAILY ACTIVITY) =====
  if (view === 'tas-routing-update') {
    return <ManualRoutingUpdateView setView={setView} Header={Header} />;
  }

  // ===== BD — Bangladesh team (Order in Hand + Upcoming Order) =====
  if (view === 'bd') {
    return <BdView setView={setView} Header={Header} />;
  }
  if (view === 'bd-orders' || view === 'bd-upcoming') {
    const up = view === 'bd-upcoming';
    return <BdOrdersView bucket={up ? 'upcoming' : 'in_hand'} titleTag={up ? 'BD · UPCOMING ORDER' : 'BD · ORDER IN HAND'}
      setView={setView} Header={Header} onOrderClick={onOrderClick} onBuyerClick={onBuyerClick} buyers={buyers} />;
  }
  if (view === 'bd-factory-assign') {
    return <BdFactoryAssignView setView={setView} Header={Header} />;
  }
  if (view === 'bd-sales-approval') {
    return <BdSalesApprovalView setView={setView} Header={Header} />;
  }
  if (view === 'bd-fi-inspection') {
    return <BdFiInspectionView setView={setView} Header={Header} />;
  }
  if (view === 'bd-final-approval') {
    return <BdFinalApprovalView setView={setView} Header={Header} />;
  }

  if (view === 'bd-shipment-update') {
    return <ShipmentUpdateView Header={Header} backTo={() => setView('bd')} />;
  }

  // ===== DEVELOPMENT — forecasting entries (inv_forecaste) =====
  if (view === 'development') {
    return <DevelopmentView setView={setView} Header={Header} />;
  }

  // ===== SEASONS — forecasting entries grouped by season =====
  if (view === 'seasons' || view === 'tas-season' || view === 'gsp-season') {
    return <DevelopmentView setView={setView} Header={Header} groupBy="season" />;
  }

  // ===== RW — garment/fabric cards (TAS, GSP, Development, TAS Status, Shipment Target) =====
  if (view === 'rw') {
    return <RwView setView={setView} Header={Header} health={health} seasonCount={seasonCount} styleCount={styleCount} />;
  }

  // ===== HOME VIEW: Health Score + RW / SBD / OPTUS / GMT FACTORY cards =====
  return (
    <div className="screen">
      <Header />
      {Array.isArray(buyers) && buyers.length > 0 && <ExportBar compact reportKey="war-room-buyers" title="War Room — Buyer Health" sections={[{ name: 'Buyers', columns: BUYER_HEALTH_COLS, rows: buyers }]} source={{ endpoint: '/dashboard', path: 'buyers' }} />}

      {/* Quick-search bar */}
      <div style={{ padding: '14px 20px 6px', position: 'relative' }}>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', fontSize: 14 }}>🔍</span>
          <input
            type="text"
            value={searchQ}
            onChange={e => setSearchQ(e.target.value)}
            placeholder="Search TAS / GSP number or buyer..."
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '10px 36px 10px 36px',
              background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8,
              color: 'var(--text)', fontSize: 13, outline: 'none', letterSpacing: 0.3
            }}
            onFocus={e => e.target.style.borderColor = 'var(--gold)'}
            onBlur={e => e.target.style.borderColor = 'var(--border)'}
          />
          {searchQ && (
            <span
              onClick={() => { setSearchQ(''); setSearchResults([]); }}
              style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', cursor: 'pointer', fontSize: 14 }}
            >✕</span>
          )}
        </div>

        {searchQ.trim().length >= 2 && (
          <div style={{
            position: 'absolute', top: '100%', left: 20, right: 20, marginTop: 4,
            background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8,
            maxHeight: 320, overflowY: 'auto', zIndex: 10,
            boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
          }}>
            {searching ? (
              <div style={{ padding: 14, color: 'var(--text3)', fontSize: 12, textAlign: 'center' }}>Searching…</div>
            ) : searchResults.length === 0 ? (
              <div style={{ padding: 14, color: 'var(--text3)', fontSize: 12, textAlign: 'center' }}>No matches</div>
            ) : (
              searchResults.map(o => {
                const isGsp = o.order_type === 'fabric';
                const accent = isGsp ? 'var(--teal)' : 'var(--gold)';
                return (
                  <div
                    key={o.iid + '_' + o.tas_no}
                    onClick={() => { track('order.search', { entityId: o.tas_no, details: { query: searchQ, picked: o.tas_no, buyer: o.buyer } }); onOrderClick && onOrderClick(o); setSearchQ(''); setSearchResults([]); }}
                    style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ color: 'var(--text)', fontSize: 13, fontWeight: 500 }}>{o.tas_no}</span>
                        <span style={{ fontSize: 9, color: accent, border: `1px solid ${accent}`, padding: '1px 5px', borderRadius: 3, letterSpacing: 1 }}>{isGsp ? 'GSP' : 'TAS'}</span>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{titleCase(o.buyer) || '—'}</div>
                    </div>
                    <div style={{ textAlign: 'right', marginLeft: 10 }}>
                      <div style={{ fontSize: 11, color: o.Delay_days < 0 ? 'var(--red)' : 'var(--text3)' }}>
                        {o.Delay_days < 0 ? `${Math.abs(o.Delay_days)}d overdue` : o.Delay_days <= 14 ? `${o.Delay_days}d left` : 'on track'}
                      </div>
                      {o.order_value > 0 && <div style={{ fontSize: 10, color: accent, marginTop: 2 }}>{fmtCr(o.order_value)}</div>}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* ─── War Room — clean professional KPI block ─── */}
      <div style={{ padding: '28px 16px 24px', textAlign: 'center' }}>
        {/* Health ring — click to see how the score is calculated */}
        <div
          onClick={() => setShowScore(true)}
          title="How is this calculated?"
          style={{
            width: 96, height: 96, margin: '0 auto 14px',
            borderRadius: '50%',
            border: `2px solid ${scoreClass === 'critical' ? 'var(--red)' : scoreClass === 'warning' ? 'var(--yellow)' : 'var(--green)'}`,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            background: 'var(--bg2)', cursor: 'pointer', transition: 'transform 0.15s'
          }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        >
          <div style={{
            fontFamily: 'Inter, sans-serif', fontSize: 32, fontWeight: 600, lineHeight: 1,
            color: scoreClass === 'critical' ? 'var(--red)' : scoreClass === 'warning' ? 'var(--yellow)' : 'var(--green)'
          }}>{health.score}</div>
          <div style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: 1.5, marginTop: 3, fontWeight: 500 }}>/ 100</div>
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginTop: 4 }}>
          Company Health Score
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4, fontWeight: 500 }}>
          ₹{((health.total_value || 0) / 10000000).toFixed(1)} Cr in pipeline
          {' · '}
          <span style={{ color: 'var(--green)' }}>
            ₹{(((health.sales_amount || 0) + (health.gsp_sales_amount || 0)) / 10000000).toFixed(1)} Cr sales
          </span>
        </div>
        <div onClick={() => setShowScore(true)} style={{ fontSize: 9, color: 'var(--teal)', marginTop: 6, letterSpacing: 1, cursor: 'pointer' }}>TAP SCORE FOR DETAILS →</div>
        {showScore && <ScoreDetailsModal health={health} onClose={() => setShowScore(false)} />}

        {/* KPI tiles — three distinct accents, sans-serif numbers, real card surface */}
        {/* Main page: 4 group cards. RW holds the garment/fabric cards (see view === 'rw'). */}
        <div style={{
          marginTop: 22,
          display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10,
          maxWidth: 520, marginLeft: 'auto', marginRight: 'auto'
        }}>
          {canSee('screen.home.rw') && (
            <KpiTile
              onClick={() => setView('rw')}
              accent="var(--gold)"
              num={fmt((health.tas_count || 0) + (health.gsp_count || 0))}
              label="RW"
              line1="TAS · GSP · development"
              line2={fmtCr(health.total_value)}
            />
          )}
          {canSee('screen.home.sbd') && (
            <KpiTile
              onClick={() => setView('bd')}
              accent="#006a4e"
              num={bdSummary ? fmt(bdSummary.in_hand?.count || 0) : 'SBD'}
              label="SBD"
              line1={bdSummary ? 'Bangladesh team · order in hand' : 'Bangladesh team'}
              line2={bdSummary ? `${fmt(bdSummary.upcoming?.count || 0)} upcoming` : 'view →'}
            />
          )}
          {canSee('screen.home.optus') && (
            <KpiTile
              accent="#8b5cf6"
              num="—"
              label="OPTUS"
              line1="coming soon"
              line2=""
            />
          )}
          {canSee('screen.home.gmt_factory') && (
            <KpiTile
              onClick={() => setView('factory')}
              accent="#b5651d"
              num="🏭"
              label="GMT FACTORY"
              line1="factory-wise orders"
              line2="view →"
            />
          )}
        </div>
      </div>
    </div>
  );
}

// RW screen — the garment/fabric cards that used to sit on the main page.
function RwView({ setView, Header, health, seasonCount, styleCount }) {
  return (
    <div className="screen">
      <Header subtitle="RW" />
      <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--border)' }}>
        <button onClick={() => setView('home')} style={{ background: 'none', border: 'none', color: 'var(--gold)', fontSize: 22, cursor: 'pointer', padding: 0 }}>←</button>
        <div style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: 22, color: 'var(--gold)' }}>RW</div>
      </div>
      <div style={{ padding: '22px 16px 24px' }}>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10,
          maxWidth: 520, marginLeft: 'auto', marginRight: 'auto'
        }}>
          {canSee('screen.rw.tas') && (
            <KpiTile
              onClick={() => setView('tas')}
              accent="var(--gold)"
              num={health.tas_count || 0}
              label="TAS"
              line1={`${fmt(health.pending_pcs)} pcs`}
              line2={fmtCr(health.tas_value)}
            />
          )}
          {canSee('screen.rw.gsp') && (
            <KpiTile
              onClick={() => setView('gsp')}
              accent="var(--teal)"
              num={health.gsp_count || 0}
              label="GSP"
              line1={`${fmt(health.pending_mtrs)} mtrs`}
              line2={fmtCr(health.gsp_value)}
            />
          )}
          {canSee('screen.rw.development') && (
            <KpiTile
              onClick={() => setView('development')}
              accent="var(--green)"
              num={seasonCount != null ? seasonCount : '—'}
              label="DEVELOPMENT"
              line1={seasonCount != null ? `${seasonCount} seasons` : 'coming soon'}
              line2={seasonCount != null ? `${fmt(styleCount || 0)} styles` : ''}
            />
          )}
          {canSee('screen.rw.tas_status') && (
            <KpiTile
              onClick={() => setView('tas-status')}
              accent="#8b5cf6"
              num="ALL"
              label="TAS STATUS"
              line1="orders & delivery"
              line2="invoice status"
            />
          )}
          {canSee('screen.rw.shipment_target') && <div>
            <KpiTile
              onClick={() => setView('shipment-target')}
              accent="#1A4F9C"
              num="📅"
              label="MONTHLY SHIPMENT TARGET"
              line1="garment + fabric · by FI / ex-factory date"
              line2="view →"
            />
          </div>}
          <div>
            <KpiTile
              onClick={() => setView('stage-matrix')}
              accent="#10b981"
              num="49"
              label="STAGE & CAPTAIN MATRIX"
              line1="49 lifecycle stages · captains"
              line2="centralized rules & criteria →"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// Reusable KPI tile for the War Room. Filled gray card (no border) matches
// the consumer-mobile-app aesthetic (Airtel/Paytm style).
function KpiTile({ onClick, accent, num, label, line1, line2 }) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: '20px 14px',
        background: 'var(--bg2)',
        border: 'none',
        borderRadius: 16,
        cursor: 'pointer',
        transition: 'background 0.15s, transform 0.15s',
        textAlign: 'center',
        minWidth: 0
      }}
      onMouseEnter={e => {
        e.currentTarget.style.background = 'var(--bg3)';
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = 'var(--bg2)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <div style={{
        fontFamily: 'Inter, sans-serif', fontSize: 30, fontWeight: 700,
        color: accent, lineHeight: 1, letterSpacing: '-0.02em'
      }}>{num}</div>
      <div style={{
        fontSize: 11, letterSpacing: 1.5, marginTop: 8, fontWeight: 700,
        color: 'var(--text2)', textTransform: 'uppercase',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
      }}>{label}</div>
      {line1 && (
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8, fontWeight: 500 }}>
          {line1}
        </div>
      )}
      {line2 && (
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3, fontWeight: 500 }}>
          {line2}
        </div>
      )}
    </div>
  );
}

// ─────────────── Change Password modal ───────────────
// Explains how the Company Health Score is computed, from the live health payload.
// score = (onTrack×1.0 + atRisk×0.5) / total × 100.
function ScoreDetailsModal({ health, onClose }) {
  const h = health || {};
  const total = h.total || 0;
  const onTrack = h.on_track || 0, atRisk = h.at_risk || 0, overdue = h.overdue || 0;
  const weighted = onTrack * 1.0 + atRisk * 0.5;
  const scoreColor = h.score < 35 ? 'var(--red)' : h.score < 60 ? 'var(--yellow)' : 'var(--green)';
  const Row = ({ label, val, weight, color, sub }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
        <span style={{ fontSize: 13, color: 'var(--text)' }}>{label}</span>
        {weight != null && <span style={{ fontSize: 10, color: 'var(--text3)' }}>× {weight}</span>}
      </div>
      <div style={{ textAlign: 'right' }}>
        <span style={{ fontSize: 15, fontWeight: 700, color }}>{fmt(val)}</span>
        {sub && <span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: 6 }}>{sub}</span>}
      </div>
    </div>
  );
  const pctOf = n => total > 0 ? Math.round(n * 100 / total) : 0;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg2)', borderRadius: 16, padding: '22px 22px 18px', width: '100%', maxWidth: 380, boxShadow: '0 10px 40px rgba(0,0,0,0.5)', textAlign: 'left' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Health Score</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: scoreColor }}>{h.score}<span style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 500 }}> / 100</span></div>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 14, lineHeight: 1.5 }}>
          Score = (On Track + ½ × At Risk) ÷ Total Orders × 100. Overdue orders count as 0.
        </div>

        <Row label="On Track" val={onTrack} weight="1.0" color="var(--green)" sub={`${pctOf(onTrack)}%`} />
        <Row label="At Risk" val={atRisk} weight="0.5" color="var(--yellow)" sub={`${pctOf(atRisk)}%`} />
        <Row label="Overdue" val={overdue} weight="0" color="var(--red)" sub={`${pctOf(overdue)}%`} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0 4px' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>Total Orders</span>
          <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{fmt(total)}</span>
        </div>

        <div style={{ background: 'var(--bg3)', borderRadius: 8, padding: '10px 12px', margin: '12px 0', fontSize: 12, color: 'var(--text2)' }}>
          ({fmt(onTrack)} + ½×{fmt(atRisk)}) ÷ {fmt(total)} × 100 = <b style={{ color: scoreColor }}>{total > 0 ? Math.round(weighted / total * 100) : 0}</b>
        </div>

        {/* TAS / GSP split */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
          {[['Garment (TAS)', h.tas_on_track, h.tas_at_risk, h.tas_overdue], ['Fabric (GSP)', h.gsp_on_track, h.gsp_at_risk, h.gsp_overdue]].map(([t, ot, ar, ov]) => (
            <div key={t} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px' }}>
              <div style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: 0.5, marginBottom: 6 }}>{t}</div>
              <div style={{ fontSize: 11, color: 'var(--green)' }}>On Track {fmt(ot || 0)}</div>
              <div style={{ fontSize: 11, color: 'var(--yellow)' }}>At Risk {fmt(ar || 0)}</div>
              <div style={{ fontSize: 11, color: 'var(--red)' }}>Overdue {fmt(ov || 0)}</div>
            </div>
          ))}
        </div>

        <button onClick={onClose} style={{ width: '100%', padding: '10px', border: 'none', borderRadius: 8, background: 'var(--teal)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', letterSpacing: 1 }}>CLOSE</button>
      </div>
    </div>
  );
}

function ChangePasswordModal({ user, onClose }) {
  const [cur, setCur]   = useState('');
  const [neu, setNeu]   = useState('');
  const [conf, setConf] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState('');
  const [ok, setOk]     = useState(false);

  function submit(e) {
    e?.preventDefault();
    setErr('');
    if (!cur || !neu || !conf) { setErr('All fields are required'); return; }
    if (neu.length < 4)        { setErr('New password must be at least 4 characters'); return; }
    if (neu !== conf)          { setErr('New password and confirmation do not match'); return; }
    if (cur === neu)           { setErr('New password must differ from current'); return; }

    setBusy(true);
    api('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword: cur, newPassword: neu })
    }).then(d => {
      setBusy(false);
      if (d?.success) {
        setOk(true);
        setTimeout(() => onClose(), 1400);
      } else {
        setErr(d?.error || 'Could not change password');
      }
    }).catch(() => { setBusy(false); setErr('Network error'); });
  }

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      backdropFilter: 'blur(2px)', zIndex: 400,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
    }}>
      <form
        onClick={e => e.stopPropagation()}
        onSubmit={submit}
        style={{
          background: 'var(--bg2)', borderRadius: 16, padding: '24px 22px 18px',
          width: '100%', maxWidth: 360, boxShadow: '0 10px 40px rgba(0,0,0,0.5)'
        }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--gold)', marginBottom: 4 }}>Change Password</div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 16 }}>
          Logged in as {user?.name}
        </div>

        {ok ? (
          <div style={{
            background: 'var(--green-bg)', color: 'var(--green)',
            padding: 14, borderRadius: 8, textAlign: 'center', fontSize: 13, fontWeight: 600
          }}>
            ✓ Password updated successfully
          </div>
        ) : (
          <>
            <Field label="Current password"  type="password" value={cur}  onChange={setCur}  autoFocus />
            <Field label="New password"      type="password" value={neu}  onChange={setNeu} />
            <Field label="Confirm new password" type="password" value={conf} onChange={setConf} />

            {err && (
              <div style={{
                background: 'var(--red-bg)', color: 'var(--red)',
                padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 12, marginTop: 4
              }}>{err}</div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <button type="button" onClick={onClose} style={{
                flex: 1, background: 'transparent', border: '1px solid var(--border2)',
                color: 'var(--text2)', padding: '10px', borderRadius: 8, cursor: 'pointer',
                fontSize: 13, fontFamily: 'inherit'
              }}>Cancel</button>
              <button type="submit" disabled={busy} style={{
                flex: 1, background: 'var(--gold)', border: 'none',
                color: 'var(--on-gold)', padding: '10px', borderRadius: 8, cursor: busy ? 'wait' : 'pointer',
                fontSize: 13, fontWeight: 700, fontFamily: 'inherit', opacity: busy ? 0.7 : 1
              }}>{busy ? 'Updating…' : 'Update'}</button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}

function Field({ label, type, value, onChange, autoFocus }) {
  const [reveal, setReveal] = useState(false);
  const isPassword = type === 'password';
  const effectiveType = isPassword && reveal ? 'text' : type;
  return (
    <label style={{ display: 'block', marginBottom: 10 }}>
      <div style={{ fontSize: 10, letterSpacing: 1.5, color: 'var(--text3)', marginBottom: 4, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ position: 'relative' }}>
        <input
          type={effectiveType}
          value={value}
          autoFocus={autoFocus}
          onChange={e => onChange(e.target.value)}
          style={{
            width: '100%', background: 'var(--bg)', border: '1px solid var(--border2)',
            borderRadius: 6, color: 'var(--text)',
            padding: isPassword ? '9px 40px 9px 12px' : '9px 12px',
            fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box'
          }}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setReveal(r => !r)}
            tabIndex={-1}
            title={reveal ? 'Hide password' : 'Show password'}
            style={{
              position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '6px 8px', lineHeight: 0,
              color: reveal ? 'var(--gold)' : 'var(--text3)',
              transition: 'color 0.15s'
            }}
          >{reveal ? <EyeOffIcon /> : <EyeIcon />}</button>
        )}
      </div>
    </label>
  );
}

// ─────────────── Eye icons (show / hide password) ───────────────
// Inline SVG so we don't pull in an icon library. Stroke uses currentColor
// so the parent button's color cascade controls tint (gold when revealed).
function EyeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  );
}
function EyeOffIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}
// Re-export for Admin.js to share
export { EyeIcon, EyeOffIcon };
